import React, { useState, useEffect, useRef } from 'react';
import { api, formatCategoryLabel, formatCategoryLabelShort, categoryIcon, cityIcon, imageUrl, phoneWithCountry, phoneForWhatsApp, numeriContatto, isWhatsAppHandle, flagUrl, SUBTYPE_OPTIONS, COMMUNITY_OPTIONS, CUISINE_OPTIONS, KOSHER_LEVELS, MIKVAH_LEVELS, SHABBAT_AMENITIES, SUPPORTS_SHABBAT_AMENITIES, AMENITY_GROUPS, HOTEL_CHAINS, effectiveKosherFood, kosherFoodMeta, isShabbatFriendly, categoryRank, CATEGORY_ORDER, resolvePickedAddress, DATED_TYPES, formatDateRange, matchSupervisionCerts, linkSupervisionTokens, parseBoundaryInput, hotelSearchNearUrl, AFFILIATE_ENABLED, shabbatUrl, zmanimUrl, guessTimezone, COUNTRY_CODES, openStatus, nextMinyan, formatDistance, foldName, nomeVisibile, tradotto } from './api';
import CategoryIcon, { StampIcon } from './CategoryIcon';
import ListSidebar from './ListSidebar';
import PlanYourTrip from './PlanYourTrip';
import UpdateBanner from './UpdateBanner';
import PrayerTimes from './PrayerTimes';
import OpeningHours from './OpeningHours';
import Menu from './Menu';
import BookingRequest from './BookingRequest';
import DestinationsPage, { vanityDomainFor, ShareThisPage } from './DestinationsPage';
import PlaceNews from './PlaceNews';
import { buildPlaceUrl, parsePlaceSlugPath, countryToCode, citySlug } from './slug';
// La riga del cambio di hechsher sta in un file suo: la usano la home,
// /kashrut-changes e la pagina News, e NewsPage.js e' importata da qui.
import KashrutChangeRow from './KashrutChangeRow';
import { tipoDaSlug, slugDaTipo } from './category-slugs';
import SupervisionPicker from './SupervisionPicker';
import RecommendedByPicker from './RecommendedByPicker';
import AreaPicker, { cityAndAreaFromPick } from './AreaPicker';
import ComboBox from './ComboBox';
import { t, getLang, setLang, LANGS, withLang, withLangPath } from './i18n';
import Breadcrumbs from './Breadcrumbs';
import MenuOrder from './MenuOrder';
import Linkify from './Linkify';

// Route-level pages are code-split (React.lazy) so they stay out of the
// initial bundle; a <Suspense> boundary around <main> covers them.
const CityGuide = React.lazy(() => import('./CityGuide'));
const TripPlanner = React.lazy(() => import('./TripPlanner'));
const RoutePlanner = React.lazy(() => import('./RoutePlanner'));
const Dashboard = React.lazy(() => import('./Dashboard'));
const MinyanPage = React.lazy(() => import('./MinyanPage'));
const NewsPage = React.lazy(() => import('./NewsPage'));
const ArticlePage = React.lazy(() => import('./ArticlePage'));

// THE MAP AND THE EDITING SUITE ARE NOT ON A READER'S PATH, and they were
// the two heaviest things in the bundle every visitor downloaded before
// seeing a single restaurant.
//
// Leaflet, its marker-cluster plugin and the React bindings arrive with
// MapPanel and CountryMap; react-image-crop arrives with ImageCropModal;
// and the owner/admin editors below are reachable only once you are signed
// in as the owner of a place. Somebody looking up where to eat in Rome paid
// for all of it.
//
// Each wrapper carries its OWN <Suspense>, so every call site below stays
// exactly as it was and none of them depends on sitting inside the boundary
// around <main> — a lazy component rendered outside one throws.
const lazily = (load, fallback = null) => {
  const Inner = React.lazy(load);
  const Wrapped = (props) => (
    <React.Suspense fallback={fallback}><Inner {...props} /></React.Suspense>
  );
  return Wrapped;
};
// UN PEZZO CHE SI ROMPE NON DEVE PORTARSI VIA LA PAGINA.
//
// React, davanti a un'eccezione in fase di render, smonta tutto l'albero: un
// errore dentro la mappa lascia bianco anche l'elenco dei risultati, che e la
// ragione per cui uno e su quella pagina. Nelle sessioni di Chrome
// automatizzato dell'audit /list andava in crash cosi (DOMException,
// insertBefore di React), e su un browser vero non si riproduce — ma il modo
// di rompersi e quello, e tanto vale che non possa piu succedere.
//
// Deve essere una classe: getDerivedStateFromError e componentDidCatch non
// esistono per i componenti a funzione.
class Riparo extends React.Component {
  constructor(props) { super(props); this.state = { rotto: false }; }

  static getDerivedStateFromError() { return { rotto: true }; }

  componentDidCatch(err, info) {
    // In console si vede comunque: nascondere l'errore all'utente non vuol
    // dire nasconderlo a chi deve ripararlo.
    // eslint-disable-next-line no-console
    console.error('[Riparo]', this.props.nome || 'componente', err, info && info.componentStack);
  }

  render() {
    if (!this.state.rotto) return this.props.children;
    if (this.props.silenzioso) return null;
    return (
      <div style={{ padding: '14px 16px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, color: '#6b7280', fontSize: 14 }}>
        {this.props.messaggio || t('This part could not be displayed.')}
      </div>
    );
  }
}

// A map that is about to exist should hold its space rather than let the
// page jump when it arrives.
const mapHolder = <div style={{ height: 400, background: '#eef1f4', borderRadius: 8 }} />;

const MapPanel = lazily(() => import('./MapPanel'), mapHolder);
const SingleMap = lazily(() => import('./MapPanel').then((m) => ({ default: m.SingleMap })), mapHolder);
const CountryMap = lazily(() => import('./CountryMap'), mapHolder);
const ImageCropModal = lazily(() => import('./ImageCropModal'));
const AddPlaceModal = lazily(() => import('./AddPlaceModal'));
const MenusManager = lazily(() => import('./MenusManager'));
const MenuEditor = lazily(() => import('./MenuEditor'));
const PlaceInventoryManager = lazily(() => import('./PlaceInventoryManager'));
const OwnersManager = lazily(() => import('./OwnersManager'));
const AddressVerify = lazily(() => import('./AddressVerify'));

// Flag image component (uses flagcdn.com PNGs — works everywhere, no emoji-font dependency)
function Flag({ country, code, size = 'h20', height = 14, style = {} }) {
  // Prefer the stable ISO country_code when the establishment carries one
  // (rename/spelling-proof); fall back to resolving the free-text name.
  const url = (code ? `https://flagcdn.com/${size}/${String(code).toLowerCase()}.png` : null) || flagUrl(country, size);
  if (!url) return <span style={{ ...style }}>🌍</span>;
  return (
    <img
      src={url}
      alt={`${country || ''} flag`}
      style={{ height, width: 'auto', verticalAlign: 'middle', borderRadius: 2, boxShadow: '0 0 0 1px rgba(0,0,0,0.1)', ...style }}
    />
  );
}

// SVG icon: Phone handset
// EmptyState — friendly placeholder for pages/tabs with no content yet.
// Use when a list has nothing to show — gives the user a clear next action
// instead of staring at blank space.
function EmptyState({ icon, title, message, actionLabel, onAction, secondaryLabel, onSecondary }) {
  return (
    <div style={{
      textAlign: 'center', padding: '48px 24px', maxWidth: 480, margin: '0 auto',
    }}>
      {icon && <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 16 }}>{icon}</div>}
      {title && <h3 style={{ color: '#1f2937', margin: '0 0 8px 0', fontSize: 20 }}>{title}</h3>}
      {message && <p style={{ color: '#6b7280', margin: '0 0 24px 0', lineHeight: 1.5 }}>{message}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            style={{ background: '#1a4d8f', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
          >
            {actionLabel}
          </button>
        )}
        {secondaryLabel && onSecondary && (
          <button
            onClick={onSecondary}
            style={{ background: 'white', color: '#1a4d8f', border: '1px solid #1a4d8f', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
          >
            {secondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// Skeleton — animated gray placeholder while real content loads. Drives its
// shimmer via the .jc-skeleton class in responsive.css (keyframes).
function Skeleton({ width = '100%', height = 14, radius = 6, style = {} }) {
  return (
    <span
      className="jc-skeleton"
      style={{ width, height, borderRadius: radius, display: 'block', ...style }}
      aria-hidden="true"
    />
  );
}

// SkeletonList — n vertical card skeletons, used while list-style pages load.
function SkeletonList({ count = 6 }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: 16, background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 12 }}>
          <Skeleton width={100} height={100} radius={8} />
          <div style={{ flex: 1 }}>
            <Skeleton width="60%" height={18} style={{ marginBottom: 8 }} />
            <Skeleton width="40%" height={12} style={{ marginBottom: 6 }} />
            <Skeleton width="30%" height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

function PhoneIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1.02 1.02 0 0 0-1.02.24l-2.2 2.2a15.045 15.045 0 0 1-6.59-6.59l2.2-2.21a.96.96 0 0 0 .25-1A11.36 11.36 0 0 1 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1zM19 12h2a9 9 0 0 0-9-9v2c3.87 0 7 3.13 7 7zm-4 0h2c0-2.76-2.24-5-5-5v2c1.66 0 3 1.34 3 3z"/>
    </svg>
  );
}

// SVG icon: Official WhatsApp logo
function WhatsAppIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
    </svg>
  );
}

// SVG icon: Envelope (email/contact)
function EmailIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/>
    </svg>
  );
}

// SVG icon: Heart (favorites). `filled` = saved/active, otherwise outline.
function HeartIcon({ size = 14, filled = false }) {
  return filled ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 21s-7.5-4.5-9.5-9.5C1.2 8 3.3 4.5 6.8 4.5c2 0 3.5 1 5.2 3 1.7-2 3.2-3 5.2-3 3.5 0 5.6 3.5 4.3 7-2 5-9.5 9.5-9.5 9.5z" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7.5-4.5-9.5-9.5C1.2 8 3.3 4.5 6.8 4.5c2 0 3.5 1 5.2 3 1.7-2 3.2-3 5.2-3 3.5 0 5.6 3.5 4.3 7-2 5-9.5 9.5-9.5 9.5z" />
    </svg>
  );
}

// SVG icon: Globe (website)
function WebIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95a15.65 15.65 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.987 7.987 0 0 1 5.08 16zm2.95-8H5.08a7.987 7.987 0 0 1 4.33-3.56A15.65 15.65 0 0 0 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"/>
    </svg>
  );
}

// Brand SVG logos — paths are the canonical "F", "in", camera-outline and X
// glyphs, simplified for round-badge use. Sized via the parent's currentColor
// so we can recolor without re-encoding.
const FacebookLogo  = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22 12a10 10 0 1 0-11.56 9.88V14.9H7.9V12h2.54V9.8c0-2.5 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.9h-2.33v6.98A10 10 0 0 0 22 12z"/>
  </svg>
);
const InstagramLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);
const XLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const LinkedInLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
  </svg>
);
const TikTokLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z"/>
  </svg>
);
const YouTubeLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M23.5 6.2a3 3 0 0 0-2.11-2.12C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.39.58A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.11 2.12C4.5 20.5 12 20.5 12 20.5s7.5 0 9.39-.58A3 3 0 0 0 23.5 17.8 31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6z"/>
  </svg>
);
const VKLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.131-.427.131-.427s-.02-1.304.576-1.496c.588-.19 1.341 1.26 2.14 1.818.604.422 1.063.33 1.063.33l2.137-.03s1.117-.071.587-.964c-.043-.073-.308-.661-1.588-1.87-1.34-1.264-1.16-1.059.453-3.246.983-1.332 1.376-2.145 1.253-2.493-.117-.332-.84-.244-.84-.244l-2.406.015s-.178-.025-.31.056c-.13.079-.212.262-.212.262s-.382 1.03-.89 1.907c-1.07 1.85-1.499 1.948-1.674 1.832-.407-.267-.305-1.075-.305-1.649 0-1.793.267-2.541-.521-2.733-.262-.064-.454-.106-1.123-.113-.858-.009-1.585.003-1.996.208-.274.137-.485.44-.356.457.159.022.519.099.71.365.246.343.237 1.114.237 1.114s.142 2.092-.331 2.352c-.325.179-.77-.186-1.724-1.866-.489-.86-.858-1.812-.858-1.812s-.071-.176-.2-.272c-.155-.115-.372-.151-.372-.151l-2.286.015s-.343.01-.469.161c-.112.135-.009.413-.009.413s1.79 4.198 3.817 6.313c1.858 1.939 3.968 1.811 3.968 1.811h.957z"/>
  </svg>
);

// Renders a row of small social-media icons for whichever URLs the place has set.
// Used on list cards (compact = 22px) and on the detail page (compact = false = 32px).
// Each link gets an aria-label so screen readers know the platform + place name.
const SOCIAL_PLATFORMS = [
  { key: 'facebook_url',  label: 'Facebook',  Logo: FacebookLogo,  bg: '#1877f2', base: 'https://www.facebook.com/' },
  { key: 'instagram_url', label: 'Instagram', Logo: InstagramLogo, bg: 'linear-gradient(135deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)', base: 'https://www.instagram.com/' },
  { key: 'youtube_url',   label: 'YouTube',   Logo: YouTubeLogo,   bg: '#FF0000', base: 'https://www.youtube.com/@' },
  { key: 'whatsapp_channel_url', label: 'WhatsApp Channel', Logo: WhatsAppIcon, bg: '#25D366', base: 'https://whatsapp.com/channel/' },
  { key: 'tiktok_url',    label: 'TikTok',    Logo: TikTokLogo,    bg: '#000000', base: 'https://www.tiktok.com/@' },
  { key: 'x_url',         label: 'X',         Logo: XLogo,         bg: '#000000', base: 'https://x.com/' },
  { key: 'linkedin_url',  label: 'LinkedIn',  Logo: LinkedInLogo,  bg: '#0a66c2', base: 'https://www.linkedin.com/' },
  { key: 'vk_url',        label: 'VK',        Logo: VKLogo,        bg: '#0077FF', base: 'https://vk.com/' },
];

// Build a working URL from whatever was entered: a full URL is used as-is; a
// scheme-less domain ("instagram.com/x", "www.x.com/y") gets https:// added;
// a bare handle ("@place" or "place") gets the platform's site URL prepended.
function socialHref(base, value) {
  const v = (value || '').trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  if (/^www\./i.test(v) || /[a-z0-9-]+\.[a-z]{2,}(\/|$)/i.test(v)) return `https://${v}`;
  return base + v.replace(/^[@/]+/, ''); // bare handle → platform base
}

function SocialIcons({ establishment, compact = false, user, onLogin }) {
  const items = SOCIAL_PLATFORMS
    .map((p) => ({ ...p, url: establishment[p.key] }))
    .filter((p) => p.url && p.url.trim().length > 0);
  if (items.length === 0) return null;

  const size = compact ? 26 : 32;
  const iconSize = compact ? 13 : 18;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 4 : 8 }}>
      {items.map((p) => {
        const url = socialHref(p.base, p.url);
        const { Logo } = p;
        // 22/10/2026, «open all locks»: le icone social si aprono per tutti.
        // Erano grigie per chi non aveva un account, come telefono e sito;
        // adesso che quelli sono aperti (services/gate-contacts.js) tenere
        // chiuse solo queste non proteggeva niente — l'account lo si chiede
        // ancora per scrivere una recensione o proporre una correzione, che
        // sono cose che uno FA, non cose che uno guarda.
        return (
          <a
            key={p.key}
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={`${p.label} — ${establishment.name}`}
            title={p.label}
            style={{
              width: size, height: size, background: p.bg, color: 'white',
              borderRadius: '50%', textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          >
            <Logo size={iconSize} />
          </a>
        );
      })}
    </span>
  );
}

// Category membership including secondary categories: true if `obj` (an
// establishment OR the editForm) is any of `types` via its primary `type` or
// any entry in `secondary_types`. Lets category-specific fields (cuisine, menu)
// show for a multi-category place under each of its categories — e.g. a kosher
// store that's also a restaurant still gets the cuisine + menu sections.
function hasAnyType(obj, types) {
  if (!obj) return false;
  const all = [obj.type, ...(Array.isArray(obj.secondary_types) ? obj.secondary_types : [])];
  return all.some((t) => t && types.includes(t));
}
const FOOD_SERVICE_TYPES = ['kosher_restaurant', 'kosher_eatery', 'kosher_catering', 'kosher_event_venue'];
// Types that can offer dine-in / takeaway (a superset — includes bakeries and the
// Shabbat-meal category, where "eat-in vs takeaway" is especially relevant).
const DINE_TYPES = ['kosher_restaurant', 'kosher_eatery', 'kosher_catering', 'kosher_event_venue', 'kosher_bakery', 'shabbat_meal'];
// Types where the Sukkot/Pesach seasonal flags are worth collecting — anywhere a
// traveler eats, stays, buys food, or prays, so "Open on Pesach" shows for hotels
// (Pesach programs!), bakeries, shops and synagogues (a communal sukkah, or a
// Chabad running Pesach services/seder), not just sit-down food service.
const SEASONAL_TYPES = ['synagogue_shul', 'kosher_restaurant', 'kosher_eatery', 'kosher_catering', 'kosher_event_venue', 'kosher_bakery', 'kosher_store', 'shabbat_meal', 'kosher_hotel', 'kosher_vacation'];
// Types whose enquiries go through Jewish Compass rather than the place's own
// phone / WhatsApp / website — those three buttons are hidden on the page, and
// the email button plus the contact form stay, so the place is still reachable.
//
// ONLY a tour guide belongs here: the guided tour IS the thing the site
// brokers, so its enquiries run through us.
//
//   NOT hotels or vacation rentals. They show phone, WhatsApp and website like
//   anyone else; the affiliate "Check availability" link sits alongside. (An
//   older comment here claimed lodging was brokered and then contradicted
//   itself two lines later — it had been stale since lodging was un-brokered.)
//   NOT a travel agency, and NOT a travel planner. Both are businesses you ring
//   up to arrange a trip; putting the phone number behind a contact form only
//   gets in the way. Their website link is still account-gated, but that is the
//   ordinary site-wide rule for every category, not brokering.
// NESSUN TIPO nasconde piu i contatti. Fino al 19/10/2026 le guide
// (jewish_tour) non mostravano ne telefono ne WhatsApp ne sito: si poteva
// solo scrivere col modulo. Menachem: «jewish tours still not showing
// whatsapp» — adesso la regola e una sola per tutti, il lucchetto
// dell'account (services/gate-contacts.js lato server), e il modulo
// contatti resta li accanto per chi preferisce scrivere.
const BROKERED_CONTACT_TYPES = [];
// Food & shopping categories that can take an "Order online" link (delivery /
// pickup / online store). Owner-editable — it's the business's own page.
const ORDER_URL_TYPES = ['kosher_restaurant', 'kosher_eatery', 'kosher_bakery', 'kosher_store', 'kosher_catering', 'judaica_store'];
// Booking a TABLE. Not a shop (nothing to reserve) and not lodging, which
// has booking_url and the affiliate disclosure sitting under it.
const RESERVATION_URL_TYPES = ['kosher_restaurant', 'kosher_eatery', 'kosher_bakery', 'kosher_catering', 'kosher_event_venue'];
// Booking a VISIT: somewhere you go to see, taste or learn, or a tour. Same
// reservation_url column, different words on the button. Matched on the
// secondary categories too: Kamisa's winery in Dalton is a shop first (the
// hechsher is on its wine) with a visitor centre booked through Tabit, and
// the shop category alone hid the field from the edit form.
const VISIT_BOOKING_TYPES = ['jewish_attraction', 'jewish_museum', 'jewish_tour', 'cooking_class'];
// "Reserve a table" unless the place is bookable only as a visit or a tour.
function reservationLabel(obj) {
  if (RESERVATION_URL_TYPES.includes(obj.type) || !hasAnyType(obj, VISIT_BOOKING_TYPES)) return 'Reserve a table';
  return obj.type === 'jewish_tour' ? 'Book a tour' : 'Book a visit';
}
// Where a gluten-free tick belongs: anywhere that serves or sells food.
// NOT the SEASONAL_TYPES list, which includes synagogues for the Pesach
// flag — reusing it would put a dietary question on every shul.
const GLUTEN_FREE_TYPES = ['kosher_restaurant', 'kosher_eatery', 'kosher_bakery', 'kosher_store',
  'kosher_catering', 'kosher_event_venue', 'kosher_hotel', 'kosher_vacation', 'shabbat_meal'];

// Dine-in and indoor/outdoor seating used to be three separate chips, so a
// restaurant with a terrace read "Dine-in · Indoor · Outdoor" — the first is
// implied by the other two. One chip says it once. `short` is the list-card
// wording; the place page has room for the longer phrase. Whole phrases are
// translated, not glued together from parts, so RTL reads correctly.
function seatingChips(e, short = false) {
  // Indoor and outdoor each get their own chip — they are two different things
  // to look for. What went is the third chip: "dine-in" is just indoor-or-
  // outdoor, so it never appears alongside them.
  const out = [];
  if (e.seating_indoor) out.push(['🪑', short ? 'Indoor' : 'Indoor dining']);
  if (e.seating_outdoor) out.push(['🌤️', short ? 'Outdoor' : 'Outdoor dining']);
  // Older rows say only that you can eat in, without saying where.
  if (!out.length && e.dine_in) out.push(['🍽️', 'Dine-in']);
  return out;
}

// Outbound actions reported to the owner, besides WhatsApp (always shown).
// Keep in step with CLICK_ACTIONS in routes/views.js — an action recorded but
// never displayed is a number nobody can act on.
const CLICK_LABELS = [
  ['phone', '📞', 'phone'],
  ['email', '✉️', 'email'],
  ['website', '🌐', 'website'],
  ['directions', '🧭', 'directions'],
  ['booking', '📅', 'booking'],
  ['order', '🛍️', 'order'],
  ['donate', '❤️', 'donate'],
  ['hotels_nearby', '🏨', 'hotels nearby'],
];

// Live "this is how it parsed" preview under the prayer-times textarea in the
// editor — calls the same backend parser used on save, debounced, so whoever
// enters times can confirm the day/season/zman breakdown before saving.
const PT_DAY_LABEL = {
  sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat',
  shabbat: 'Shabbat', holiday: 'Holiday', high_holiday: 'High Holiday',
  rosh_chodesh: 'Rosh Chodesh', fast: 'Fast', daily: 'Daily', weekday: 'Weekday',
};
const PT_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
function ptFmtDays(d) {
  if (!d || !d.length) return '';
  if (d.includes('daily') || d.length === 7) return 'Daily';
  const s = [...d].sort((a, b) => PT_ORDER.indexOf(a) - PT_ORDER.indexOf(b));
  const idx = s.map((x) => PT_ORDER.indexOf(x));
  const contiguous = s.length >= 3 && idx.every((v) => v >= 0) && idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  return contiguous ? `${PT_DAY_LABEL[s[0]]}–${PT_DAY_LABEL[s[s.length - 1]]}` : s.map((x) => PT_DAY_LABEL[x] || x).join(', ');
}
function ptFmtTime(t) {
  if (t === 'candle_lighting') return 'candle lighting';
  if (t === 'sundown') return 'sundown';
  if (t === 'plag_hamincha') return 'plag';
  const m = /^(\d{2}):(\d{2})$/.exec(t);
  if (!m) return t;
  let h = parseInt(m[1], 10); const p = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12; else if (h > 12) h -= 12;
  return `${h}:${m[2]} ${p}`;
}
function PrayerTimesPreview({ raw }) {
  const [parsed, setParsed] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const text = (raw || '').trim();
    if (!text) { setParsed(null); return undefined; }
    setLoading(true);
    const id = setTimeout(() => {
      api.previewPrayerTimes(text)
        .then((r) => setParsed(r))
        .catch(() => setParsed(null))
        .finally(() => setLoading(false));
    }, 500);
    return () => clearTimeout(id);
  }, [raw]);
  if (!raw || !raw.trim()) return null;
  const svcRows = (label, arr) => (arr && arr.length ? (
    <div style={{ marginBottom: 4 }}>
      <span style={{ fontWeight: 700 }}>{label}: </span>
      {arr.map((e, i) => (
        <span key={i}>
          {i > 0 && '; '}
          {/* Un minyan continuo non ha ore: senza questa riga l'anteprima
              mostrava i giorni e basta, e chi stava correggendo il testo
              credeva di aver perso gli orari. */}
          {ptFmtDays(e.days)} {e.continuous
            ? `🔁 ${e.continuous.every ? `${t('a minyan every')} ${e.continuous.every} ${t('min')}`
              : t('minyanim one after another')}, ${e.continuous.from} → ${e.continuous.until}`
            : (e.times || []).map(ptFmtTime).join(', ')}
          {e.season ? ` (${e.season})` : ''}
        </span>
      ))}
    </div>
  ) : null);
  const empty = parsed && !((parsed.shacharit || []).length || (parsed.mincha || []).length || (parsed.arvit || []).length || (parsed.kabbalat_shabbat || []).length || (parsed.notes || []).length);
  return (
    <div style={{ marginTop: 8, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: COLORS.text }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        {t("Preview — how this will be read")}{loading ? ' …' : ''}
      </div>
      {!parsed ? (
        <div style={{ color: COLORS.muted }}>{t('Parsing…')}</div>
      ) : empty ? (
        <div style={{ color: COLORS.muted }}>{t('No structured times detected — the raw text will be shown as-is on the page.')}</div>
      ) : (
        <>
          {svcRows('Shacharit', parsed.shacharit)}
          {svcRows('Mincha', parsed.mincha)}
          {svcRows('Kabbalat Shabbat', parsed.kabbalat_shabbat)}
          {svcRows('Arvit / Maariv', parsed.arvit)}
          {Array.isArray(parsed.notes) && parsed.notes.map((n, i) => (
            <div key={i} style={{ color: '#92400e', marginTop: 4 }}>ℹ️ {n}</div>
          ))}
        </>
      )}
    </div>
  );
}

// Feather "share-2" glyph.
const ShareIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

// Share a place. On mobile/PWA this opens the native share sheet (WhatsApp,
// Messages, …); everywhere else it copies the canonical slug URL to the
// clipboard and shows a brief "Copied" confirmation. The shared link carries
// per-place OG tags, so it previews nicely in chats.
function SharePlace({ establishment }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = () => {
    const path = buildPlaceUrl(establishment) || `/place/${establishment.id}`;
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : 'https://www.jewishcompass.com';
    return withLang(origin + path);
  };
  const onShare = async () => {
    const url = shareUrl();
    const title = nomeVisibile(establishment) || 'Jewish Compass';
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title, text: title, url }); return; }
      catch (e) { if (e && e.name === 'AbortError') return; } // user dismissed → do nothing
    }
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1600); };
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else { done(); }
  };
  return (
    <button
      type="button"
      onClick={onShare}
      title={t("Share this place")}
      aria-label={t('Share this place')}
      style={{
        background: 'white', color: copied ? '#059669' : COLORS.muted,
        border: `1px solid ${copied ? '#059669' : COLORS.border}`,
        padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 14,
        fontWeight: 600, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      <ShareIcon size={16} /> {copied ? t('Copied') : t('Share')}
    </button>
  );
}

// Generic "share this page" — shares the CURRENT url (so it works on the home
// page, any filtered list, the Minyanim page, News, etc.), not a specific place.
// `variant`: 'pill' (header nav, light-on-dark), 'drawer' (mobile menu row),
// 'inline' (a bordered button for placing inside page content).
function ShareButton({ url, title, variant = 'pill', style }) {
  const [copied, setCopied] = useState(false);
  const onShare = async () => {
    const shareUrl = withLang(url || (typeof window !== 'undefined' && window.location ? window.location.href : 'https://www.jewishcompass.com'));
    const shareTitle = title || (typeof document !== 'undefined' && document.title) || 'Jewish Compass';
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title: shareTitle, url: shareUrl }); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1600); };
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl).then(done).catch(done);
    } else { done(); }
  };
  const label = copied ? t('Copied') : t('Share');
  const longLabel = copied ? t('Copied') : t('Share this page');
  const base = { cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 };
  const styles_ = {
    pill: { ...base, color: 'white', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999, padding: '6px 14px', fontSize: 14, fontWeight: 600 },
    drawer: { ...base, gap: 12, width: '100%', textAlign: 'left', padding: '14px 18px', fontSize: 16, background: 'none', border: 'none', borderBottom: `1px solid ${COLORS.border}`, color: COLORS.text },
    // Account-dropdown row (desktop): matches the other menu items' look.
    menuitem: { ...base, gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', fontSize: 14, background: 'none', border: 'none', color: copied ? '#059669' : COLORS.text },
    inline: { ...base, color: copied ? '#059669' : COLORS.muted, background: 'white', border: `1px solid ${copied ? '#059669' : COLORS.border}`, padding: '8px 14px', borderRadius: 6, fontSize: 14, fontWeight: 600 },
  };
  const long = variant === 'drawer' || variant === 'menuitem';
  return (
    <button type="button" role={variant === 'menuitem' ? 'menuitem' : undefined} onClick={onShare} title={t('Share this page')} aria-label={t('Share this page')}
            onMouseEnter={variant === 'menuitem' ? (e) => (e.currentTarget.style.background = COLORS.bg) : undefined}
            onMouseLeave={variant === 'menuitem' ? (e) => (e.currentTarget.style.background = 'none') : undefined}
            style={{ ...styles_[variant], ...style }}>
      <ShareIcon size={variant === 'drawer' ? 18 : 16} /> {long ? longLabel : label}
    </button>
  );
}

// Responsiveness trust badge — the % of contact messages a place replies to.
// The API only sends reply_rate for genuinely responsive places (>50%, ≥3
// messages), so we just render it whenever it's present.
function ReplyRateBadge({ rate, size = 12 }) {
  if (rate == null) return null;
  return (
    <span
      title={t("Share of visitor messages this place has replied to")}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontSize: size, fontWeight: 700, whiteSpace: 'nowrap' }}
    >
      {t("💬 Replies to")} {rate}{t("% of messages")}
    </span>
  );
}

// Kashrut certifier logos for a place. Matches the place's supervision text to
// the known certifier entities (whole-token match) and shows each one's logo,
// linking to the certifier's page. Renders nothing when no matched certifier
// has a logo. `certs` is the kosherCertifications() list; `onOpen(cert)` (opt)
// hooks SPA navigation, else it falls back to an /place/:id href.
function CertLogos({ supervision, certifierIds, certs, onOpen, size = 30, showName = false }) {
  // Show every matched certifier. Those with an uploaded logo show it; those
  // without fall back to the site's kosher stamp so they still read as a
  // certification badge (and stay clickable through to the certifier's page).
  // Prefer the stable id link so a renamed certifier keeps its logo.
  const matched = matchSupervisionCerts(supervision, certs, certifierIds);
  if (matched.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {matched.map((c) => (
        <a
          key={c.id}
          href={withLangPath(`/place/${c.id}`)}
          onClick={(e) => { certFocusTarget = c.id; if (onOpen) { e.preventDefault(); e.stopPropagation(); onOpen(c); } else { e.stopPropagation(); } }}
          title={`Certified by ${c.name}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', color: 'inherit' }}
        >
          {c.main_image_url ? (
            <img
              src={imageUrl(c.main_image_url)}
              alt={`${c.name} kosher certification`}
              loading="lazy"
              style={{ height: size, width: 'auto', maxWidth: size * 2.6, objectFit: 'contain', background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}
            />
          ) : (
            <span
              aria-label={`${c.name} kosher certification`}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: size, width: size, color: '#1a4d8f', background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}
            >
              <StampIcon size={`${Math.round(size * 0.66)}px`} />
            </span>
          )}
          {showName && <span style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</span>}
        </a>
      ))}
    </span>
  );
}

// True when a place is linked to its certifier by the stable id, whether or
// not anyone also wrote the certifier's name into the free-text `supervision`.
// A council register gives us the id and nothing else, so a block that gates
// on the text alone hides a hechsher we do hold.
const hasCertifierLink = (est) => Array.isArray(est && est.supervision_certifier_ids)
  && est.supervision_certifier_ids.length > 0;

// True when an establishment acts as a kosher certifier — by primary type OR as
// a secondary role (e.g. a Chabad house that is ALSO a hechsher). Used so the
// "Places we certify" view shows for certifiers regardless of primary type.
const isCertifierEst = (est) => !!est && (
  est.type === 'kosher_certification_hechsher'
  || (Array.isArray(est.secondary_types) && est.secondary_types.includes('kosher_certification_hechsher'))
);
// Set to a certifier's id when its logo is clicked (from a kosher place), so its
// detail page opens focused on the certification — scrolls to "Places we
// certify" instead of its other-category content. Cleared once consumed.
let certFocusTarget = null;

// Two headline badges for lodging (hotels / apartments / vacation rentals):
//   🕯️ Shabbat-friendly — has Shabbos-observance infrastructure amenities
//   ✅/🍳 Kosher food     — effective kosher-food tier (explicit or inferred);
//                           green "certified" when a supervision is set, amber
//                           "self-declared" otherwise (honest for apartments
//                           whose kosher kitchen carries no formal hechsher).
// Renders nothing for non-lodging types or when neither signal applies.
function LodgingBadges({ est, size = 'sm', inline = false }) {
  if (!est || !SUPPORTS_SHABBAT_AMENITIES.includes(est.type)) return null;
  const food = effectiveKosherFood(est);
  const foodMeta = kosherFoodMeta(food);
  const shabbat = isShabbatFriendly(est);
  const certified = food !== 'none' && est.supervision && String(est.supervision).trim().length > 0;
  const pad = size === 'sm' ? '1px 7px' : '3px 11px';
  const fs = size === 'sm' ? 11 : 12.5;
  const chips = [];
  if (shabbat) {
    chips.push(
      <span key="shab" style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', padding: pad, borderRadius: 10, fontSize: fs, fontWeight: 600 }}>
        {t("🕯️ Shabbat-friendly")}
      </span>
    );
  }
  if (food !== 'none' && foodMeta) {
    chips.push(
      <span
        key="food"
        title={certified ? `Certified kosher — ${est.supervision}` : 'Kosher food — self-declared (no listed certification)'}
        style={{
          background: certified ? '#ecfdf5' : '#fffbeb', color: certified ? '#065f46' : '#92400e',
          border: `1px solid ${certified ? '#a7f3d0' : '#fde68a'}`, padding: pad, borderRadius: 10, fontSize: fs, fontWeight: 600,
        }}
      >
        {certified ? '✅' : foodMeta.icon} {foodMeta.short}{!certified && size !== 'sm' ? (" " + t("· self-declared")) : ''}
      </span>
    );
  }
  if (!chips.length) return null;
  if (inline) return <>{chips}</>; // caller places the chips in its own flex row
  return <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>{chips}</div>;
}

// Build a short human summary of a place's opening_hours.schedule for the
// 🕐 icon's title attribute on list cards. We try to convey the "shape" of
// the schedule in a few words rather than the full timetable — so a visitor
// hovering the icon learns "Open daily" / "Open Mon–Fri, Sat closed" /
// "Open on Shabbat" without expanding the card.
// Il campo «orari» di una sinagoga a volte tiene le TEFILLOT invece degli
// orari di apertura: si riconosce dal nome della preghiera, ed è la differenza
// fra «chiuso» e «non c'è minyan adesso».
const PREGHIERE = /shacharit|shachris|shacharis|mincha|minha|maariv|ma'ariv|arvit|arbit|kabbalat|minyan|davening|שחרית|מנחה|מעריב|ערבית|קבלת שבת|מניין/i;

// Per la pastiglia del prossimo minyan: «sat» da solo non dice niente a chi
// legge, e «Shabbat» è il nome che la gente cerca.
const GIORNO_LUNGO = { sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Shabbat' };

function summarizeHours(establishment) {
  if (!establishment) return 'Has opening hours';
  if (establishment.type === 'synagogue_shul') return 'Has prayer times';

  const sched = establishment.opening_hours?.schedule;
  if (!Array.isArray(sched) || sched.length === 0) {
    // Free-text hours only — fall back to a generic label.
    return establishment.hours ? 'Has opening hours' : 'Has opening hours';
  }

  // Helper — does this entry indicate "open" (has time ranges) or "closed"?
  const openEntries  = sched.filter((e) => Array.isArray(e.ranges) && e.ranges.length > 0);
  const closedEntries = sched.filter((e) => e.closed);
  const noteEntries  = sched.filter((e) => e.note && !(Array.isArray(e.ranges) && e.ranges.length > 0));

  // Collect days that ever appear as open
  const openDays = new Set();
  for (const e of openEntries) (e.days || []).forEach((d) => openDays.add(d));
  const closedDays = new Set();
  for (const e of closedEntries) (e.days || []).forEach((d) => closedDays.add(d));

  // Sniff for notes that should bubble up as the headline
  const allNotes = [...sched.map((e) => e.note || ''), establishment.hours || ''].join(' ').toLowerCase();
  if (/seasonal|hours\s*vary|varies/.test(allNotes)) return 'Seasonal — hours vary';
  if (openEntries.length === 0 && noteEntries.length > 0) {
    // No fixed hours, only notes → use the first note (e.g. "Upon reservation only")
    const n = noteEntries[0].note;
    return n.charAt(0).toUpperCase() + n.slice(1);
  }

  if (openDays.size === 0) return 'Closed';
  if (openDays.has('daily')) return 'Open daily';

  const weekdays = ['mon', 'tue', 'wed', 'thu', 'fri'];
  const all7 = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const openSet = openDays;

  // All 7 days open
  if (all7.every((d) => openSet.has(d))) return 'Open every day';
  // Mon-Fri exactly
  if (weekdays.every((d) => openSet.has(d)) && !openSet.has('sat') && !openSet.has('sun')) {
    return 'Open Mon–Fri';
  }
  // Mon-Thu (no Fri/Sat/Sun) — common for kosher restaurants
  if (['mon', 'tue', 'wed', 'thu'].every((d) => openSet.has(d)) && !openSet.has('fri') && !openSet.has('sat') && !openSet.has('sun')) {
    return 'Open Mon–Thu';
  }
  // Sat only
  if (openSet.size === 1 && openSet.has('sat')) return 'Open on Shabbat';
  // Friday only (common pre-Shabbat pickup)
  if (openSet.size === 1 && openSet.has('fri')) return 'Open on Friday';

  // Generic "open N days" summary
  const open = [...openSet].length;
  if (closedDays.size > 0) {
    return `Open ${open} day${open === 1 ? '' : 's'}, closed others`;
  }
  return `Open ${open} day${open === 1 ? '' : 's'}/week`;
}

// Split a comma-separated cuisine string into trimmed unique entries.
// Handles "Italian, Pizza, ; Bakery" (commas or semicolons, any spacing).
function splitCuisines(s) {
  if (!s) return [];
  return s.split(/\s*[,;]\s*/).map((x) => x.trim()).filter(Boolean);
}
function joinCuisines(arr) {
  // Re-join with a consistent separator; dedupe (case-insensitive)
  const seen = new Set();
  const out = [];
  for (const v of arr) {
    const k = v.toLowerCase();
    if (!seen.has(k)) { seen.add(k); out.push(v); }
  }
  return out.join(', ');
}

// Cuisine multi-select — checkboxes for the preset list + a free-text input
// to add anything custom. Round-trips through a comma-separated string so the
// backend stays unchanged (it already splits on commas for the /cuisines filter).
function CuisineMultiSelect({ value, options, onChange, allowCustom = false }) {
  const selected = splitCuisines(value);
  const [custom, setCustom] = React.useState('');

  const toggle = (opt) => {
    const lower = opt.toLowerCase();
    const isOn = selected.some((s) => s.toLowerCase() === lower);
    const next = isOn
      ? selected.filter((s) => s.toLowerCase() !== lower)
      : [...selected, opt];
    onChange(joinCuisines(next));
  };

  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    onChange(joinCuisines([...selected, v]));
    setCustom('');
  };

  const removeChip = (s) => {
    onChange(joinCuisines(selected.filter((x) => x !== s)));
  };

  const knownLowercase = new Set(options.map((o) => o.toLowerCase()));
  const customSelected = selected.filter((s) => !knownLowercase.has(s.toLowerCase()));

  return (
    <div>
      {/* Selected chips at the top — quick removal */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {selected.map((s) => (
            <span
              key={s}
              style={{
                background: '#fef3c7', color: '#92400e',
                padding: '4px 10px 4px 10px', borderRadius: 999,
                fontSize: 12, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {s}
              <button
                type="button"
                onClick={() => removeChip(s)}
                aria-label={`Remove ${s}`}
                style={{ background: 'transparent', border: 'none', color: '#92400e', cursor: 'pointer', padding: 0, fontWeight: 700, fontSize: 14, lineHeight: 1 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Preset checkboxes */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((opt) => {
          const isOn = selected.some((s) => s.toLowerCase() === opt.toLowerCase());
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              style={{
                padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                border: `1px solid ${isOn ? '#92400e' : '#d1d5db'}`,
                background: isOn ? '#fef3c7' : 'white',
                color: isOn ? '#92400e' : '#1f2937',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {isOn ? '✓ ' : '+ '}{opt}
            </button>
          );
        })}
      </div>

      {/* Custom add — admins only. Everyone else picks from the curated list so
          the cuisine taxonomy stays clean (no duplicates / combined values). */}
      {allowCustom ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input
            type="text"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
            placeholder={t("Admin: add a new cuisine…")}
            style={{
              flex: 1, padding: '6px 10px',
              border: '1px solid #d1d5db', borderRadius: 6,
              fontSize: 13, fontFamily: 'inherit', outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!custom.trim()}
            style={{
              padding: '6px 12px', background: '#1a4d8f', color: 'white',
              border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
              cursor: custom.trim() ? 'pointer' : 'not-allowed', opacity: custom.trim() ? 1 : 0.5,
            }}
          >
            {t("Add")}
          </button>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          {t("Pick from the list. Need a cuisine that isn't here? Ask an admin to add it.")}
        </div>
      )}

      {customSelected.length > 0 && (
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          {allowCustom ? (t("Custom:") + " ") : (t("Not in the standard list:") + " ")}{customSelected.join(', ')}
        </div>
      )}
    </div>
  );
}

// Build a WhatsApp URL with a friendly pre-filled message. The message
// introduces the visitor — "Hi {place}, I'm writing to you from JewishCompass.com"
// — so the business knows where the lead came from when they reply.
//
// WHEN WE ARE WRITING TO A PERSON, GREET THE PERSON AND NAME THE BUSINESS.
// A place's contact rows are a named human with their own number — the
// gabbai, the rabbi, the manager — and the message opened "Hi Ahavat
// Yisrael" into their personal WhatsApp, which reads as a mailshot to the
// one person most likely to answer. Their name alone is no better: a rabbi
// listed against four shuls has no idea which one this is about.
//
// THE LISTING'S LINK GOES AT THE END (Menachem, 18/10/2026: "include the
// link to the place to the owner"), so the place sees which page of the
// site the visitor is writing from.
function whatsappUrl(number, placeName, personName, placeUrl) {
  if (!number) return '';
  const person = (personName || '').trim();
  const place = (placeName || '').trim();
  let greeting;
  if (person && place) greeting = `Hi ${person}, I'm writing to you from JewishCompass.com about ${place}`;
  else if (person) greeting = `Hi ${person}, I'm writing to you from JewishCompass.com`;
  else if (place) greeting = `Hi ${place}, I'm writing to you from JewishCompass.com`;
  else greeting = `Hi, I'm writing to you from JewishCompass.com`;
  if (placeUrl) greeting += `\n${placeUrl}`;
  return `https://wa.me/${number}?text=${encodeURIComponent(greeting)}`;
}

// The listing's canonical page on the live site, for messages to the place.
function placeLink(est) {
  if (!est) return '';
  return `https://www.jewishcompass.com${buildPlaceUrl(est) || `/place/${est.id}`}`;
}

// An email to one of the place's contact people, opened in the visitor's own
// mail app with a subject and the same greeting and listing link.
function mailtoUrl(email, placeName, personName, placeUrl) {
  const place = (placeName || '').trim();
  const person = (personName || '').trim();
  const hello = person ? `Hi ${person},` : (place ? `Hi ${place},` : 'Hi,');
  const about = person && place ? ` about ${place}` : '';
  const body = `${hello}\n\nI'm writing to you from JewishCompass.com${about}.\n${placeUrl || ''}\n\n`;
  const subject = place ? `${place} — via JewishCompass.com` : 'Via JewishCompass.com';
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// Phone display with Call + WhatsApp buttons
// `country` is used to add the international prefix if missing
// `whatsappNumber` (optional) overrides the WhatsApp link target if provided
// `placeName` pre-fills the WhatsApp greeting message so the business
// immediately sees where the inquiry came from.
// `hasWhatsApp` (optional, default true): when explicitly false, the WhatsApp
// button is hidden — for businesses whose phone can't receive WhatsApp.
// In compact mode, the phone number text is hidden — just the icons
function PhoneActions({ phone, country, whatsappNumber, placeName, placeUrl, hasWhatsApp = true, compact = false, estId = null }) {
  const tel = phoneWithCountry(phone, country);
  const wa = phoneForWhatsApp(whatsappNumber || phone, country);
  const btn = compact
    ? { width: 26, height: 26 }
    : { width: 32, height: 32 };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {!compact && <span>{tel}</span>}
      {/* Niente bottone CHIAMA se non c'e' niente da comporre — un nome
          utente di WhatsApp non si chiama. Vedi isWhatsAppHandle. */}
      {tel && (
        <a
          href={`tel:${tel}`}
          title={`Call ${tel}`}
          onClick={(e) => { e.stopPropagation(); if (estId) api.trackClick(estId, 'phone'); }}
          style={{ ...btn, background: '#1a4d8f', color: 'white', borderRadius: '50%', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'transform 0.15s' }}
        >
          <PhoneIcon size={compact ? 13 : 16} />
        </a>
      )}
      {hasWhatsApp && (
        <a
          href={whatsappUrl(wa, placeName, null, placeUrl)}
          target="_blank"
          rel="noreferrer"
          title={`WhatsApp ${tel}`}
          onClick={(e) => { e.stopPropagation(); if (estId) api.trackClick(estId, 'whatsapp'); }}
          style={{ ...btn, background: '#25D366', color: 'white', borderRadius: '50%', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'transform 0.15s' }}
        >
          <WhatsAppIcon size={compact ? 13 : 16} />
        </a>
      )}
    </span>
  );
}

const COLORS = {
  primary: '#1a4d8f',
  primaryDark: '#0d3163',
  accent: '#d4af37',
  bg: '#f7f8fa',
  card: '#ffffff',
  text: '#1f2937',
  muted: '#6b7280',
  border: '#e5e7eb',
  danger: '#dc2626',
  success: '#059669',
};

// I TRE TASTI DELLA BARRA, TUTTI UGUALI.
//
// Menachem, 27/10/2026: «ingrandisci il titolo Jewish Compass su
// mobile e uniforma i bottoni in alto». Erano tre cose diverse
// cresciute in momenti diversi: il «+» con 12px di fianco,
// l'hamburger con 10, la posta nuda senza riquadro e con 6. Allineati
// non erano, e il titolo non poteva crescere perche' se lo mangiavano
// loro.
//
// Quadrati e identici: si misurano una volta sola (38×38 + 6 di
// spazio = 126px per tutti e tre) e quello che avanza va al nome del
// sito, che e' la prima cosa che uno deve leggere.
const TASTO_BARRA = {
  width: 38,
  height: 38,
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  background: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 8,
  color: 'white',
  fontSize: 18,
  lineHeight: 1,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const styles = {
  app: { fontFamily: 'system-ui, -apple-system, sans-serif', background: COLORS.bg, minHeight: '100vh', color: COLORS.text },
  header: { background: COLORS.primary, color: 'white', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  logo: { fontSize: 22, fontWeight: 700, cursor: 'pointer' },
  nav: { display: 'flex', gap: 16, alignItems: 'center' },
  navBtn: { background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.3)', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  navBtnPrimary: { background: COLORS.accent, color: COLORS.primaryDark, border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  container: { maxWidth: 1200, margin: '0 auto', padding: '24px 32px' },
  hero: { background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`, color: 'white', paddingTop: 48, paddingRight: 32, paddingBottom: 56, paddingLeft: 32, textAlign: 'center' },
  heroTitle: { fontSize: 42, fontWeight: 700, margin: '0 0 12px 0' },
  heroSub: { fontSize: 18, opacity: 0.9, maxWidth: 600, margin: '0 auto' },
  searchBar: { display: 'flex', gap: 8, maxWidth: 600, margin: '24px auto 0', background: 'white', padding: 8, borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
  searchInput: { flex: 1, border: 'none', outline: 'none', padding: '10px 14px', fontSize: 15, borderRadius: 6, color: COLORS.text },
  sectionTitle: { fontSize: 24, fontWeight: 700, margin: '32px 0 16px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  card: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 20, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center' },
  cardHover: { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
  cardIcon: { fontSize: 36, marginBottom: 12 },
  cardTitle: { fontSize: 15, fontWeight: 600, marginBottom: 4 },
  cardCount: { fontSize: 13, color: COLORS.muted },
  listCard: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 20, marginBottom: 12, cursor: 'pointer', transition: 'all 0.2s' },
  listCardTitle: { fontSize: 17, fontWeight: 600, marginBottom: 6, color: COLORS.primary },
  listCardMeta: { fontSize: 13, color: COLORS.muted, display: 'flex', gap: 16, flexWrap: 'wrap' },
  filterBar: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, justifyContent: 'center', alignItems: 'center' },
  select: { padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14, background: 'white', cursor: 'pointer' },
  button: { background: COLORS.primary, color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  buttonOutline: { background: 'white', color: COLORS.primary, border: `1px solid ${COLORS.primary}`, padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { background: 'white', padding: 32, borderRadius: 12, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
  modalTitle: { fontSize: 22, fontWeight: 700, marginTop: 0, marginBottom: 16, color: COLORS.primary },
  input: { width: '100%', padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' },
  label: { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: COLORS.muted },
  error: { background: '#fee2e2', color: COLORS.danger, padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 },
  success: { background: '#d1fae5', color: COLORS.success, padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 },
  empty: { textAlign: 'center', padding: 60, color: COLORS.muted },
  detailHeader: { background: 'white', padding: 32, borderRadius: 12, marginBottom: 20, border: `1px solid ${COLORS.border}` },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, marginTop: 20 },
  detailField: { background: COLORS.bg, padding: 16, borderRadius: 8 },
  detailLabel: { fontSize: 12, fontWeight: 600, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 4 },
  detailValue: { fontSize: 15, color: COLORS.text },
  spinner: { textAlign: 'center', padding: 60, color: COLORS.muted, fontSize: 16 },
};

// Set by DetailPage while a place page is open, so the Minyanim button can
// scope to that place's city (the place URL carries only a slug/country-code,
// not the real city/country names the minyanim filter needs). Cleared on unmount.
let currentPlaceGeo = null;

// Compute the /minyanim href based on the current page so a user browsing
// /list?city=Rome lands on /minyanim?country=Italy&city=Rome (already
// scoped), and a place page scopes to that place's city. Otherwise the
// default /minyanim.
function minyanimHrefFromCurrentLocation() {
  try {
    const path = window.location.pathname;
    const qs = new URLSearchParams(window.location.search);
    // List page carries country/city/area as query params — inherit them.
    if (path === '/list') {
      const out = new URLSearchParams();
      const country = qs.get('country');
      const city = qs.get('city');
      if (country) out.set('country', country);
      if (city) out.set('city', city);
      const tail = out.toString();
      return tail ? `/minyanim?${tail}` : '/minyanim';
    }
    // On a place detail page, scope to the open place's city/country.
    if (currentPlaceGeo && (currentPlaceGeo.city || currentPlaceGeo.country)) {
      const out = new URLSearchParams();
      if (currentPlaceGeo.country) out.set('country', currentPlaceGeo.country);
      if (currentPlaceGeo.city) out.set('city', currentPlaceGeo.city);
      const tail = out.toString();
      return tail ? `/minyanim?${tail}` : '/minyanim';
    }
  } catch (_) {
    // window.location can be unavailable in SSR / test envs — fall through.
  }
  return '/minyanim';
}

// Header notification bell — shows owner replies to the user's reviews and
// messages. "Unread" is anything newer than the last time the user opened the
// bell, tracked in localStorage (no server-side read state needed). Clicking
// an item goes to that place; opening the bell clears the unread badge.
// Mail icon with an unread-message badge. Reuses the notifications feed,
// counting only message-type items (a place messaged/replied) newer than the
// last time the user opened their messages. Clicking opens the messages inbox.
function MailButton({ user, onOpen }) {
  const [count, setCount] = useState(0);
  React.useEffect(() => {
    if (!user) return undefined;
    let active = true;
    const load = () => api.myNotifications().then((d) => {
      if (!active) return;
      const msgs = (Array.isArray(d) ? d : []).filter((i) => i.type === 'new_message' || i.type === 'visitor_reply' || i.type === 'message_reply');
      let seen = '';
      try { seen = localStorage.getItem('jc_mail_seen') || ''; } catch (_) { /* ignore */ }
      setCount(msgs.filter((i) => !seen || new Date(i.at) > new Date(seen)).length);
    }).catch(() => {});
    load();
    const id = setInterval(load, 90_000);
    return () => { active = false; clearInterval(id); };
  }, [user]);
  const click = () => {
    try { localStorage.setItem('jc_mail_seen', new Date().toISOString()); } catch (_) { /* ignore */ }
    setCount(0);
    onOpen();
  };
  return (
    <button
      onClick={click}
      title={t("My messages")}
      aria-label={`My messages${count ? ` (${count} unread)` : ''}`}
      style={{ ...TASTO_BARRA, position: 'relative' }}
    >
      ✉️
      {count > 0 && (
        <span style={{
          position: 'absolute', top: -2, right: -2, background: '#ef4444', color: 'white',
          borderRadius: 999, minWidth: 17, height: 17, fontSize: 10.5, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
        }}>{count > 9 ? '9+' : count}</span>
      )}
    </button>
  );
}


// Compact EN / עברית language toggle. Switching persists + reloads (RTL flips
// via <html dir> on load). Translates UI chrome only — place content stays as
// owners entered it.
// `panel` = rendered on a light surface (the mobile drawer) instead of the dark
// header bar, so it flips to dark text on a subtle tinted pill.
function LangSwitch({ compact = false, panel = false }) {
  const cur = getLang();
  const wrapBg = panel ? '#eef2f7' : 'rgba(255,255,255,0.12)';
  const wrapBorder = panel ? '1px solid #d7dce3' : '1px solid rgba(255,255,255,0.18)';
  // 4+ languages → a compact dropdown instead of a button row.
  return (
    <select
      value={cur}
      onChange={(e) => setLang(e.target.value)}
      aria-label={t('Language')}
      title={t('Language')}
      style={{
        background: wrapBg, border: wrapBorder, borderRadius: 999,
        color: panel ? '#1f2937' : 'white',
        padding: compact ? '4px 10px' : '5px 12px', paddingInlineEnd: 26,
        fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      {LANGS.map((l) => (
        <option key={l.code} value={l.code} style={{ color: '#1f2937', background: 'white' }}>
          {l.name}
        </option>
      ))}
    </select>
  );
}

function Header({ user, onLogin, onLogout, onHome, onDashboard, onFavorites, onProfile, onMessages, onAddPlace }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = React.useRef(null);

  // Track viewport width so we can swap to a hamburger on narrow screens.
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 640);
  React.useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Sticky top bar. On mobile the bar is tall (the search wraps to its own row),
  // so auto-hide it on scroll-DOWN and reveal it on scroll-UP to reclaim reading
  // space; on wider screens it just stays pinned. Paused while the drawer is open.
  const [hidden, setHidden] = useState(false);
  React.useEffect(() => {
    if (!isNarrow) { setHidden(false); return; }
    let last = window.scrollY;
    const onScroll = () => {
      if (mobileMenuOpen) return;
      const y = window.scrollY;
      if (y > last && y > 90) setHidden(true);       // scrolled down past the bar
      else if (y < last - 4) setHidden(false);        // any upward scroll reveals it
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isNarrow, mobileMenuOpen]);

  // Close dropdown when clicking outside, or pressing Escape.
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Close mobile drawer on Escape, and lock body scroll while it's open.
  React.useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Close menu before navigating so the new page doesn't render with it open.
  const go = (fn) => { setMenuOpen(false); fn(); };

  const displayName = user?.firstName || user?.first_name || user?.email || '';
  const roleBadge = user?.role === 'admin' ? 'ADMIN' : user?.role === 'owner' ? 'OWNER' : null;

  const itemStyle = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px', cursor: 'pointer', fontSize: 14, color: COLORS.text,
    border: 'none', background: 'none', width: '100%', textAlign: 'left', fontFamily: 'inherit',
  };

  // Mobile drawer content — shared between the narrow-screen menu and the
  // dropdown so the same handlers fire.
  const mobileGo = (fn) => { setMobileMenuOpen(false); fn(); };
  const mobileItem = {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
    fontSize: 16, background: 'none', border: 'none', width: '100%', textAlign: 'left',
    color: COLORS.text, fontFamily: 'inherit', cursor: 'pointer', borderBottom: `1px solid ${COLORS.border}`,
  };

  return (
    <>
    {/* Sul telefono il margine laterale scende da 14 a 10 e lo spazio
        fra gli elementi da 10 a 6: sono dodici pixel che a 320 di
        larghezza decidono se il titolo ci sta o va a capo. */}
    <div className="jc-topbar" style={{ ...styles.header, flexWrap: 'wrap', rowGap: 10, ...(isNarrow ? { paddingLeft: 10, paddingRight: 10, columnGap: 6 } : {}), position: 'sticky', top: 0, zIndex: 900, transform: hidden ? 'translateY(-100%)' : 'translateY(0)', transition: 'transform .25s ease', willChange: 'transform' }}>
      {/* IL LOGO, PIU' GRANDE DI PRIMA.
          Menachem: «la prima riga deve avere il logo, il più, la posta
          e l'hamburger», e poi «ingrandisci il titolo Jewish Compass su
          mobile». Le due cose litigavano finché i tre tasti erano di
          tre misure diverse: a 320 pixel, tolto il margine, restano 292
          e i tasti se ne prendevano 139. Ora ne prendono 126 esatti
          (38+38+38 più 6+6), e il nome del sito può stare a 19px invece
          che a 16 restando sulla stessa riga. */}
      <div
        style={{ ...styles.logo, fontSize: isNarrow ? 18 : 22, whiteSpace: 'nowrap' }}
        onClick={onHome}
      >
        {t("🧭 Jewish Compass")}
      </div>

      {/* Search — inline in the bar when there's room; wraps to its own row
          beneath on narrow screens. */}
      <div style={{ order: isNarrow ? 3 : 0, flex: isNarrow ? '1 1 100%' : '1 1 260px', minWidth: 0, maxWidth: isNarrow ? 'none' : 520, margin: isNarrow ? 0 : '0 20px' }}>
        <AddressSearch variant="compact" showButton={false} placeholder={t("Search places, cities, countries…")} />
      </div>

      {/* Mobile: Near me + hamburger. Wide screens: full nav. */}
      {isNarrow ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* IL «PIÙ» TORNA NELLA BARRA, SUL CELLULARE.
              Menachem, 27/10/2026: «rimetti il più di add a place in
              mobile nella barra sopra vicino alla posta». Era finito nel
              menu quando la barra aveva otto comandi — ma quel
              ragionamento valeva per lo schermo largo: sul telefono la
              barra ne ha due, c'è posto, e chi aggiunge un posto lo fa
              col telefono in mano mentre è lì davanti.
              Resta visibile anche senza login: chi tocca si trova la
              finestra di accesso, che è come scoprire che la cosa si
              può fare. */}
          {/* GIALLO, E QUINDI NON L'EMOJI. Menachem: «in più deve
              essere giallo». Il ➕ è un'emoji e il suo colore lo
              decide il sistema — sul telefono esce viola, che sulla
              barra blu non c'entra niente. Un «+» di testo invece si
              colora, e si usa l'oro del sito, lo stesso del tasto
              «aggiungi» nel menu a scomparsa. */}
          <button
            onClick={onAddPlace}
            title={t('Add a place')}
            aria-label={t('Add a place')}
            // E DEVE SEMBRARE UN BOTTONE. Nudo, accanto all'hamburger
            // che ha il suo riquadro, sembrava un carattere capitato lì
            // per sbaglio. Adesso il riquadro è lo stesso di tutti e
            // tre; l'oro resta solo a lui, perché è l'azione
            // principale.
            style={{ ...TASTO_BARRA, color: COLORS.accent, fontSize: 22, fontWeight: 700 }}
          >
            +
          </button>
          {user && <MailButton user={user} onOpen={onMessages} />}
          <button
            aria-label={t('Open menu')}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(true)}
            style={TASTO_BARRA}
          >
            ☰
          </button>
        </div>
      ) : (
      <div style={styles.nav}>
        <LangSwitch />
        {/* Near-me lives on the 📍 pin inside the search bar now (both desktop
            and mobile), so there's no separate Near-me button in the nav. */}
        {/* Minyanim — site-wide entry point to the prayer-time finder.
            Lives in the header so it's discoverable from every page. */}
        <a
          href={minyanimHrefFromCurrentLocation()}
          onClick={(e) => {
            // Recompute on click so we use the latest URL (the href is fixed
            // at component render time and might be stale after navigation).
            const target = minyanimHrefFromCurrentLocation();
            if (target !== e.currentTarget.getAttribute('href')) {
              e.currentTarget.setAttribute('href', target);
            }
          }}
          title={t("Find a minyan in this city or near you")}
          style={{
            color: 'white', textDecoration: 'none',
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 999, padding: '6px 14px',
            fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          🕍 {t('Minyanim')}
        </a>

        {/* News — editorial articles, discoverable site-wide. */}
        <a
          href="/news"
          title={t("Jewish & kosher travel news and place spotlights")}
          style={{
            color: 'white', textDecoration: 'none',
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 999, padding: '6px 14px',
            fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          📰 {t('News')}
        </a>

        {/* Logged-out: a compact "Menu" dropdown holding Share + Route planner,
            mirroring the account dropdown signed-in users get. (Mobile keeps
            these in the drawer.) Reuses the same menuOpen/menuRef/go as below —
            only one of the two dropdowns ever renders (logged-in XOR out). */}
        {!user && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title={t('Menu')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(255,255,255,0.12)', color: 'white',
                border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999,
                padding: '6px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              ☰ {t('Menu')} <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
            </button>
            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 8, minWidth: 200,
                  background: 'white', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  border: `1px solid ${COLORS.border}`, overflow: 'hidden', zIndex: 50,
                }}
              >
                <button role="menuitem" style={{ ...itemStyle, fontWeight: 700, borderBottom: `1px solid ${COLORS.border}` }} onClick={() => go(onAddPlace)}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  ➕ {t('Add a place')}
                </button>
                <ShareButton variant="menuitem" style={{ borderBottom: `1px solid ${COLORS.border}` }} />
                <button role="menuitem" style={itemStyle} onClick={() => go(() => { window.location.href = '/route'; })}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  🚗 {t('Route planner')}
                </button>
              </div>
            )}
          </div>
        )}

        {user && <MailButton user={user} onOpen={onMessages} />}
        {user ? (
          <div ref={menuRef} style={{ position: 'relative' }}>
            {/* Single yellow pill — name + dropdown chevron. The role (if any)
                is shown as a subtle line inside the open menu, not duplicated
                here on the trigger. */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Account menu for ${displayName}`}
              title={t("Account menu")}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: COLORS.accent, color: COLORS.primaryDark,
                border: 'none', borderRadius: 999,
                padding: '6px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </span>
              <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
            </button>

            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 8, minWidth: 220,
                  background: 'white', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  border: `1px solid ${COLORS.border}`, overflow: 'hidden', zIndex: 50,
                }}
              >
                {/* Email + role header (small, non-interactive) */}
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}`, background: COLORS.bg }}>
                  <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.email}
                  </div>
                  {roleBadge && (
                    <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      {roleBadge}
                    </div>
                  )}
                </div>
                {/* Share the current page — lives here on desktop so the nav
                    bar stays uncluttered; closes the menu after firing. */}
                <ShareButton variant="menuitem" style={{ borderBottom: `1px solid ${COLORS.border}` }} />
                {/* Route planner — tucked here on desktop for logged-in users. */}
                <button role="menuitem" style={{ ...itemStyle, fontWeight: 700 }} onClick={() => go(onAddPlace)}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  ➕ {t('Add a place')}
                </button>
                <button role="menuitem" style={itemStyle} onClick={() => go(() => { window.location.href = '/route'; })}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  🚗 {t('Route planner')}
                </button>
                <button role="menuitem" style={itemStyle} onClick={() => go(onMessages)}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  ✉️ {t('My messages')}
                </button>
                <button role="menuitem" style={itemStyle} onClick={() => go(onProfile)}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  👤 {t('Edit profile')}
                </button>
                <button role="menuitem" style={itemStyle} onClick={() => go(onFavorites)}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  <span style={{ color: '#ef4444' }}>♥</span> {t('Favorites')}
                </button>
                <button role="menuitem" style={itemStyle}
                        onClick={() => go((user.role === 'admin' || user.role === 'owner') ? onDashboard : onProfile)}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  📊 {t('Dashboard')}
                </button>
                <div style={{ borderTop: `1px solid ${COLORS.border}` }} />
                <button role="menuitem" style={{ ...itemStyle, color: COLORS.danger }} onClick={() => go(onLogout)}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                  ↪ {t('Log out')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button style={styles.navBtnPrimary} onClick={onLogin}>{t('Login / Register')}</button>
        )}
        {/* "Add a place" lives in the Menu / account dropdown now, not the bar.
            The bar had grown to eight controls; contributing a place is an
            occasional act, so it belongs behind the menu rather than beside
            search. Mobile keeps it in the drawer, as before. */}
      </div>
      )}
    </div>{/* end sticky top bar */}

      {/* Mobile drawer — slides in over everything when hamburger is tapped.
          Rendered OUTSIDE the sticky bar: the bar uses transform/will-change for
          the mobile auto-hide, which would otherwise become the containing block
          for these position:fixed panels and trap them inside the header. */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop closes the drawer when tapped */}
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4000 }}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label={t('Mobile menu')}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(86vw, 320px)',
              background: 'white', color: COLORS.text, boxShadow: '-8px 0 24px rgba(0,0,0,0.15)',
              zIndex: 4001, display: 'flex', flexDirection: 'column', overflow: 'auto',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ fontWeight: 700, color: COLORS.primary }}>{t('Menu')}</div>
              <button
                aria-label={t('Close menu')}
                onClick={() => setMobileMenuOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, lineHeight: 1, color: COLORS.muted, padding: 4 }}
              >
                ×
              </button>
            </div>

            {/* Language selector — lives in the drawer on mobile. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: `1px solid ${COLORS.border}` }}>
              <span style={{ fontSize: 14, color: COLORS.muted }}>🌐 {t('Language')}</span>
              <LangSwitch panel />
            </div>

            {/* Site-wide quick links */}
            <button style={mobileItem} onClick={() => mobileGo(() => { window.location.href = minyanimHrefFromCurrentLocation(); })}>
              🕍 {t('Find a minyan')}
            </button>
            <button style={mobileItem} onClick={() => mobileGo(() => { window.location.href = '/news'; })}>
              📰 {t('News')}
            </button>
            <button style={mobileItem} onClick={() => mobileGo(() => { window.location.href = '/route'; })}>
              🚗 {t('Route planner')}
            </button>
            {/* Share the current page */}
            <ShareButton variant="drawer" />

            {/* Primary action */}
            <button style={{ ...mobileItem, background: COLORS.accent, color: COLORS.primaryDark, fontWeight: 700 }} onClick={() => mobileGo(onAddPlace)}>
              ➕ {t('Add Place')}
            </button>

            {user ? (
              <>
                <div style={{ padding: '12px 18px', background: COLORS.bg, fontSize: 13, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.email}
                  </div>
                  {(user.role === 'admin' || user.role === 'owner') && (
                    <div style={{ marginTop: 2, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{user.role}</div>
                  )}
                </div>
                <button style={mobileItem} onClick={() => mobileGo(onMessages)}>✉️ {t('My messages')}</button>
                <button style={mobileItem} onClick={() => mobileGo(onProfile)}>👤 {t('Edit profile')}</button>
                <button style={mobileItem} onClick={() => mobileGo(onFavorites)}><span style={{ color: '#ef4444' }}>♥</span> {t('Favorites')}</button>
                <button style={mobileItem} onClick={() => mobileGo((user.role === 'admin' || user.role === 'owner') ? onDashboard : onProfile)}>📊 {t('Dashboard')}</button>
                <button style={{ ...mobileItem, color: COLORS.danger }} onClick={() => mobileGo(onLogout)}>↪ {t('Log out')}</button>
              </>
            ) : (
              <button style={mobileItem} onClick={() => mobileGo(onLogin)}>🔑 {t('Login / Register')}</button>
            )}
          </div>
        </>
      )}
    </>
  );
}

function AuthModal({ onClose, onLogin, initialMode = 'login', initialEmail = '' }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('client');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const googleBtnRef = React.useRef(null);

  // Close on Escape (backdrop click already closes; the × button too).
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Render the Google Sign-In button once GIS finishes loading. Tries a few
  // times in case the <script> tag hasn't initialized yet.
  React.useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return; // not configured — silently hide the button
    let cancelled = false;
    let attempt = 0;

    const tryInit = () => {
      if (cancelled) return;
      if (window.google && window.google.accounts && window.google.accounts.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: async (response) => {
              setError('');
              setLoading(true);
              try {
                const res = await api.googleLogin(response.credential);
                localStorage.setItem('jwt_token', res.token);
                onLogin(res.user);
                onClose();
              } catch (err) {
                setError(err.message || 'Google sign-in failed');
              } finally {
                setLoading(false);
              }
            },
          });
          if (googleBtnRef.current) {
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              theme: 'outline',
              size: 'large',
              width: 320,
              text: mode === 'login' ? 'signin_with' : 'signup_with',
            });
          }
        } catch (e) {
          console.error('Google Sign-In init error:', e);
        }
        return;
      }
      if (attempt++ < 30) setTimeout(tryInit, 100); // ~3 seconds max
    };
    tryInit();
    return () => { cancelled = true; };
    // Re-render the button label when the user toggles between login/register
  }, [mode, GOOGLE_CLIENT_ID, onLogin, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await api.login(email, password);
        localStorage.setItem('jwt_token', res.token);
        onLogin(res.user);
        onClose();
      } else if (mode === 'forgot') {
        await api.forgotPassword(email);
        // Generic message — never reveal whether the email actually exists.
        setSuccess('If an account exists for that email, a reset link has been sent. Check your inbox (and spam folder).');
      } else {
        const res = await api.register({ email, password, firstName, lastName, role });
        if (res.requiresApproval) {
          setSuccess('Account created! Awaiting admin approval before you can log in.');
        } else {
          setSuccess('Account created! You can now log in.');
          setTimeout(() => setMode('login'), 1500);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={{ ...styles.modalContent, position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("Close")}
          style={{ position: 'absolute', top: 8, right: 10, width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'none', color: COLORS.muted, fontSize: 26, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
        >
          ×
        </button>
        <h2 style={{ ...styles.modalTitle, paddingRight: 32 }}>
          {mode === 'login' ? t('Login') : mode === 'forgot' ? t('Reset password') : t('Create Account')}
        </h2>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {/* Google Sign-In button. Hidden when REACT_APP_GOOGLE_CLIENT_ID isn't
            set, and hidden during forgot-password (irrelevant there). */}
        {GOOGLE_CLIENT_ID && mode !== 'forgot' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }} ref={googleBtnRef} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: COLORS.muted, fontSize: 12, margin: '4px 0 12px' }}>
              <div style={{ flex: 1, height: 1, background: COLORS.border }} />
              <span>{t('or use email')}</span>
              <div style={{ flex: 1, height: 1, background: COLORS.border }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>{t('Email')}</label>
          <input style={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          {mode !== 'forgot' && (
            <>
              <label style={styles.label}>{t('Password')}</label>
              <input style={styles.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
            </>
          )}
          {mode === 'register' && (
            <>
              <label style={styles.label}>{t('First Name')}</label>
              <input style={styles.input} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              <label style={styles.label}>{t('Last Name')}</label>
              <input style={styles.input} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              <label style={styles.label}>{t('Account Type')}</label>
              <select style={styles.input} value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="client">{t('Client (book hotels & tours)')}</option>
                <option value="owner">{t('Owner (manage hotel/tour) - needs admin approval')}</option>
              </select>
              {role === 'owner' && (
                <p style={{ fontSize: 12, color: COLORS.muted, margin: '6px 0 0', lineHeight: 1.4 }}>
                  {t('Your name is only used for your account. When you reply to a visitor\'s enquiry, your message is sent in the name of your business — not under your personal name.')}
                </p>
              )}
            </>
          )}
          <button type="submit" style={{ ...styles.button, width: '100%', marginTop: 8 }} disabled={loading}>
            {loading ? t('Please wait...') : mode === 'login' ? t('Login') : mode === 'forgot' ? t('Send reset link') : t('Create Account')}
          </button>
        </form>

        {/* Forgot-password link only in login mode */}
        {mode === 'login' && (
          <p style={{ textAlign: 'center', marginTop: 12, marginBottom: 0, fontSize: 13 }}>
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: COLORS.muted, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
              onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
            >
              {t('Forgot password?')}
            </button>
          </p>
        )}
        {mode === 'forgot' && (
          <p style={{ textAlign: 'center', marginTop: 12, marginBottom: 0, fontSize: 13 }}>
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
            >
              {t('← Back to login')}
            </button>
          </p>
        )}

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 14, display: mode === 'forgot' ? 'none' : 'block' }}>
          {mode === 'login' ? t("Don't have an account? ") : t('Already have an account? ')}
          <button style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 14, fontWeight: 600 }} onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setSuccess(''); }}>
            {mode === 'login' ? t('Register') : t('Login')}
          </button>
        </p>
      </div>
    </div>
  );
}

// AddressSearch — combined autocomplete + geocode for "find places near X".
// Accepts:
//   - any place / city / country already on the site (uses its exact coords)
//   - any address / landmark / area (geocoded via Nominatim)
//
// As the user types, debounced calls to /api/search-suggest show matching
// places/cities/countries below the input. Picking one uses its coordinates
// directly (no Nominatim roundtrip). Hitting Enter or clicking "Search the
// map for '…'" geocodes the raw text.
// Dismiss the on-screen keyboard by blurring whatever input is focused.
function blurActiveInput() {
  const el = typeof document !== 'undefined' ? document.activeElement : null;
  if (el && typeof el.blur === 'function') el.blur();
}

// Let the user get the keyboard out of the way to see the page: dragging/
// scrolling ANYWHERE except inside the results list dismisses the keyboard.
// (Scrolling the results themselves keeps it — they're browsing suggestions.)
function useDismissKeyboardOnScroll(dropRef, active) {
  React.useEffect(() => {
    if (!active) return;
    const onMove = (e) => {
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      blurActiveInput();
    };
    document.addEventListener('touchmove', onMove, { passive: true });
    return () => document.removeEventListener('touchmove', onMove);
  }, [dropRef, active]);
}

// Cap an autocomplete dropdown so it fits ABOVE the on-screen keyboard instead
// of being covered by it (a real problem in the installed PWA). Measures the
// dropdown's top against the visual viewport (which shrinks when the keyboard
// opens) and returns a max-height; recomputes on keyboard show/hide + scroll.
function useKeyboardSafeMaxHeight(ref, active, fallback = 380) {
  const [maxH, setMaxH] = React.useState(fallback);
  React.useEffect(() => {
    if (!active) { setMaxH(fallback); return; }
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const recompute = () => {
      const el = ref.current; if (!el) return;
      const top = el.getBoundingClientRect().top - (vv ? vv.offsetTop : 0);
      const vh = vv ? vv.height : window.innerHeight;
      setMaxH(Math.max(140, Math.min(fallback, Math.round(vh - top - 12))));
    };
    recompute();
    const id = setTimeout(recompute, 350); // after the keyboard finishes animating in
    if (vv) { vv.addEventListener('resize', recompute); vv.addEventListener('scroll', recompute); }
    window.addEventListener('scroll', recompute, true);
    return () => { clearTimeout(id); if (vv) { vv.removeEventListener('resize', recompute); vv.removeEventListener('scroll', recompute); } window.removeEventListener('scroll', recompute, true); };
  }, [ref, active, fallback]);
  return maxH;
}

// Defaults that are user-visible text stay null here and are translated at
// render. A default parameter is evaluated when the module loads, before the
// language is known, and never re-runs when the language changes — so t() in a
// default would freeze the string in whatever language happened to be active.
function AddressSearch({ variant = 'compact', placeholder = null, showButton = true }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [geoSuggestions, setGeoSuggestions] = useState([]);   // OpenStreetMap results
  const [geoLoading, setGeoLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Which row the keyboard is on. Enter acts on THIS row, so the list
  // has to show which one it is — before, Enter silently took the first
  // suggestion and you could not tell what you were about to open.
  const [activeIdx, setActiveIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = React.useRef(null);
  const geoDebounceRef = React.useRef(null);
  // The query the dropdown is allowed to answer for. Two suggest calls can be
  // in flight at once and they do not come back in the order they were sent,
  // so without this the SLOWER one wins and you are reading the results for
  // something you have already finished typing.
  const wantRef = React.useRef('');
  const dropRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const dropMaxH = useKeyboardSafeMaxHeight(dropRef, showSuggestions && query.trim().length >= 2);
  useDismissKeyboardOnScroll(dropRef, showSuggestions);
  // Blur the SEARCH INPUT directly (not document.activeElement, which may have
  // moved by tap time) to reliably close the mobile keyboard on selection. The
  // deferred second blur covers webviews that re-assert focus mid-tap.
  const dismissKeyboard = () => {
    const el = inputRef.current;
    if (el && el.blur) { el.blur(); setTimeout(() => { if (el.blur) el.blur(); }, 60); }
  };

  // Debounced DB-side suggestions (places / cities / countries already on
  // Jewish Compass). Quick, no rate-limit concerns.
  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 3) { setSuggestions(null); return; }   // come il server: sotto i 3 l'indice trigram non serve
    wantRef.current = q;
    debounceRef.current = setTimeout(() => {
      api.searchSuggest(q)
        .then((r) => {
          if (wantRef.current !== q) return;   // a newer keystroke owns the box
          setSuggestions(r); setShowSuggestions(true);
        })
        .catch(() => { if (wantRef.current === q) setSuggestions(null); });
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  // Debounced OpenStreetMap suggestions (streets, cities, landmarks anywhere
  // in the world). Longer debounce because Nominatim is rate-limited to
  // 1 req/sec — we throttle aggressively so a fast typer doesn't waste calls.
  React.useEffect(() => {
    if (geoDebounceRef.current) clearTimeout(geoDebounceRef.current);
    const q = query.trim();
    if (q.length < 3) { setGeoSuggestions([]); setGeoLoading(false); return; }
    setGeoLoading(true);
    geoDebounceRef.current = setTimeout(() => {
      api.geocodeSuggest(q)
        .then((rs) => {
          if (wantRef.current !== q) return;
          setGeoSuggestions(Array.isArray(rs) ? rs : []); setGeoLoading(false);
        })
        .catch(() => { if (wantRef.current === q) { setGeoSuggestions([]); setGeoLoading(false); } });
    }, 450);
    return () => geoDebounceRef.current && clearTimeout(geoDebounceRef.current);
  }, [query]);

  const [locating, setLocating] = useState(false);

  // Jump straight to the distance-sorted list once we have coords + a label.
  const goNear = (coords, label) => {
    dismissKeyboard();
    setShowSuggestions(false);
    setQuery('');
    navigateTo(buildPath('list', { sort: 'nearest', near_lat: coords.lat, near_lng: coords.lng, near_label: label }));
  };

  // The pin is a "near me" trigger — grab the visitor's location and show the
  // distance-sorted list. (Runs client-side so it uses the visitor's IP/GPS.)
  const useMyLocation = () => {
    setError('');
    if (!navigator.geolocation) { setError("Your browser doesn't support geolocation."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLocating(false); goNear({ lat: pos.coords.latitude, lng: pos.coords.longitude }, 'me'); },
      (err) => { setLocating(false); setError(err.code === 1 ? 'Location permission denied' : "Couldn't get your location"); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  // Open a place's detail page directly.
  const openPlace = (p) => {
    dismissKeyboard();
    setShowSuggestions(false);
    setQuery('');
    navigateTo(buildPath('detail', null, p.id));
  };

  // Show what's near a place.
  const pickPlaceNearby = async (p) => {
    if (p.coords && Number.isFinite(p.coords.lat) && Number.isFinite(p.coords.lng)) {
      goNear(p.coords, p.name);
      return;
    }
    // Fallback: place exists but has no stored coordinates — geocode by address
    setSubmitting(true);
    try {
      const c = await api.geocode([p.name, p.city, p.country].filter(Boolean).join(', '));
      goNear(c, p.name);
    } catch (err) {
      setError("Couldn't locate that place on the map.");
    } finally {
      setSubmitting(false);
    }
  };

  // Clicking a city/country in the dropdown navigates to that filter on the
  // list page — the natural "show me places in Milan" behaviour. Previously we
  // geocoded the place and routed to a distance-sorted "near Milan" page,
  // which surprised people who just wanted the city's listings.
  const pickCity = (c) => {
    dismissKeyboard();
    setShowSuggestions(false);
    setQuery('');
    navigateTo(buildPath('list', { country: c.country, city: c.city }));
  };

  const pickCountry = (c) => {
    dismissKeyboard();
    setShowSuggestions(false);
    setQuery('');
    navigateTo(buildPath('list', { country: c.country }));
  };
  const pickState = (s) => {
    dismissKeyboard();
    setShowSuggestions(false);
    setQuery('');
    navigateTo(buildPath('list', { country: s.country, state: s.state }));
  };
  const pickArea = (a) => {
    dismissKeyboard();
    setShowSuggestions(false);
    setQuery('');
    navigateTo(buildPath('list', { country: a.country, city: a.city, area: a.area }));
  };

  const submitRaw = async (e) => {
    e.preventDefault();
    setError('');
    const q = query.trim();
    if (q.length < 2) return;
    setShowSuggestions(false);
    setSubmitting(true);
    try {
      const coords = await api.geocode(q);
      goNear(coords, q);
    } catch (err) {
      setError(err.message || "Couldn't find that location — try a different spelling.");
    } finally {
      setSubmitting(false);
    }
  };

  // Free-text search across the directory — what the list page's own search box
  // does, but reachable from the header search. Goes to /list?search=…, which
  // matches name, address, city, supervision, notes and the rest server-side.
  const searchSite = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const q = query.trim();
    if (q.length < 2) return;
    setShowSuggestions(false);
    setQuery('');
    navigate('list', { search: q });
  };

  // Enter → jump to the FIRST suggestion we hold ourselves (country → state →
  // city → neighbourhood → place). When we hold nothing by that name, Enter
  // searches the DIRECTORY for the words typed — it does not fall through to
  // the map. Somebody typing "sushi", "glatt" or a rabbi's name wants our
  // listings; sending them to whatever street Nominatim matched, often in
  // another country, answers a question they did not ask. The map is still
  // one click away on the 🗺️ rows and the "Search the map for…" row below them.
  const submitFirst = (e) => {
    if (e) e.preventDefault();
    if (query.trim().length < 2) return;
    const s = suggestions;
    if (s) {
      if ((s.countries || []).length) return pickCountry(s.countries[0]);
      if ((s.states || []).length) return pickState(s.states[0]);
      if ((s.cities || []).length) return pickCity(s.cities[0]);
      if ((s.areas || []).length) return pickArea(s.areas[0]);
      if ((s.places || []).length) return openPlace(s.places[0]);
    }
    return searchSite(e);
  };

  const hero = variant === 'hero';
  const inputStyle = hero
    ? { flex: 1, padding: '14px 18px', border: 'none', borderRadius: 999, fontSize: 16, fontFamily: 'inherit', outline: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
    : { flex: 1, padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14, fontFamily: 'inherit', outline: 'none' };
  const btnStyle = hero
    ? { ...styles.button, padding: '12px 22px', fontSize: 14 }
    : { ...styles.button, padding: '8px 14px', fontSize: 13 };

  // Pick a Nominatim suggestion — it already has lat/lng; use its
  // display_name as the human label for the chip on the list page.
  const pickGeo = (g) => {
    goNear({ lat: g.lat, lng: g.lng }, shortenGeoName(g.display_name));
  };
  // Trim Nominatim's wordy "display_name" into something more readable
  // without losing the city. Nominatim returns parts ordered specific → general:
  //   "3, Via Luigi Luzzatti, Esquilino, Municipio Roma I, Roma, Roma Capitale, Lazio, 00185, Italia"
  // We collapse "3, Via Luigi Luzzatti" into "Via Luigi Luzzatti 3", filter out
  // admin-region noise and postcodes, then keep:
  //   • the immediate locality (first middle part)
  //   • the last 2 middle parts (which usually capture city + region)
  // …deduped. That keeps the disambiguating town AND the actual city visible.
  function shortenGeoName(name) {
    if (!name) return '';
    let parts = name.split(',').map((p) => p.trim()).filter(Boolean);

    // Collapse "3, Via Luigi Luzzatti, …" into "Via Luigi Luzzatti 3, …"
    if (parts.length >= 2 && /^\d+[a-z]?$/i.test(parts[0])) {
      parts = [`${parts[1]} ${parts[0]}`, ...parts.slice(2)];
    }
    if (parts.length <= 3) return parts.join(', ');

    const country = parts[parts.length - 1];
    const head = parts[0];

    // Drop admin-region noise (Municipio Roma I, Greater London, Roma Capitale,
    // Counties, Provincia, Stadtbezirk, …) and postcodes (any leading digits
    // or UK-style letter-digit mix).
    const adminNoise = /\b(greater|municipio|municipality|comune di|borough|district|arrondissement|stadtbezirk|county|counties|provincia|province|capitale|metropolitan|region|regione)\b/i;
    const postcode = /^\d{4,}|^[A-Z]{1,2}\d/;
    const middle = parts.slice(1, -1).filter((p) => !postcode.test(p) && !adminNoise.test(p));

    // Build the kept list — locality + city + region, deduped.
    const kept = [];
    const push = (s) => { if (s && !kept.includes(s)) kept.push(s); };
    if (middle.length > 0) push(middle[0]);
    if (middle.length >= 2) push(middle[middle.length - 2]);
    if (middle.length >= 1) push(middle[middle.length - 1]);

    return [head, ...kept, country].filter(Boolean).join(', ');
  }

  const hasSuggestions =
    suggestions &&
    (suggestions.places.length + suggestions.cities.length + suggestions.countries.length + ((suggestions.states || []).length) + ((suggestions.areas || []).length)) > 0;
  const hasGeoSuggestions = geoSuggestions.length > 0;

  // Every row you can land on, in the order they appear on screen. Keeping
  // one list means the arrow keys, the highlight and Enter cannot disagree
  // about what is selected.
  const nCountries = suggestions ? (suggestions.countries || []).length : 0;
  const nStates = suggestions ? (suggestions.states || []).length : 0;
  const nCities = suggestions ? (suggestions.cities || []).length : 0;
  const nAreas = suggestions ? (suggestions.areas || []).length : 0;
  const navItems = [];
  if (suggestions) {
    (suggestions.countries || []).forEach((c) => navItems.push({ run: () => pickCountry(c) }));
    (suggestions.states || []).forEach((x) => navItems.push({ run: () => pickState(x) }));
    (suggestions.cities || []).forEach((c) => navItems.push({ run: () => pickCity(c) }));
    (suggestions.areas || []).forEach((a) => navItems.push({ run: () => pickArea(a) }));
    (suggestions.places || []).forEach((pl) => navItems.push({ run: () => openPlace(pl) }));
  }
  (geoSuggestions || []).forEach((g) => navItems.push({ run: () => pickGeo(g) }));
  const nSite = navItems.length;
  const siteIdx = navItems.length;
  navItems.push({ run: () => searchSite() });
  const mapIdx = navItems.length;
  navItems.push({ run: () => submitRaw() });

  // A new set of suggestions means the old highlight points at nothing.
  const navSig = `${query}|${navItems.length}`;
  const lastSigRef = React.useRef(navSig);
  if (lastSigRef.current !== navSig) { lastSigRef.current = navSig; if (activeIdx !== 0) setActiveIdx(0); }

  const onKeyDown = (e) => {
    // INVIO DEVE SEMPRE FARE QUALCOSA, e prima non lo faceva. Due buchi: il
    // controllo qui sotto usciva quando il menu era chiuso, e l'input non sta
    // dentro un <form>, quindi non c'era nemmeno un invio di riserva; e
    // `activeIdx` poteva puntare oltre la fine dell'elenco (viene azzerato
    // durante il render, e la chiusura di questo gestore vede il valore
    // vecchio), nel qual caso `navItems[activeIdx]` era undefined e il tasto
    // moriva li. Chi scrive una cosa e preme Invio si aspetta dei risultati,
    // non il silenzio: senza niente di evidenziato si cerca in tutto il sito.
    if (e.key === 'Enter') {
      if (query.trim().length < 2) return;
      e.preventDefault();
      const item = (showSuggestions && navItems[activeIdx]) || { run: () => searchSite() };
      item.run();
      return;
    }
    if (!showSuggestions || query.trim().length < 2) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const d = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIdx((i) => (i + d + navItems.length) % navItems.length);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Keep the highlighted row in view when the arrows walk past the fold.
  React.useEffect(() => {
    const box = dropRef.current;
    if (!box) return;
    const el = box.querySelector('[data-nav-active="1"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, showSuggestions]);

  // Shared behaviour for every selectable row: the highlight follows the
  // mouse too, so hovering and arrowing never fight each other.
  const rowProps = (i, base) => ({
    'data-nav-active': i === activeIdx ? '1' : '0',
    role: 'option',
    'aria-selected': i === activeIdx,
    onMouseEnter: () => setActiveIdx(i),
    style: { ...base, background: i === activeIdx ? COLORS.bg : 'white' },
  });

  return (
    <form
      className={hero ? 'jc-address-search-hero' : 'jc-address-search-compact'}
      onSubmit={submitFirst}
      style={{ width: '100%', position: 'relative' }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: hero ? 'nowrap' : 'wrap' }}>
        <button
          type="button"
          className="jc-search-pin"
          onClick={useMyLocation}
          disabled={locating}
          title={t("Use my location — show places near me")}
          aria-label={t('Show places near me')}
          style={{ background: '#eef4fb', border: `1px solid ${COLORS.primary}33`, borderRadius: 999, padding: hero ? '8px 14px' : '7px 12px', cursor: locating ? 'wait' : 'pointer', fontSize: hero ? 14 : 13, fontWeight: 600, color: COLORS.primary, whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1, opacity: locating ? 0.6 : 1 }}
        >
          📍 {locating ? t('Locating…') : t('Near me')}
        </button>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setError(''); }}
          onFocus={() => suggestions && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={showSuggestions && query.trim().length >= 2}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={placeholder || t("Place, address, or landmark (e.g. 'Marais, Paris' or 'Eiffel Tower')")}
          style={inputStyle}
        />
        {showButton && (
          <button type="submit" style={btnStyle} disabled={submitting}>
            {submitting ? t('Searching…') : t('Search')}
          </button>
        )}
      </div>

      {/* Autocomplete dropdown — places (with coords), cities, countries, plus
          a "search the map" raw-geocode fallback at the bottom. */}
      {showSuggestions && query.trim().length >= 2 && (
        <div
          ref={dropRef}
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6,
            background: 'white', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            color: COLORS.text, textAlign: 'left', maxHeight: dropMaxH, overflowY: 'auto',
            // Leaflet's pane stack tops out at ~700 with the Expand button at 1000;
            // pick a value above both so the autocomplete always wins over the map.
            zIndex: 1500,
          }}
        >
          {(hasSuggestions || hasGeoSuggestions) ? (
            <>
              {/* Order: broadest scope first → countries, cities, places.
                  Addresses & landmarks (Nominatim geo hits) sit at the end
                  since they're external & most specific. */}
              {suggestions && suggestions.countries.length > 0 && (
                <div>
                  <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Countries')}</div>
                  {suggestions.countries.map((c, i) => (
                    <div key={c.country} onClick={() => pickCountry(c)}
                      {...rowProps(i, { padding: '8px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 })}>
                      <span>🌍 {t(c.country)}</span>
                      <span style={{ color: COLORS.muted, fontSize: 12 }}>{c.count} {c.count === 1 ? t('place') : t('places')}</span>
                    </div>
                  ))}
                </div>
              )}
              {suggestions && (suggestions.states || []).length > 0 && (
                <div style={{ borderTop: (suggestions.countries.length > 0) ? `1px solid ${COLORS.border}` : 'none' }}>
                  <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('States / Regions')}</div>
                  {suggestions.states.map((s, i) => (
                    <div key={`${s.country}-${s.state}`} onClick={() => pickState(s)}
                      {...rowProps(nCountries + i, { padding: '8px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 })}>
                      <span>🏛️ {s.state}, {t(s.country)}</span>
                      <span style={{ color: COLORS.muted, fontSize: 12 }}>{s.count} {s.count === 1 ? t('place') : t('places')}</span>
                    </div>
                  ))}
                </div>
              )}
              {suggestions && suggestions.cities.length > 0 && (
                <div style={{ borderTop: (suggestions.countries.length > 0 || (suggestions.states || []).length > 0) ? `1px solid ${COLORS.border}` : 'none' }}>
                  <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Cities')}</div>
                  {suggestions.cities.map((c, i) => (
                    <div key={`${c.country}-${c.city}`} onClick={() => pickCity(c)}
                      {...rowProps(nCountries + nStates + i, { padding: '8px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 })}>
                      <span>🏙️ {t(c.city)}, {t(c.country)}</span>
                      <span style={{ color: COLORS.muted, fontSize: 12 }}>{c.count} {c.count === 1 ? t('place') : t('places')}</span>
                    </div>
                  ))}
                </div>
              )}
              {suggestions && (suggestions.areas || []).length > 0 && (
                <div style={{ borderTop: (suggestions.countries.length + (suggestions.states || []).length + suggestions.cities.length > 0) ? `1px solid ${COLORS.border}` : 'none' }}>
                  <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Neighborhoods')}</div>
                  {suggestions.areas.map((a, i) => (
                    <div key={`${a.country}-${a.city}-${a.area}`} onClick={() => pickArea(a)}
                      {...rowProps(nCountries + nStates + nCities + i, { padding: '8px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 })}>
                      <span>📍 {a.area}, {a.city}</span>
                      <span style={{ color: COLORS.muted, fontSize: 12 }}>{a.count} {a.count === 1 ? t('place') : t('places')}</span>
                    </div>
                  ))}
                </div>
              )}
              {suggestions && suggestions.places.length > 0 && (
                <div style={{ borderTop: (suggestions.countries.length + suggestions.cities.length > 0) ? `1px solid ${COLORS.border}` : 'none' }}>
                  <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Places on Jewish Compass')}</div>
                  {suggestions.places.map((p, i) => (
                    <div key={p.id}
                      onClick={() => openPlace(p)}
                      title={t("Open this place's page")}
                      {...rowProps(nCountries + nStates + nCities + nAreas + i, { padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 14, borderBottom: `1px solid ${COLORS.bg}`, cursor: 'pointer' })}>
                      <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span><CategoryIcon type={p.type} /></span>
                          <span style={{ fontWeight: 600 }}>{p.name}</span>
                        </div>
                        <div style={{ color: COLORS.muted, fontSize: 12 }}>
                          {[p.address, p.city, p.country].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); pickPlaceNearby(p); }}
                          style={{ background: 'white', color: COLORS.primary, border: `1px solid ${COLORS.primary}`, padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          title={t("Show places near this one")}
                        >
                          📍 {t('Nearby')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {hasGeoSuggestions && (
                <div style={{ borderTop: hasSuggestions ? `1px solid ${COLORS.border}` : 'none' }}>
                  <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t('Addresses & landmarks')} {geoLoading && '…'}
                  </div>
                  {geoSuggestions.map((g, i) => (
                    <div key={`${g.lat}-${g.lng}-${i}`} onClick={() => pickGeo(g)}
                      {...rowProps(nSite - geoSuggestions.length + i, { padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 })}>
                      <span style={{ fontSize: 14 }}>🗺️</span>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {shortenGeoName(g.display_name)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
          {/* Free-text search of the directory itself — the same thing the list
              page's own search box does, reachable from the header. The
              suggestions above only ever offer a country / region / city / area /
              place to JUMP to, and the row below geocodes an address; neither
              can find "sushi", "glatt" or a rabbi's name, which is what someone
              typing a word rather than a placename usually wants. */}
          {/* When nothing matched, say so. Two bare action rows under an
              empty list look like a broken dropdown rather than an answer. */}
          {!hasSuggestions && !hasGeoSuggestions && !geoLoading && (
            <div style={{ padding: '12px 14px 4px', fontSize: 13, color: COLORS.muted }}>
              {t('Nothing on the site matches that name.')}
            </div>
          )}
          <div
            onClick={searchSite}
            {...rowProps(siteIdx, { padding: '10px 14px', cursor: 'pointer', borderTop: hasSuggestions ? `1px solid ${COLORS.border}` : 'none', fontSize: 13, color: COLORS.primary, fontWeight: 600 })}
          >
            🔍 {t('Search the whole site for')} "{query.trim()}"
          </div>
          {/* Always show the raw-geocode fallback so addresses/landmarks not in
              our DB still resolve (Eiffel Tower, Via Veneto 100 Roma, etc.). */}
          <div
            onClick={submitRaw}
            {...rowProps(mapIdx, { padding: '10px 14px', cursor: 'pointer', borderTop: `1px solid ${COLORS.border}`, fontSize: 13, color: COLORS.primary, fontWeight: 600 })}
          >
            🔎 {t('Search the map for')} "{query.trim()}"
          </div>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 6, fontSize: 13, color: hero ? '#fbbf24' : COLORS.danger }}>
          {error}
        </div>
      )}
    </form>
  );
}

// SearchBar — reusable search input with autocomplete dropdown.
// Variants:
//   variant="hero"    — big white input + button (used on home page)
//   variant="compact" — slim input + button (used in detail page header)
function SearchBar({ onSearch, onSelect, variant = 'compact', placeholder = null, autoFocus = false, showButton = true }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestTimer = React.useRef(null);
  const dropRef = React.useRef(null);
  const hasSuggest = !!(suggestions && (suggestions.places.length + suggestions.cities.length + suggestions.countries.length) > 0);
  const dropMaxH = useKeyboardSafeMaxHeight(dropRef, showSuggestions && hasSuggest);
  useDismissKeyboardOnScroll(dropRef, showSuggestions);

  React.useEffect(() => {
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    const q = query.trim();
    if (q.length < 3) { setSuggestions(null); return; }   // come il server: sotto i 3 l'indice trigram non serve
    suggestTimer.current = setTimeout(() => {
      api.searchSuggest(q)
        .then((r) => { setSuggestions(r); setShowSuggestions(true); })
        .catch(() => setSuggestions(null));
    }, 180);
    return () => suggestTimer.current && clearTimeout(suggestTimer.current);
  }, [query]);

  const hero = variant === 'hero';
  const inputStyle = hero ? styles.searchInput : {
    flex: 1, padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6,
    fontSize: 14, fontFamily: 'inherit', outline: 'none',
  };
  const btnStyle = hero ? styles.button : { ...styles.button, padding: '8px 14px', fontSize: 13 };

  const submit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    // Enter → act on the FIRST suggestion in the dropdown order (place → city →
    // country), exactly as if it were clicked; fall back to a plain text search.
    const s = suggestions;
    if (s) {
      if (s.places && s.places.length) { const p = s.places[0]; setQuery(''); return onSelect ? onSelect(p) : onSearch(p.name); }
      if (s.cities && s.cities.length) { const c = s.cities[0]; setQuery(''); return onSearch('', { country: c.country, city: c.city }); }
      if (s.countries && s.countries.length) { const c = s.countries[0]; setQuery(''); return onSearch('', { country: c.country }); }
    }
    if (query.trim()) onSearch(query.trim());
  };

  return (
    <form style={{ ...(hero ? styles.searchBar : { display: 'flex', gap: 8, alignItems: 'center' }), position: 'relative', width: '100%' }} onSubmit={submit}>
      <input
        style={inputStyle}
        placeholder={placeholder || t('Search places, cities, countries...')}
        value={query}
        autoFocus={autoFocus}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => suggestions && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
      />
      {showButton && <button type="submit" style={btnStyle}>{t('Search')}</button>}

      {showSuggestions && hasSuggest && (
        <div
          ref={dropRef}
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6,
            background: 'white', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            textAlign: 'left', maxHeight: dropMaxH, overflowY: 'auto',
            // Above Leaflet's panes (≤700) and the map's Expand button (1000)
            // so the dropdown isn't obscured when it extends over a map.
            zIndex: 1500, color: COLORS.text,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.places.length > 0 && (
            <div>
              <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Places')}</div>
              {suggestions.places.map((p) => (
                <div
                  key={p.id}
                  onClick={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); setShowSuggestions(false); setQuery(''); onSelect ? onSelect(p) : onSearch(p.name); }}
                  style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 }}
                  onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span><span style={{ marginRight: 6 }}><CategoryIcon type={p.type} /></span>{p.name}</span>
                  <span style={{ color: COLORS.muted, fontSize: 12 }}>{[p.city, p.country].filter(Boolean).join(', ')}</span>
                </div>
              ))}
            </div>
          )}
          {suggestions.cities.length > 0 && (
            <div style={{ borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Cities')}</div>
              {suggestions.cities.map((c) => (
                <div
                  key={`${c.country}-${c.city}`}
                  onClick={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); setShowSuggestions(false); setQuery(''); onSearch('', { country: c.country, city: c.city }); }}
                  style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 }}
                  onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>📍 {t(c.city)}, {t(c.country)}</span>
                  <span style={{ color: COLORS.muted, fontSize: 12 }}>{c.count} {c.count === 1 ? t('place') : t('places')}</span>
                </div>
              ))}
            </div>
          )}
          {suggestions.countries.length > 0 && (
            <div style={{ borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Countries')}</div>
              {suggestions.countries.map((c) => (
                <div
                  key={c.country}
                  onClick={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); setShowSuggestions(false); setQuery(''); onSearch('', { country: c.country }); }}
                  style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 }}
                  onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>🌍 {t(c.country)}</span>
                  <span style={{ color: COLORS.muted, fontSize: 12 }}>{c.count} {c.count === 1 ? t('place') : t('places')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

// "Near me" button used on the home hero. Asks the browser for location,
// then navigates to the distance-sorted list. Self-contained — owns its own
// loading + permission-denied states so the parent doesn't have to know.
function NearMeButton() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const click = () => {
    setError('');
    if (!navigator.geolocation) {
      setError("Your browser doesn't support geolocation.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        navigateTo(buildPath('list', {
          sort: 'nearest',
          near_lat: pos.coords.latitude,
          near_lng: pos.coords.longitude,
          near_label: 'me',
        }));
      },
      (err) => {
        setBusy(false);
        setError(err.code === 1 ? 'Location permission denied' : "Couldn't get your location");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <button
        type="button"
        onClick={click}
        disabled={busy}
        style={{
          background: 'white',
          color: COLORS.primary,
          border: 'none',
          padding: '10px 20px',
          borderRadius: 999,
          cursor: busy ? 'wait' : 'pointer',
          fontSize: 14,
          fontWeight: 700,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          opacity: busy ? 0.75 : 1,
        }}
      >
        {busy ? `📍 ${t('Locating…')}` : `📍 ${t('Near me')}`}
      </button>
      {error && (
        <div style={{ color: 'white', background: 'rgba(220,38,38,0.85)', fontSize: 12, marginTop: 8, padding: '4px 10px', borderRadius: 6, display: 'inline-block' }}>
          {error}
        </div>
      )}
    </div>
  );
}

// Public Facebook Page whose latest posts show on the homepage. Change this to
// the real Jewish Compass Page URL. Set to '' (empty) to hide the section.
const FB_PAGE_URL = 'https://www.facebook.com/jewishcompass.com';

// Facebook Page Plugin (official, free, no SDK). Renders the Page's timeline in
// an iframe. Lazy: the iframe src is only set once the section scrolls near the
// viewport, so no Facebook request/cookie fires on initial page load and it
// never blocks the hero. Falls back silently if FB_PAGE_URL is empty.
function FacebookFeed() {
  const ref = React.useRef(null);
  const [inView, setInView] = useState(false);
  const [width, setWidth] = useState(340);

  useEffect(() => {
    if (!ref.current || !FB_PAGE_URL) return undefined;
    const el = ref.current;
    // Plugin max width is 500; size to the container (min 280) so it fits mobile.
    const measure = () => setWidth(Math.max(280, Math.min(500, el.clientWidth)));
    measure();
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { measure(); setInView(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  if (!FB_PAGE_URL) return null;
  const src = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(FB_PAGE_URL)}`
    + `&tabs=timeline&width=${width}&height=500&small_header=true&adapt_container_width=true`
    + `&hide_cover=false&show_facepile=false`;

  return (
    <div style={{ marginTop: 8 }}>
      <h2 style={styles.sectionTitle}>{t("📘 Latest from Facebook")}</h2>
      <div ref={ref} style={{ display: 'flex', justifyContent: 'center', minHeight: inView ? 0 : 520 }}>
        {inView ? (
          <iframe
            title={t("Jewish Compass on Facebook")}
            src={src}
            width={width}
            height={500}
            style={{ border: 'none', overflow: 'hidden', borderRadius: 12, maxWidth: '100%' }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen
            allow="encrypted-media"
            loading="lazy"
          />
        ) : (
          <div style={{ color: COLORS.muted, fontSize: 13, alignSelf: 'center' }}>{t('Loading feed…')}</div>
        )}
      </div>
    </div>
  );
}

// Homepage "Near You" — a mini map plus a list of the closest places. Opt-in
// and privacy-first: it auto-loads ONLY if the browser has already granted
// geolocation (no fresh permission prompt on page load); otherwise it offers a
// slim button. Per spec, if there is nothing within NEARBY_RADIUS_KM the whole
// section is hidden (status 'hidden' → renders null), as are denied/error/no-
// geolocation cases.
const NEARBY_RADIUS_KM = 200;
function NearbyPlaces({ onSelect }) {
  const [status, setStatus] = useState('idle'); // idle | locating | ready | hidden
  const [places, setPlaces] = useState([]);
  const [center, setCenter] = useState(null);

  const locate = React.useCallback(() => {
    if (!navigator.geolocation) { setStatus('hidden'); return; }
    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCenter({ lat, lng });
        api.establishments({ near_lat: lat, near_lng: lng, radius_km: NEARBY_RADIUS_KM, sort: 'nearest', limit: 12 })
          .then((r) => {
            const list = (r.results || []).filter((p) => typeof p.distance_km === 'number' && p.distance_km <= NEARBY_RADIUS_KM);
            setPlaces(list);
            setStatus(list.length ? 'ready' : 'hidden'); // nothing within 200 km → don't show
          })
          .catch(() => setStatus('hidden'));
      },
      () => setStatus('hidden'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }, []);

  useEffect(() => {
    // Auto-locate only when permission is already granted, so we never trigger
    // a fresh browser prompt just from landing on the homepage.
    if (!navigator.permissions || !navigator.geolocation) return;
    navigator.permissions.query({ name: 'geolocation' })
      .then((res) => { if (res.state === 'granted') locate(); })
      .catch(() => { /* Permissions API unsupported — leave the opt-in button */ });
  }, [locate]);

  if (status === 'hidden') return null;

  if (status === 'idle') {
    return (
      <div style={{ marginTop: 8, marginBottom: 4 }}>
        <button
          type="button"
          onClick={locate}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: 'white',
            border: `1px solid ${COLORS.border}`, borderRadius: 999, padding: '9px 18px',
            cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.primary, fontFamily: 'inherit',
          }}
        >
          📍 {t('Find kosher places near you')}
        </button>
      </div>
    );
  }

  if (status === 'locating') {
    return <div style={{ marginTop: 8, color: COLORS.muted, fontSize: 14 }}>{t('📍 Finding places near you…')}</div>;
  }

  const fmtDist = (d, country) => formatDistance(d, country);
  return (
    <div style={{ marginTop: 8 }}>
      <h2 style={styles.sectionTitle}>{t('📍 Near You')}</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 380px', minWidth: 280 }}>
          <Riparo nome="MapPanel (vicino a me)" messaggio={t('Map unavailable — the list below still works.')}>
            <MapPanel results={places} onSelect={onSelect} searchPin={{ lat: center.lat, lng: center.lng, label: 'Your location' }} />
          </Riparo>
        </div>
        {/* List scrolls within the map's height (MapPanel renders a 400px map)
            so the two columns stay aligned and long result sets don't push the
            page down. */}
        <div style={{ flex: '1 1 280px', minWidth: 240, maxHeight: 400, overflowY: 'auto', paddingRight: 4 }}>
          {places.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelect && onSelect(p)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 10,
                padding: '9px 12px', marginBottom: 8, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <span style={{ fontSize: 20, lineHeight: 1, flex: '0 0 auto' }}><CategoryIcon type={p.type} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                <span style={{ display: 'block', fontSize: 12, color: COLORS.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[p.city, p.country].filter(Boolean).join(', ') || formatCategoryLabel(p.type)}
                </span>
              </span>
              <span style={{ flex: '0 0 auto', fontSize: 12, fontWeight: 700, color: COLORS.primary }}>{fmtDist(p.distance_km, p.country)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Zmanim panel for a specific CITY (uses the city's own coordinates + timezone,
// not the visitor's) — shown at the top of the list when a city/metro is
// selected. Fetches this week's Shabbat times + today's sunrise/sunset. Renders
// nothing until data arrives, and nothing if the location has no times.
function CityZmanim({ lat, lng, country, cityName, tz: tzProp }) {
  const [shab, setShab] = useState(null);
  const [z, setZ] = useState(null);
  const [dead, setDead] = useState(false);

  const fmtISO = (iso) => {
    const m = /T(\d{2}):(\d{2})/.exec(String(iso || ''));
    if (!m) return null;
    let h = parseInt(m[1], 10); const min = m[2];
    const period = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12; else if (h > 12) h -= 12;
    return `${h}:${min} ${period}`;
  };

  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
    let alive = true;
    setShab(null); setZ(null); setDead(false);
    const tz = tzProp || guessTimezone(country, lat, lng);
    Promise.allSettled([
      fetch(shabbatUrl(lat, lng, tz)).then((r) => (r.ok ? r.json() : Promise.reject(new Error('x')))),
      fetch(zmanimUrl(lat, lng, tz)).then((r) => (r.ok ? r.json() : Promise.reject(new Error('x')))),
    ]).then(([s, zz]) => {
      if (!alive) return;
      if (s.status === 'fulfilled') {
        const items = Array.isArray(s.value.items) ? s.value.items : [];
        const c = items.find((i) => i.category === 'candles');
        const h = items.find((i) => i.category === 'havdalah');
        const p = items.find((i) => i.category === 'parashat');
        // `location` is resolved server-side — used as the panel's title when no
        // cityName is passed (the home page, where we only have coordinates).
        setShab({ candles: c ? fmtISO(c.date) : null, havdalah: h ? fmtISO(h.date) : null, parasha: p ? p.title : null, location: s.value.location || null });
      }
      if (zz.status === 'fulfilled') {
        const tt = (zz.value && zz.value.times) || {};
        setZ({
          netz: tt.sunrise ? fmtISO(tt.sunrise) : null,
          shema: tt.sofZmanShma ? fmtISO(tt.sofZmanShma) : null,
          chatzos: tt.chatzot ? fmtISO(tt.chatzot) : null,
          sunset: tt.sunset ? fmtISO(tt.sunset) : null,
        });
      }
      if (s.status !== 'fulfilled' && zz.status !== 'fulfilled') setDead(true);
    });
    return () => { alive = false; };
  }, [lat, lng, country, tzProp]);

  if (dead) return null;
  const hasAny = (shab && (shab.candles || shab.havdalah)) || (z && (z.netz || z.shema || z.chatzos || z.sunset));
  if (!hasAny) return null; // still loading, or nothing to show

  const block = (label, value) => (value ? (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t(label)}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.primary }}>{value}</div>
    </div>
  ) : null);

  return (
    <div style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #ffffff 70%)', border: `1px solid ${COLORS.accent}66`, borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.text }}>
        🕯️ {t('Zmanim')}{(cityName || (shab && shab.location)) ? ` · ${cityName || shab.location}` : ''}
      </div>
      {shab && shab.parasha && <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>{shab.parasha}</div>}
      <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 8 }}>
        {block('Candle lighting', shab && shab.candles)}
        {block('Havdalah', shab && shab.havdalah)}
        {block('Netz (sunrise)', z && z.netz)}
        {block('Sof Zman Shema', z && z.shema)}
        {block('Chatzos', z && z.chatzos)}
        {block('Sunset', z && z.sunset)}
      </div>
    </div>
  );
}

// Home-page zmanim — the SAME full panel as the city page (CityZmanim), but for
// the VISITOR's own location. Privacy-first like ShabbatTimes: auto-loads only
// when geolocation is already granted (never prompts on load), otherwise offers a
// slim opt-in button. Passes the browser's IANA timezone so DST/clock is correct.
const ZMANIM_LOC_KEY = 'jc_zmanim_loc';
function HomeZmanim() {
  const [loc, setLoc] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | busy | ready | hidden
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const tz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || undefined;

  const load = React.useCallback(() => {
    if (!navigator.geolocation) { setStatus('hidden'); return; }
    setStatus('busy');
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setStatus('ready'); },
      () => setStatus('hidden'),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 },
    );
  }, []);

  useEffect(() => {
    // A location the visitor set by hand wins over geolocation — on desktop the
    // browser locates by IP, which lands on the ISP's hub city (often 100+ km
    // off) and would give zmanim for the wrong town.
    try {
      const saved = JSON.parse(localStorage.getItem(ZMANIM_LOC_KEY) || 'null');
      if (saved && Number.isFinite(saved.lat) && Number.isFinite(saved.lng)) { setLoc(saved); setStatus('ready'); return; }
    } catch (e) { /* ignore */ }
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((p) => { if (p.state === 'granted') load(); }).catch(() => {});
    }
  }, [load]);

  // Debounced place lookup for the "wrong town?" picker.
  useEffect(() => {
    if (!picking || q.trim().length < 3) { setHits([]); return undefined; }
    const id = setTimeout(() => {
      api.geocodeSuggest(q.trim()).then((r) => setHits(Array.isArray(r) ? r.slice(0, 5) : [])).catch(() => setHits([]));
    }, 300);
    return () => clearTimeout(id);
  }, [q, picking]);

  const choose = (h) => {
    const next = { lat: Number(h.lat), lng: Number(h.lng), name: (h.display_name || '').split(',')[0].trim() || null };
    try { localStorage.setItem(ZMANIM_LOC_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
    setLoc(next); setStatus('ready'); setPicking(false); setQ(''); setHits([]);
  };
  const useMyLocation = () => {
    try { localStorage.removeItem(ZMANIM_LOC_KEY); } catch (e) { /* ignore */ }
    setPicking(false); setQ(''); setHits([]); load();
  };

  const picker = (
    <div style={{ marginTop: 6 }}>
      {!picking ? (
        <button type="button" onClick={() => setPicking(true)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: 'rgba(255,255,255,0.85)', textDecoration: 'underline', fontFamily: 'inherit' }}>
          {t('Wrong place? Set your location')}
        </button>
      ) : (
        <div style={{ background: 'white', borderRadius: 8, padding: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}>
          <input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t('Type a city or town…')}
            style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: `1px solid ${COLORS.border}`, borderRadius: 6, outline: 'none', fontFamily: 'inherit' }}
          />
          {hits.map((h, i) => (
            <div key={`${h.lat}-${h.lng}-${i}`} onClick={() => choose(h)}
              style={{ padding: '7px 8px', cursor: 'pointer', fontSize: 12.5, color: COLORS.text, borderTop: `1px solid ${COLORS.bg}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bg}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
              📍 {h.display_name}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 8, fontSize: 11.5 }}>
            <button type="button" onClick={useMyLocation} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: COLORS.primary, fontWeight: 600, fontFamily: 'inherit' }}>{t('Use my location')}</button>
            <button type="button" onClick={() => { setPicking(false); setQ(''); setHits([]); }} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: COLORS.muted, fontFamily: 'inherit' }}>{t('Cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );

  if (status === 'ready' && loc) {
    return (
      <div>
        <CityZmanim lat={loc.lat} lng={loc.lng} tz={tz} cityName={loc.name || null} />
        {picker}
      </div>
    );
  }
  if (status === 'busy') return <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>🕯️ {t('Loading…')}</div>;
  if (status === 'idle') {
    return (
      <div>
        <button
          type="button"
          onClick={load}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'white', border: `1px solid ${COLORS.accent}`, borderRadius: 999, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COLORS.primary, fontFamily: 'inherit' }}
        >
          🕯️ {t('Zmanim')}
        </button>
        {picker}
      </div>
    );
  }
  return picker;
}

// QUANDO UNA KASHRUT CAMBIA, È UNA NOTIZIA — e va dove si leggono le notizie.
//
// Menachem, 20/10/2026: «I want to have on the home page changes in kashrut,
// for example yesterday one place became not kosher, now I need to take off
// Landau from Pizza Story… the last 5 changes and possibility to see all the
// rest, not all changes should show there, some are changes that were a
// mistake but these two for example are real changes». E poi: «you could put
// it on top of latest news».
//
// Il database li registra tutti da solo, ma qui arriva solo quello che
// l'admin ha segnato come pubblico: una correzione di un nostro errore non è
// una notizia, la pizzeria che perde il lanciarsi sì.
// La pagina con tutti i cambi, dietro «See all».
function KashrutChangesPage() {
  const [lista, setLista] = useState(null);
  useEffect(() => {
    api.kashrutChanges(100).then((r) => setLista((r && r.changes) || [])).catch(() => setLista([]));
  }, []);
  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 16px 60px' }}>
      <h1 style={{ ...styles.sectionTitle, marginTop: 0 }}>{t('✡️ Kashrut updates')}</h1>
      <p style={{ color: COLORS.muted, fontSize: 14, marginTop: 0 }}>
        {t('Places whose hechsher has actually changed — gained, lost or swapped. Corrections to our own records are not listed here.')}
      </p>
      {lista === null && <div style={{ color: COLORS.muted }}>{t('Loading…')}</div>}
      {lista && lista.length === 0 && <div style={{ color: COLORS.muted }}>{t('Nothing to report yet.')}</div>}
      {lista && lista.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          {lista.map((c) => <KashrutChangeRow key={c.id} c={c} grande />)}
        </div>
      )}
    </div>
  );
}

function HomePage({ onCategorySelect, onSearch, onSelect }) {
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [topCities, setTopCities] = useState([]);
  const [topPlaces, setTopPlaces] = useState([]);        // most visited, 30d
  const [topVisitedCities, setTopVisitedCities] = useState([]); // most visited cities, 30d
  const [topContacted, setTopContacted] = useState([]);         // most contacted (email+whatsapp), 30d
  const [latestNews, setLatestNews] = useState([]);             // most recent published articles
  const [cambiKashrut, setCambiKashrut] = useState([]);         // gli ultimi 5 cambi di hechsher
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState([]);
  const [reviewOfDay, setReviewOfDay] = useState(null);         // showcased review, rotates daily
  // SCEGLI UN PAESE E TI ESCONO LE CITTÀ.
  // Menachem, 21/10/2026: «quando si sceglie un paese forse possono apparire
  // le maggiori città». La tendina mandava dritti alla lista di tutta la
  // Francia — 1.652 schede — e la prima cosa che uno doveva fare lì era
  // restringere a una città, che è la domanda che aveva in testa fin
  // dall'inizio. Meglio chiedergliela qui, dove costa un tocco.
  const [paeseScelto, setPaeseScelto] = useState('');
  const [cittaDelPaese, setCittaDelPaese] = useState([]);

  useEffect(() => {
    Promise.all([
      api.categories(),
      api.countries(),
      api.cities({ sort: 'count', limit: 12 }),
      api.establishments({ sort: 'recent', limit: 30 }),
    ])
      .then(([cats, cs, tc, rec]) => {
        // On-page category displays are ordered by size (most places first);
        // the category dropdowns re-sort themselves alphabetically.
        const sortedCats = [...cats].sort((a, b) => (parseInt(b.count) || 0) - (parseInt(a.count) || 0));
        setCategories(sortedCats);
        setCountries(cs);
        setTopCities(tc);
        setRecent(rec.results || []);
        setLoading(false);
      })
      .catch((e) => { console.error(e); setLoading(false); });
    // Visit-based rankings — independent (and optional); never block the page.
    api.topVisitedPlaces(30, 16).then((r) => setTopPlaces(Array.isArray(r) ? r : [])).catch(() => {});
    api.topVisitedCities(30, 16).then((r) => setTopVisitedCities(Array.isArray(r) ? r : [])).catch(() => {});
    api.topContactedPlaces(30, 16).then((r) => setTopContacted(Array.isArray(r) ? r : [])).catch(() => {});
    // QUATTRO, NON SETTE. Menachem, 26/10/2026: «show less news on the
    // homepage», poi «forse meglio 4 articoli». Ogni articolo qui e' una
    // scheda a tutta larghezza con l'immagine alta 120px: sette facevano
    // mezza pagina di notizie prima di arrivare al resto. Quattro fanno
    // vedere che la sezione e' viva, e chi ne vuole altre ha «See all →»
    // proprio sopra.
    api.news({ limit: 4 }).then((r) => setLatestNews(Array.isArray(r) ? r : [])).catch(() => {});
    api.kashrutChanges(5).then((r) => setCambiKashrut((r && r.changes) || [])).catch(() => {});
    // The server picks which review to show, so everyone sees the same one.
    api.reviewOfTheDay().then((r) => setReviewOfDay(r && r.review ? r : null)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <>
        <div style={{ ...styles.hero, paddingBottom: 32 }}>
          <Skeleton width="60%" height={42} style={{ background: 'rgba(255,255,255,0.2)', maxWidth: 400, margin: '0 auto 16px' }} />
          <Skeleton width="80%" height={20} style={{ background: 'rgba(255,255,255,0.15)', maxWidth: 600, margin: '0 auto 24px' }} />
          <Skeleton width="100%" height={56} style={{ borderRadius: 999, maxWidth: 600, margin: '0 auto' }} />
        </div>
        <div style={styles.container}>
          <Skeleton width={180} height={28} style={{ margin: '24px 0 16px' }} />
          <div style={styles.grid}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} style={styles.card}>
                <Skeleton width={48} height={48} radius={8} style={{ margin: '0 auto 12px' }} />
                <Skeleton width="70%" height={16} style={{ margin: '0 auto 8px' }} />
                <Skeleton width="40%" height={12} style={{ margin: '0 auto' }} />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  const totalEstablishments = categories.reduce((sum, c) => sum + parseInt(c.count), 0);

  // Spotlight of the day — a featured place that rotates each calendar day
  // (stable within the day). Drawn from recently-added places that have a
  // photo, so the banner always looks good.
  const spotlightPool = (recent || []).filter((p) => p.main_image_url);
  const dayIdx = Math.floor(Date.now() / (24 * 3600 * 1000));
  const spotlight = spotlightPool.length ? spotlightPool[dayIdx % spotlightPool.length] : null;

  return (
    <>
      <div style={{ ...styles.hero, textAlign: 'left', paddingTop: 26, paddingBottom: 30 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
          {/* Two columns (top-aligned): stats + welcome + browse dropdowns on the
              left, the zmanim panel on the right — so the zmanim sits high, level
              with the stats. Wraps to one column on narrow screens. */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ flex: '1 1 380px', minWidth: 260 }}>
              {/* Stats */}
              <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap', marginBottom: 18 }}>
                {[
                  { n: `${totalEstablishments.toLocaleString()}+`, l: 'Places' },
                  { n: countries.length, l: 'Countries' },
                  { n: categories.length, l: 'Categories' },
                ].map((s) => (
                  <div key={s.l}>
                    <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{s.n}</div>
                    <div style={{ fontSize: 12, opacity: 0.8, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, marginTop: 4 }}>{t(s.l)}</div>
                  </div>
                ))}
              </div>
              <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 16px' }}>{t('Find Jewish life, wherever you go.')}</h1>
              {/* Browse dropdowns (search lives in the top bar) */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <select
                  value={paeseScelto}
                  onChange={(e) => {
                    const c = e.target.value;
                    setPaeseScelto(c); setCittaDelPaese([]);
                    if (!c) return;
                    api.cities({ country: c, sort: 'count', limit: 12 })
                      .then((r) => setCittaDelPaese(Array.isArray(r) ? r : []))
                      // se le città non arrivano la tendina deve comunque
                      // portare da qualche parte: si va alla lista del paese
                      .catch(() => onSearch('', { country: c }));
                  }}
                  style={{ padding: '10px 14px', fontSize: 15, borderRadius: 8, border: 'none', cursor: 'pointer', minWidth: 210, color: COLORS.text, fontWeight: 600 }}
                >
                  <option value="">🌍 {t('Jump to a country')}…</option>
                  {[...countries]
                    .sort((a, b) => a.country.localeCompare(b.country))
                    .map((c) => (
                      <option key={c.country} value={c.country}>{t(c.country)} ({c.count})</option>
                    ))}
                </select>
                <select
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) onCategorySelect(e.target.value); }}
                  style={{ padding: '10px 14px', fontSize: 15, borderRadius: 8, border: 'none', cursor: 'pointer', minWidth: 210, color: COLORS.text, fontWeight: 600 }}
                >
                  <option value="">🗂️ {t('Jump to a category')}…</option>
                  {[...categories]
                    .sort((a, b) => formatCategoryLabelShort(a.type).localeCompare(formatCategoryLabelShort(b.type)))
                    .map((c) => (
                      <option key={c.type} value={c.type}>{formatCategoryLabelShort(c.type)} ({c.count})</option>
                    ))}
                </select>
              </div>
              {/* Le città del paese appena scelto, la più piena per prima. */}
              {paeseScelto && cittaDelPaese.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 8 }}>
                    {/* IL PAESE STA DENTRO LA FRASE, non attaccato dopo.
                        «Where in» + nome del paese si traduce solo in
                        inglese: in ebraico la ב va attaccata alla parola
                        («בצרפת», non «ב צרפת»), e in francese «Où en» è
                        giusto per la Francia e sbagliato per il Canada, che
                        vuole «au». Col segnaposto ogni lingua scrive la
                        frase sua — stesso modo di `{what} in {where}` più
                        in basso. */}
                    {t('Where in {country}?').replace('{country}', t(paeseScelto))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {cittaDelPaese.map((c) => (
                      <button
                        key={`${c.city}-${c.country}`}
                        onClick={() => onSearch('', { country: paeseScelto, city: c.city })}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: 'white', borderRadius: 999, padding: '7px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        {t(c.city)} <span style={{ opacity: 0.75, fontWeight: 500 }}>({c.count})</span>
                      </button>
                    ))}
                    <button
                      onClick={() => onSearch('', { country: paeseScelto })}
                      style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.35)', color: 'white', borderRadius: 999, padding: '7px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      {t('All of {country}').replace('{country}', t(paeseScelto))} →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Zmanim for the visitor's location — same full panel as the city page. */}
            <div style={{ flex: '0 1 340px', minWidth: 250 }}>
              <HomeZmanim />
            </div>
          </div>
        </div>
      </div>

      <div style={styles.container}>
        {/* Near you — first thing under the welcome (only shows once the
            visitor shares their location). */}
        <NearbyPlaces onSelect={onSelect} />


        {/* Compact chip rows — icon + name + (count). Replaces the big-card
            grids so the whole directory fits at a glance. */}
        {/* Explore by country — interactive world map (clustered flag + count
            markers) with a collapsible full country list beneath. Sits above
            the browse section. */}
        {countries.length > 0 && (
          <div style={{ margin: '8px 0 32px' }}>
            <h2 style={styles.sectionTitle}>🌍 {t('Explore by country')}</h2>
            <CountryMap countries={countries} onSelectCountry={(c) => onSearch('', { country: c })} height={430} />
            <details style={{ marginTop: 14 }}>
              <summary style={{ cursor: 'pointer', color: COLORS.primary, fontWeight: 600, fontSize: 14 }}>{t('See the full country list')}</summary>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: 8, marginTop: 12 }}>
                {countries.map((c) => (
                  <button key={c.country} onClick={() => onSearch('', { country: c.country })}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '9px 13px', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.text, fontFamily: 'inherit', textAlign: 'left' }}>
                    <Flag country={c.country} code={c.country_code} height={14} style={{ borderRadius: 2 }} />
                    {c.country} <span style={{ color: COLORS.muted, fontWeight: 500 }}>({c.count})</span>
                  </button>
                ))}
              </div>
            </details>
          </div>
        )}

        {/* Review of the day — opens the daily pair, directly above the
            Spotlight banner. Kept light and quiet on purpose: the Spotlight
            below it is a saturated blue/gold block, and two heavy banners
            stacked would fight each other. The gold rule down the left edge is
            the only thing tying it to the brand, so it reads as a quotation
            rather than another card. The server picks the review, once a day. */}
        {reviewOfDay && reviewOfDay.review && (() => {
          const r = reviewOfDay.review;
          const body = String(r.body || '').trim();
          const excerpt = body.length > 260 ? `${body.slice(0, 257).trimEnd()}…` : body;
          const place = {
            id: r.establishment_id, name: r.place_name, city: r.city,
            country: r.country, type: r.type, main_image_url: r.main_image_url,
          };
          return (
            <div
              onClick={() => onSelect && onSelect(place)}
              style={{
                // The card had no bottom margin and leaned on the Spotlight
                // banner below it for the gap. Spotlight only renders when
                // there is one to show, so on every other day the quotation
                // ran straight into "Most Visited" with nothing between them.
                // container is normal block flow, so this collapses against
                // Spotlight's 36px top margin rather than adding to it.
                cursor: 'pointer', margin: '8px 0 32px', padding: '18px 22px',
                background: COLORS.card, borderRadius: 14,
                border: `1px solid ${COLORS.border}`, borderLeft: `4px solid ${COLORS.accent}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.10)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: COLORS.muted, marginBottom: 8 }}>
                💬 {t('Review of the day')}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ color: COLORS.accent, fontSize: 16, letterSpacing: 1 }}>
                  {'★'.repeat(r.rating)}<span style={{ color: COLORS.border }}>{'★'.repeat(Math.max(0, 5 - r.rating))}</span>
                </span>
                {r.title && <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.text }}>{r.title}</span>}
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.55, color: COLORS.text, fontStyle: 'italic' }}>
                “{excerpt}”
              </div>
              <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 10 }}>
                {r.author ? `— ${r.author}, ` : '— '}
                <span style={{ color: COLORS.primary, fontWeight: 600 }}>
                  <CategoryIcon type={r.type} /> {r.place_name}
                </span>
                {[r.city, r.country].filter(Boolean).length > 0 && ` · ${[r.city, r.country].filter(Boolean).join(', ')}`}
              </div>
            </div>
          );
        })()}

        {/* Spotlight of the day — deliberately styled as a branded blue/gold
            banner (NOT a white bordered card) so it never reads as one of the
            news cards further down the page. */}
        {spotlight && (
          <div
            onClick={() => onSelect && onSelect(spotlight)}
            style={{
              display: 'flex', alignItems: 'stretch', gap: 0, cursor: 'pointer',
              borderRadius: 14, overflow: 'hidden', margin: '36px 0 40px',
              border: `2px solid ${COLORS.accent}`,
              background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
              boxShadow: '0 8px 24px rgba(13,49,99,0.28)',
              color: 'white',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 12px 32px rgba(13,49,99,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,49,99,0.28)'; }}
          >
            <div style={{ flex: '0 0 38%', maxWidth: 320, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
              <img src={imageUrl(spotlight.main_image_url)} alt={spotlight.name} style={{ width: '100%', height: '100%', maxHeight: 220, objectFit: 'cover' }} />
            </div>
            <div style={{ flex: 1, padding: '18px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: COLORS.accent, marginBottom: 6 }}>
                ✨ {t('Spotlight of the day')}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'white', marginBottom: 4 }}>
                <CategoryIcon type={spotlight.type} /> {spotlight.name}
              </div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', marginBottom: 10 }}>
                {[spotlight.city, spotlight.country].filter(Boolean).join(', ')} · {formatCategoryLabel(spotlight.type)}
              </div>
              <span style={{ alignSelf: 'flex-start', color: COLORS.accent, fontWeight: 700, fontSize: 14 }}>{t('View →')}</span>
            </div>
          </div>
        )}

        {/* Two columns: left = Most Visited / Most Contacted, right = Latest
            news stacked vertically. Wraps to one column on narrow screens. */}
        {(() => {
          const chipRow = {
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
            gap: 8, marginBottom: 20,
          };
          const chip = {
            display: 'flex', alignItems: 'center', gap: 6, background: 'white',
            border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '9px 13px',
            cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.text, fontFamily: 'inherit',
            textAlign: 'left', lineHeight: 1.2, justifyContent: 'flex-start',
          };
          const num = { color: COLORS.muted, fontWeight: 500 };
          const ico = { fontSize: 16, lineHeight: 1 };
          const sub = { fontSize: 13, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 8px' };
          const col = { flex: '1 1 360px', minWidth: 0 };
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' }}>
              {/* LEFT — Most Visited / Most Contacted */}
              <div style={col}>
                <h2 style={{ ...styles.sectionTitle, marginTop: 0 }}>{t('🔥 Most Popular · last 30 days')}</h2>

                {/* PRIMA LE CITTA', POI I POSTI.
                    Menachem, 25/10/2026: «in the home page the most visited
                    put the cities on top of the places». Chi arriva sulla home
                    quasi sempre sta decidendo DOVE va, non quale negozio
                    aprire: la citta' e' la domanda grossa e il posto viene
                    dopo. E una riga di citta' porta a una lista intera,
                    mentre una riga di posti porta a dieci schede singole. */}
                {topVisitedCities.length > 0 && (
                  <>
                    <div style={sub}>{t('Cities')}</div>
                    <div style={chipRow}>
                      {topVisitedCities.map((c) => (
                        <button key={`v-${c.city}-${c.country}`} style={chip} onClick={() => onSearch('', { country: c.country, city: c.city })} title={`${c.unique_visitors} ${c.unique_visitors === 1 ? 'visitor' : 'visitors'} who came back to a listing or got in touch`}>
                          <span style={ico}>{cityIcon(c.city)}</span>
                          {t(c.city)} <span style={num}>({c.unique_visitors})</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div style={sub}>{t('Places')}</div>
                {topPlaces.length > 0 ? (
                  <div style={chipRow}>
                    {topPlaces.map((p) => (
                      <button key={p.id} style={chip} onClick={() => onSelect && onSelect(p)} title={`${p.unique_visitors} ${p.unique_visitors === 1 ? 'visitor' : 'visitors'} who came back to it or got in touch`}>
                        <span style={ico}><CategoryIcon type={p.type} /></span>
                        {p.name} <span style={num}>({p.unique_visitors})</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: COLORS.muted, fontSize: 13, marginBottom: 20 }}>{t('No visits recorded yet — check back soon.')}</div>
                )}

                {topContacted.length > 0 && (
                  <>
                    <div style={sub}>{t('📞 Most Contacted')}</div>
                    <div style={chipRow}>
                      {topContacted.map((p) => (
                        <button key={`c-${p.id}`} style={chip} onClick={() => onSelect && onSelect(p)} title={`${p.contacts} email + WhatsApp click${p.contacts === 1 ? '' : 's'} in the last 30 days`}>
                          <span style={ico}><CategoryIcon type={p.type} /></span>
                          {p.name} <span style={num}>({p.contacts})</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* RIGHT — i cambi di kashrut e, sotto, le notizie */}
              <div style={col}>
                {cambiKashrut.length > 0 && (
                  <div style={{ marginBottom: 26 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                      <h2 style={{ ...styles.sectionTitle, marginTop: 0 }}>{t('✡️ Kashrut updates')}</h2>
                      <a href="/kashrut-changes" style={{ color: COLORS.primary, fontWeight: 600, fontSize: 14, textDecoration: 'none', whiteSpace: 'nowrap' }}>{t('See all →')}</a>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                      {cambiKashrut.map((c) => <KashrutChangeRow key={c.id} c={c} />)}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <h2 style={{ ...styles.sectionTitle, marginTop: 0 }}>{t('📰 Latest news')}</h2>
                  <a href="/news" style={{ color: COLORS.primary, fontWeight: 600, fontSize: 14, textDecoration: 'none', whiteSpace: 'nowrap' }}>{t('See all →')}</a>
                </div>
                {latestNews.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
                    {latestNews.map((a) => (
                      <a key={a.id} href={`/news/${a.slug}`}
                         style={{ display: 'flex', gap: 12, textDecoration: 'none', color: 'inherit', background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden' }}>
                        {a.cover_image_url && (
                          <div style={{ flex: '0 0 168px', alignSelf: 'stretch', minHeight: 120, backgroundImage: `url(${a.cover_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                        )}
                        <div style={{ padding: '12px 14px', minWidth: 0 }}>
                          {(a.published_at || a.created_at) && (
                            <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 5 }}>
                              {new Date(a.published_at || a.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                            </div>
                          )}
                          <div style={{ fontWeight: 700, color: COLORS.primary, fontSize: 15, lineHeight: 1.3 }}>{tradotto(a, 'title')}</div>
                          {a.excerpt && <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 6, lineHeight: 1.4 }}>{a.excerpt}</div>}
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: COLORS.muted, fontSize: 13, marginTop: 12 }}>{t('No news yet — check back soon.')}</div>
                )}
              </div>
            </div>
          );
        })()}

        <FacebookFeed />
        {/* Recently added — in fondo, non in cima. Menachem, 21/10/2026:
            «ultimi aggiunti puoi metterlo in fondo». A chi arriva la prima
            volta non interessa cosa abbiamo aggiunto ieri, interessa
            trovare un posto: sopra ci vanno le città e le categorie, e
            questo resta per chi scorre fino in fondo. */}
        {recent.length > 0 && (
          <>
            <h2 style={styles.sectionTitle}>{t('Recently Added')}</h2>
            <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12, marginBottom: 16, scrollSnapType: 'x mandatory' }}>
              {recent.map((est) => (
                <div
                  key={est.id}
                  onClick={() => onSelect && onSelect(est)}
                  style={{
                    flex: '0 0 200px', scrollSnapAlign: 'start', cursor: 'pointer',
                    background: 'white', borderRadius: 10, border: `1px solid ${COLORS.border}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ position: 'relative', height: 120, background: COLORS.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {est.main_image_url ? (
                      <img src={imageUrl(est.main_image_url)} alt={est.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ fontSize: 48 }}><CategoryIcon type={est.type} /></span>
                    )}
                    <div
                      title={formatCategoryLabel(est.type)}
                      style={{ position: 'absolute', top: 6, left: 6, width: 30, height: 30, background: 'white', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
                    >
                      <CategoryIcon type={est.type} />
                    </div>
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nomeVisibile(est)}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[est.city, est.country].filter(Boolean).map((x) => t(x)).join(', ') || formatCategoryLabel(est.type)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </>
  );
}

// DidYouMean — empty-state for the list page. When a search query yielded no
// results, fetch alternatives via /api/search-suggest and show them as
// clickable hints. If there's no query at all (filters with no search), just
// show the plain "no results" line.
function DidYouMean({ query, onSelect }) {
  const [suggestions, setSuggestions] = useState(null);

  React.useEffect(() => {
    const q = (query || '').trim();
    if (q.length < 3) { setSuggestions(null); return; }   // come il server: sotto i 3 l'indice trigram non serve
    api.searchSuggest(q).then(setSuggestions).catch(() => setSuggestions(null));
  }, [query]);

  if (!query) {
    return <div style={styles.empty}>{t('No results found. Try changing filters.')}</div>;
  }
  const hasAny = suggestions && (suggestions.places.length + suggestions.cities.length + suggestions.countries.length) > 0;

  return (
    <div style={{ ...styles.empty, paddingTop: 32, paddingBottom: 32 }}>
      <div style={{ fontSize: 16, marginBottom: 12 }}>
        {t("No results for")} <strong>"{query}"</strong>.
      </div>
      {hasAny ? (
        <>
          <div style={{ color: COLORS.muted, marginBottom: 12 }}>{t('Did you mean…')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420, margin: '0 auto', textAlign: 'left' }}>
            {suggestions.places.slice(0, 5).map((p) => (
              <button
                key={p.id}
                onClick={() => onSelect && onSelect(p)}
                style={{
                  background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8,
                  padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  gap: 12, fontSize: 14, fontFamily: 'inherit',
                }}
              >
                <span><span style={{ marginRight: 6 }}><CategoryIcon type={p.type} /></span>{p.name}</span>
                <span style={{ color: COLORS.muted, fontSize: 12 }}>{[p.city, p.country].filter(Boolean).join(', ')}</span>
              </button>
            ))}
            {suggestions.cities.slice(0, 3).map((c) => (
              <button
                key={`${c.country}-${c.city}`}
                onClick={() => navigateTo(buildPath('list', { country: c.country, city: c.city }))}
                style={{
                  background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8,
                  padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  gap: 12, fontSize: 14, fontFamily: 'inherit',
                }}
              >
                <span>📍 {t(c.city)}, {t(c.country)}</span>
                <span style={{ color: COLORS.muted, fontSize: 12 }}>{c.count} {c.count === 1 ? t('place') : t('places')}</span>
              </button>
            ))}
            {suggestions.countries.slice(0, 2).map((c) => (
              <button
                key={c.country}
                onClick={() => navigateTo(buildPath('list', { country: c.country }))}
                style={{
                  background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8,
                  padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  gap: 12, fontSize: 14, fontFamily: 'inherit',
                }}
              >
                <span>🌍 {t(c.country)}</span>
                <span style={{ color: COLORS.muted, fontSize: 12 }}>{c.count} {c.count === 1 ? t('place') : t('places')}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div style={{ color: COLORS.muted, fontSize: 13 }}>
          {t("Try a shorter or different keyword, or")} <button onClick={() => navigateTo(buildPath('list'))} style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>{t('browse everything')}</button>.
        </div>
      )}
    </div>
  );
}

// Editorial description block for a country/city landing page. Shows the
// stored heading + paragraphs to visitors (and search engines render it too);
// admins get an inline editor to write/update the text.
function GeoDescription({ country, city, user }) {
  const [data, setData] = useState(undefined); // undefined=loading, null=none, {heading,body,kosher_list_pdf}
  const [editing, setEditing] = useState(false);
  const [heading, setHeading] = useState('');
  const [body, setBody] = useState('');
  const [pdf, setPdf] = useState('');           // country kosher-list PDF url
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isCountry = !city;                       // the PDF is a country-level thing
  const where = [city, country].filter(Boolean).join(', ');

  React.useEffect(() => {
    let active = true;
    setData(undefined); setEditing(false);
    api.geoDescription({ country, city })
      .then((d) => { if (active) { setData(d); setHeading(d?.heading || ''); setBody(d?.body || ''); setPdf(d?.kosher_list_pdf || ''); } })
      .catch(() => { if (active) setData(null); });
    return () => { active = false; };
  }, [country, city]);

  const onPickPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const { url } = await api.uploadKosherListPdf(file); setPdf(url); }
    catch (err) { alert(`${t('Upload failed:')} ${err.message}`); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const save = async () => {
    if (!body.trim() && !(isCountry && pdf)) return;
    setSaving(true);
    try {
      const payload = { country, city: city || undefined, heading: heading.trim() || undefined, body: body.trim() };
      if (isCountry) payload.kosher_list_pdf = pdf || '';
      await api.saveGeoDescription(payload);
      setData({ ...(data || {}), heading: heading.trim() || null, body: body.trim(), kosher_list_pdf: isCountry ? (pdf || null) : (data?.kosher_list_pdf || null) });
      setEditing(false);
    } catch (e) {
      alert(`${t("Couldn't save:")} ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (data === undefined) return null; // loading — don't flash

  // Editor (admin)
  if (editing) {
    return (
      <div style={{ background: '#fffdf5', border: `1px dashed ${COLORS.accent}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
          {t("Editing —")} {where}
        </div>
        <input
          style={{ ...styles.input, marginBottom: 8 }}
          value={heading}
          onChange={(e) => setHeading(e.target.value)}
          placeholder={`Heading (optional) — e.g. "Jewish ${where}"`}
        />
        <textarea
          style={{ ...styles.input, minHeight: 140, fontFamily: 'inherit', resize: 'vertical' }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t("A few paragraphs on the Jewish history and present of this place. Separate paragraphs with a blank line.")}
        />
        {isCountry && (
          <div style={{ marginTop: 10, padding: 12, background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>📄 {t('Kosher list')} ({country})</div>
            <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 8 }}>{t('Link to a PDF or a kosher-list website, or upload a PDF.')}</div>
            <input
              style={{ ...styles.input, fontSize: 13 }}
              value={pdf}
              onChange={(e) => setPdf(e.target.value)}
              placeholder="https://…"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
              {pdf && <a href={pdf} target="_blank" rel="noreferrer" style={{ color: COLORS.primary, fontWeight: 600 }}>{t('Open current')}</a>}
              <label style={{ ...styles.buttonOutline, cursor: 'pointer', margin: 0, fontSize: 13, padding: '6px 12px' }}>
                {uploading ? t('Uploading…') : t('Upload PDF')}
                <input type="file" accept="application/pdf,.pdf" onChange={onPickPdf} style={{ display: 'none' }} disabled={uploading} />
              </label>
              {pdf && <button style={{ ...styles.buttonOutline, fontSize: 13, padding: '6px 12px', color: COLORS.danger }} onClick={() => setPdf('')}>{t('Remove')}</button>}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button style={styles.button} onClick={save} disabled={saving || (!body.trim() && !(isCountry && pdf))}>
            {saving ? (t("Saving…")) : (t("💾 Save"))}
          </button>
          <button style={styles.buttonOutline} onClick={() => setEditing(false)}>{t('Cancel')}</button>
        </div>
      </div>
    );
  }

  // Nothing written yet: show an admin "add" prompt, or nothing for visitors.
  if (!data) {
    return isAdmin ? (
      <div style={{ marginBottom: 16 }}>
        <button style={styles.buttonOutline} onClick={() => setEditing(true)}>
          {t("✍️ Add a description")}{isCountry ? ' / kosher-list PDF' : ''} for {where}
        </button>
      </div>
    ) : null;
  }

  const hasBody = !!(data.body && data.body.trim());

  // Render the description + country kosher-list PDF.
  return (
    <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
      {(hasBody || data.heading) && (
        <h1 style={{ color: COLORS.primary, marginTop: 0, marginBottom: 10, fontSize: 22 }}>
          {data.heading || `Jewish ${where}`}
        </h1>
      )}
      {hasBody && data.body.split(/\n\s*\n/).map((p, i) => (
        <p key={i} style={{ color: COLORS.text, lineHeight: 1.65, margin: '0 0 10px 0' }}>{p.trim()}</p>
      ))}
      {isAdmin && (
        <button
          style={{ ...styles.buttonOutline, marginTop: 10, fontSize: 13, padding: '6px 12px' }}
          onClick={() => setEditing(true)}
        >
          {t("✍️ Edit")}
        </button>
      )}
    </div>
  );
}

// Categories where a "send one request to all of these in this city" broadcast
// makes sense (contactable services). Excludes non-contactable types like
// cemeteries, eruvim, museums, info points. Mirrors the backend's label map.
const BROADCASTABLE_TYPES = new Set([
  'synagogue_shul', 'kosher_restaurant', 'kosher_eatery', 'kosher_catering',
  'kosher_hotel', 'kosher_vacation', 'jewish_tour', 'jewish_driver',
  'kosher_store', 'mikvah_ritual_bath', 'jewish_photographer',
]);

// Pick one or more hechsherim and see only places that hold them.
//
// There are over 150 certifiers, so this is a searchable list rather
// than a row of chips, ordered by how many places each one certifies —
// somebody filtering by hechsher almost always wants one of the big
// ones, and the long tail is what the search box is for.
//
// Multi-select is OR, not AND: "Badatz Eda HaCharedit or Rav Landa"
// is the question people actually ask, because a seminary or a rav
// gives a LIST of acceptable hechsherim, not a single one.
// CHIP VELOCI — i filtri che si vedono SENZA aprire il menu a tendina.
//
// Menachem, 3/11/2026: «quali filtri dovremmo avere senza aprire il menu?
// pensavo aperto-adesso e hechsher». La risposta non puo' essere una lista
// fissa, perche' un chip che non sa rispondere e' peggio di nessun chip: gli
// orari di apertura ce li ha il 6% delle schede vive, e «Aperto adesso»
// sempre in vista, su una citta' da 53 posti, ne lascerebbe tre — non perche'
// gli altri siano chiusi, ma perche' non lo sappiamo. La pagina gia' se ne
// scusa da sola in piccolo, e un filtro che chiede scusa non va in prima fila.
//
// Quindi UN CHIP COMPARE QUANDO I RISULTATI CHE HAI DAVANTI SANNO RISPONDERE:
// almeno un sesto delle schede di questa ricerca ha il campo, e almeno tre.
// Cosi' «Aperto adesso» si accende da solo nelle citta' dove gli orari li
// abbiamo riempiti — Karmiel li ha dal consiglio religioso — e resta nascosto
// dove mentirebbe; e «Mehadrin» compare in Israele e sparisce a Buenos Aires,
// senza una lista di citta' da mantenere a mano.
//
// LE COMUNITA' NON SONO UN CHIP SOLO. Menachem, stesso giorno: «uno puo'
// scegliere fra Chabad o altre, anche altre che sono listate, come per esempio
// Young Israel». Quindi i chip delle comunita' escono dai CONTEGGI di
// /communities per QUESTA ricerca: a Karmiel esce Chabad, a Parigi Consistoire,
// a Londra United Synagogue, e Young Israel dove ne abbiamo. Il menu completo
// resta nel rail; questi sono i primi tre.
const CHIP_MINIMO = 3;      // sotto le tre schede un chip non serve a nessuno
const CHIP_QUOTA = 1 / 6;   // e deve coprire almeno un sesto della ricerca
const CHIP_CIBO = ['kosher_restaurant', 'kosher_eatery', 'kosher_bakery', 'kosher_catering'];
// I due livelli sono lo stesso scaffale per chi cerca. «Mehadrin» da solo e' un
// confronto ESATTO lato server (kosherLevelConditions), quindi chiederlo senza
// il secondo lascerebbe fuori chi ha solo «Mehadrin min hamehadrin».
const CHIP_MEHADRIN = 'Mehadrin,Mehadrin min hamehadrin';

function Chip({ on, onClick, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      title={title}
      style={{ padding: '8px 13px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${on ? COLORS.primary : COLORS.border}`, background: on ? COLORS.primary : '#fff', color: on ? '#fff' : COLORS.text }}
    >
      {children}
    </button>
  );
}

function ChipVeloci({ filters, updateFilter, results, total, near }) {
  const tipi = String(filters.type || '').split(',').map((x) => x.trim()).filter(Boolean);
  const soloCibo = tipi.length > 0 && tipi.every((x) => CHIP_CIBO.includes(x));
  const soloShul = tipi.length === 1 && tipi[0] === 'synagogue_shul';

  const [generi, setGeneri] = React.useState([]);
  const [comunita, setComunita] = React.useState([]);
  const [nusachim, setNusachim] = React.useState([]);
  // Stringa, non un oggetto: un literal nella lista delle dipendenze e' un
  // oggetto nuovo a ogni render e le fetch non si fermerebbero mai — e' la
  // stessa ragione per cui ListSidebar tiene `nearKey`.
  const ambito = JSON.stringify([filters.country, filters.city, filters.area, filters.type, near]);
  React.useEffect(() => {
    let vivo = true;
    const scope = { country: filters.country, city: filters.city, area: filters.area, ...near };
    const metti = (fn) => (r) => { if (vivo) fn(Array.isArray(r) ? r : []); };
    const niente = (fn) => () => { if (vivo) fn([]); };
    if (soloCibo) api.subtypes({ ...scope, type: tipi.join(',') }).then(metti(setGeneri)).catch(niente(setGeneri));
    else setGeneri([]);
    if (soloShul) {
      api.communities({ ...scope, type: 'synagogue_shul' }).then(metti(setComunita)).catch(niente(setComunita));
      api.subtypes({ ...scope, type: 'synagogue_shul' }).then(metti(setNusachim)).catch(niente(setNusachim));
    } else { setComunita([]); setNusachim([]); }
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambito, soloCibo, soloShul]);

  const basta = (n, su) => Number(n) >= CHIP_MINIMO && Number(n) >= Math.ceil((su || 0) * CHIP_QUOTA);

  // Carne / Latte / Parve. Il conteggio arriva da /subtypes, gia' scopato come
  // il resto della pagina, percio' il numero sul chip e' il numero che il chip
  // restituira'.
  const generiScelti = String(filters.subtype || '').split(',').map((x) => x.trim()).filter(Boolean);
  const SEGNO_GENERE = { Meat: '🥩', Dairy: '🥛', Parve: '🥗' };
  const chipGeneri = !soloCibo ? [] : ['Meat', 'Dairy', 'Parve']
    .map((v) => ({ v, n: Number((generi.find((o) => o.subtype === v) || {}).count || 0) }))
    .filter((o) => basta(o.n, total) || generiScelti.includes(o.v));

  // Che shul è questa. DUE CAMPI, UNA SOLA DOMANDA: fuori da Israele la
  // risposta è la rete — Chabad a New York, United Synagogue a Londra,
  // Consistoire a Parigi — e sta in `community`; in Israele la rete non
  // vuol dire niente e la risposta è il NUSACH — Sefardi, Ashkenaz, Ari,
  // Teimani — che sta in `subtype`. Misurato: a Parigi escono Chabad (59)
  // e Consistoire (38), a Londra United Synagogue (41), a Karmiel la
  // colonna community ha tre schede su 34 e il nusach le ha quasi tutte.
  // Perciò i candidati sono i due insiemi messi insieme e ordinati per
  // quanti sono: la città decide da sé che cosa chiedere.
  const vocabolario = (COMMUNITY_OPTIONS.synagogue_shul || []).map((x) => String(x).toLowerCase());
  const vocabNusach = (SUBTYPE_OPTIONS.synagogue_shul || []).map((x) => String(x).toLowerCase());
  const chipShul = !soloShul ? [] : [
    ...(comunita || [])
      .filter((o) => vocabolario.includes(String(o.value || '').trim().toLowerCase()))
      .map((o) => ({ campo: 'community', v: o.value, n: Number(o.count) })),
    ...(nusachim || [])
      .filter((o) => vocabNusach.includes(String(o.subtype || '').trim().toLowerCase()))
      .map((o) => ({ campo: 'subtype', v: o.subtype, n: Number(o.count) })),
  ]
    .filter((o) => basta(o.n, total)
      || (o.campo === 'community' ? String(filters.community || '') === o.v : generiScelti.includes(o.v)))
    .sort((a, b) => b.n - a.n)
    .slice(0, 3);

  // Orari e Mehadrin si misurano sulle schede GIA' CARICATE: ce le abbiamo in
  // mano, e una richiesta in piu' solo per sapere se mostrare un bottone
  // sarebbe una richiesta sprecata. Per una citta' sono tutte; per una ricerca
  // larga sono le prime duecento, che come campione bastano.
  const righe = results || [];
  // Le tefillot nel campo orari non contano: una sinagoga «aperta» alle 7:30
  // perche' c'e' shacharit non e' quello che uno cerca, ed e' la stessa regola
  // che il filtro applica poi ai risultati.
  const conOrari = righe.filter((e) => String(e.hours || '').trim() && !PREGHIERE.test(e.hours || '')).length;
  const conMehadrin = righe.filter((e) => /mehadrin/i.test(e.supervision_level || '')).length;
  const apertoOn = filters.open_now === 'true';
  const mehadrinOn = /mehadrin/i.test(String(filters.kosher_level || ''));
  const mostraAperto = apertoOn || basta(conOrari, righe.length);
  const mostraMehadrin = mehadrinOn || basta(conMehadrin, righe.length);

  if (!chipGeneri.length && !chipShul.length && !mostraAperto && !mostraMehadrin) return null;

  return (
    <>
      {chipGeneri.map(({ v, n }) => (
        <Chip
          key={v}
          on={generiScelti.includes(v)}
          title={t('Show only places of this kind')}
          onClick={() => updateFilter('subtype', (generiScelti.includes(v)
            ? generiScelti.filter((x) => x !== v)
            : [...generiScelti, v]).join(','))}
        >
          {SEGNO_GENERE[v]} {t(v)}{n ? ` (${n})` : ''}
        </Chip>
      ))}
      {chipShul.map((o) => {
        const acceso = o.campo === 'community'
          ? String(filters.community || '') === o.v
          : generiScelti.includes(o.v);
        return (
          <Chip
            key={`${o.campo}:${o.v}`}
            on={acceso}
            title={o.campo === 'community' ? t('Filter by community') : t('Filter by nusach')}
            onClick={() => (o.campo === 'community'
              ? updateFilter('community', acceso ? '' : o.v)
              : updateFilter('subtype', (acceso
                ? generiScelti.filter((x) => x !== o.v)
                : [...generiScelti, o.v]).join(',')))}
          >
            {'🕍'} {t(o.v)} ({o.n})
          </Chip>
        );
      })}
      {mostraMehadrin && (
        <Chip
          on={mehadrinOn}
          title={t('Show only places with a Mehadrin standard')}
          onClick={() => updateFilter('kosher_level', mehadrinOn ? '' : CHIP_MEHADRIN)}
        >
          {'✡️'} {t('Mehadrin')}
        </Chip>
      )}
      {mostraAperto && (
        <Chip
          on={apertoOn}
          title={t('Open now — places without opening hours are not shown.')}
          onClick={() => updateFilter('open_now', apertoOn ? '' : 'true')}
        >
          {'🕐'} {t('Open now')}
        </Chip>
      )}
    </>
  );
}

function CertifierFilter({ certs, counts, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const boxRef = React.useRef(null);
  const selected = String(value || '').split(',').map((x) => x.trim()).filter(Boolean);

  React.useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  // A certifier that certifies nothing is noise in a filter menu — and
  // "nothing" means nothing HERE. `counts` holds the numbers for the current
  // view (city, category, "near me"); `certified_count` is the worldwide
  // figure that comes with the shared, logo-bearing certifier list. Prefer
  // the scoped one whenever it has arrived, so a visitor with four places
  // around them is offered the two hechsherim those places carry instead of
  // all 168. An already-selected certifier always stays listed, or
  // unticking it would mean hunting for a row that had vanished.
  const countOf = (c) => (counts ? (counts[c.id] || 0) : (c.certified_count || 0));
  const usable = (certs || []).filter((c) => countOf(c) > 0 || selected.includes(c.id));
  const needle = q.trim().toLowerCase();
  const shown = usable
    .filter((c) => !needle
      || String(c.name || '').toLowerCase().includes(needle)
      || String(c.city || '').toLowerCase().includes(needle))
    .sort((a, b) => countOf(b) - countOf(a)
      || String(a.name).localeCompare(String(b.name)));

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next.join(','));
  };

  const on = selected.length > 0;
  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={t('Show only places with a particular hechsher')}
        style={{ padding: '8px 13px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${on ? COLORS.primary : COLORS.border}`, background: on ? COLORS.primary : '#fff', color: on ? '#fff' : COLORS.text }}
      >
        {'\uD83D\uDEE1\uFE0F'} {on
          ? (selected.length === 1
            ? (usable.find((c) => c.id === selected[0]) || {}).name || t('Hechsher')
            : `${selected.length} ${t('hechsherim')}`)
          : t('Hechsher')}
        <span style={{ marginInlineStart: 6, opacity: 0.7 }}>{open ? '\u25B4' : '\u25BE'}</span>
      </button>

      {open && (
        <div style={{ position: 'absolute', top: '100%', insetInlineStart: 0, marginTop: 6, width: 320, maxWidth: '90vw', background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.18)', zIndex: 1400, overflow: 'hidden' }}>
          <div style={{ padding: 10, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('Search hechsherim\u2026')}
              style={{ flex: 1, minWidth: 0, padding: '7px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
            />
            {on && (
              <button type="button" onClick={() => { onChange(''); setQ(''); }}
                style={{ background: 'none', border: 'none', color: COLORS.primary, fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t('Clear')}
              </button>
            )}
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {shown.length === 0 && (
              <div style={{ padding: '14px', fontSize: 13, color: COLORS.muted }}>{t('No hechsher by that name.')}</div>
            )}
            {shown.map((c) => {
              const checked = selected.includes(c.id);
              return (
                <label key={c.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', background: checked ? COLORS.bg : '#fff', fontSize: 14 }}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} style={{ cursor: 'pointer' }} />
                  {c.main_image_url
                    ? <img src={c.main_image_url} alt="" style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }} />
                    : <span style={{ width: 22, textAlign: 'center', flexShrink: 0 }}>{'\uD83D\uDD0F'}</span>}
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.name}
                    {c.city && <span style={{ color: COLORS.muted, fontSize: 12 }}> {'\u00B7'} {t(c.city)}</span>}
                  </span>
                  <span style={{ color: COLORS.muted, fontSize: 12, flexShrink: 0 }} title={t("Places with this hechsher in the current view")}>{countOf(c)}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Our names follow one shape: "Latin name (שם בעברית)". Setting the
// whole string as one heading gives a long line that wraps mid-phrase and
// buries the Hebrew in brackets; splitting it gives a title and a subtitle.
//
// Only the LAST bracketed group is taken, and only when it is Hebrew, so
// "Golda — הרצל 7 (גולדה)" and "Noya (מסעדת נויה)" both work while
// "Rami Levy Mifromal (Bakery)" is left exactly as it is.
function splitPlaceName(name) {
  const full = String(name || '').trim();
  const m = full.match(/^(.*\S)\s*\(([^()]*)\)\s*$/);
  if (!m) return { title: full, subtitle: null };
  const inner = m[2].trim();
  const hebrew = /[\u0590-\u05FF]/.test(inner);
  // A title that would be left empty means the name is Hebrew-only.
  if (!hebrew || !m[1].trim()) return { title: full, subtitle: null };
  return { title: m[1].trim(), subtitle: inner };
}

// WHAT A VISITOR IS TOLD, AND WHAT IS BOOKKEEPING. The importers record
// where each fact came from in `notes` — "Listed by the Safed Rabbinate.
// Source: mdzefat.co.il." That is worth keeping and worth showing, but it
// is not advice, and it was being set in a yellow "Good to know" callout:
// the loudest treatment on the page given to its least useful sentence.
// Here it is separated out and set quietly instead.
function splitNotes(notes, name) {
  const paras = String(notes || '').split(/\n{2,}/).map((x) => x.trim()).filter(Boolean);

  // THE NAME IS NOT A TIP. The importers write the Hebrew name as the first
  // line of `notes` on purpose, so search reaches it — but nothing told this
  // function that, so it went into the yellow callout. Half of every "Good to
  // know" box on the site, 6,493 of 12,857, said nothing but the name of the
  // place you were already looking at.
  const squash = (x) => String(x || '').replace(/[\s"'\u05F3\u05F4().,\-\u2013\u2014/|]/g, '').toLowerCase();
  const n = squash(name);
  const echoesName = (x) => {
    const k = squash(x);
    return k.length > 2 && n.length > 2 && (n.includes(k) || k.includes(n));
  };

  // Bookkeeping: true, worth keeping, but it is not advice. It belongs in the
  // quiet grey line under the page, not in the loudest box on it.
  const isProvenance = (x) => /(^|\s)Source:/i.test(x)
    || /^Listed by /i.test(x)
    || /as published:/i.test(x)
    || /^Also published as /i.test(x)
    || /^Superseded by /i.test(x)
    || /^Reported as holding /i.test(x)
    // How OUR register works is not something a visitor needs in a callout.
    || /council publishes the certificate status/i.test(x)
    || /^Kashrut detail \(from /i.test(x)
    // "Listed under: מסעדה בשרית" is the register's own category for the
    // place, the same kind of fact as "as published:". Not a tip.
    || /^Listed under:/i.test(x)
    || /^Trade as published:/i.test(x)
    || /^Certificate as published:/i.test(x)
    || /^Kashrut (kind )?as published:/i.test(x)
    // Editorial history: when a listing was merged, renamed, unlisted or
    // corrected, and who said so. Useful to us, noise to a traveller.
    || /^(MERGED|UNLISTED|LOGO|REBRAND|LISTED AS|SUPPLIER NOT RECORDED)\b/i.test(x)
    || /\b(merged into this one|unite il|assorbit|doppion)\b/i.test(x)
    || /\bper indicazione di Menachem\b|\bsupplied by Menachem\b|\bConfermato da Menachem\b|\bConfirmed by Menachem\b/i.test(x);

  const kept = paras.filter((x) => !echoesName(x));
  return {
    advice: kept.filter((x) => !isProvenance(x)).join('\n\n'),
    provenance: kept.filter(isProvenance).join(' '),
  };
}

function ListPage({ initialFilters, onSelect, onHome, user, favorites, onToggleFavorite, onLogin }) {
  const [filters, setFilters] = useState(initialFilters || {});
  const [contactTarget, setContactTarget] = useState(null);
  const [broadcastTarget, setBroadcastTarget] = useState(null); // {type,typeLabel,city,country,count} — one request to a whole category in a city
  const [results, setResults] = useState([]);
  // IL FALLIMENTO DEVE AVERE UN BOTTONE. Se la prima pagina muore (un
  // deploy, un 502 del gateway, la rete del telefono), prima restava lo
  // zero muto: nell'app installata non c'e barra dell'indirizzo, quindi
  // nessun modo di riprovare (segnalato da Menachem il 16/10).
  // retryTick sta nelle dipendenze dell'effetto: il bottone lo
  // incrementa e il fetch riparte da solo.
  const [loadError, setLoadError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  // I FILTRI DIETRO UN BOTTONE, SUL TELEFONO.
  //
  // Menachem, 21/10/2026: «come possiamo fare la navigazione più user
  // friendly?». Misurato su un telefono da 375×812, la pagina di Roma:
  // cinque tendine impilate, il conteggio, il riquadro «Share», il filtro
  // hechsher, il testo editoriale, la mappa a schermo intero, tre banner di
  // affiliazione e altri sette filtri — e il PRIMO POSTO a y=2590. Tre
  // schermate e mezza di scorrimento prima di vedere un locale, su una
  // pagina alta 46.367 pixel.
  //
  // Nessuno di quei pezzi è di troppo: è l'ordine. Dodici controlli impilati
  // sono un modulo, non una navigazione, e un modulo su un telefono sta
  // dietro un bottone. Sul desktop, dove lo spazio c'è e i filtri si vedono
  // tutti in una riga, non cambia niente.
  const [stretto, setStretto] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [filtriAperti, setFiltriAperti] = useState(false);
  React.useEffect(() => {
    const onResize = () => setStretto(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const resultsRef = React.useRef([]);
  useEffect(() => { resultsRef.current = results; }, [results]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [metros, setMetros] = useState([]);
  const [states, setStates] = useState([]);
  // Kosher certifiers (with logos) — loaded once so list cards can show the
  // certifier's logo on certified places. Cheap (one small list).
  const [koshCerts, setKoshCerts] = useState([]);
  // Browse list shows the "certifies N places" badge → needs the counts variant.
  useEffect(() => { api.kosherCertifications(true).then(setKoshCerts).catch(() => setKoshCerts([])); }, []);
  // User's geographic anchor for the "Near …" sort. Either:
  //   - device GPS (after they click "Near me"), OR
  //   - geocoded address (typed into the address search, passed in via URL).
  // Initialize from URL so direct links from the home page work on first load.
  const [userCoords, setUserCoords] = useState(() => {
    const lat = parseFloat(initialFilters?.near_lat);
    const lng = parseFloat(initialFilters?.near_lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  });
  const [nearLabel, setNearLabel] = useState(() => initialFilters?.near_label || null);
  const [geoError, setGeoError] = useState(null);

  // THE MENU AND THE LIST HAVE TO ASK THE SAME QUESTION. The backend's
  // near filter needs all three of near_lat, near_lng and radius_km —
  // any one missing and it applies NO distance condition at all. The
  // results list defaulted an unset radius to 1.5km; the category menu
  // passed filters.radius_km straight through, which is undefined until
  // somebody picks one. So the list showed what is nearby and the menu
  // counted the entire directory beside it: "Restaurants 1,284" over a
  // list holding three.
  //
  // One function, used by both, so they cannot drift apart again.
  // 'any' deliberately sends no radius — that is the "everywhere, sorted
  // by distance" mode, and the menu should count everything too.
  const nearParams = React.useCallback(() => {
    if (!userCoords) return {};
    const out = { near_lat: userCoords.lat, near_lng: userCoords.lng };
    const r = filters.radius_km;
    if (r === 'any') return out;
    if (r === undefined || r === null || r === '') { out.radius_km = 1.5; return out; }
    const parsed = parseFloat(r);
    if (Number.isFinite(parsed) && parsed > 0) out.radius_km = parsed;
    return out;
  }, [userCoords, filters.radius_km]);
  // One-click "exit near mode" — wipes all the near-related filters
  // (sort=nearest, near_lat, near_lng, near_label, radius_km) so the user
  // returns to a normal filtered list. The AddressSearch input at the top
  // of the page is the way to start a new near-search.
  const clearNearFilter = () => {
    const next = { ...filters };
    // Only flip sort back if it was the distance-based one. If the user had
    // already switched sort to A-Z etc., keep that preference after clearing.
    if (next.sort === 'nearest') delete next.sort;
    delete next.near_lat;
    delete next.near_lng;
    delete next.near_label;
    delete next.radius_km;
    setFilters(next);
    setUserCoords(null);
    setNearLabel(null);
    navigateTo(buildPath('list', next));
  };

  // Request browser geolocation. Returns a promise that resolves with {lat,lng}
  // or rejects with an Error if the user denied or it timed out.
  const requestGeolocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Geolocation not supported by your browser'));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error(err.code === 1 ? 'Location permission denied' : 'Could not determine your location')),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  });

  // "Near me" sort: when activated, request location (if not already known)
  // then switch the sort. Toggling off goes back to the default category sort.
  const handleNearMe = async () => {
    if (filters.sort === 'nearest') {
      // Toggle off
      setFilters({ ...filters, sort: undefined });
      navigateTo(buildPath('list', { ...filters, sort: undefined }));
      return;
    }
    setGeoError(null);
    try {
      const coords = userCoords || (await requestGeolocation());
      if (!userCoords) setUserCoords(coords);
      const next = { ...filters, sort: 'nearest', near_lat: coords.lat, near_lng: coords.lng };
      setFilters(next);
      navigateTo(buildPath('list', next));
    } catch (e) {
      setGeoError(e.message);
    }
  };

  // Sync URL → state (e.g. when navigating from home with new filters)
  useEffect(() => {
    setFilters(initialFilters || {});
    // Re-seed the geographic anchor when the URL provides one (back/forward
    // navigation, link from home page).
    const lat = parseFloat(initialFilters?.near_lat);
    const lng = parseFloat(initialFilters?.near_lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setUserCoords({ lat, lng });
      setNearLabel(initialFilters?.near_label || null);
    }
  }, [JSON.stringify(initialFilters)]);

  // Categories reload whenever the active location filter changes so the
  // dropdown only shows types that actually exist within the selected scope
  // — and their counts are scoped to that location too. With no country/city
  // it's a global list.
  // EVERY filter that narrows the results has to be passed here too, or the
  // "Jump to a category" menu offers categories the current search doesn't
  // contain — searching Calabria listed all the categories when the results
  // held only synagogues and attractions. `type` is deliberately left out: the
  // menu lists all categories in scope, including the one currently selected.
  useEffect(() => {
    api.categories({
      country: filters.country, state: filters.state, metro: filters.metro,
      city: filters.city, area: filters.area, search: filters.search,
      // A "near me" search scopes the counts too — otherwise the menu
      // offers categories that have nothing within the radius, and
      // prints a count for the whole directory next to a list of three.
      ...nearParams(),
      include_closed: filters.include_closed,
    })
      .then((cats) => setCategories([...cats].sort((a, b) => parseInt(b.count) - parseInt(a.count))))
      .catch(() => setCategories([]));
  }, [filters.country, filters.state, filters.metro, filters.city, filters.area,
      filters.search, filters.include_closed, nearParams]);

  // The Hechsher menu's numbers, scoped like every other menu on the page.
  // null until the first answer arrives, and null again if the request
  // fails — CertifierFilter falls back to the global counts rather than
  // showing an empty menu, because a menu with nothing in it reads as
  // "no hechsherim here" and that would be a lie told by a network error.
  const [certCounts, setCertCounts] = useState(null);
  useEffect(() => {
    let live = true;
    api.certifierCounts({
      type: filters.type, country: filters.country, city: filters.city,
      area: filters.area, include_closed: filters.include_closed,
      ...nearParams(),
    })
      .then((m) => { if (live) setCertCounts(m || {}); })
      .catch(() => { if (live) setCertCounts(null); });
    return () => { live = false; };
  }, [filters.type, filters.country, filters.city, filters.area,
      filters.include_closed, nearParams]);

  // Countries reload when the category or the SEARCH changes — searching
  // "Calabria" should offer Italy, not all 41 countries.
  useEffect(() => {
    api.countries({ type: filters.type, search: filters.search, include_closed: filters.include_closed })
      .then(setCountries).catch(() => setCountries([]));
  }, [filters.type, filters.search, filters.include_closed]);

  // States reload when country, category or search changes. An OPTIONAL filter.
  useEffect(() => {
    api.states({ country: filters.country, type: filters.type, search: filters.search, include_closed: filters.include_closed })
      .then(setStates).catch(() => setStates([]));
  }, [filters.country, filters.type, filters.search, filters.include_closed]);

  // Cities reload when country/state/category change. Cities load from the
  // country alone — selecting a state only NARROWS them, it isn't required, so
  // the city dropdown still shows every city in the country when no state is set.
  useEffect(() => {
    if (filters.metro) {
      // When a metro is chosen, list the towns inside it so you can narrow.
      api.cities({ metro: filters.metro, type: filters.type, include_closed: filters.include_closed }).then(setCities).catch(() => setCities([]));
    } else if (filters.country || filters.state) {
      api.cities({ country: filters.country, state: filters.state, type: filters.type, include_closed: filters.include_closed }).then(setCities).catch(() => setCities([]));
    } else {
      setCities([]);
    }
  }, [filters.country, filters.state, filters.type, filters.metro, filters.include_closed]);

  // Metro areas available in the selected country — and, if a state/region is
  // chosen, only metros that actually have places in that state.
  useEffect(() => {
    if (!filters.country) { setMetros([]); return; }
    api.metros({ country: filters.country, state: filters.state || undefined })
      .then((r) => setMetros(Array.isArray(r) ? r : [])).catch(() => setMetros([]));
  }, [filters.country, filters.state]);

  // Areas reload when city changes
  useEffect(() => {
    if (filters.city) {
      api.areas({ city: filters.city, country: filters.country, type: filters.type, include_closed: filters.include_closed }).then(setAreas).catch(() => setAreas([]));
    } else {
      setAreas([]);
    }
  }, [filters.city, filters.country, filters.type, filters.include_closed]);


  // Offset-based pagination. Fixed page size (the API caps `limit` at 200 to
  // prevent whole-database dumps), so "Show more" fetches the NEXT page by
  // offset and appends, rather than re-requesting an ever-growing limit.
  const PAGE_SIZE = 200;
  const [loadingMore, setLoadingMore] = useState(false);

  // Build the query params for a given offset, including the near/radius bits.
  // "Open now" (Menachem, 18/10/2026). Whether a place is open is worked out
  // in the browser by openStatus — hours tied to Shabbat or a zman, seasons,
  // the place's own time zone — and the server can't say it. So with the
  // filter on, every page of the search is fetched and only the places open
  // right now are kept; the count and the map follow. Places with no opening
  // hours drop out, since nobody can say they're open. Capped, so a search
  // across the whole site doesn't pull every listing.
  const OPEN_NOW_CAP = 1600;
  const [openNowCapped, setOpenNowCapped] = useState(false);
  const buildListParams = (offset) => {
    const params = { ...filters, limit: PAGE_SIZE, offset };
    delete params.open_now;
    // Same near/radius rule the category menu uses — see nearParams.
    delete params.radius_km;
    Object.assign(params, nearParams());
    return params;
  };

  // First page — refetched whenever filters or the near-anchor change. A
  // stale-response guard (the `active` flag) prevents an earlier slow request
  // from overwriting a newer one when filters change rapidly.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    const firstPage = async () => {
      if (filters.open_now !== 'true') return api.establishments(buildListParams(0));
      let all = []; let tot = 0;
      do {
        const r = await api.establishments(buildListParams(all.length));
        tot = r.total || 0;
        if (!r.results || !r.results.length) break;
        all = all.concat(r.results);
      } while (active && all.length < tot && all.length < OPEN_NOW_CAP);
      // Fuori dal filtro «aperto adesso» chi ha le tefillot nel campo orari,
      // per lo stesso motivo per cui non ha la pastiglia: una sinagoga «aperta»
      // alle 7:30 perché c'è shacharit non è quello che uno cerca.
      const open = all.filter((e) => !PREGHIERE.test(e.hours || '') && (() => { const st = openStatus(e); return !!(st && st.open); })());
      return { results: open, total: open.length, capped: tot > all.length };
    };
    firstPage()
      .then((r) => { if (active) { setResults(r.results); setTotal(r.total); setOpenNowCapped(!!r.capped); setLoading(false); } })
      .catch((e) => { if (active) { console.error(e); setLoadError(true); setLoading(false); } });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), userCoords, retryTick]);

  // "Show more" — fetch the next page by offset and append. Async variant
  // returns a promise so we can keep loading until a target section exists.
  // The in-flight guard must be a REF, not the `loadingMore` state: React applies
  // state asynchronously, so two triggers firing in the same tick (the scroll
  // sentinel twice, or the sentinel racing jumpToCategory's loop) both read the
  // same resultsRef length, request the SAME offset, and append that page twice —
  // which is why a place could appear twice in a long list. resultsRef is also
  // advanced immediately so back-to-back pages compute the right offset, and the
  // append de-dupes by id as a belt-and-braces guard.
  const loadingMoreRef = React.useRef(false);
  // Filter generation: if the user changes city/category while a page is in
  // flight, that response belongs to the OLD result set — drop it instead of
  // appending another location's places onto the new list.
  const filtersKey = JSON.stringify(filters);
  const filtersKeyRef = React.useRef(filtersKey);
  useEffect(() => { filtersKeyRef.current = filtersKey; }, [filtersKey]);
  const loadMoreAsync = async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    const startedFor = filtersKeyRef.current;
    setLoadingMore(true);
    try {
      const r = await api.establishments(buildListParams(resultsRef.current.length));
      if (filtersKeyRef.current !== startedFor) return;   // filters moved on
      setResults((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const merged = [...prev, ...(r.results || []).filter((x) => !seen.has(x.id))];
        resultsRef.current = merged;
        return merged;
      });
    } catch (e) { console.error(e); } finally { loadingMoreRef.current = false; setLoadingMore(false); }
  };
  const loadMore = () => { loadMoreAsync(); };

  // Infinite scroll: auto-fetch the next page when the sentinel near the bottom
  // scrolls into view. Without this, big cities (e.g. London = 332) only showed
  // the first 200 unless you noticed the "Show more" button — and the category
  // grouping made the truncation look like the page simply hadn't loaded. The
  // button stays as a manual fallback. Re-arms after each load (loadingMore dep).
  const sentinelRef = React.useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !loadingMore && resultsRef.current.length < total) {
        loadMoreAsync();
      }
    }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, loadingMore]);

  // Side-menu navigation: scroll to a category's section, loading more pages
  // first if that category hasn't been fetched yet (results are paginated, so a
  // category can exist in the location but not yet be on screen).
  const jumpToCategory = async (type) => {
    const scroll = () => {
      const el = document.getElementById(`cat-${type}`);
      if (!el) return false;
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
      return true;
    };
    if (scroll()) return;
    let guard = 0;
    while (!document.getElementById(`cat-${type}`) && resultsRef.current.length < total && guard < 25) {
      // eslint-disable-next-line no-await-in-loop
      await loadMoreAsync();
      guard += 1;
    }
    setTimeout(scroll, 150); // let the new sections paint
  };

  const updateFilter = (k, v) => {
    const next = { ...filters, [k]: v || undefined };
    // Clear cascading sub-filters. Only LOCATION changes (country/city) clear
    // their child location filters — picking a different category must NOT
    // wipe out the city/area the user just drilled into.
    if (k === 'country') {
      next.city = undefined;
      next.area = undefined;
      next.state = undefined;
      next.metro = undefined;
    }
    // Picking a metro resets the town/area (start with the whole metro). But a
    // city can then be chosen WITHIN the metro to narrow — so selecting a city
    // does NOT clear the metro.
    if (k === 'metro') { next.city = undefined; next.area = undefined; }
    // State is optional; changing it re-scopes city/area and the (state-scoped)
    // metro list, so drop any metro that may no longer apply.
    if (k === 'state') {
      next.city = undefined;
      next.area = undefined;
      next.metro = undefined;
    }
    if (k === 'city') {
      // A city stands on its own — selecting one must NOT set the State filter.
      // (State/region tagging is inconsistent in the data, so forcing the state
      // narrowed the results and showed a misleading count. State stays an
      // independent, optional filter the user picks deliberately.)
      next.area = undefined;
    }
    if (k === 'type') {
      // Type owns its own children (subtype/community/cuisine), nothing else.
      next.subtype = undefined;
      next.community = undefined;
      next.cuisine = undefined;
      // Kosher-food / Shabbat-friendly are lodging-only refinements — don't let
      // them linger (hidden) when switching to a non-lodging category.
      next.kosher_food = undefined;
      next.shabbat_friendly = undefined;
    }
    // When the user broadens to "All Countries" or "All Categories" while
    // a near filter is active, drop the near filter too — they're asking to
    // browse broadly, not stay constrained to a 1.5 km radius around an
    // unrelated point.
    if ((k === 'country' && !v) || (k === 'type' && !v)) {
      if (next.sort === 'nearest') {
        delete next.sort;
        delete next.near_lat;
        delete next.near_lng;
        delete next.near_label;
        delete next.radius_km;
        setUserCoords(null);
        setNearLabel(null);
      }
    }
    setFilters(next);
    navigateTo(buildPath('list', next));
  };

  // Build the ordered list of cards to render. In a category-grouped browse
  // (default sort, no single type filter) a multi-category place is shown once
  // PER category — so e.g. a synagogue that's also an Information Point appears
  // under both sections. Filtered/other sorts render each place once (the
  // filtered category, or its primary).
  const listSortMode = filters.sort || 'category';
  const listCatType = (e) => (filters.type && hasAnyType(e, [filters.type]) ? filters.type : e.type);
  const displayItems = (() => {
    if (listSortMode === 'category' && !filters.type) {
      const out = [];
      for (const est of results) {
        const cats = [...new Set([est.type, ...(Array.isArray(est.secondary_types) ? est.secondary_types : [])])].filter(Boolean);
        for (const cat of cats) out.push({ est, cat });
      }
      out.sort((a, b) => categoryRank(a.cat) - categoryRank(b.cat) || (a.est.name || '').localeCompare(b.est.name || ''));
      return out;
    }
    return results.map((est) => ({ est, cat: listSortMode === 'category' ? listCatType(est) : null }));
  })();

  // Il rail dei filtri, scritto una volta sola: sul desktop sta nella colonna
  // di sinistra, sul telefono dentro il pannello «Filtri». Due posti, un
  // componente — due copie sarebbero due cose da tenere allineate.
  const railFiltri = (
    <ListSidebar
      filters={filters}
      updateFilter={updateFilter}
      categories={categories}
      results={results}
      sortMode={filters.sort || 'category'}
      onJumpCategory={jumpToCategory}
      onClosest={handleNearMe}
      near={nearParams()}
    />
  );

  return (
    <div style={styles.container}>
      {/* Breadcrumb sits at the very top of the content area — the convention
          on Google, Booking.com, Amazon — so users see "where am I" before any
          controls and can hop up the hierarchy without scanning the page. */}
      <Breadcrumbs filters={filters} />

      {/* Sul telefono il pannello dei filtri si apre da qui. Il bottone dice
          quanti ne sono attivi, perché un filtro dimenticato dentro un
          pannello chiuso è il modo più veloce per non trovare niente e non
          capire perché. */}
      {stretto && (() => {
        const SCOPO = ['country', 'state', 'city', 'area', 'type', 'subtype', 'radius_km',
          'kosher_food', 'nusach', 'community', 'cuisine', 'kosher_level', 'hechsher',
          'recommended_by', 'open_now', 'shabbat'];
        const attivi = SCOPO.filter((k) => filters[k]).length;
        return (
          <button
            onClick={() => setFiltriAperti((v) => !v)}
            aria-expanded={filtriAperti}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              width: '100%', marginBottom: 14, padding: '12px 16px', fontFamily: 'inherit',
              background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 10,
              fontSize: 15, fontWeight: 600, color: COLORS.text, cursor: 'pointer',
            }}
          >
            <span>🔎 {t('Filters')}{attivi > 0 && (
              <span style={{ marginLeft: 8, background: COLORS.primary, color: 'white', borderRadius: 999, padding: '1px 9px', fontSize: 13 }}>{attivi}</span>
            )}</span>
            <span style={{ color: COLORS.muted, fontSize: 13 }}>{filtriAperti ? '▲' : '▼'}</span>
          </button>
        );
      })()}

      {/* "Start a new search" panel — groups everything that changes the
          scope (typed search, scope dropdowns, geolocation toggle, radius
          choice) into one block so the user sees them as a unit. */}
      <div style={{ marginBottom: 20, display: stretto && !filtriAperti ? 'none' : undefined }}>
        {/* "Near me" now lives only in the top-bar search pin. When a near/
            distance filter is active we show a chip here to clear it. */}
        {userCoords && (() => {
          const shortLabel = nearLabel
            ? (nearLabel.length > 24 ? nearLabel.slice(0, 24) + '…' : nearLabel)
            : 'you';
          const bg = nearLabel ? COLORS.primary : '#ef4444';
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                onClick={clearNearFilter}
                title={t("Clear near filter")}
                style={{ background: bg, color: 'white', border: `1px solid ${bg}`, padding: '10px 12px 10px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
              >
                {t("📍 Near")} {nearLabel ? `"${shortLabel}"` : shortLabel}
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', fontSize: 12 }}>✕</span>
              </button>
            </div>
          );
        })()}
        {/* Filter row — location (country → state → city → area) and category
            (+ its sub-filters) all flow together on one line when they fit,
            wrapping onto extra lines only when the screen is too narrow. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          {/* Country / region / city / area are hidden while a "near me" search
              is running. Inside a couple of kilometres you are in ONE country,
              one region and one city, so the four menus offer choices that
              either do nothing or quietly contradict the radius. Hiding them
              leaves the location governed by exactly one thing — the pin chip
              above, which carries its own ✕ — so the way back out is obvious
              rather than buried among dropdowns that appear to still apply. */}
          {!userCoords && (
          <select
            style={styles.select}
            value={(countries.find((c) => c.country.toLowerCase() === (filters.country || '').toLowerCase())?.country) || ''}
            onChange={(e) => updateFilter('country', e.target.value)}
          >
            <option value="">🌍 {t('All Countries')}</option>
            {[...countries]
              .sort((a, b) => a.country.localeCompare(b.country))
              .map((c) => (
                <option key={c.country} value={c.country}>{t(c.country)} ({c.count})</option>
              ))}
          </select>
          )}
          {!userCoords && states.length > 0 && (
            // State / province / region — OPTIONAL. Independent of the city
            // dropdown, which still lists every city in the country.
            <select
              style={styles.select}
              value={(states.find((s) => foldName(s.state) === foldName(filters.state))?.state) || ''}
              onChange={(e) => updateFilter('state', e.target.value)}
            >
              <option value="">🏛️ {t('All States/Regions')}</option>
              {[...states]
                .sort((a, b) => a.state.localeCompare(b.state))
                .map((s) => (
                  <option key={s.state} value={s.state}>{s.state} ({s.count})</option>
                ))}
            </select>
          )}
          {filters.country && metros.length > 0 && (
            // Metro-area filter — one radius that scoops up all the suburbs
            // (Thornhill, Brookline, Oak Park…) without listing them.
            <select
              style={styles.select}
              value={filters.metro || ''}
              onChange={(e) => updateFilter('metro', e.target.value)}
            >
              <option value="">🏙️ {t('Any metro area')}</option>
              {metros.map((m) => (
                <option key={m.key} value={m.key}>{m.label} ({t('metro area')})</option>
              ))}
            </select>
          )}
          {!userCoords && (filters.country || filters.state) && cities.length > 0 && (
            // Resolve the selected city to the option's exact casing so a
            // shared link with ?city=barcelona still shows "Barcelona" selected
            // (HTML <select> matches the value string case-sensitively).
            <select
              style={styles.select}
              value={(cities.find((c) => c.city.toLowerCase() === (filters.city || '').toLowerCase())?.city) || ''}
              onChange={(e) => updateFilter('city', e.target.value)}
            >
              <option value="">🏙️ {filters.metro ? t('All towns in this metro area') : t('All Cities')}</option>
              {[...cities]
                .sort((a, b) => a.city.localeCompare(b.city))
                .map((c) => (
                  <option key={c.city} value={c.city}>{t(c.city)} ({c.count})</option>
                ))}
            </select>
          )}
          {!userCoords && filters.city && areas.length > 0 && (
            <select style={styles.select} value={filters.area || ''} onChange={(e) => updateFilter('area', e.target.value)}>
              <option value="">📍 {t('All Areas')}</option>
              {[...areas]
                .sort((a, b) => a.area.localeCompare(b.area))
                .map((a) => (
                  <option key={a.area} value={a.area}>{a.area} ({a.count})</option>
                ))}
            </select>
          )}

          {/* Category. Everything that narrows WITHIN a category — nusach,
              meat/dairy, community, cuisine, kosher standard — lives in the
              list rail. Having some here and some there meant the same
              control appeared in two places depending on whether a category
              happened to be chosen. The rule is now one sentence: this row is
              where and what, the rail is everything that narrows it. */}
          <select style={styles.select} value={filters.type || ''} onChange={(e) => updateFilter('type', e.target.value)}>
            <option value="">🗂️ {t('All Categories')}</option>
            {[...categories]
              .sort((a, b) => formatCategoryLabelShort(a.type).localeCompare(formatCategoryLabelShort(b.type)))
              .map((c) => (
                <option key={c.type} value={c.type}>{formatCategoryLabelShort(c.type)} ({c.count})</option>
              ))}
          </select>
        </div>
        {/* Radius — a slider rather than the seven pills this used to be. The
            gesture matches the intent ("nothing nearby, widen it") and it is one
            control instead of seven, which matters on the filter row. The stops
            stay discrete so the value is always a round number a person would
            say out loud; the slider just walks between them. */}
        {userCoords && (() => {
          const STOPS = [
            { value: '0.5', label: '500 m' },
            { value: '1', label: '1 km' },
            { value: '1.5', label: '1.5 km' },
            { value: '3', label: '3 km' },
            { value: '5', label: '5 km' },
            { value: '10', label: '10 km' },
            { value: 'any', label: 'Any distance' },
          ];
          const current = String(filters.radius_km || '1.5');
          const idx = Math.max(0, STOPS.findIndex((o) => o.value === current));
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('Within')}
              </span>
              <input
                type="range"
                min={0}
                max={STOPS.length - 1}
                step={1}
                value={idx}
                aria-label={t('Search radius')}
                onChange={(e) => {
                  const v = STOPS[Number(e.target.value)].value;
                  // 1.5 km is the default, so it travels as "no parameter".
                  updateFilter('radius_km', v === '1.5' ? undefined : v);
                }}
                style={{ width: 200, accentColor: COLORS.primary, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary, minWidth: 92 }}>
                {t(STOPS[idx].label)}
              </span>
            </div>
          );
        })()}
        {/* TUTTI I FILTRI NELLO STESSO POSTO.
            Menachem, 21/10/2026: «i filtri devono essere tutti insieme».
            Aveva ragione: il bottone ne apriva cinque qui e altri cinque
            seicento pixel più in basso, dopo la mappa e i banner — uno li
            trovava per caso. Sul telefono il rail entra dentro questo
            pannello, così «Filtri» apre una cosa sola e la chiude tutta. */}
        {stretto && filtriAperti && railFiltri}
      </div>
      {/* Title row — the H2 reads what's currently shown ("Restaurant · 12
          results"). Sort moved into the left rail; the kosher-food filter (for
          lodging categories) stays on the right. Wraps under on narrow screens. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16, padding: '10px 14px', background: COLORS.bg, borderRadius: 8 }}>
        <h2 style={{ ...styles.sectionTitle, margin: 0 }}>
          {filters.subtype && `${filters.subtype} `}
          {filters.type ? formatCategoryLabel(filters.type) : (t("All Establishments"))}
          <span style={{ color: COLORS.muted, fontSize: 16, fontWeight: 400 }}> · {total} result{total === 1 ? '' : 's'}</span>
          {filters.open_now === 'true' && (
            <span style={{ display: 'block', color: COLORS.muted, fontSize: 12, fontWeight: 400, marginTop: 2 }}>
              🕐 {openNowCapped
                ? t('Open now, among the first places of this search — choose a city to see them all.')
                : t('Open now — places without opening hours are not shown.')}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Share badge — when the current destination has a memorable vanity
              domain (e.g. jewishrome.com), surface it as a shareable link. */}
          {(() => {
            const v = vanityDomainFor({ country: filters.country, city: filters.city });
            return v ? <ShareThisPage domain={v.domain} scope={v.scope} /> : null;
          })()}
          {/* Lodging headline filters — two simple on/off toggles derived from
              the amenities on the listing: "Kosher food" (any kosher food /
              kitchen option) and "Shabbat-friendly" (any Shabbat-observance or
              nearby-Jewish-life amenity). Lodging categories only. */}
          {SUPPORTS_SHABBAT_AMENITIES.includes(filters.type) && (
            <>
              <button
                type="button"
                onClick={() => updateFilter('kosher_food', filters.kosher_food ? '' : '1')}
                aria-pressed={!!filters.kosher_food}
                title={t("Only show places that serve or provide kosher food")}
                style={{ padding: '8px 13px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${filters.kosher_food ? COLORS.primary : COLORS.border}`, background: filters.kosher_food ? COLORS.primary : '#fff', color: filters.kosher_food ? '#fff' : COLORS.text }}
              >
                🍽️ {t('Kosher food')}
              </button>
              <button
                type="button"
                onClick={() => updateFilter('shabbat_friendly', filters.shabbat_friendly ? '' : '1')}
                aria-pressed={!!filters.shabbat_friendly}
                title={t("Only show Shabbat-friendly places")}
                style={{ padding: '8px 13px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${filters.shabbat_friendly ? COLORS.primary : COLORS.border}`, background: filters.shabbat_friendly ? COLORS.primary : '#fff', color: filters.shabbat_friendly ? '#fff' : COLORS.text }}
              >
                🕯️ {t('Shabbat-friendly')}
              </button>
            </>
          )}
          {/* Which hechsher. Not limited to any category: a shop, a bakery
              and a hotel all carry one, and "only places with a hechsher I
              accept" is the question this whole directory exists to answer. */}
          <CertifierFilter
            certs={koshCerts}
            counts={certCounts}
            value={filters.certifier || ''}
            onChange={(v) => updateFilter('certifier', v)}
          />
          {/* Gli altri chip veloci: carne/latte, le comunita' di questa
              ricerca, Mehadrin, aperto adesso. Compaiono da soli quando i
              risultati sanno rispondere — vedi ChipVeloci. */}
          <ChipVeloci
            filters={filters}
            updateFilter={updateFilter}
            results={results}
            total={total}
            near={nearParams()}
          />
          {/* Admin data-quality filters — find listings to backfill. */}
          {user?.role === 'admin' && (
            <>
              <button
                type="button"
                onClick={() => updateFilter('no_logo', filters.no_logo ? '' : 'true')}
                aria-pressed={!!filters.no_logo}
                title={t('Admin: listings with no logo/image')}
                style={{ padding: '8px 13px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${filters.no_logo ? '#7c3aed' : COLORS.border}`, background: filters.no_logo ? '#7c3aed' : '#fff', color: filters.no_logo ? '#fff' : COLORS.text }}
              >
                🖼️ {t('No logo')}
              </button>
              <button
                type="button"
                onClick={() => updateFilter('no_email', filters.no_email ? '' : 'true')}
                aria-pressed={!!filters.no_email}
                title={t('Admin: listings with no email')}
                style={{ padding: '8px 13px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${filters.no_email ? '#7c3aed' : COLORS.border}`, background: filters.no_email ? '#7c3aed' : '#fff', color: filters.no_email ? '#fff' : COLORS.text }}
              >
                ✉️ {t('No email')}
              </button>
              {/* «Nessun modo di prenotare»: ne' il link dell'albergo
                  (booking_url) ne' quello del tavolo (reservation_url).
                  Serve a trovare il buco che resta — i 69 alberghi senza
                  link di prenotazione stanno ancora in lista. */}
              <button
                type="button"
                onClick={() => updateFilter('no_booking', filters.no_booking ? '' : 'true')}
                aria-pressed={!!filters.no_booking}
                title={t('Admin: listings with no booking or reservation link')}
                style={{ padding: '8px 13px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${filters.no_booking ? '#7c3aed' : COLORS.border}`, background: filters.no_booking ? '#7c3aed' : '#fff', color: filters.no_booking ? '#fff' : COLORS.text }}
              >
                🛏️ {t('No booking link')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Broadcast a single request to every place of this category in the city
          (e.g. all drivers in Rome). Only for contactable service categories,
          when a category AND a city are chosen and there are places to reach. */}
      {filters.type && filters.city && total > 0 && BROADCASTABLE_TYPES.has(filters.type) && (() => {
        // IL PLURALE NON SI FABBRICA ATTACCANDO UNA «s» A UN'ETICHETTA
        // TRADOTTA. Qui `s` esce gia da t(): in inglese «Hotels & Stays»
        // funzionava per caso, in italiano dava «Hotel & soggiornis» e in
        // ebraico attaccava una s latina in fondo a una parola ebraica. Le
        // etichette di categoria sono gia sostantivi plurali o collettivi
        // nella loro lingua — «Hotels & Stays», «Cruises», «Senior Living»,
        // «מלונות ולינה» — quindi la cosa giusta e lasciarle stare. La «s»
        // resta solo in inglese e solo dove manca davvero.
        const s = formatCategoryLabelShort(filters.type);
        const plural = getLang() === 'en' && !/s$/i.test(s) ? `${s}s` : s;
        return (
          <button
            onClick={() => setBroadcastTarget({ type: filters.type, city: filters.city, country: filters.country || null, count: total, typeLabel: plural })}
            style={{ width: '100%', textAlign: 'left', background: '#eef4ff', border: `1px solid ${COLORS.primary}`, color: COLORS.primary, padding: '12px 16px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <span style={{ fontSize: 20 }}>📣</span>
            <span>
              {t("Send one request to")} {total > 5 ? `5 of the ${total}` : `all ${total}`} {plural.toLowerCase()} in {filters.city}
              <span style={{ display: 'block', fontWeight: 400, fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                {t("Interested places reply straight to you — handy for a driver, a room, a table, or a place for Shabbat.")}
              </span>
            </span>
          </button>
        );
      })()}

      {/* "Near me" / geocoding error (denied, unsupported, timeout, etc.) */}
      {geoError && (
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
          📍 {geoError}
        </div>
      )}

      {/* Editorial country/city description (SEO + visitor context). Shown
          when browsing a country or city without a type/search filter. */}
      {(filters.country || filters.city) && !filters.type && !filters.search && (
        <GeoDescription country={filters.country} city={filters.city} user={user} />
      )}

      {/* I LINK DI VIAGGIO, SOPRA LA MAPPA.
          Menachem, 4/11/2026, in due tempi: prima «deve uscire dai filtri»
          — e uscire voleva dire uscire davvero, perche' sulla colonna
          stretta TUTTO il rail entra nel pannello «Filtri», per cui
          spostarlo in fondo al rail lo metteva in fondo alla tendina, non
          fuori. Poi «mettilo sopra la mappa»: qui sta fra la descrizione
          del posto e la mappa, cioe' nel punto in cui uno ha appena letto
          dov'e' finito e sta per guardare dove sono le cose. */}
      <PlanYourTrip filters={filters} />

      {/* Map showing all places with coordinates. If the user is searching
          near a point (from address autocomplete, Near me, or the "near"
          query params), we also drop a bright gold pin on that exact spot
          so they can see where the map decided their search landed. */}
      {!loading && (results.length > 0 || userCoords) && (
        <Riparo nome="MapPanel (lista)" messaggio={t('Map unavailable — the list below still works.')}>
          <MapPanel
            results={results}
            onSelect={onSelect}
            searchPin={userCoords ? { lat: userCoords.lat, lng: userCoords.lng, label: nearLabel } : null}
          />
        </Riparo>
      )}

      {/* List body: sticky left rail (trip links, filter, toggle, category
          jump) beside the results. The rail lives OUTSIDE the loading/empty/
          results branches so its filter box stays put even on a 0-result query. */}
      <div className="jc-list-body">
        {/* `categories` is passed in full, INCLUDING when a type is selected.
            Withholding it made sense when this rail only jumped between
            sections of one page: a single-category page has nothing to jump
            between. It stopped making sense the moment the rail grew
            tick-boxes, because picking a category then removed the control
            for picking a different one -- the one moment you need it. The
            guard inside ListSidebar could never fix that; the data was not
            arriving. CategoryToc offers the jump only for a category that is
            actually on the page. */}
        {/* Sulla colonna stretta il rail è altri sette filtri impilati fra la
            mappa e i risultati — entra nello stesso pannello del bottone
            «Filtri», perché è la stessa cosa: scegliere cosa vedere. Sul
            desktop resta la colonna di sinistra, dove non è in mezzo a
            niente. */}
        {/* Sul desktop resta la colonna di sinistra; sul telefono è già
            dentro il pannello «Filtri» qui sopra. */}
        {!stretto && railFiltri}
        <div className="jc-list-main">
      {/* Zmanim for the selected city/metro — computed from the loaded places'
          coordinates (centroid), shown above the results. */}
      {(() => {
        if (!(filters.city || filters.metro)) return null;
        const pts = results.map((r) => r.coordinates).filter((c) => c && Number.isFinite(c.y) && Number.isFinite(c.x));
        if (!pts.length) return null;
        const lat = pts.reduce((a, c) => a + c.y, 0) / pts.length;
        const lng = pts.reduce((a, c) => a + c.x, 0) / pts.length;
        const country = filters.country || (results[0] && results[0].country) || null;
        const cityName = filters.city || filters.metro;
        return <CityZmanim lat={lat} lng={lng} country={country} cityName={cityName} />;
      })()}
      {loading ? (
        <SkeletonList count={6} />
      ) : loadError && results.length === 0 ? (
        // Non e "zero posti": e "il caricamento non e riuscito", e la
        // differenza merita di essere detta — con una via d'uscita.
        <div style={{ ...styles.empty, paddingTop: 32, paddingBottom: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 16, marginBottom: 12 }}>{t('The page could not load.')}</div>
          <button
            type="button"
            onClick={() => setRetryTick((n) => n + 1)}
            style={{ padding: '10px 28px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: COLORS.primary, color: '#fff', fontSize: 15, fontWeight: 600 }}
          >
            {t('Try again')}
          </button>
        </div>
      ) : results.length === 0 ? (
        userCoords ? (
          // "Near …" returned nothing — most useful action is widening the
          // radius (often a wrong-Via-Cellini geocode), or clearing the
          // anchor entirely.
          <div style={{ ...styles.empty, paddingTop: 32, paddingBottom: 32 }}>
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              {t("No places within")} {(parseFloat(filters.radius_km) || 1.5).toString()} km of <strong>{nearLabel || (t("your location"))}</strong>.
            </div>
            <div style={{ color: COLORS.muted, fontSize: 13, marginBottom: 16 }}>
              {t("Try widening the radius, or check the address was geocoded to the right place.")}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
              {['3', '5', '10', 'any'].map((r) => (
                <button
                  key={r}
                  onClick={() => updateFilter('radius_km', r)}
                  style={{ background: 'white', color: COLORS.primary, border: `1px solid ${COLORS.primary}`, borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  {t("Within")} {r === 'any' ? (t("any distance")) : `${r} km`}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 13 }}>
              <button
                onClick={() => updateFilter('sort', '')}
                style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}
              >
                {t("Clear \"near\" filter")}
              </button>
            </div>
          </div>
        ) : (
          <DidYouMean query={filters.search} onSelect={onSelect} />
        )
      ) : (
        // Results grid with a "Jump to category" dropdown stacked above it.
        // Places stay grouped under category section headers (default sort).
        <>
          {displayItems.map(({ est, cat }, idx) => {
            // Grouping key is the category assigned to this card (a place can
            // appear under several category groups — see displayItems above).
            const sortMode = listSortMode;
            // on the card either; contact goes through email or the booking link.
            // Matched certifier logo(s) for this place. When one shows, the
            // generic green "K" is redundant, so we drop it.
            const certLogos = matchSupervisionCerts(est.supervision, koshCerts, est.supervision_certifier_ids).filter((c) => c.main_image_url);
            const groupKey = sortMode === 'category' ? cat : sortMode === 'city' ? est.city : null;
            const prevItem = idx > 0 ? displayItems[idx - 1] : null;
            const prevGroupKey = prevItem ? (sortMode === 'category' ? prevItem.cat : sortMode === 'city' ? prevItem.est.city : null) : null;
            const showDivider = groupKey && groupKey !== prevGroupKey;
            const groupLabel = sortMode === 'category'
              ? `${categoryIcon[cat] || '📍'}  ${formatCategoryLabel(cat)}`
              // groupKey resta la citta' vera (serve a confrontare le righe
              // fra loro); qui si traduce solo l'etichetta che si legge.
              : (est.city ? t(est.city) : t('No City'));
            // Icon/label follow the section's category, so under "Information
            // Point" the card reads Information Point (not its primary type).
            // The "+ …" badges then list the place's OTHER categories.
            const cardType = cat || est.type;
            const otherCats = [...new Set([est.type, ...(Array.isArray(est.secondary_types) ? est.secondary_types : [])])]
              .filter((t) => t && t !== cardType);
            return (
              <React.Fragment key={`${est.id}:${cat || 'x'}`}>
                {showDivider && (
                  <div
                    id={sortMode === 'category' ? `cat-${cat}` : undefined}
                    style={{ margin: '24px 0 12px', padding: '8px 16px', background: COLORS.primary, color: 'white', borderRadius: 6, fontWeight: 600, fontSize: 15, scrollMarginTop: 80, display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    {sortMode === 'category'
                      ? <><CategoryIcon type={cat} />{formatCategoryLabel(cat)}</>
                      : groupLabel}
                  </div>
                )}
            <div style={{ ...styles.listCard, ...((est.is_closed || est.no_longer_kosher) ? { opacity: 0.6, background: '#fafafa' } : {}), position: 'relative' }} onClick={() => onSelect(est)}>
              {/* Favorite (heart) button — overlay top-right */}
              {onToggleFavorite && (
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleFavorite(est.id); }}
                  title={favorites && favorites.has(est.id) ? t('Remove from favorites') : t('Save to favorites')}
                  aria-label={(favorites && favorites.has(est.id) ? t('Remove {name} from favorites') : t('Save {name} to favorites')).replace('{name}', nomeVisibile(est))}
                  aria-pressed={favorites && favorites.has(est.id)}
                  style={{
                    position: 'absolute', top: 10, right: 10,
                    background: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb',
                    borderRadius: '50%', width: 32, height: 32, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    color: favorites && favorites.has(est.id) ? '#ef4444' : '#9ca3af',
                    padding: 0, zIndex: 1,
                  }}
                >
                  <HeartIcon size={16} filled={favorites && favorites.has(est.id)} />
                </button>
              )}
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                {/* LEFT: icon (or image with icon badge) + category name caption */}
                <div style={{ width: 100, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{ position: 'relative', width: 100, height: 100, background: COLORS.bg, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {est.main_image_url ? (
                      <>
                        <img
                          src={imageUrl(est.main_image_url)}
                          alt={est.name}
                          style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: 8, display: 'block' }}
                        />
                        <div
                          title={formatCategoryLabel(cardType)}
                          style={{ position: 'absolute', top: -6, left: -6, width: 36, height: 36, background: 'white', borderRadius: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}
                        >
                          <CategoryIcon type={cardType} />
                        </div>
                      </>
                    ) : (
                      <div
                        title={formatCategoryLabel(cardType)}
                        style={{ width: 100, height: 100, background: COLORS.bg, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44 }}
                      >
                        <CategoryIcon type={cardType} />
                      </div>
                    )}
                    {/* Photo count overlay — number of gallery photos only
                        (the main image/logo isn't counted). */}
                    {(() => {
                      const gallery = Array.isArray(est.gallery_images) ? est.gallery_images.length : 0;
                      return gallery > 0 ? (
                        <div title={`${gallery} more photo${gallery > 1 ? 's' : ''}`} style={{ position: 'absolute', bottom: 3, right: 3, background: 'rgba(0,0,0,0.6)', color: 'white', borderRadius: 6, padding: '1px 5px', fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>
                          📷 {gallery}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.primary, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
                    {formatCategoryLabel(cardType)}
                    {/* This place's OTHER categories — so a multi-category place
                        reads as all of them, without repeating the section it's
                        currently shown under. */}
                    {otherCats.map((t) => (
                      <div key={t} style={{ fontSize: 10, fontWeight: 500, color: COLORS.muted, marginTop: 2 }}>
                        + {formatCategoryLabelShort(t)}
                      </div>
                    ))}
                    {/* Non-kind subtypes only (Nusach, store type, Women/Men/…);
                        Meat/Dairy/Parve are shown as prominent pills below.
                        LA COMUNITA' STA QUI ACCANTO AL NUSACH, perche' sono
                        la stessa domanda posta due volte: il nusach dice come
                        si prega, la comunita' chi prega, e in diaspora e'
                        quest'ultima a far scegliere una shul (Chabad,
                        Consistoire, United Synagogue). La scheda singola la
                        mostrava gia'; la lista no, ed e' la lista che si
                        guarda per prima.
                        Dedotta senza ripetizioni: a Petach Tikva centinaia di
                        schede hanno nusach Yemenite E comunita' Yemenite, e
                        «Yemenite, Yemenite» sarebbe solo rumore. */}
                    {(() => {
                      const other = splitCuisines(est.subtype).filter((s) => !['meat', 'dairy', 'parve', 'pareve', 'parev'].includes(s.toLowerCase()));
                      const viste = new Set(other.map((s) => s.trim().toLowerCase()));
                      const com = String(est.community || '').trim();
                      // «Yemenite (Baladi)» accanto a nusach «Yemenite» non e'
                      // un doppione: aggiunge il rito. «Yemenite» secco si'.
                      const pezzi = com && !viste.has(com.toLowerCase())
                        ? [...other, com] : other;
                      return pezzi.length ? (
                        <div style={{ fontSize: 10, fontWeight: 500, color: COLORS.muted, marginTop: 2 }}>{pezzi.map((s) => t(s)).join(', ')}</div>
                      ) : null;
                    })()}
                    {/* Cuisine sits right under the kosher-kind subtype so
                        both tags read as one block. Joined with " · " so
                        multiple cuisines stay readable in the narrow column. */}
                    {est.cuisine && splitCuisines(est.cuisine).length > 0 && (
                      <div style={{ fontSize: 10, fontWeight: 500, color: '#92400e', marginTop: 2 }}>
                        🍴 {splitCuisines(est.cuisine).map((c) => t(c)).join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
                {/* RIGHT: text content (always starts at same X position) */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.listCardTitle}>
                    {/* IL NOME NELLA LINGUA DI CHI GUARDA.
                        Da quando l'ebraico non sta piu' appeso al nome fra
                        parentesi ma in `translations`, una pagina in ebraico
                        mostrava «102 Grill Bar» invece di «102 גריל בר»: la
                        traduzione c'era e nessuno la leggeva. La scheda
                        singola usava gia' nomeVisibile(); la lista no, ed e'
                        la lista che si guarda per prima. */}
                    {nomeVisibile(est)}
                    {est.is_closed && (
                      <span style={{ marginLeft: 8, background: COLORS.danger, color: 'white', padding: '2px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, verticalAlign: 'middle' }}>
                        {t("CLOSED")}
                      </span>
                    )}
                    {est.no_longer_kosher && !est.is_closed && (
                      <span style={{ marginLeft: 8, background: '#92400e', color: 'white', padding: '2px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, verticalAlign: 'middle' }}>
                        {t("NO LONGER KOSHER")}
                      </span>
                    )}
                    {/* Owner-managed: this place has been claimed by an owner who
                        keeps it up to date — signals a more trustworthy listing. */}
                    {est.owner_id && !est.is_closed && !est.no_longer_kosher && (
                      <span title={t("Kept up to date by the owner")} style={{ marginLeft: 8, background: '#e0edff', color: COLORS.primary, padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: 0.3, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        {t("✓ Owner-managed")}
                      </span>
                    )}
                  </div>
                  {/* Kosher-food level, kashrut level (Glatt / Chalav Israel) and
                      sukkah all share ONE wrapping row so the card stays compact. */}
                  {(() => {
                    const levels = est.supervision_level ? (() => {
                      const all = splitCuisines(est.supervision_level);
                      const PRIORITY = [(t("Glatt")), (t("Chalav Israel"))];
                      return [...PRIORITY.filter((p) => all.includes(p)), ...all.filter((l) => !PRIORITY.includes(l))].slice(0, 2);
                    })() : [];
                    return (
                      <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Open-now status (from structured opening hours, in the
                            place's own timezone) — the first, most scannable chip. */}
                        {(() => {
                          // «Niente pastiglia dove ci sono le tefilot» (Menachem,
                          // 19/10/2026). Non su TUTTE le sinagoghe: su quelle il cui
                          // campo orari tiene le preghiere invece degli orari di
                          // visita. Bevis Marks aveva dentro «Shacharit: weekday
                          // 7:30 AM…» e la lista scriveva «chiuso» solo perché in
                          // quel momento non c'era minyan. Il museo di Casale, la
                          // Choral di Mosca e la Jerusalem di Praga hanno orari di
                          // visita veri e la pastiglia se la tengono.
                          if (PREGHIERE.test(est.hours || '')) return null;
                          const st = openStatus(est);
                          if (!st) return null;
                          const cfg = st.open
                            ? (st.closingSoon
                                ? { bg: '#fef3c7', bd: '#fde68a', fg: '#92400e', icon: '🟠', label: t('Closing soon') }
                                : { bg: '#dcfce7', bd: '#bbf7d0', fg: '#166534', icon: '🟢', label: t('Open now') })
                            : st.seasonClosed
                              ? { bg: '#e0e7ff', bd: '#c7d2fe', fg: '#3730a3', icon: '🗓️', label: t('Closed for the season') }
                              : { bg: '#fee2e2', bd: '#fecaca', fg: '#991b1b', icon: '🔴', label: t('Closed') };
                          return (
                            <span style={{ background: cfg.bg, color: cfg.fg, border: `1px solid ${cfg.bd}`, padding: '2px 8px', borderRadius: 10, fontSize: 11.5, fontWeight: 700 }}>
                              {cfg.icon} {cfg.label}
                            </span>
                          );
                        })()}
                        {/* PROSSIMO MINYAN. Un negozio nella lista dice se è
                            aperto; una sinagoga non diceva niente, anche con
                            tutte le tefillot sulla sua pagina. Qui si legge
                            l'ora della prossima, nel fuso della sinagoga. Se
                            gli orari ci sono ma dipendono dal sole e non si
                            possono calcolare, si dice almeno che ci sono. */}
                        {/* MA SOLO DOVE LA SCHEDA E' LI' COME SINAGOGA.
                            Menachem, 26/10/2026: «un posto che è sia
                            sinagoga sia Shabbat Meals, quando nella lista
                            si vede come Shabbat Meals, non deve avere gli
                            orari della sinagoga». Giusto: un posto compare
                            sotto ogni sua categoria, e sotto «Shabbat Meal»
                            chi guarda sta cercando un pasto — l'ora di
                            shacharit li' non risponde a niente e fa sembrare
                            che il pasto sia alle 8:30. Gli orari di
                            preghiera sono del RUOLO sinagoga, quindi si
                            mostrano SOLO sotto quell'intestazione.
                            `cat` è nullo quando la lista non raggruppa per
                            categoria: li' non c'è un ruolo da rispettare.

                            Sette schede avevano gli orari senza portare quel
                            ruolo da nessuna parte — «Synagogue — Verona» era
                            schedata come tour — e con questa regola gli
                            orari sparivano dalla lista. La risposta non è
                            allargare la regola: è dare a quelle sette il
                            ruolo che hanno davvero, ed è stato fatto. */}
                        {(() => {
                          if (cat && cat !== 'synagogue_shul') return null;
                          const m = nextMinyan(est);
                          if (!m) return null;
                          // Minyan continuo: non c'e' un'ora da scrivere, c'e'
                          // una finestra. Se e' aperta adesso lo si dice e
                          // basta — «Minyan now, every 15 min» e' la risposta
                          // a chi sta cercando un minyan in questo momento.
                          const giorno = m.today ? '' : ` · ${t(GIORNO_LUNGO[m.dayCode] || m.dayCode)}`;
                          // Il passo solo se la sinagoga lo dichiara: a Zichron
                          // Moshe i minyanim si susseguono e nessuno conta i
                          // minuti.
                          const passo = m.every ? ` · ${t('every')} ${m.every} ${t('min')}` : '';
                          const testo = m.solo ? t('Prayer times')
                            : m.running ? `${t('Minyan now')}${passo}`
                              : `${t(m.label)} ${m.time}${passo}${giorno}`;
                          return (
                            <span title={t('Next minyan')} style={{
                              background: m.running ? '#dcfce7' : '#eef2ff',
                              color: m.running ? '#166534' : '#3730a3',
                              border: `1px solid ${m.running ? '#bbf7d0' : '#c7d2fe'}`,
                              padding: '2px 8px', borderRadius: 10, fontSize: 11.5, fontWeight: 700,
                            }}>
                              {m.running ? '🔁' : '🕯️'} {testo}
                            </span>
                          );
                        })()}
                        {/* Open summary chip: the free-text "Open / Season" field
                            ("Daily", "Summer only") when set. When it's EMPTY, fall
                            back to info derived from the opening hours — a schedule
                            summary, or the first line of free-text hours — unless the
                            open-now status chip above already conveys the hours.
                            Truncated; full text on hover. */}
                        {(() => {
                          const raw = (est.open_days || '').trim();
                          let display = raw;
                          // When there's no free-text "Open / Season" summary, derive a
                          // short summary from the STRUCTURED schedule only ("Open daily",
                          // "Open on Shabbat", …). Never dump the raw `hours`/prayer text —
                          // for synagogues that field holds prayer times, which is what was
                          // leaking "first words of the prayer times" onto the card.
                          if (!display && !openStatus(est) && est.type !== 'synagogue_shul'
                              && est.opening_hours?.schedule?.length > 0) {
                            display = t(summarizeHours(est));
                          }
                          if (!display) return null;
                          return (
                            <span title={raw || display} style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '2px 8px', borderRadius: 10, fontSize: 11.5, fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }}>
                              {display}
                            </span>
                          );
                        })()}
                        {/* Kosher Kind — Meat / Dairy / Parve as prominent
                            colour-coded pills so it's scannable at a glance. */}
                        {(() => {
                          const K = {
                            meat: { label: 'Meat', icon: '🥩', bg: '#fef2f2', bd: '#fecaca', fg: '#b91c1c' },
                            dairy: { label: 'Dairy', icon: '🧀', bg: '#eff6ff', bd: '#bfdbfe', fg: '#1d4ed8' },
                            parve: { label: 'Parve', icon: '🌿', bg: '#f0fdf4', bd: '#bbf7d0', fg: '#15803d' },
                          };
                          const norm = (s) => ({ pareve: 'parve', parev: 'parve' }[s.toLowerCase()] || s.toLowerCase());
                          const kinds = [...new Set(splitCuisines(est.subtype).map(norm).filter((s) => K[s]))];
                          return kinds.map((k) => (
                            <span key={k} style={{ background: K[k].bg, color: K[k].fg, border: `1px solid ${K[k].bd}`, padding: '2px 8px', borderRadius: 10, fontSize: 11.5, fontWeight: 700 }}>{K[k].icon} {t(K[k].label)}</span>
                          ));
                        })()}
                        <LodgingBadges est={est} inline />
                        {/* Certifier cards: how many places carry this hechsher —
                            ONLY when this card represents the certification category
                            (cardType), not when the same place appears under another
                            of its categories (e.g. as an information center). */}
                        {cardType === 'kosher_certification_hechsher' && (() => {
                          const ci = koshCerts.find((c) => c.id === est.id);
                          const n = ci ? (ci.certified_count || 0) : 0;
                          return n > 0 ? (
                            <span title={`Places under ${est.name}'s supervision`} style={{ background: '#eef2ff', color: COLORS.primary, border: '1px solid #c7d2fe', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                              {t("✡️ Certifies")} {n} place{n === 1 ? '' : 's'}
                            </span>
                          ) : null;
                        })()}
                        {/* `t(lvl)` e non `lvl`: Glatt, Halak, Bishul Beit
                            Yosef sono parole ebraiche, e su una pagina in
                            ebraico vanno lette גלאט, חלק, בישול בית יוסף —
                            non in caratteri latini. Il dizionario le aveva
                            gia tutte (LEVELS in data/vocab-i18n.js), era
                            questa riga che non glielo chiedeva. */}
                        {levels.map((lvl) => (
                          <span key={lvl} style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{t(lvl)}</span>
                        ))}
                        {est.has_sukkah && (
                          <span style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>🌿 {t('Sukkah')}</span>
                        )}
                        {est.open_on_pesach && (
                          <span style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>🫓 {t('Open on Pesach')}</span>
                        )}
                        {est.gluten_free && (
                          <span style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>🌾 {t('Gluten-free')}</span>
                        )}
                        {(() => {
                          return seatingChips(est, true).map(([icon, label]) => (
                            <span key={label} style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{icon} {t(label)}</span>
                          ));
                        })()}
                        {est.takeaway && (
                          <span style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>🥡 {t('Takeaway')}</span>
                        )}
                        {est.wheelchair_accessible && (
                          <span style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>♿ {t('Accessible')}</span>
                        )}
                        {String(est.recommended_by || '').trim() && (
                          <span title={`${t('Recommended by')} ${est.recommended_by}`}
                            style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                            👍 {est.recommended_by}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <div style={styles.listCardMeta}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      📍 {[est.address, est.area, est.city].filter(Boolean).join(', ')}
                      {est.country && (
                        <>
                          <Flag country={t(est.country)} code={est.country_code} height={12} />
                          <span>{t(est.country)}</span>
                        </>
                      )}
                      {est.reply_rate != null && <ReplyRateBadge rate={est.reply_rate} />}
                      {/* Distance shown when "Near me" sort is active */}
                      {typeof est.distance_km === 'number' && (
                        <span dir="ltr" style={{ background: COLORS.bg, color: COLORS.primary, padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600, display: 'inline-block' }}>
                          {formatDistance(est.distance_km, est.country)}
                        </span>
                      )}
                    </span>
                  </div>
                  {/* Star rating — hotels / accommodations only */}
                  {est.rate > 0 && ['kosher_hotel', 'accommodation', 'shabbat_friendly_accommodation', 'kosher_vacation'].includes(est.type) && (
                    <div style={{ marginTop: 4, color: '#f59e0b', fontSize: 14, letterSpacing: 1 }} title={`${est.rate}-star hotel`}>
                      {'★'.repeat(est.rate)}<span style={{ color: '#e5e7eb' }}>{'★'.repeat(Math.max(0, 5 - est.rate))}</span>
                    </div>
                  )}
                  {/* WHEN IT RUNS, for the programmes that run once. For a
                      Pesach hotel or a Chanukah ski week this is the single
                      most important fact on the card — more than the address,
                      which is why it sits above the hechsher row rather than
                      among the small print. */}
                  {(est.date_from || est.date_to) && (
                    <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: '#eef2ff', color: COLORS.primary, border: '1px solid #c7d2fe',
                      padding: '2px 9px', borderRadius: 10, fontSize: 12, fontWeight: 700 }}>
                      📅 {formatDateRange(est.date_from, est.date_to)}
                    </div>
                  )}
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {est.supervision && certLogos.length === 0 && (
                      <span
                        title={`Kosher: ${est.supervision}${est.supervision_level ? ` (${est.supervision_level})` : ''}`}
                        style={{ width: 26, height: 26, background: '#059669', color: 'white', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                      >
                        K
                      </span>
                    )}
                    {/* Certifier logo(s) — replaces the generic K badge for places
                        whose supervision names a certifier we have a logo for. */}
                    <CertLogos supervision={est.supervision} certifierIds={est.supervision_certifier_ids} certs={koshCerts} size={24} onOpen={(c) => onSelect(c)} />
                    {est.ai_whatsapp_number && (
                      <a
                        href={whatsappUrl(phoneForWhatsApp(est.ai_whatsapp_number, est.country), est.name, null, placeLink(est))}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => { e.stopPropagation(); api.trackClick(est.id, 'whatsapp'); }}
                        title={t("AI WhatsApp chatbot — instant replies")}
                        style={{ width: 26, height: 26, background: 'linear-gradient(135deg, #6366f1, #25D366)', color: 'white', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.2)', textDecoration: 'none' }}
                      >
                        🤖
                      </a>
                    )}
                    {(est.phone || est.whatsapp_number) && (
                      <PhoneActions
                        estId={est.id}
                        phone={est.phone}
                        whatsappNumber={est.whatsapp_number}
                        country={t(est.country)}
                        placeName={est.name}
                        placeUrl={placeLink(est)}
                        hasWhatsApp={est.has_whatsapp !== false}
                        compact
                      />
                    )}
                    {est.email && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setContactTarget(est); }}
                        title={t("Send a message")}
                        aria-label={`Send a message to ${est.name}`}
                        style={{ width: 26, height: 26, background: COLORS.primary, color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', padding: 0 }}
                      >
                        <EmailIcon size={13} />
                      </button>
                    )}
                    {/* NELLA LISTA SI DICE CHE CI SONO, NON SI SCOPRONO.
                        Venti schede aperte insieme sarebbero venti chiamate
                        per dei numeri che quasi nessuno guardera': la
                        pastiglia lascia passare il clic alla scheda, e il
                        bottone vero sta li'. E' anche il percorso che
                        serve a noi - la pagina della scheda e' il posto in
                        cui si arriva, non una tappa da saltare. */}
                    {Array.isArray(est.contacts_hidden) && est.contacts_hidden.length > 0 && (
                      <button
                        title={t('Open the listing to see the phone, WhatsApp and website')}
                        style={{ background: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb', padding: '2px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        📞 {t('Contacts')}
                      </button>
                    )}
                    <SocialIcons establishment={est} compact user={user} onLogin={onLogin} />
                    {est.type !== 'kosher_hotel' && est.website && (
                      true ? (
                        <a
                          href={est.website.startsWith('http') ? est.website : `https://${est.website}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => { e.stopPropagation(); api.trackClick(est.id, 'website'); }}
                          title={`Visit website: ${est.website}`}
                          style={{ width: 26, height: 26, background: '#6b7280', color: 'white', borderRadius: '50%', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                        >
                          <WebIcon size={13} />
                        </a>
                      ) : (
                        // Guests: greyed; prompts registration instead of opening.
                        <button
                          onClick={(e) => { e.stopPropagation(); onLogin && onLogin(); }}
                          title={t("Create a free account to visit the website")}
                          aria-label={t('Register to visit website')}
                          style={{ width: 26, height: 26, background: '#9ca3af', color: 'white', border: 'none', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', cursor: 'pointer', opacity: 0.55, padding: 0 }}
                        >
                          <WebIcon size={13} />
                        </button>
                      )
                    )}
                    {est.booking_url && (
                      <a
                        href={est.booking_url}
                        target="_blank"
                        rel="nofollow sponsored noopener noreferrer"
                        onClick={(e) => { e.stopPropagation(); api.trackClick(est.id, 'booking'); }}
                        title={t("Book — affiliate link; we may earn a commission at no extra cost to you")}
                        style={{ background: COLORS.accent, color: COLORS.primaryDark, padding: '4px 12px', borderRadius: 999, textDecoration: 'none', fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                      >
                        {t("🛏️ Book")}
                      </a>
                    )}
                    {/* Hotels nearby — hotels.com sorted by distance from this
                        place's address (affiliate). Not shown for hotels. */}
                    {AFFILIATE_ENABLED && (est.address || est.city) && !['kosher_hotel', 'kosher_vacation'].includes(est.type) && (
                      <a
                        href={hotelSearchNearUrl([est.address, est.area, est.city, est.country].filter(Boolean).join(', '))}
                        target="_blank"
                        rel="noreferrer sponsored"
                        onClick={(e) => { e.stopPropagation(); api.trackClick(est.id, 'hotels_nearby'); }}
                        title={t("Hotels closest to this place on hotels.com — we may earn a commission at no extra cost to you")}
                        style={{ background: 'white', color: COLORS.primary, border: `1px solid ${COLORS.border}`, padding: '4px 10px', borderRadius: 999, textDecoration: 'none', fontWeight: 600, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        {t("🏨 Hotels nearby")}
                      </a>
                    )}
                  </div>
                  {/* Contact people — up to two. Each shows the name + small
                      WhatsApp/Email icons that reach that specific person
                      (NOT the establishment's main phone/email, which already
                      have icons in the row above). Falls back to legacy
                      contact_phone for WhatsApp when contact_whatsapp is
                      blank, so older data still surfaces a button. */}
                  {(() => {
                    const contacts = [
                      {
                        name: est.contact_name,
                        title: est.contact_title,
                        // Due campi, a volte due numeri: si mostrano tutti e
                        // due (numeriContatto, api.js — «Not showing both
                        // numbers of Raskin», 2/11/2026).
                        phones: numeriContatto(est.contact_phone, est.contact_whatsapp),
                        // WhatsApp only when the number is actually on WhatsApp.
                        whatsapp: est.contact_has_whatsapp === false ? null : (est.contact_whatsapp || est.contact_phone),
                        email: est.contact_email,
                      },
                      {
                        name: est.contact2_name,
                        title: est.contact2_title,
                        phones: numeriContatto(est.contact2_whatsapp),
                        whatsapp: est.contact2_has_whatsapp === false ? null : est.contact2_whatsapp,
                        email: est.contact2_email,
                      },
                    ].filter((c) => c.email || c.name || c.whatsapp || c.phones.length)
                      // Ogni bottone esiste solo se porta da qualche parte: un
                      // nome utente di WhatsApp («@Rabbi.Landa») si scrive ma
                      // non si chiama, e un campo che dice «Israel» non e'
                      // nessuna delle due cose.
                      .map((c) => ({
                        ...c,
                        tels: c.phones.map((p) => phoneWithCountry(p, est.country)).filter(Boolean),
                        wa: phoneForWhatsApp(c.whatsapp, est.country),
                        handle: isWhatsAppHandle(c.whatsapp)
                          ? `@${phoneForWhatsApp(c.whatsapp, est.country)}` : null,
                      }));
                    if (contacts.length === 0) return null;
                    return (
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, color: COLORS.text }}>
                        {contacts.map((c, idx) => (
                          <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            {c.name && <span><span style={{ color: COLORS.muted }}>👤</span> {c.name}{c.title ? <span style={{ color: COLORS.muted }}> · {c.title}</span> : ''}</span>}
                            {c.tels.map((tel) => (
                              <a
                                key={tel}
                                href={`tel:${tel}`}
                                onClick={(e) => { e.stopPropagation(); api.trackClick(est.id, 'phone'); }}
                                title={c.name ? `Call ${c.name} — ${tel}` : `Call ${tel}`}
                                style={{ width: 22, height: 22, background: COLORS.primary, color: 'white', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}
                              >
                                <PhoneIcon size={11} />
                              </a>
                            ))}
                            {c.wa && (
                              <a
                                href={whatsappUrl(c.wa, est.name, c.name, placeLink(est))}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => { e.stopPropagation(); api.trackClick(est.id, 'whatsapp'); }}
                                title={c.handle
                                  ? `WhatsApp ${c.handle}${c.name ? ` — ${c.name}` : ''}`
                                  : (c.name ? `WhatsApp ${c.name}` : 'WhatsApp')}
                                style={{ width: 22, height: 22, background: '#25D366', color: 'white', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}
                              >
                                <WhatsAppIcon size={11} />
                              </a>
                            )}
                            {c.email && (
                              <a
                                href={mailtoUrl(c.email, est.name, c.name, placeLink(est))}
                                onClick={(e) => e.stopPropagation()}
                                title={c.name ? `Email ${c.name}` : 'Email'}
                                style={{ width: 22, height: 22, background: COLORS.primary, color: 'white', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}
                              >
                                <EmailIcon size={11} />
                              </a>
                            )}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
              </React.Fragment>
            );
          })}

          {/* "Show more" — fetches the next page by offset and appends.
              The API caps each request at 200 rows, so big result sets are
              paged through rather than pulled in one shot. */}
          {results.length < total && (
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              {/* Sentinel — when it scrolls near the viewport, infinite-scroll
                  auto-loads the next page. Sits above the button. */}
              <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                style={{
                  background: COLORS.primary, color: 'white',
                  border: 'none', padding: '10px 22px', borderRadius: 999,
                  fontSize: 14, fontWeight: 600, cursor: loadingMore ? 'wait' : 'pointer',
                  opacity: loadingMore ? 0.7 : 1,
                }}
              >
                {loadingMore ? (t("Loading…")) : `Show ${Math.min(total - results.length, PAGE_SIZE)} more · ${results.length} of ${total}`}
              </button>
            </div>
          )}
        </>
      )}
        </div>
      </div>

      {/* Contact modal triggered from list cards */}
      {contactTarget && (
        <ContactModal establishment={contactTarget} user={user} onClose={() => setContactTarget(null)} onRequireLogin={() => { setContactTarget(null); onLogin && onLogin(); }} />
      )}
      {/* Broadcast request modal — one message to a whole category in the city */}
      {broadcastTarget && (
        <ContactModal broadcast={broadcastTarget} user={user} onClose={() => setBroadcastTarget(null)} onRequireLogin={() => { setBroadcastTarget(null); onLogin && onLogin(); }} />
      )}
    </div>
  );
}

// Lightbox: full-screen image viewer with keyboard navigation
function Lightbox({ images, index, onClose, onChange, captionName, captions }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onChange((index - 1 + images.length) % images.length);
      else if (e.key === 'ArrowRight') onChange((index + 1) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, images.length, onChange, onClose]);

  const goPrev = (e) => { e.stopPropagation(); onChange((index - 1 + images.length) % images.length); };
  const goNext = (e) => { e.stopPropagation(); onChange((index + 1) % images.length); };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        title={t("Close (ESC)")}
        style={{ position: 'absolute', top: 20, right: 24, background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', width: 44, height: 44, borderRadius: '50%', fontSize: 22, cursor: 'pointer', fontWeight: 700 }}
      >
        ✕
      </button>
      {images.length > 1 && (
        <>
          <button
            onClick={goPrev}
            title={t("Previous (←)")}
            style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', width: 56, height: 56, borderRadius: '50%', fontSize: 30, cursor: 'pointer', fontWeight: 700 }}
          >
            ‹
          </button>
          <button
            onClick={goNext}
            title={t("Next (→)")}
            style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', width: 56, height: 56, borderRadius: '50%', fontSize: 30, cursor: 'pointer', fontWeight: 700 }}
          >
            ›
          </button>
        </>
      )}
      <img
        src={images[index]}
        alt={(captions && captions[index]) || `${captionName} ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 4, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
      />
      <div style={{ position: 'absolute', bottom: 20, left: 0, right: 0, textAlign: 'center', color: 'white', fontSize: 14 }}>
        {captions && captions[index]
          ? <><span style={{ fontWeight: 600 }}>{captions[index]}</span> · {index + 1} / {images.length}</>
          : <>{captionName} · {index + 1} / {images.length}</>}
      </div>
    </div>
  );
}

// Contact form modal
// Admin-only tool (shown in the editor): search for a duplicate listing and
// merge it INTO the current place. The current place survives; the duplicate is
// absorbed (fields, supervision, gallery, category, all child records) then
// deleted. Mirrors scripts/merge-places.js via POST /admin/.../merge.
// DUE MODI PER NON DOVER CERCARE A MANO.
//
// Menachem, 20/10/2026, davanti a due «Beit Kneset Chabad» a dieci numeri di
// distanza sulla stessa via: «per metterli insieme serve un modo più
// semplice… se ci sono due posti vicini automaticamente appaiono», e poi
// «si potrebbe anche mettere l'opzione di copiare l'indirizzo di uno e
// quando si va nell'altro si inserisce nel merge, così siamo sicuri che si
// stanno mettendo insieme i due giusti».
//
// Quindi: i VICINI escono da soli, in ordine di distanza, senza scrivere
// niente; e un posto si può COPIARE (il tasto qui sotto) e incollare nella
// scheda dell'altro, che è l'unico modo di essere certi di quale si prende
// quando i due si chiamano uguale.
const CLIP = 'jc_merge_clip';
const leggiClip = () => { try { return JSON.parse(localStorage.getItem(CLIP) || 'null'); } catch (e) { return null; } };

// UNA RIGA DI TRADUZIONI ACCANTO AL SUO CAMPO, CHIUSA.
//
// Menachem, 26/10/2026: «the fields translated should be next to each field
// but hidden, there should be a button to show them». Il primo tentativo era
// un riquadro unico in fondo al modulo con tutti i campi ripetuti: per
// tradurre il nome dovevi scorrere fino in fondo e ritrovare «Name» lontano
// dalla casella del nome. Cosi' invece sta attaccato, e di suo non si vede:
// chi non traduce non se ne accorge nemmeno.
//
// Il pallino acceso dice che una traduzione c'e' gia', senza doverla aprire.
const LINGUE_SCHEDA = [['en', 'English'], ['he', 'עברית'], ['it', 'Italiano'], ['fr', 'Français'], ['es', 'Español'], ['ru', 'Русский'], ['de', 'Deutsch']];

function CampiTradotti({ campo, valori, onChange, multiriga, conLinguaBase }) {
  const [aperto, setAperto] = useState(false);
  const mie = (valori && valori[campo]) || {};
  // La lingua in cui e' scritto il campo qui sopra. La sua casella non si
  // mostra: sarebbe lo stesso testo due volte, ed e' il motivo per cui il
  // titolo sembrava avere cinque campi invece di quattro.
  const base = (valori && valori._lang) || 'en';
  const quante = Object.keys(mie).filter((l) => l !== base && String(mie[l] || '').trim()).length;
  const scrivi = (l, v) => {
    // Si rimanda l'oggetto INTERO: il server sostituisce la colonna, e
    // mandarne un pezzo cancellerebbe le altre lingue.
    const tr = { ...(valori || {}) };
    const perCampo = { ...(tr[campo] || {}) };
    if (v.trim()) perCampo[l] = v; else delete perCampo[l];
    if (Object.keys(perCampo).length) tr[campo] = perCampo; else delete tr[campo];
    onChange(tr);
  };
  const Casella = multiriga ? 'textarea' : 'input';
  return (
    <div style={{ marginTop: 4 }}>
      <button
        type="button"
        onClick={() => setAperto(!aperto)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: quante ? '#1a4d8f' : '#6b7280', fontSize: 12, fontWeight: 600 }}
      >
        {aperto ? '▾' : '▸'} 🌍 {t('Other languages')}
        {quante > 0 && <span style={{ marginLeft: 6, background: '#1a4d8f', color: 'white', borderRadius: 999, padding: '0 6px', fontSize: 11 }}>{quante}</span>}
      </button>
      {aperto && conLinguaBase && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
          {t('The text above is written in')}{' '}
          <select
            value={base}
            onChange={(e) => onChange({ ...(valori || {}), _lang: e.target.value })}
            style={{ fontSize: 12, padding: '2px 4px' }}
          >
            {LINGUE_SCHEDA.map(([l, nome]) => <option key={l} value={l}>{nome}</option>)}
          </select>
        </div>
      )}
      {aperto && (
        <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 6 }}>
          {LINGUE_SCHEDA.filter(([l]) => l !== base).map(([l, nome]) => (
            <Casella
              key={l}
              rows={multiriga ? 3 : undefined}
              style={{ ...styles.input, direction: l === 'he' ? 'rtl' : 'ltr', resize: multiriga ? 'vertical' : undefined }}
              placeholder={nome}
              value={mie[l] || ''}
              onChange={(e) => scrivi(l, e.target.value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MergePlacesTool({ keep, onMerged }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [vicini, setVicini] = useState([]);
  const [clip, setClip] = useState(leggiClip);
  const [sel, setSel] = useState(null);       // chosen duplicate
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // I vicini, appena si apre. Raggio stretto: un doppione sta a metri, non a
  // chilometri, e un elenco lungo è di nuovo una ricerca.
  useEffect(() => {
    const c = keep && keep.coordinates;
    const lat = c && (typeof c.y === 'number' ? c.y : c.lat);
    const lng = c && (typeof c.x === 'number' ? c.x : c.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) { setVicini([]); return undefined; }
    let active = true;
    api.establishments({ near_lat: lat, near_lng: lng, radius_km: 0.3, sort: 'nearest', limit: 12 })
      .then((r) => { if (active) setVicini((r.results || []).filter((e) => e.id !== keep.id)); })
      .catch(() => { if (active) setVicini([]); });
    return () => { active = false; };
  }, [keep]);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setResults([]); return undefined; }
    let active = true;
    const h = setTimeout(() => {
      api.establishments({ search: t, limit: 8 })
        .then((r) => { if (active) setResults((r.results || []).filter((e) => e.id !== keep.id)); })
        .catch(() => { if (active) setResults([]); });
    }, 250);
    return () => { active = false; clearTimeout(h); };
  }, [q, keep.id]);

  const choose = (e) => { setSel(e); setResults([]); setQ(''); setPreview(null); setError(''); };
  const doPreview = async () => {
    setError(''); setBusy(true);
    try { setPreview(await api.mergePlaces(keep.id, { removeId: sel.id, dryRun: true })); }
    catch (e) { setError(e.message || 'Preview failed'); }
    finally { setBusy(false); }
  };
  const doMerge = async () => {
    if (!window.confirm(`Merge "${sel.name}" INTO "${keep.name}"?\n\n"${sel.name}" will be permanently deleted and everything attached to it (messages, reviews, rooms, photos, bookings…) moved into this place. This cannot be undone.`)) return;
    setError(''); setBusy(true);
    try {
      await api.mergePlaces(keep.id, { removeId: sel.id });
      alert(`Merged. "${sel.name}" was absorbed into "${keep.name}" and removed.`);
      onMerged && onMerged();
    } catch (e) { setError(e.message || 'Merge failed'); setBusy(false); }
  };

  const childTotal = preview && preview.child_records
    ? Object.values(preview.child_records).reduce((a, b) => a + b, 0) : 0;

  return (
    <div style={{ marginTop: 20, padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: COLORS.bg }}>
      <div style={{ fontWeight: 700, color: COLORS.primary, marginBottom: 4 }}>{t('🔀 Merge a duplicate into this place')}</div>
      <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 8 }}>
        {t("Find the duplicate listing. It gets absorbed into")} <strong>{keep.name}</strong> {t("and then deleted.")}
      </div>
      {!sel ? (
        <div style={{ position: 'relative' }}>
          {/* Copiato altrove e incollato qui: l'unico modo sicuro quando i
              due si chiamano uguale. */}
          {clip && clip.id && clip.id !== keep.id && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8, padding: '6px 10px', background: '#fff7e6', border: '1px solid #f0d9a8', borderRadius: 6, fontSize: 13 }}>
              <span>{t('Copied:')} <strong>{clip.name}</strong>{clip.address ? <span style={{ color: COLORS.muted }}> — {clip.address}</span> : null}</span>
              <button onClick={() => choose(clip)} style={{ ...styles.buttonOutline, padding: '4px 10px', fontSize: 12 }}>{t('Use as the duplicate')}</button>
              <button onClick={() => { try { localStorage.removeItem(CLIP); } catch (e) { /* niente */ } setClip(null); }}
                style={{ background: 'none', border: 'none', color: COLORS.muted, cursor: 'pointer', fontSize: 12 }}>{t('forget')}</button>
            </div>
          )}
          <button
            onClick={() => {
              const c = { id: keep.id, name: keep.name, address: keep.address || '', city: keep.city || '' };
              try { localStorage.setItem(CLIP, JSON.stringify(c)); } catch (e) { /* niente */ }
              setClip(c);
            }}
            style={{ ...styles.buttonOutline, padding: '4px 10px', fontSize: 12, marginBottom: 8 }}
            title={t('Then open the other listing: it will offer this one as the duplicate.')}
          >
            {t('📋 Copy this place for a merge')}
          </button>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Search the duplicate by name…")}
            style={{ ...styles.input, marginBottom: 0 }}
          />
          {/* I vicini, senza cercare. */}
          {vicini.length > 0 && !q.trim() && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.muted, marginBottom: 4 }}>
                {t('Nearby — a duplicate is almost always one of these')}
              </div>
              <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, maxHeight: 220, overflowY: 'auto' }}>
                {vicini.map((e) => (
                  <div key={e.id} onClick={() => choose(e)}
                    style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${COLORS.bg}`, display: 'flex', justifyContent: 'space-between', gap: 8 }}
                    onMouseEnter={(ev) => { ev.currentTarget.style.background = COLORS.bg; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.background = 'white'; }}>
                    <span>
                      <span style={{ fontWeight: 600 }}>{e.name}</span>
                      <span style={{ color: COLORS.muted }}> — {formatCategoryLabel(e.type)}{e.address ? ` · ${e.address}` : ''}</span>
                    </span>
                    <span style={{ color: Number(e.distance_km) * 1000 > 60 ? COLORS.muted : COLORS.primary, whiteSpace: 'nowrap', fontWeight: Number(e.distance_km) * 1000 > 60 ? 400 : 700 }}>
                      {Number.isFinite(Number(e.distance_km))
                        ? `${Math.round(Number(e.distance_km) * 1000)} m${Number(e.distance_km) * 1000 > 60 ? ` — ${t('too far to be the same')}` : ''}`
                        : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {results.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, marginTop: 4, maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
              {results.map((e) => (
                <div key={e.id} onClick={() => choose(e)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 14, borderBottom: `1px solid ${COLORS.bg}` }}
                  onMouseEnter={(ev) => ev.currentTarget.style.background = COLORS.bg}
                  onMouseLeave={(ev) => ev.currentTarget.style.background = 'white'}>
                  <span style={{ fontWeight: 600 }}>{e.name}</span>
                  <span style={{ color: COLORS.muted }}> — {formatCategoryLabel(e.type)}{e.city ? ` · ${e.city}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>{t('Duplicate:')} <strong>{sel.name}</strong> <span style={{ color: COLORS.muted }}>({formatCategoryLabel(sel.type)}{sel.city ? ` · ${sel.city}` : ''})</span></span>
            <button onClick={() => { setSel(null); setPreview(null); }} style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>change</button>
          </div>
          {error && <div style={{ color: COLORS.danger, fontSize: 13, marginBottom: 8 }}>{error}</div>}
          {preview && (
            <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 10, marginBottom: 8, fontSize: 12.5, color: COLORS.text }}>
              <div>{t('Fields filled on this place:')} <strong>{preview.fields_filled.length ? preview.fields_filled.join(', ') : 'none'}</strong></div>
              <div>{t('Combined supervision:')} <strong>{preview.merged_supervision || 'none'}</strong></div>
              {preview.secondary_types.length > 0 && <div>{t('Also listed under:')} <strong>{preview.secondary_types.map(formatCategoryLabel).join(', ')}</strong></div>}
              <div>{t('Records moved:')} <strong>{childTotal}</strong>{childTotal > 0 ? ` (${Object.entries(preview.child_records).map(([t, n]) => `${t}: ${n}`).join(', ')})` : ''}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!preview && (
              <button onClick={doPreview} disabled={busy} style={{ ...styles.buttonOutline, padding: '8px 14px' }}>
                {busy ? (t("Checking…")) : (t("Preview merge"))}
              </button>
            )}
            <button onClick={doMerge} disabled={busy} style={{ background: COLORS.danger, color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
              {busy ? (t("Merging…")) : (t("🔀 Merge & delete duplicate"))}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactModal({ establishment, user, onClose, onRequireLogin, mode = 'contact', broadcast = null }) {
  const isRes = mode === 'reservation'; // "Book a table" (restaurants)
  const isBroadcast = !!broadcast;      // one request → every place of a category in a city
  const [name, setName] = useState(user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [resTime, setResTime] = useState(''); // reservation time
  const [party, setParty] = useState(2);       // reservation party size
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sentCount, setSentCount] = useState(0); // broadcast: how many places got it


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      if (isBroadcast) {
        const res = await api.broadcastRequest({
          type: broadcast.type, city: broadcast.city, country: broadcast.country || null,
          name, email, phone, subject, message, date_from: dateFrom || null, date_to: dateTo || null,
        });
        setSentCount(res.sent || 0);
        setSuccess(true);
        return;
      }
      let payload;
      if (isRes) {
        // A table reservation is a structured message the restaurant confirms.
        const parts = ['🍽️ Table reservation request'];
        if (dateFrom) parts.push(`Date: ${dateFrom}`);
        if (resTime) parts.push(`Time: ${resTime}`);
        parts.push(`Party of ${party}`);
        const composed = parts.join(' · ') + (message.trim() ? `\n\nNotes: ${message.trim()}` : '');
        payload = { name, email, phone, subject: 'Table reservation', message: composed, date_from: dateFrom || null, date_to: null };
      } else {
        payload = { name, email, phone, subject, message, date_from: dateFrom || null, date_to: dateTo || null };
      }
      await api.sendContactMessage(establishment.id, payload);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={{ ...styles.modalContent, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        {success ? (
          <>
            <h2 style={{ ...styles.modalTitle, color: COLORS.success }}>✓ {isBroadcast ? t('Request sent!') : isRes ? t('Reservation request sent!') : t('Message Sent!')}</h2>
            {isBroadcast ? (
              <p style={{ color: COLORS.muted, fontSize: 15 }}>
                {t("Your request went to")} <strong>{sentCount}</strong> {broadcast.typeLabel} in {broadcast.city}{t(". Interested places reply straight to your email")}{user ? (" " + t("— follow the replies under “My messages.”")) : (t(". Create a free account with this email to keep every reply in one place."))}
              </p>
            ) : (
              <p style={{ color: COLORS.muted, fontSize: 15 }}>
                {t("Your")} {isRes ? (t("table reservation request")) : 'message'} {t("has been sent to")} <strong>{establishment.name}</strong>{t(". They'll confirm by email")}{user ? (t(", and you can follow the conversation in your account under “My messages.”")) : (t(". Create a free account with this email to reply back and keep the conversation in one place."))}
              </p>
            )}
            <button style={{ ...styles.button, width: '100%', marginTop: 12 }} onClick={onClose}>{t('Close')}</button>
          </>
        ) : (
          <>
            <h2 style={styles.modalTitle}>{isBroadcast ? `📣 Request all ${broadcast.count} ${broadcast.typeLabel} in ${broadcast.city}` : isRes ? `🍽️ Book a table at ${establishment.name}` : `✉️ ${t('Contact')} ${establishment.name}`}</h2>
            <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 16 }}>
              {isBroadcast
                ? `One request goes to every ${broadcast.typeLabel} in ${broadcast.city} at once — the ones who can help reply straight to you. Great for finding a driver, a room, a table, or a place for Shabbat.`
                : isRes
                ? (t("Send your preferred date, time and party size. The restaurant confirms by email — this is a request, not a guaranteed booking."))
                : (t("Your message will be sent privately. The establishment will reply directly to your email."))}
            </p>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={styles.label}>{t('Your Name')} *</label>
                  <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div>
                  <label style={styles.label}>{t('Your Email')} *</label>
                  <input style={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
              </div>
              <label style={styles.label}>{t('Phone (optional)')}</label>
              <input style={styles.input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 234 567 890" />

              {isRes ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={styles.label}>{t('Date *')}</label>
                      <input style={styles.input} type="date" value={dateFrom} min={new Date().toISOString().slice(0, 10)}
                             onChange={(e) => setDateFrom(e.target.value)} required aria-label={t('Reservation date')} />
                    </div>
                    <div>
                      <label style={styles.label}>{t('Time *')}</label>
                      <input style={styles.input} type="time" value={resTime}
                             onChange={(e) => setResTime(e.target.value)} required aria-label={t('Reservation time')} />
                    </div>
                    <div>
                      <label style={styles.label}>{t('Guests *')}</label>
                      <input style={styles.input} type="number" min="1" max="50" value={party}
                             onChange={(e) => setParty(Math.max(1, parseInt(e.target.value, 10) || 1))} required aria-label={t('Party size')} />
                    </div>
                  </div>
                  <label style={styles.label}>{t('Special requests (optional)')}</label>
                  <textarea
                    style={{ ...styles.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t("High chair, allergies, occasion, seating preference…")}
                    maxLength={2000}
                  />
                </>
              ) : (
                <>
                  <label style={styles.label}>{t('Subject (optional)')}</label>
                  <input style={styles.input} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("e.g. Booking inquiry, Reservation, Question...")} />
                  <label style={styles.label}>{t("Dates (optional) — from / to")}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
                    <input style={styles.input} type="date" value={dateFrom}
                           max={dateTo || undefined}
                           onChange={(e) => setDateFrom(e.target.value)} aria-label={t('From date')} />
                    <input style={styles.input} type="date" value={dateTo}
                           min={dateFrom || undefined}
                           onChange={(e) => setDateTo(e.target.value)} aria-label={t('To date')} />
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 12 }}>
                    {t("For a stay, event or catering — let them know which dates you have in mind.")}
                  </div>
                  <label style={styles.label}>{t('Message')} *</label>
                  <textarea
                    style={{ ...styles.input, minHeight: 120, resize: 'vertical', fontFamily: 'inherit' }}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    placeholder={t('Write your message here…')}
                    maxLength={5000}
                  />
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: -8, marginBottom: 12, textAlign: 'right' }}>
                    {message.length} / 5000
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button type="submit" style={{ ...styles.button, flex: 1 }} disabled={sending}>
                  {sending ? t('Sending…') : (isBroadcast ? `📣 ${t('Send request to all')}` : isRes ? `🍽️ ${t('Request table')}` : `📨 ${t('Send message')}`)}
                </button>
                <button type="button" style={styles.buttonOutline} onClick={onClose} disabled={sending}>{t('Cancel')}</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// Modal for submitting a claim
function ClaimModal({ establishment, user, onClose, onSubmitted }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await api.submitClaim(establishment.id, message);
      onSubmitted(res.claim);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={{ ...styles.modalContent, maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>{t("🏢 Claim")} {establishment.name}</h2>
        <p style={{ color: COLORS.muted, fontSize: 14, marginTop: 0, marginBottom: 16 }}>
          {t("Submit a claim to manage this listing. An admin will review your request and contact you.")}
        </p>
        {error && <div style={styles.error}>{error}</div>}
        <form onSubmit={submit}>
          <label style={styles.label}>{t('Why are you claiming this listing? (optional but helpful)')}</label>
          <textarea
            style={{ ...styles.input, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={`Example: "I'm the owner of this restaurant" or "I work as the rabbi at this synagogue"`}
            maxLength={1000}
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" style={styles.buttonOutline} onClick={onClose} disabled={submitting}>{t('Cancel')}</button>
            <button type="submit" style={styles.button} disabled={submitting}>
              {submitting ? (t("Submitting...")) : (t("📨 Submit Claim"))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// SuggestEditModal — opens from a place's detail page. Lets any logged-in
// user propose changes to common text fields, upload photos, and add a note.
// Goes to the admin/owner approval queue.
function SuggestEditModal({ establishment, onClose, onSubmitted }) {
  // Pre-populate the form with current values so the user can edit-in-place
  // and we know which fields were actually changed.
  const initial = {
    name: establishment.name || '',
    address: establishment.address || '',
    city: establishment.city || '',
    country: establishment.country || '',
    phone: establishment.phone || '',
    toll_free: establishment.toll_free || '',
    whatsapp_number: establishment.whatsapp_number || '',
    email: establishment.email || '',
    website: establishment.website || '',
    booking_url: establishment.booking_url || '',
    hours: establishment.hours || '',
    supervision: establishment.supervision || '',
    contact_name: establishment.contact_name || '',
    contact_title: establishment.contact_title || '',
    description: establishment.description || '',
    is_closed: !!establishment.is_closed,
  };
  const [form, setForm] = useState(initial);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const setField = (k, v) => setForm({ ...form, [k]: v });

  // Compute the diff so we only send actually-changed fields to the server.
  const diff = () => {
    const out = {};
    for (const k of Object.keys(initial)) {
      if (form[k] !== initial[k]) out[k] = form[k];
    }
    return out;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const changes = diff();
    if (Object.keys(changes).length === 0 && files.length === 0 && !note.trim()) {
      setError('Nothing to suggest yet — change a field, add a photo, or write a note.');
      return;
    }
    setSending(true);
    try {
      // Upload photos first (sequentially — small N, simpler error handling).
      const image_urls = [];
      if (files.length > 0) {
        setUploading(true);
        for (const f of files) {
          const r = await api.uploadSuggestionImage(establishment.id, f);
          image_urls.push(r.url);
        }
        setUploading(false);
      }
      await api.suggestEdit(establishment.id, { changes, image_urls, note: note.trim() || undefined });
      setSuccess(true);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.message || 'Could not submit suggestion.');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.modal} onClick={onClose}>
        <div style={{ ...styles.modalContent, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
          <h2 style={{ ...styles.modalTitle, color: COLORS.success }}>{t('✓ Thanks!')}</h2>
          <p style={{ color: COLORS.muted, marginBottom: 20 }}>
            {t("Your suggestion is in the queue. An admin or the place's owner will review it shortly.")}
          </p>
          <button style={styles.button} onClick={onClose}>{t("Close")}</button>
        </div>
      </div>
    );
  }

  const fieldRow = (label, key, type = 'text') => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </label>
      {type === 'textarea' ? (
        <textarea
          style={{ ...styles.input, minHeight: 70, fontFamily: 'inherit', resize: 'vertical' }}
          value={form[key]}
          onChange={(e) => setField(key, e.target.value)}
        />
      ) : (
        <input
          type={type}
          style={styles.input}
          value={form[key]}
          onChange={(e) => setField(key, e.target.value)}
        />
      )}
    </div>
  );

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={{ ...styles.modalContent, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <h2 style={styles.modalTitle}>{t("✏️ Suggest an edit for")} {establishment.name}</h2>
          <p style={{ color: COLORS.muted, fontSize: 13, marginTop: -8, marginBottom: 16 }}>
            {t("Update anything that's wrong or missing. Photos and changes go into a queue and will be reviewed before going live.")}
          </p>

          {fieldRow('Name', 'name')}
          {fieldRow('Address', 'address')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {fieldRow('City', 'city')}
            {fieldRow('Country', 'country')}
          </div>
          {fieldRow('Phone', 'phone', 'tel')}
          {fieldRow('Toll-free (works only inside the country)', 'toll_free', 'tel')}
          {fieldRow('WhatsApp', 'whatsapp_number', 'tel')}
          {fieldRow('Email', 'email', 'email')}
          {fieldRow('Website', 'website')}
          {fieldRow('Hours / Prayer times', 'hours', 'textarea')}
          {fieldRow('Kosher supervision', 'supervision')}
          {fieldRow('Contact person', 'contact_name')}
          {fieldRow('Contact title / role', 'contact_title')}
          {fieldRow('Description', 'description', 'textarea')}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={!!form.is_closed}
              onChange={(e) => setField('is_closed', e.target.checked)}
            />
            {t("This place has closed permanently")}
          </label>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              {t("Photos (optional)")}
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
              style={{ fontSize: 13 }}
            />
            {files.length > 0 && (
              <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
                {files.length} photo{files.length === 1 ? '' : 's'} {t("selected (max 5).")}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              {t("Note for the reviewer (optional)")}
            </label>
            <textarea
              style={{ ...styles.input, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }}
              placeholder={t("Anything that helps the admin understand the change")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {error && <div style={{ color: COLORS.danger, fontSize: 13, marginBottom: 10 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" style={styles.buttonOutline} onClick={onClose} disabled={sending}>{t('Cancel')}</button>
            <button type="submit" style={styles.button} disabled={sending}>
              {uploading ? (t("Uploading photos…")) : sending ? (t("Sending…")) : (t("Submit for review"))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// SiteContactModal — generic "contact us" popup opened from the footer. Not
// tied to any establishment; the backend forwards the message to the admin.
function SiteContactModal({ onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await api.siteContact({ name, email, subject, message });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={{ ...styles.modalContent, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        {success ? (
          <>
            <h2 style={{ ...styles.modalTitle, color: COLORS.success }}>{t('✓ Message Sent!')}</h2>
            <p style={{ color: COLORS.muted, marginBottom: 20 }}>
              {t("Thanks for reaching out — we'll get back to you as soon as we can.")}
            </p>
            <button style={styles.button} onClick={onClose}>{t("Close")}</button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 style={styles.modalTitle}>{t('✉️ Contact Jewish Compass')}</h2>
            <p style={{ color: COLORS.muted, fontSize: 13, marginTop: -8, marginBottom: 16 }}>
              {t("Questions, feedback, suggesting a place, or anything else — send us a note.")}
            </p>
            <input style={styles.input} placeholder={t("Your name *")} required value={name} onChange={(e) => setName(e.target.value)} />
            <input style={styles.input} type="email" placeholder={t("Email *")} required value={email} onChange={(e) => setEmail(e.target.value)} />
            <input style={styles.input} placeholder={t("Subject (optional)")} value={subject} onChange={(e) => setSubject(e.target.value)} />
            <textarea style={{ ...styles.input, minHeight: 120, fontFamily: 'inherit', resize: 'vertical' }} placeholder={t("Your message *")} required value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} />
            {error && <div style={{ color: COLORS.danger, fontSize: 13, marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" style={styles.buttonOutline} onClick={onClose} disabled={sending}>{t('Cancel')}</button>
              <button type="submit" style={styles.button} disabled={sending}>{sending ? (t("Sending…")) : (t("Send Message"))}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// PrivacyPage — GDPR-friendly notice describing what data the site stores,
// why, and how users can exercise their rights. Plain English on purpose.
function PrivacyPage({ onContact }) {
  return (
    <div style={{ ...styles.container, maxWidth: 760, lineHeight: 1.6 }}>
      <h1 style={{ color: COLORS.primary }}>{t('Privacy Policy')}</h1>
      <p style={{ color: COLORS.muted, fontSize: 13 }}>{t("Last updated:")} {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}</p>

      <p>
        {t("Jewish Compass (jewishcompass.com) is a directory of Jewish and kosher places worldwide. We are committed to handling your personal data carefully and in accordance with the EU General Data Protection Regulation (GDPR).")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('What data we collect')}</h2>
      <ul>
        <li><b>{t('Account information')}</b> {t('— if you register, we store your email, name, and an encrypted password. This is used to authenticate you and let you save favorites.')}</li>
        <li><b>{t('Authentication token')}</b> {t("— when you log in we store a JWT token in your browser's localStorage so you stay signed in. It contains your user ID and role, and is required for the site to work after login.")}</li>
        <li><b>{t('Favorites')}</b> {t('— places you save with the heart icon are stored against your account.')}</li>
        <li><b>{t('Location (Near me)')}</b> {t('— only if you click the "Near me" button. We never store or transmit your coordinates outside of the request that returns nearby places.')}</li>
        <li><b>{t('Contact form submissions')}</b> {t("— when you message a place's owner or the site, we keep the message so the recipient can read it.")}</li>
        <li><b>{t('Server logs')}</b> {t('— like every website, our server temporarily records IP addresses and request paths for security and debugging.')}</li>
      </ul>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t("What we don't do")}</h2>
      <ul>
        <li>{t('No advertising trackers.')}</li>
        <li>{t('No analytics or behavioural profiling cookies.')}</li>
        <li>{t('No selling, renting, or sharing your data with third parties for marketing.')}</li>
      </ul>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Third-party services')}</h2>
      <ul>
        <li><b>{t('OpenStreetMap / Nominatim')}</b> {t('— for map tiles and address lookup. Your browser sends them your IP when loading maps.')}</li>
        <li><b>{t('Hebcal API')}</b> {t('— fetched for candle-lighting times on synagogue pages.')}</li>
        <li><b>{t('flagcdn.com')}</b> {t('— for country flag images.')}</li>
        <li><b>{t('Stripe')}</b> {t('— payment processing on the rare booking flow. Card data goes directly to Stripe, never to us.')}</li>
      </ul>
      <p>{t('Each of these has its own privacy policy.')}</p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Your rights under GDPR')}</h2>
      <p>{t('You have the right to:')}</p>
      <ul>
        <li>{t('Access the personal data we hold about you.')}</li>
        <li>{t('Correct or update it.')}</li>
        <li>{t('Have it deleted (the "right to be forgotten").')}</li>
        <li>{t('Export your data in a portable format.')}</li>
        <li>{t('Object to processing or lodge a complaint with your local data protection authority.')}</li>
      </ul>
      <p>
        {t("To exercise any of these rights, contact us through")}{' '}
        <button type="button" onClick={onContact} style={{ background: 'none', border: 'none', color: COLORS.primary, textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>{t('the contact form')}</button>{' '}
        {t("and we'll respond within 30 days.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Data retention')}</h2>
      <p>
        {t("We keep account data for as long as your account is active. Contact form messages are kept until the recipient deletes them. Server logs are rotated after a few weeks.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Contact')}</h2>
      <p>
        {t("For any privacy-related question, please use the")}{' '}
        <button type="button" onClick={onContact} style={{ background: 'none', border: 'none', color: COLORS.primary, textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>{t('contact form')}</button>.
      </p>
    </div>
  );
}

// TermsPage — Terms of Use. States ownership of the directory content and
// explicitly prohibits scraping, bulk copying, and reproduction, so we have
// clear standing for a takedown if the data or images are lifted.
function TermsPage({ onContact }) {
  return (
    <div style={{ ...styles.container, maxWidth: 760, lineHeight: 1.6 }}>
      <h1 style={{ color: COLORS.primary }}>{t('Terms of Use')}</h1>
      <p style={{ color: COLORS.muted, fontSize: 13 }}>{t("Last updated:")} {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}</p>

      <p>
        {t("Welcome to Jewish Compass (jewishcompass.com), a directory of Jewish and kosher places worldwide. By accessing or using this website you agree to these Terms of Use. If you do not agree, please do not use the site.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Ownership of content')}</h2>
      <p>
        {t("The directory — including the compilation, selection, and arrangement of all listings, descriptions, opening hours, prayer times, supervision details, photographs we have produced, and other content on this site — is the property of Jewish Compass and is protected by copyright and database rights. The collection as a whole is an original work regardless of whether individual facts are public.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Permitted use')}</h2>
      <p>
        {t("You may browse the site and use the information for your own personal, non-commercial purposes — to find a synagogue, a kosher restaurant, a mikvah, and so on. You may share links to our pages freely.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Prohibited use')}</h2>
      <p>{t('Without our prior written permission, you may not:')}</p>
      <ul>
        <li>{t("Scrape, crawl, harvest, or use automated tools to extract listings or data from the site, in whole or in part.")}</li>
        <li>{t('Copy, reproduce, republish, or redistribute the directory or any substantial part of it on another website, app, or database.')}</li>
        <li>{t('Download, hotlink, or reuse our photographs except where you are the rights-holder of that specific image.')}</li>
        <li>{t('Use the content to build, train, or populate a competing directory or dataset.')}</li>
        <li>{t('Circumvent any technical measures (rate limits, access controls) that protect the site.')}</li>
      </ul>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Accuracy & no warranty')}</h2>
      <p>
        {t("We work to keep listings accurate but make no guarantee. Kosher supervision, opening hours, and prayer times change — always verify directly with the establishment before relying on them. Listings do not constitute an endorsement or a halachic ruling on any establishment's kosher status.")}
      </p>
      <p>
        {t("The information on this site is provided \"as is\", for general guidance only. Much of it is supplied by business owners or third parties and may be incomplete, out of date, or incorrect.")} <strong>{t("Jewish Compass is not responsible for the accuracy, completeness, or reliability of any information on the site, and accepts no liability for any loss or damage arising from reliance on it.")}</strong> {t("Use of the site and its information is entirely at your own risk.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Kosher supervision (hashgacha)')}</h2>
      <p>
        {t("Any kosher certification, hechsher, or supervising body (\"hashgacha\") shown on a listing is reproduced for information only, as reported to us by the establishment or a third party. Its appearance on Jewish Compass is")} <strong>not</strong> {t("a recommendation, endorsement, approval, or verification of that supervision, of the certifying body, or of the establishment's kosher status, and it is")} <strong>not</strong> {t("a halachic ruling. We do not vet, certify, or vouch for any hashgacha, and standards of kashrut vary between communities and individuals.")} <strong>{t("Always confirm a place's current kosher supervision directly with the establishment and rely on your own rabbinic authority.")}</strong> {t("Jewish Compass takes no responsibility and accepts no liability for any reliance on supervision information shown on the site.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Listings & submissions')}</h2>
      <p>
        {t("Business owners may claim and edit their listing. By submitting content (a place, photo, or edit) you confirm you have the right to do so and grant us a licence to display it on the site. We may review, edit, or remove any submission.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Enforcement')}</h2>
      <p>
        {t("We monitor for unauthorised copying and reserve the right to block access, pursue a takedown (including under the DMCA and equivalent laws), and seek any other remedy available to us against parties who breach these terms.")}
      </p>

      <h2 style={{ color: COLORS.primary, fontSize: 20, marginTop: 24 }}>{t('Contact')}</h2>
      <p>
        {t("Questions about these terms, or requests for permission to use our content, can be sent via the")}{' '}
        <button type="button" onClick={onContact} style={{ background: 'none', border: 'none', color: COLORS.primary, textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>{t('contact form')}</button>.
      </p>
    </div>
  );
}

// OurStoryPage — the history of the site: JewishItaly.org (2001) → JewishEurope.org
// (2008) → Jewish Compass (2026). A warm heritage page, linked from the footer.
function OurStoryPage() {
  const serif = 'ui-serif, "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, "Times New Roman", serif';
  const gold = '#a9791f';
  const soft = COLORS.muted;
  const Tag = ({ children }) => (
    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: gold, marginBottom: 12 }}>{children}</div>
  );
  const H2 = ({ children }) => (
    <h2 style={{ fontFamily: serif, fontSize: 'clamp(22px,3.4vw,29px)', fontWeight: 600, lineHeight: 1.2, margin: '0 0 10px', color: COLORS.text, letterSpacing: '-.01em' }}>{children}</h2>
  );
  const Step = ({ yr, name, desc }) => (
    <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: '16px 16px 14px', boxShadow: '0 1px 2px rgba(0,0,0,.05)' }}>
      <div style={{ fontFamily: serif, fontSize: 13, fontWeight: 600, color: gold, letterSpacing: '.04em' }}>{yr}</div>
      <h3 style={{ fontFamily: serif, fontSize: 17, margin: '2px 0 6px', fontWeight: 600, color: COLORS.text }}>{name}</h3>
      <div style={{ fontSize: 12.5, color: soft, lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
  return (
    <div style={{ ...styles.container, maxWidth: 760, lineHeight: 1.65 }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: gold, marginBottom: 16 }}>{t('Est. 2001 · Our Story')}</div>
      <h1 style={{ fontFamily: serif, fontWeight: 600, fontSize: 'clamp(30px,6vw,50px)', lineHeight: 1.08, letterSpacing: '-.015em', margin: '0 0 18px', color: COLORS.text }}>
        {t("It began with a traveler's")} <em style={{ color: COLORS.primary }}>question</em>.
      </h1>
      <p style={{ fontFamily: serif, fontSize: 'clamp(18px,2.6vw,21px)', lineHeight: 1.5, color: COLORS.text, margin: '0 0 8px', maxWidth: '60ch' }}>
        {t("One directory, born to answer the oldest questions on the road —")} <span style={{ fontStyle: 'italic', color: COLORS.primary }}>{t('Where can I daven? Where can I eat? Where is the mikvah?')}</span> {t("— and quietly growing ever since.")}
      </p>
      <div style={{ fontFamily: serif, fontSize: 'clamp(38px,10vw,82px)', fontWeight: 600, letterSpacing: '-.02em', color: 'transparent', WebkitTextStroke: `1.2px ${COLORS.primary}`, margin: '22px 0 6px', lineHeight: 1 }}>{t('2001–2026')}</div>

      <div style={{ height: 1, background: '#e7ddc8', margin: '36px 0' }} />

      <section style={{ marginBottom: 36 }}>
        <Tag>{t('The beginning — 2001 · JewishItaly.org')}</Tag>
        <H2>{t('Answering the questions that kept arriving')}</H2>
        <p>{t('Long before travel apps,')} <strong>{t('Rabbi Menachem Lazar')}</strong> {t('was fielding a steady stream of the same messages. Travelers heading to Italy were writing in through the Italian Chabad website, asking the practical questions every Jewish traveler carries — where to find a minyan, somewhere kosher to eat, a mikvah, a warm community.')}</p>
        <p>{t('So he built the answer: a simple directory where a visitor could reach those places directly, themselves. In 2001 it went online as')} <strong>{t('JewishItaly.org')}</strong>.</p>
      </section>

      <section style={{ marginBottom: 36 }}>
        <Tag>{t('From Italy to all of Europe — 2008')}</Tag>
        <H2>{t('JewishEurope.org')}</H2>
        <p>{t('It worked — and the requests kept coming, now for other countries too.')} <span style={{ fontStyle: 'italic', color: COLORS.primary }}>{t('Could you add France? Switzerland? Hungary?')}</span> {t('So the directory grew beyond Italy, and in 2008 it became')} <strong>{t('JewishEurope.org')}</strong> {t('— a guide to Jewish and kosher life across the continent, organized the way people actually travel: country by country, city by city. Synagogues and minyanim, kosher restaurants and shops, mikvaot, and community contacts, all in one place.')}</p>
      </section>

      <section style={{ marginBottom: 8 }}>
        <Tag>{t('A new chapter — 2026')}</Tag>
        <H2>{t('The same mission, a far wider reach: Jewish Compass')}</H2>
        <p>{t("In 2026 the whole thing was rebuilt from the ground up as")} <strong>{t("Jewish Compass")}</strong> {t("— same purpose, modern foundations. Interactive maps, listings that owners keep up to date themselves, kosher certifications shown with their real hechsher logos, and coverage that now reaches well beyond Europe. The familiar addresses so many came to know all lead right here.")}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 22 }}>
          <Step yr="2001" name="JewishItaly.org" desc="Born from travelers' questions to Italian Chabad — a directory for Italy." />
          <Step yr="2008" name="JewishEurope.org" desc="Grew country by country into a guide for all of Jewish & kosher Europe." />
          <Step yr="2026" name="Jewish Compass" desc="Rebuilt and worldwide — live maps, owner-managed, certified." />
        </div>
      </section>

      <p style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 'clamp(18px,3vw,22px)', lineHeight: 1.45, color: COLORS.primary, textAlign: 'center', margin: '44px auto 0', maxWidth: '34ch' }}>
        {t("The name changed twice. The purpose never did — to help you find a warm shul, a kosher table, and a welcome, wherever the road takes you.")}
      </p>
      <p style={{ textAlign: 'center', color: soft, fontSize: 13, marginTop: 20 }}>© 2001–{new Date().getFullYear()} {t("· Rabbi Menachem Lazar · JewishItaly → JewishEurope → Jewish Compass")}</p>
    </div>
  );
}

// Footer — Privacy link, copyright, Contact link (opens SiteContactModal).
// Sticky-feeling: separated visually with a top border, full-width.
// Site-wide social handles. Centralised so the header chip and footer pick
// from the same source. If we add Twitter/X or LinkedIn for the company later,
// just append here.
const SITE_SOCIALS = [
  { label: 'Facebook',  href: 'https://www.facebook.com/jewishcompasscom', Logo: FacebookLogo,  bg: '#1877f2' },
  { label: 'Instagram', href: 'https://www.instagram.com/jewishcompass',   Logo: InstagramLogo, bg: 'linear-gradient(135deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)' },
];

function SiteSocialIcons({ size = 28, iconSize = 14 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {SITE_SOCIALS.map((s) => {
        const { Logo } = s;
        return (
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noreferrer"
            aria-label={`Jewish Compass on ${s.label}`}
            title={s.label}
            style={{
              width: size, height: size, background: s.bg, color: 'white',
              borderRadius: '50%', textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            }}
          >
            <Logo size={iconSize} />
          </a>
        );
      })}
    </span>
  );
}

function Footer({ onContact }) {
  return (
    <footer style={{ marginTop: 48, borderTop: `1px solid ${COLORS.border}`, background: 'white', padding: '20px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Newsletter signup removed — the newsletter audience is simply the
            users list (everyone is opted in by default and can opt out from
            their profile), so a separate email-capture box was redundant. */}
        {/* Links row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, fontSize: 13, color: COLORS.muted }}>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <a href="/destinations" style={{ color: COLORS.primary, textDecoration: 'none' }}>🌍 {t('Destinations')}</a>
            <a href="/our-story" style={{ color: COLORS.primary, textDecoration: 'none' }}>{t('Our Story')}</a>
            <a href="/privacy" style={{ color: COLORS.primary, textDecoration: 'none' }}>{t('Privacy')}</a>
            <a href="/terms" style={{ color: COLORS.primary, textDecoration: 'none' }}>{t('Terms')}</a>
            <button
              type="button"
              onClick={onContact}
              style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', padding: 0, fontSize: 13, fontFamily: 'inherit' }}
            >
              {t('Contact')}
            </button>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: COLORS.muted }}>{t('Follow:')}</span>
              <SiteSocialIcons size={26} iconSize={13} />
            </span>
          </div>
          <div>© 2001–{new Date().getFullYear()} {t("Menachem Lazar")}</div>
          <div style={{ color: COLORS.muted, fontSize: 12, marginTop: 4 }}>
            {t("The new home of")} <strong>{t('jewisheurope.org')}</strong> {t("— Europe's Jewish & kosher directory, rebuilt.")}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ReviewTokenPage — the no-login "leave a review from the emailed link" screen
// (/review/:token). The token names the place and the reviewer's email, so the
// visitor only has to pick stars and type. No account, no password, no sign-in
// wall: the people we ask are overwhelmingly people who only ever used the
// contact form, and asking them to register first is what kept reviews at zero.
function ReviewTokenPage({ token }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);
  const [rating, setRating] = React.useState(0);
  const [hover, setHover] = React.useState(0);
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [name, setName] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [done, setDone] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    api.reviewByToken(token)
      .then((d) => {
        if (!alive) return;
        setData(d);
        // Coming back to an earlier review edits it rather than starting over.
        if (d.existing) {
          setRating(d.existing.rating || 0);
          setTitle(d.existing.title || '');
          setBody(d.existing.body || '');
        }
        setLoading(false);
      })
      .catch((e) => { if (!alive) return; setError(e.message || 'This link is invalid or has expired.'); setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const submit = async () => {
    if (!rating) return;
    setSaving(true);
    try {
      const saved = await api.submitReviewByToken(token, { rating, title, body, name });
      setDone(saved);
    } catch (e) {
      alert(e.message || 'Could not save your review.');
    } finally { setSaving(false); }
  };

  const wrap = { maxWidth: 640, margin: '40px auto', padding: '0 16px' };
  if (loading) return <div style={wrap}><p style={{ color: COLORS.muted }}>{t('Loading…')}</p></div>;
  if (error) {
    return (
      <div style={wrap}>
        <h2 style={{ color: COLORS.primary }}>{t('Review link unavailable')}</h2>
        <p style={{ color: COLORS.text }}>{error}</p>
        <p style={{ color: COLORS.muted, fontSize: 14 }}>
          {t("Please use the link in the most recent email we sent you.")}
        </p>
      </div>
    );
  }

  const where = [data.city, data.country].filter(Boolean).join(', ');

  if (done) {
    return (
      <div style={wrap}>
        <h2 style={{ color: COLORS.primary, marginBottom: 8 }}>🙏 {t('Thank you')}</h2>
        <p style={{ color: COLORS.text }}>
          {t("Your review of")} <strong>{data.establishmentName}</strong> {t("has been received. A Jewish Compass admin checks reviews before they go live, so it will appear on the page shortly.")}
        </p>
        <p style={{ color: COLORS.muted, fontSize: 14 }}>
          {t("We've kept it under")} <strong>{done.accountEmail || data.email}</strong>{t(", so that address can edit this review later — use “forgot password” on the sign-in page to set a password and take over the account whenever you like.")}
        </p>
        <p style={{ marginTop: 20 }}>
          <a href={withLangPath(`/place/${data.establishmentId}`)} style={{ color: COLORS.primary }}>
            {t('Go to the place page')} →
          </a>
        </p>
      </div>
    );
  }

  const star = (n) => (
    <span
      key={n}
      onClick={() => setRating(n)}
      onMouseEnter={() => setHover(n)}
      onMouseLeave={() => setHover(0)}
      role="button"
      aria-label={`${n} star${n > 1 ? 's' : ''}`}
      style={{ cursor: 'pointer', fontSize: 34, lineHeight: 1, color: (hover || rating) >= n ? '#f5a623' : '#d1d5db', userSelect: 'none' }}
    >★</span>
  );

  return (
    <div style={wrap}>
      <h2 style={{ color: COLORS.primary, marginBottom: 4 }}>
        {data.existing ? t('Update your review') : t('How was it?')}
      </h2>
      <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0 }}>
        <strong>{data.establishmentName}</strong>{where ? ` — ${where}` : ''}{t(". No login needed — this link is yours.")}
      </p>

      <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 16, background: '#fff' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>{[1, 2, 3, 4, 5].map(star)}</div>

        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder={t('Title (optional)')}
          style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }}
        />
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={t('A sentence or two about your visit (optional)')} rows={5}
          style={{ width: '100%', padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', marginBottom: 10 }}
        />
        {!data.existing && (
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder={t('Your name (optional)')}
            style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
        <button
          type="button" onClick={submit} disabled={saving || !rating}
          style={{ background: COLORS.primary, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: (saving || !rating) ? 'not-allowed' : 'pointer', opacity: (saving || !rating) ? 0.6 : 1 }}
        >
          {saving ? t('Saving…') : (data.existing ? t('Update review') : t('Post review'))}
        </button>
        {!rating && <span style={{ color: COLORS.muted, fontSize: 13 }}>{t('Pick a star rating to continue')}</span>}
      </div>
      <p style={{ color: COLORS.muted, fontSize: 12, marginTop: 14 }}>
        {t("Reviews are checked by an admin before they appear.")}
      </p>
    </div>
  );
}

// ThreadReplyPage — the no-login "reply via emailed magic link" screen
// (/reply/:token). Loads one conversation by its token and lets a visitor who
// has no account reply straight back. The token gates access to that thread.
function ThreadReplyPage({ token }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);
  const [thread, setThread] = React.useState([]);
  const [text, setText] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sentOk, setSentOk] = React.useState(false);
  const [inbox, setInbox] = React.useState(null);
  const [showInbox, setShowInbox] = React.useState(false);
  const [inboxLoading, setInboxLoading] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    api.threadByToken(token)
      .then((d) => { if (!alive) return; setData(d); setThread(d.thread || []); setLoading(false); })
      .catch((e) => { if (!alive) return; setError(e.message || 'This link is invalid or has expired.'); setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const r = await api.replyByToken(token, body);
      setThread((t) => [...t, { sender_role: data.viewerRole || 'user', body, created_at: (r.reply && r.reply.created_at) || new Date().toISOString() }]);
      setText(''); setSentOk(true);
    } catch (e) {
      alert(e.message || 'Could not send your reply.');
    } finally { setSending(false); }
  };

  const wrap = { maxWidth: 640, margin: '40px auto', padding: '0 16px' };
  if (loading) return <div style={wrap}><p style={{ color: COLORS.muted }}>{t('Loading your conversation…')}</p></div>;
  if (error) {
    return (
      <div style={wrap}>
        <h2 style={{ color: COLORS.primary }}>{t('Conversation unavailable')}</h2>
        <p style={{ color: COLORS.text }}>{error}</p>
        <p style={{ color: COLORS.muted, fontSize: 14 }}>
          {t("Please use the link in the most recent email. If you have an account, you can also open the conversation from")} <a href="/profile" style={{ color: COLORS.primary }}>{t("your profile")}</a>.
        </p>
      </div>
    );
  }

  // The magic link is one-per-role: the visitor who sent the message ('user') or
  // the place answering ('owner'). "Mine" flips accordingly, and the other side
  // is labelled with the place name (visitor's view) or the sender's name (owner's).
  const asOwner = data.viewerRole === 'owner';
  const otherName = asOwner ? (data.senderName || 'Visitor') : data.establishmentName;
  // An inbox for owners who have no account. The magic link promises "no
  // login needed", and then showed exactly one conversation — so the only way
  // to see whether anyone had answered was to reopen every past notification
  // email. Loaded on demand, because most visits here are to answer the one
  // message the email was about.
  const openInbox = async () => {
    if (inbox) { setShowInbox((v) => !v); return; }
    setInboxLoading(true);
    try {
      const r = await api.threadInbox(token);
      setInbox(r); setShowInbox(true);
    } catch (e) {
      setInbox({ error: e.message || 'Could not load your conversations.' });
      setShowInbox(true);
    } finally { setInboxLoading(false); }
  };
  const bubble = (role, body, key) => {
    const mine = role === (data.viewerRole || 'user');
    return (
      <div key={key} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
        <div style={{ maxWidth: '80%', background: mine ? COLORS.primary : '#f1f5f9', color: mine ? '#fff' : COLORS.text, padding: '10px 14px', borderRadius: 12, whiteSpace: 'pre-wrap', fontSize: 14 }}>
          <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 3 }}>{mine ? (t("You")) : otherName}</div>
          {body}
        </div>
      </div>
    );
  };

  return (
    <div style={wrap}>
      <h2 style={{ color: COLORS.primary, marginBottom: 4 }}>💬 {asOwner ? (data.senderName || (t("Visitor"))) : data.establishmentName}</h2>
      <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0 }}>
        {asOwner
          ? <>{t("Reply to this message")}{data.subject ? ` — ${data.subject}` : ''}{t(". You're answering as")} {data.establishmentName} {t("— no login needed.")}</>
          : <>{t("Your conversation")}{data.subject ? ` — ${data.subject}` : ''}{t(". Reply below — no login needed.")}</>}
      </p>
      <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 16, background: '#fff', marginBottom: 16 }}>
        {bubble('user', data.original.body, 'orig')}
        {thread.map((m, i) => bubble(m.sender_role, m.body, i))}
      </div>
      <textarea
        value={text} onChange={(e) => { setText(e.target.value); setSentOk(false); }}
        placeholder={t("Write your reply…")} rows={4}
        style={{ width: '100%', padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <button
          type="button" onClick={send} disabled={sending || !text.trim()}
          style={{ background: COLORS.primary, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: (sending || !text.trim()) ? 'not-allowed' : 'pointer', opacity: (sending || !text.trim()) ? 0.6 : 1 }}
        >
          {sending ? (t("Sending…")) : (t("Send reply"))}
        </button>
        {sentOk && <span style={{ color: COLORS.success, fontSize: 13 }}>{t("✓ Sent —")} {asOwner ? (t("the visitor")) : (t("the place"))} {t("has been notified.")}</span>}
      </div>

      {/* All conversations — owners only. Without this, the only way to see
          whether a message had been answered was to reopen its email. */}
      {asOwner && (
        <div style={{ marginTop: 28, borderTop: `1px solid ${COLORS.border}`, paddingTop: 16 }}>
          <button
            type="button" onClick={openInbox} disabled={inboxLoading}
            style={{ background: 'none', border: 'none', padding: 0, color: COLORS.primary, fontWeight: 600, fontSize: 14, cursor: inboxLoading ? 'wait' : 'pointer' }}
          >
            {inboxLoading ? t('Loading…') : `📥 ${t('All conversations for')} ${data.establishmentName} ${showInbox ? '▲' : '▼'}`}
          </button>

          {showInbox && inbox && inbox.error && (
            <div style={{ marginTop: 10, fontSize: 13, color: COLORS.danger }}>{inbox.error}</div>
          )}

          {showInbox && inbox && !inbox.error && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, color: COLORS.muted, marginBottom: 10 }}>
                {inbox.conversations.length} {inbox.conversations.length === 1 ? t('conversation') : t('conversations')} ·{' '}
                {inbox.conversations.filter((c) => c.waitingOnYou).length} {t('waiting on you')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {inbox.conversations.map((c) => (
                  <a
                    key={c.id} href={withLangPath(`/reply/${c.replyToken}`)}
                    style={{
                      display: 'block', textDecoration: 'none', color: 'inherit',
                      border: `1px solid ${c.waitingOnYou ? COLORS.accent : COLORS.border}`,
                      background: c.waitingOnYou ? '#fffbeb' : '#fff',
                      borderRadius: 8, padding: '10px 12px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <strong style={{ color: COLORS.primary, fontSize: 14 }}>{c.sender_name}</strong>
                      <span style={{ fontSize: 12, color: COLORS.muted }}>
                        {new Date(c.created_at).toLocaleDateString()}
                        {c.waitingOnYou
                          ? <strong style={{ color: '#92400e' }}> · {t('needs a reply')}</strong>
                          : <span style={{ color: COLORS.success }}> · {t('answered')}</span>}
                      </span>
                    </div>
                    {(c.date_from || c.date_to) && (
                      <div style={{ fontSize: 12, color: COLORS.primary, fontWeight: 600, marginTop: 2 }}>
                        📅 {[c.date_from, c.date_to].filter(Boolean).map((d) => new Date(d).toLocaleDateString()).join(' – ')}
                      </div>
                    )}
                    <div style={{ fontSize: 13, color: COLORS.text, marginTop: 4 }}>
                      {String(c.message || '').slice(0, 150)}{String(c.message || '').length > 150 ? '…' : ''}
                    </div>
                    {(c.thread || []).length > 0 && (
                      <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
                        {c.thread.length} {c.thread.length === 1 ? t('reply') : t('replies')}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// NotFoundPage — 404. Friendly, with a search bar to recover.
function NotFoundPage({ onSelect, onSearch }) {
  return (
    <div style={{ ...styles.container, textAlign: 'center', paddingTop: 64, paddingBottom: 64 }}>
      <div style={{ fontSize: 80, lineHeight: 1, marginBottom: 16 }}>🧭</div>
      <h1 style={{ color: COLORS.primary, marginBottom: 12 }}>{t('Lost your bearings?')}</h1>
      <p style={{ color: COLORS.muted, marginBottom: 28, maxWidth: 480, margin: '0 auto 28px' }}>
        {t("The page you're looking for doesn't exist — or maybe a place moved or was renamed. Try a search, or head back to the home page.")}
      </p>
      <div style={{ maxWidth: 480, margin: '0 auto 16px' }}>
        <AddressSearch variant="compact" placeholder={t("Type a place, address, or landmark…")} />
      </div>
      <button style={styles.buttonOutline} onClick={() => navigateTo('/')}>{t('← Back to home')}</button>
    </div>
  );
}

// VerifyEmailPage — destination of the email-verification link. Reads ?token=
// from the URL, posts it, shows success or error.
function VerifyEmailPage({ onUserReload, onDone }) {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'already' | 'error'
  const [msg, setMsg] = useState('');

  React.useEffect(() => {
    if (!token) { setStatus('error'); setMsg('Missing token. Use the link from the email we sent.'); return; }
    api.verifyEmail(token)
      .then((r) => {
        if (r.alreadyVerified) { setStatus('already'); }
        else { setStatus('success'); onUserReload && onUserReload(); }
      })
      .catch((e) => { setStatus('error'); setMsg(e.message || 'Verification failed.'); });
  }, [token, onUserReload]);

  return (
    <div style={{ ...styles.container, maxWidth: 480, textAlign: 'center', paddingTop: 48 }}>
      {status === 'verifying' && <><h2>{t('Verifying your email…')}</h2><p style={{ color: COLORS.muted }}>{t('One second.')}</p></>}
      {status === 'success' && (
        <>
          <h1 style={{ color: COLORS.success }}>{t('✓ Email confirmed')}</h1>
          <p style={{ color: COLORS.muted, marginBottom: 16 }}>
            {t("Thanks — your account is now fully active. You can write reviews and suggest edits.")}
          </p>
          <button style={styles.button} onClick={onDone}>{t('Continue')}</button>
        </>
      )}
      {status === 'already' && (
        <>
          <h1>{t('Already verified')}</h1>
          <p style={{ color: COLORS.muted, marginBottom: 16 }}>{t('This link was used before — your email is confirmed.')}</p>
          <button style={styles.button} onClick={onDone}>{t('Continue')}</button>
        </>
      )}
      {status === 'error' && (
        <>
          <h1 style={{ color: COLORS.danger }}>{t("Couldn't verify")}</h1>
          <p style={{ color: COLORS.muted, marginBottom: 16 }}>{msg}</p>
          <button style={styles.button} onClick={onDone}>{t('Back to home')}</button>
        </>
      )}
    </div>
  );
}

// ResetPasswordPage — destination of the email reset link. Reads ?token=
// from the URL, asks for a new password, posts to /api/auth/reset-password.
function ResetPasswordPage({ onDone }) {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (newPassword !== confirm) { setMsg({ type: 'error', text: 'Passwords do not match.' }); return; }
    if (newPassword.length < 8) { setMsg({ type: 'error', text: 'Password must be at least 8 characters.' }); return; }
    setSaving(true);
    try {
      await api.resetPassword(token, newPassword);
      setMsg({ type: 'success', text: 'Password updated. You can now log in with the new one.' });
      setTimeout(onDone, 1500);
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Could not reset password.' });
    } finally {
      setSaving(false);
    }
  };

  const msgStyle = (m) => m && ({
    background: m.type === 'success' ? '#d1fae5' : '#fee2e2',
    border: `1px solid ${m.type === 'success' ? '#86efac' : '#fca5a5'}`,
    color: m.type === 'success' ? '#065f46' : '#991b1b',
    padding: '8px 12px', borderRadius: 6, fontSize: 13, marginTop: 8,
  });

  return (
    <div style={{ ...styles.container, maxWidth: 480 }}>
      <h1 style={{ color: COLORS.primary }}>{t('Set a new password')}</h1>
      {!token ? (
        <p style={{ color: COLORS.danger }}>{t("This page needs a valid reset link. Please request a new one from the Login dialog.")}</p>
      ) : (
        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('New password')}</label>
          <input style={styles.input} type="password" autoComplete="new-password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Confirm new password')}</label>
          <input style={styles.input} type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {msg && <div style={msgStyle(msg)}>{msg.text}</div>}
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button type="submit" style={styles.button} disabled={saving}>
              {saving ? (t("Saving…")) : (t("Set new password"))}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// One contact-message conversation in the user's "My messages" list: the
// original message, the back-and-forth thread (owner + the user's own
// replies), and a box to reply back to the place.
function MyMessageThread({ m, onReplied }) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const thread = m.thread || [];
  const send = async () => {
    const text = reply.trim();
    if (!text) return;
    setSending(true);
    try {
      const saved = await api.userReplyToMessage(m.id, text);
      setReply('');
      onReplied(saved);
    } catch (e) {
      alert(`Couldn't send reply: ${e.message}`);
    } finally {
      setSending(false);
    }
  };
  return (
    <div id={`msg-${m.id}`} style={{ padding: '14px 0', borderBottom: `1px solid ${COLORS.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <a href={withLangPath(`/place/${m.establishment_id}`)} style={{ color: COLORS.primary, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
          {m.establishment_name}
        </a>
        <span style={{ fontSize: 11, color: COLORS.muted }}>{m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}</span>
      </div>
      {m.subject && <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginTop: 4 }}>{m.subject}</div>}
      {/* Your original message */}
      <div style={{ fontSize: 13, color: COLORS.text, marginTop: 6, padding: '8px 12px', background: COLORS.bg, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, marginBottom: 2 }}>{t("You")}</div>
        <Linkify text={m.message} />
      </div>
      {/* Did the place open the notification email? (best-effort via SendGrid) */}
      {m.email_opened_at && (
        <div style={{ fontSize: 11, color: '#166534', marginTop: 4, fontWeight: 600 }}>
          {t("👁 Seen by the place ·")} {new Date(m.email_opened_at).toLocaleDateString()}
        </div>
      )}
      {/* Thread */}
      {thread.map((t) => {
        const isOwner = t.sender_role === 'owner';
        return (
          <div key={t.id} style={{
            fontSize: 13, color: COLORS.text, marginTop: 6, padding: '8px 12px',
            background: isOwner ? '#eff6ff' : COLORS.bg,
            borderLeft: isOwner ? `3px solid ${COLORS.primary}` : `3px solid ${COLORS.border}`,
            borderRadius: 6, whiteSpace: 'pre-wrap',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: isOwner ? COLORS.primary : COLORS.muted, marginBottom: 2 }}>
              {isOwner ? m.establishment_name : (t("You"))}{t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString()}` : ''}
            </div>
            <Linkify text={t.body} />
          </div>
        );
      })}
      {thread.length === 0 && (
        <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 6, fontStyle: 'italic' }}>{t('⏳ Awaiting a reply')}</div>
      )}
      {/* Reply back to the place */}
      <div style={{ marginTop: 8 }}>
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder={`Reply to ${m.establishment_name}…`}
          rows={2}
          style={{ width: '100%', boxSizing: 'border-box', padding: 8, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
        />
        <div style={{ textAlign: 'right', marginTop: 6 }}>
          <button
            onClick={send}
            disabled={sending || !reply.trim()}
            style={{ background: COLORS.primary, color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (sending || !reply.trim()) ? 0.6 : 1 }}
          >
            {sending ? (t("Sending…")) : (t("➤ Send reply"))}
          </button>
        </div>
      </div>
    </div>
  );
}

// UserDashboard — the dashboard for a regular (non-owner) logged-in user:
// their sent Messages (with replies) and their Booking Requests. Owners/admins
// get the full <Dashboard> instead. Reuses <MyMessageThread>.
function UserDashboard({ user, initialTab, onSelectTab }) {
  const [tab, setTab] = useState(initialTab === 'requests' ? 'requests' : 'messages');
  const [msgs, setMsgs] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [loadingBk, setLoadingBk] = useState(true);
  useEffect(() => {
    let active = true;
    api.myMessages().then((d) => { if (active) setMsgs(Array.isArray(d) ? d : []); }).catch(() => { if (active) setMsgs([]); }).finally(() => { if (active) setLoadingMsgs(false); });
    api.myBookingRequests().then((d) => { if (active) setBookings((d && d.requests) || []); }).catch(() => { if (active) setBookings([]); }).finally(() => { if (active) setLoadingBk(false); });
    return () => { active = false; };
  }, []);
  const selectTab = (t) => { setTab(t); if (onSelectTab) onSelectTab(t); };
  const tabStyle = (on) => ({ padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 14, fontWeight: 600, border: `1px solid ${on ? COLORS.primary : COLORS.border}`, background: on ? COLORS.primary : 'white', color: on ? 'white' : COLORS.text, fontFamily: 'inherit' });
  return (
    <div style={styles.container}>
      <h1 style={{ color: COLORS.primary, marginBottom: 4 }}>{t('🏠 My Dashboard')}</h1>
      <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 20 }}>{t('Your messages and booking requests.')}</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button style={tabStyle(tab === 'messages')} onClick={() => selectTab('messages')}>{t("✉️ Messages")}{msgs.length ? ` (${msgs.length})` : ''}</button>
        <button style={tabStyle(tab === 'requests')} onClick={() => selectTab('requests')}>{t("📋 Requests")}{bookings.length ? ` (${bookings.length})` : ''}</button>
      </div>
      {tab === 'messages' && (
        <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20 }}>
          <h2 style={{ color: COLORS.primary, fontSize: 18, margin: '0 0 12px' }}>{t('My messages')}</h2>
          <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 16 }}>{t("Messages you've sent to places, and their replies. You'll also get an email when a place replies.")}</p>
          {loadingMsgs ? (
            <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20 }}>{t('Loading…')}</div>
          ) : msgs.length === 0 ? (
            <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20, fontSize: 14 }}>{t('No messages yet — open a place and use the ✉️ Contact button to reach them.')}</div>
          ) : (
            msgs.map((m) => (
              <MyMessageThread key={m.id} m={m}
                onReplied={(reply) => setMsgs((prev) => prev.map((x) => x.id === m.id ? { ...x, thread: [...(x.thread || []), reply] } : x))} />
            ))
          )}
        </div>
      )}
      {tab === 'requests' && (
        <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20 }}>
          <h2 style={{ color: COLORS.primary, fontSize: 18, margin: '0 0 12px' }}>{t('My booking requests')}</h2>
          {loadingBk ? (
            <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20 }}>{t('Loading…')}</div>
          ) : bookings.length === 0 ? (
            <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20, fontSize: 14 }}>{t('No booking requests yet.')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {bookings.map((b) => (
                <div key={b.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 600, color: COLORS.text }}>{b.establishment_name || b.place_name || b.establishment || (t("Booking request"))}</div>
                  <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 3 }}>
                    {b.status ? `Status: ${b.status}` : ''}{(b.check_in && b.check_out) ? `${b.status ? ' · ' : ''}${b.check_in} → ${b.check_out}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ProfilePage — logged-in users edit their name/email and change password.
// Two separate forms so a save in one doesn't require touching the other.
function ProfilePage({ user, onUserUpdate, onLogin }) {
  const [firstName, setFirstName] = useState(user?.firstName || user?.first_name || '');
  const [lastName, setLastName] = useState(user?.lastName || user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [newsletterOptIn, setNewsletterOptIn] = useState(user?.newsletter_opt_in !== false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null); // { type: 'success'|'error', text }

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  // User's edit/photo submissions (pending or recently reviewed).
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  React.useEffect(() => {
    if (!user) return;
    setLoadingSubmissions(true);
    api.mySubmissions()
      .then((d) => setSubmissions(d.submissions || []))
      .catch(() => setSubmissions([]))
      .finally(() => setLoadingSubmissions(false));
  }, [user]);

  // User's booking requests (rooms / tours they requested)
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const reloadBookings = React.useCallback(() => {
    if (!user) return;
    setLoadingBookings(true);
    api.myBookingRequests()
      .then((d) => setBookings(d.requests || []))
      .catch(() => setBookings([]))
      .finally(() => setLoadingBookings(false));
  }, [user]);
  React.useEffect(() => { reloadBookings(); }, [reloadBookings]);

  const cancelBooking = async (id) => {
    if (!window.confirm(t('Cancel this booking request?'))) return;
    try { await api.cancelBookingRequest(id); reloadBookings(); }
    catch (e) { alert(`Couldn't cancel: ${e.message}`); }
  };

  // Messages the user sent to places, with the owner's reply (if any).
  const [myMsgs, setMyMsgs] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  React.useEffect(() => {
    if (!user) return;
    setLoadingMsgs(true);
    api.myMessages()
      .then((d) => setMyMsgs(Array.isArray(d) ? d : []))
      .catch(() => setMyMsgs([]))
      .finally(() => setLoadingMsgs(false));
  }, [user]);

  // Deep-link from a notification: /profile?msg=<id> scrolls to and briefly
  // highlights that conversation once the messages have loaded.
  const didFocusMsgRef = React.useRef(false);
  React.useEffect(() => {
    if (didFocusMsgRef.current || loadingMsgs) return;
    let msgId = null, view = null;
    try {
      const sp = new URLSearchParams(window.location.search);
      msgId = sp.get('msg');
      view = sp.get('view');
    } catch (_) { /* ignore */ }
    // ?view=messages (header "My messages" link) → scroll to the section.
    if (!msgId && view === 'messages') {
      didFocusMsgRef.current = true;
      setTimeout(() => {
        document.getElementById('my-messages')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return;
    }
    // ?msg=<id> (notification deep-link) → scroll to + highlight that thread.
    if (!msgId || !myMsgs.some((m) => m.id === msgId)) return;
    didFocusMsgRef.current = true;
    setTimeout(() => {
      const el = document.getElementById(`msg-${msgId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'background 0.4s';
      el.style.background = '#fef9c3';
      setTimeout(() => { el.style.background = ''; }, 2200);
    }, 200);
  }, [myMsgs, loadingMsgs]);

  // The user's reviews, with the owner's reply (if any).
  const [myReviews, setMyReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  React.useEffect(() => {
    if (!user) return;
    setLoadingReviews(true);
    api.myReviews()
      .then((d) => setMyReviews(Array.isArray(d) ? d : []))
      .catch(() => setMyReviews([]))
      .finally(() => setLoadingReviews(false));
  }, [user]);

  if (!user) {
    return (
      <div style={{ ...styles.container, textAlign: 'center', padding: 48 }}>
        <h2>{t('Sign in to manage your profile')}</h2>
        <button style={styles.button} onClick={onLogin}>{t('Log in / Register')}</button>
      </div>
    );
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setSavingProfile(true);
    try {
      const updated = await api.updateProfile({ firstName, lastName, email, newsletterOptIn });
      // Normalize the camelCase form the rest of the app expects
      onUserUpdate({
        ...user,
        email: updated.email,
        firstName: updated.first_name,
        lastName: updated.last_name,
        first_name: updated.first_name,
        last_name: updated.last_name,
        newsletter_opt_in: updated.newsletter_opt_in,
      });
      setProfileMsg({ type: 'success', text: 'Profile saved.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.message || 'Could not save profile.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New password and confirmation do not match.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    setSavingPassword(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMsg({ type: 'success', text: 'Password updated.' });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.message || 'Could not change password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const msgStyle = (m) => m && ({
    background: m.type === 'success' ? '#d1fae5' : '#fee2e2',
    border: `1px solid ${m.type === 'success' ? '#86efac' : '#fca5a5'}`,
    color: m.type === 'success' ? '#065f46' : '#991b1b',
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: 13,
    marginTop: 8,
  });

  return (
    <div style={{ ...styles.container, maxWidth: 640 }}>
      <h1 style={{ color: COLORS.primary, marginBottom: 4 }}>{t('My Profile')}</h1>
      <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 24 }}>
        {t("Signed in as")} <strong>{user.email}</strong> {t('· Role:')} <strong>{user.role}</strong>
      </p>

      {/* === Profile info === */}
      <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20, marginBottom: 24 }}>
        <h2 style={{ color: COLORS.primary, fontSize: 18, margin: '0 0 12px 0' }}>{t('Account information')}</h2>
        <form onSubmit={saveProfile}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('First name')}</label>
          <input style={styles.input} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Last name')}</label>
          <input style={styles.input} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t("Email")}</label>
          <input style={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 14, color: COLORS.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={newsletterOptIn} onChange={(e) => setNewsletterOptIn(e.target.checked)} />
            {t("Receive occasional email updates (new places & features)")}
          </label>
          {profileMsg && <div style={msgStyle(profileMsg)}>{profileMsg.text}</div>}
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button type="submit" style={styles.button} disabled={savingProfile}>
              {savingProfile ? (t("Saving…")) : (t("Save changes"))}
            </button>
          </div>
        </form>
      </div>

      {/* === Password === */}
      <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20 }}>
        <h2 style={{ color: COLORS.primary, fontSize: 18, margin: '0 0 12px 0' }}>{t('Change password')}</h2>
        <form onSubmit={savePassword}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Current password')}</label>
          <input style={styles.input} type="password" autoComplete="current-password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('New password')}</label>
          <input style={styles.input} type="password" autoComplete="new-password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('Confirm new password')}</label>
          <input style={styles.input} type="password" autoComplete="new-password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          {passwordMsg && <div style={msgStyle(passwordMsg)}>{passwordMsg.text}</div>}
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button type="submit" style={styles.button} disabled={savingPassword}>
              {savingPassword ? (t("Updating…")) : (t("Update password"))}
            </button>
          </div>
        </form>
      </div>

      {/* === My submissions === */}
      <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20, marginTop: 24 }}>
        <h2 style={{ color: COLORS.primary, fontSize: 18, margin: '0 0 12px 0' }}>{t('My submissions')}</h2>
        <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          {t("Edits and photos you've suggested. Pending items are waiting for an admin or the place's owner to review.")}
        </p>
        {loadingSubmissions ? (
          <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20 }}>{t('Loading…')}</div>
        ) : submissions.length === 0 ? (
          <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20, fontSize: 14 }}>
            {t("You haven't submitted any edits yet. Tap \"Suggest an edit\" on any place's page to start.")}
          </div>
        ) : (
          <div>
            {submissions.map((s) => {
              const statusStyles = {
                pending:  { bg: '#fef3c7', color: '#92400e', label: '⏳ Pending review' },
                approved: { bg: '#d1fae5', color: '#065f46', label: '✓ Approved' },
                rejected: { bg: '#fee2e2', color: '#991b1b', label: '✕ Rejected' },
              };
              const sx = statusStyles[s.status] || statusStyles.pending;
              return (
                <div key={s.id} style={{ padding: '10px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <a href={withLangPath(`/place/${s.establishment_id}`)} style={{ color: COLORS.primary, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
                      {/* My-bookings / My-submissions rows: we only have the id here,
                          not the full place row, so we link by /place/{uuid} which the
                          backend will redirect to the canonical slug on click. */}
                      {s.establishment_name}
                    </a>
                    <span style={{ display: 'inline-flex', alignItems: 'center', background: sx.bg, color: sx.color, padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                      {sx.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
                    {t("Submitted")} {new Date(s.created_at).toLocaleDateString()}
                    {s.reviewed_at && ` · reviewed ${new Date(s.reviewed_at).toLocaleDateString()}`}
                  </div>
                  {s.status === 'rejected' && s.rejection_reason && (
                    <div style={{ fontSize: 13, color: COLORS.text, marginTop: 6, padding: '6px 10px', background: '#fef2f2', borderRadius: 6 }}>
                      {t("Reason:")} {s.rejection_reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* === My bookings === */}
      <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20, marginTop: 24 }}>
        <h2 style={{ color: COLORS.primary, fontSize: 18, margin: '0 0 12px 0' }}>{t('My bookings')}</h2>
        <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          {t("Rooms and tours you've requested. The host will reply by email and the status here updates.")}
        </p>
        {loadingBookings ? (
          <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20 }}>{t('Loading…')}</div>
        ) : bookings.length === 0 ? (
          <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20, fontSize: 14 }}>
            {t("You haven't requested any bookings yet. Browse")} <a href="/list?type=kosher_hotel" style={{ color: COLORS.primary }}>hotels</a> or <a href="/list?type=jewish_tour" style={{ color: COLORS.primary }}>tours</a> {t("to get started.")}
          </div>
        ) : (
          <div>
            {bookings.map((b) => {
              const statusStyles = {
                pending:   { bg: '#fef3c7', color: '#92400e', label: '⏳ Pending owner reply' },
                confirmed: { bg: '#d1fae5', color: '#065f46', label: '✓ Accepted' },
                cancelled: { bg: '#fee2e2', color: '#991b1b', label: '✕ Declined / cancelled' },
                expired:   { bg: '#f3f4f6', color: '#6b7280', label: '⌛ Expired — no response' },
              };
              const sx = statusStyles[b.status] || statusStyles.pending;
              const isTour = !!b.tour_instance_id;
              const isDriver = !!b.driver_trip_id || (!b.room_id && !b.tour_instance_id);
              const nights = b.check_in && b.check_out
                ? Math.ceil((new Date(b.check_out) - new Date(b.check_in)) / (1000 * 60 * 60 * 24))
                : 0;
              const rideWhen = b.pickup_at ? new Date(b.pickup_at).toLocaleString() : '';
              return (
                <div key={b.id} style={{ padding: '10px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <a href={withLangPath(`/place/${b.establishment_id}`)} style={{ color: COLORS.primary, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
                      {b.establishment_name}
                    </a>
                    <span style={{ display: 'inline-flex', alignItems: 'center', background: sx.bg, color: sx.color, padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                      {sx.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
                    {isDriver
                      ? `🚗 ${b.trip_title || (t("Ride"))}${rideWhen ? ` · ${rideWhen}` : ''}${b.flight_number ? ` · ✈ ${b.flight_number}` : ''}${b.pickup ? ` · ${b.pickup}${b.dropoff ? ` → ${b.dropoff}` : ''}` : (b.dropoff ? ` · → ${b.dropoff}` : '')} · ${b.passengers || 1} passenger${(b.passengers || 1) === 1 ? '' : 's'}`
                      : isTour
                      ? `Tour · ${b.tour_date ? new Date(b.tour_date).toLocaleDateString() : ''} · ${b.adults} adult${b.adults === 1 ? '' : 's'}${b.children > 0 ? `, ${b.children} child${b.children === 1 ? '' : 'ren'}` : ''}`
                      : `${b.room_type || (t("Room"))} · ${b.check_in ? new Date(b.check_in).toLocaleDateString() : ''} → ${b.check_out ? new Date(b.check_out).toLocaleDateString() : ''} (${nights} night${nights === 1 ? '' : 's'}) · ${b.adults} adult${b.adults === 1 ? '' : 's'}${b.children > 0 ? `, ${b.children} child${b.children === 1 ? '' : 'ren'}` : ''}`}
                    {b.total_price > 0 && ` · ${b.trip_currency && isDriver ? b.trip_currency + ' ' : ''}${parseFloat(b.total_price).toFixed(2)}`}
                  </div>
                  {b.decision_note && b.status === 'cancelled' && (
                    <div style={{ fontSize: 13, color: COLORS.text, marginTop: 6, padding: '6px 10px', background: '#fef2f2', borderRadius: 6 }}>
                      {t("Reply from")} {isDriver ? 'driver' : 'host'}: {b.decision_note}
                    </div>
                  )}
                  {b.status === 'pending' && (
                    <div style={{ marginTop: 6, textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => cancelBooking(b.id)}
                        style={{ background: 'transparent', border: 'none', color: COLORS.danger, cursor: 'pointer', fontSize: 12, padding: 0 }}
                      >
                        {t("Cancel this request")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* === My reviews === */}
      <div style={{ background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 20, marginTop: 24 }}>
        <h2 style={{ color: COLORS.primary, fontSize: 18, margin: '0 0 12px 0' }}>{t('My reviews')}</h2>
        <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          {t("Reviews you've left, and any reply from the owner.")}
        </p>
        {loadingReviews ? (
          <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20 }}>{t('Loading…')}</div>
        ) : myReviews.length === 0 ? (
          <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20, fontSize: 14 }}>
            {t("You haven't written any reviews yet.")}
          </div>
        ) : (
          <div>
            {myReviews.map((r) => (
              <div key={r.id} style={{ padding: '12px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <a href={withLangPath(`/place/${r.establishment_id}`)} style={{ color: COLORS.primary, fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
                    {r.establishment_name}
                  </a>
                  <span style={{ color: '#f59e0b', fontSize: 13 }}>
                    {'★'.repeat(r.rating)}<span style={{ color: '#e5e7eb' }}>{'★'.repeat(Math.max(0, 5 - r.rating))}</span>
                  </span>
                </div>
                {r.title && <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginTop: 4 }}>{r.title}</div>}
                {r.body && <div style={{ fontSize: 13, color: COLORS.text, marginTop: 4, whiteSpace: 'pre-wrap' }}><Linkify text={r.body} /></div>}
                {r.hidden_at && <div style={{ fontSize: 12, color: COLORS.danger, marginTop: 4 }}>{t('⚠ Hidden by a moderator')}</div>}
                {r.owner_reply && (
                  <div style={{ fontSize: 13, color: COLORS.text, marginTop: 8, padding: '8px 12px', background: '#eff6ff', borderLeft: `3px solid ${COLORS.primary}`, borderRadius: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, marginBottom: 2 }}>
                      {t("Reply from")} {r.establishment_name}{r.owner_reply_at ? ` · ${new Date(r.owner_reply_at).toLocaleDateString()}` : ''}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}><Linkify text={r.owner_reply} /></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === My messages === */}
      {/* Messages moved to the dashboard (/dashboard/messages). */}

      {/* === Danger Zone — self-serve account delete (GDPR) === */}
      <div style={{ background: 'white', border: `1px solid #fecaca`, borderRadius: 8, padding: 20, marginTop: 24 }}>
        <h2 style={{ color: COLORS.danger, fontSize: 18, margin: '0 0 8px 0' }}>{t('Danger zone')}</h2>
        <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          {t("Permanently delete your account. Your reviews stay public but are anonymised (your name and email are removed). Pending submissions are dropped. This cannot be undone.")}
        </p>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm(t('Delete your Jewish Compass account?\n\nReviews you wrote will be anonymised (name and email removed).\nFavorites, pending submissions, and pending bookings will be dropped.\n\nThis cannot be undone.'))) return;
            if (!window.confirm(t('Are you absolutely sure?'))) return;
            try {
              await api.deleteMyAccount();
              localStorage.removeItem('jwt_token');
              window.location.href = '/';
            } catch (e) {
              alert(`Could not delete: ${e.message}`);
            }
          }}
          style={{
            background: 'white', color: COLORS.danger,
            border: `1px solid ${COLORS.danger}`, padding: '8px 16px',
            borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {t("🗑️ Delete my account")}
        </button>
      </div>
    </div>
  );
}

// FavoritesPage — the user's saved places. Renders a simplified list view
// (no filters/sort/map) since favorites are a small curated set and most users
// would scan rather than filter. Clicking a card opens the detail page.
function FavoritesPage({ user, onSelect, onLogin, favorites, onToggleFavorite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.favorites()
      .then((rows) => { setItems(rows); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user]);

  // When a place is unfavorited from this page, remove it from the visible list
  // immediately. The parent's optimistic toggle already updates the Set.
  useEffect(() => {
    if (!favorites) return;
    setItems((prev) => prev.filter((e) => favorites.has(e.id)));
  }, [favorites]);

  if (!user) {
    return (
      <div style={{ ...styles.container, textAlign: 'center', padding: 48 }}>
        <h2>{t('Save your favorite places')}</h2>
        <p style={{ color: COLORS.muted, marginBottom: 16 }}>
          {t("Log in to bookmark synagogues, restaurants, hotels, and more for easy access later.")}
        </p>
        <button style={styles.button} onClick={onLogin}>{t('Log in / Register')}</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.sectionTitle}>
        <span style={{ color: '#ef4444', marginRight: 8 }}>♥</span>
        {t("My Favorites")}
      </h2>
      {loading ? (
        <SkeletonList count={3} />
      ) : items.length === 0 ? (
        <EmptyState
          icon="♡"
          title={t("No saved places yet")}
          message="Tap the heart icon on any synagogue, restaurant, hotel, or attraction to keep it here for quick access later."
          actionLabel="Browse places"
          onAction={() => navigateTo('/list')}
        />
      ) : (
        items.map((est) => (
          <div key={est.id} style={{ ...styles.listCard, position: 'relative' }} onClick={() => onSelect(est)}>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleFavorite(est.id); }}
              title={t("Remove from favorites")}
              style={{
                position: 'absolute', top: 10, right: 10,
                background: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb',
                borderRadius: '50%', width: 32, height: 32, display: 'flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                color: '#ef4444', padding: 0, zIndex: 1,
              }}
            >
              <HeartIcon size={16} filled />
            </button>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ width: 80, height: 80, flexShrink: 0, background: COLORS.bg, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', fontSize: 36 }}>
                {est.main_image_url ? (
                  <img src={imageUrl(est.main_image_url)} alt={est.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <span><CategoryIcon type={est.type} /></span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.listCardTitle}>{nomeVisibile(est)}</div>
                <div style={styles.listCardMeta}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <CategoryIcon type={est.type} /> {formatCategoryLabel(est.type)}
                    {(est.city || est.country) && <> · 📍 {[est.city, est.country].filter(Boolean).map((x) => t(x)).join(', ')}</>}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// Compact 5-star renderer that supports half-star averages via gradient.
function StarRow({ value = 0, size = 14, color = '#f59e0b' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => {
        const fillPct = Math.max(0, Math.min(1, value - (i - 1))) * 100;
        return (
          <span key={i} style={{ position: 'relative', display: 'inline-block', width: size, height: size, fontSize: size, lineHeight: 1 }}>
            <span style={{ color: '#e5e7eb' }}>★</span>
            <span style={{ position: 'absolute', top: 0, left: 0, color, overflow: 'hidden', width: `${fillPct}%`, whiteSpace: 'nowrap' }}>★</span>
          </span>
        );
      })}
    </span>
  );
}

// Modal — user writes (or edits) their review for a place.
function ReviewModal({ establishment, existing, onClose, onSaved }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState(existing?.title || '');
  const [body, setBody] = useState(existing?.body || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (rating < 1) { setError('Please choose a star rating.'); return; }
    setSaving(true);
    try {
      const saved = await api.saveReview(establishment.id, { rating, title, body });
      onSaved(saved);
    } catch (err) {
      setError(err.message || 'Could not save review.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!existing || !window.confirm(t('Delete your review?'))) return;
    try {
      await api.deleteReview(existing.id);
      onSaved(null);
    } catch (err) {
      setError(err.message || 'Could not delete.');
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={{ ...styles.modalContent, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <h2 style={styles.modalTitle}>{existing ? t('Update your review') : t('Write a review')}</h2>
          <p style={{ color: COLORS.muted, fontSize: 13, marginTop: -6, marginBottom: 14 }}>
            {t("For")} {establishment.name}
          </p>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            {t("Your rating")}
          </label>
          <div style={{ marginBottom: 14, fontSize: 30, color: '#f59e0b', lineHeight: 1, userSelect: 'none' }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <span
                key={i}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(i)}
                style={{ cursor: 'pointer', marginRight: 2 }}
              >
                {(hover || rating) >= i ? '★' : '☆'}
              </span>
            ))}
            {rating > 0 && <span style={{ fontSize: 14, color: COLORS.muted, marginLeft: 8 }}>{rating} / 5</span>}
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            {t("Title (optional)")}
          </label>
          <input style={styles.input} maxLength={150} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("e.g. Lovely shul, warm community")} />

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            {t("Review (optional)")}
          </label>
          <textarea
            style={{ ...styles.input, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
            maxLength={4000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("Share your experience to help others.")}
          />

          {error && <div style={{ color: COLORS.danger, fontSize: 13, marginBottom: 8 }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            {existing ? (
              <button type="button" onClick={remove} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}>
                {t("Delete review")}
              </button>
            ) : <span />}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={styles.buttonOutline} onClick={onClose} disabled={saving}>{t('Cancel')}</button>
              <button type="submit" style={styles.button} disabled={saving}>{saving ? (t("Saving…")) : (existing ? (t("Update")) : (t("Post review")))}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ReviewsSection — embedded inside DetailPage. Loads reviews, shows summary,
// individual reviews, "write a review" button. Admins/owners get hide controls.
// Small "Report" link for places or reviews. Opens a prompt for the reason
// and posts to the reports queue. Hidden for non-logged-in users (no point —
// the API requires auth and we'd just send them to login mid-action).
function ReportButton({ entityType, entityId, user, label = null, style }) {
  const [sent, setSent] = useState(false);
  const handle = async () => {
    if (!user) { alert(t('Please log in to report.')); return; }
    const reason = window.prompt(
      entityType === 'establishment'
        ? 'Report this place. Briefly tell us what\'s wrong (closed, incorrect info, spam, …):'
        : 'Report this review. Briefly tell us why (abusive, off-topic, spam, …):'
    );
    if (reason === null) return;
    try {
      await api.createReport(entityType, entityId, reason || null);
      setSent(true);
    } catch (e) {
      alert(`Couldn't report: ${e.message}`);
    }
  };
  if (sent) return <span style={{ fontSize: 12, color: '#6b7280', ...style }}>{t('✓ Reported')}</span>;
  return (
    <button
      type="button"
      onClick={handle}
      style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 12, padding: 0, ...style }}
    >
      {label || t('⚑ Report')}
    </button>
  );
}

// Inline "Reply" toggle for the owner/admin under each review. Shows a small
// link until clicked, then a textarea + Save / Cancel / Delete buttons. Pre-
// fills with the existing reply when editing.
function OwnerReplyToggle({ review, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(review.owner_reply || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.replyToReview(review.id, text.trim());
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      alert(`Reply failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(t('Delete your reply to this review?'))) return;
    setBusy(true);
    try { await api.replyToReview(review.id, ''); setText(''); setOpen(false); onSaved && onSaved(); }
    catch (e) { alert(`Delete failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ background: 'none', border: 'none', color: '#1a4d8f', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}
      >
        {review.owner_reply ? (t("✏️ Edit reply")) : (t("💬 Reply"))}
      </button>
    );
  }

  return (
    <div style={{ flex: '1 1 100%', marginTop: 6 }}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("Thank the reviewer, clarify a misunderstanding, or add follow-up info…")}
        maxLength={2000}
        style={{ width: '100%', minHeight: 70, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
        {review.owner_reply && (
          <button type="button" onClick={remove} disabled={busy} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            {t("Delete reply")}
          </button>
        )}
        <button type="button" onClick={() => setOpen(false)} disabled={busy} style={{ background: 'white', color: '#1f2937', border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
          {t("Cancel")}
        </button>
        <button type="button" onClick={save} disabled={busy || !text.trim()} style={{ background: '#1a4d8f', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (busy || !text.trim()) ? 0.6 : 1 }}>
          {busy ? (t("Saving…")) : (t("Post reply"))}
        </button>
      </div>
    </div>
  );
}

function ReviewsSection({ establishment, user, onLogin }) {
  const [data, setData] = useState({ summary: { count: 0, average: 0 }, reviews: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [myReview, setMyReview] = useState(null);

  const isAdmin = user?.role === 'admin';
  const isPlaceOwner = user?.id && establishment.owner_id === user.id;

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.reviews(establishment.id);
      setData(d);
      if (user) {
        try { setMyReview(await api.myReview(establishment.id)); } catch { /* ignore */ }
      } else {
        setMyReview(null);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [establishment.id, user]);

  React.useEffect(() => { load(); }, [load]);

  // The review-request email links straight here with ?review=1. Open the form
  // on arrival rather than making the reader hunt for it — that email is the
  // whole reason they're on the page. Runs once: the param is stripped after
  // handling so a refresh or a back-navigation doesn't reopen the modal.
  const reviewLinkHandled = React.useRef(false);
  React.useEffect(() => {
    if (reviewLinkHandled.current || loading) return;
    let wants = false;
    try { wants = new URLSearchParams(window.location.search).get('review') === '1'; } catch (e) { /* no URL */ }
    if (!wants) return;
    reviewLinkHandled.current = true;
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('review');
      window.history.replaceState({}, '', u.pathname + u.search + u.hash);
    } catch (e) { /* leave the URL alone */ }
    if (isPlaceOwner) return;                 // owners can't review their own place
    if (user) setShowModal(true);
    else if (onLogin) onLogin();              // logged out — ask them to sign in first
  }, [loading, user, isPlaceOwner, onLogin]);

  const onSaved = (saved) => {
    setShowModal(false);
    setMyReview(saved);
    load();
  };

  const hide = async (id) => {
    const reason = window.prompt('Hide this review? Optional reason:');
    if (reason === null) return;
    try { await api.hideReview(id, reason || null); load(); }
    catch (e) { alert(`Hide failed: ${e.message}`); }
  };

  const unhide = async (id) => {
    try { await api.unhideReview(id); load(); }
    catch (e) { alert(`Unhide failed: ${e.message}`); }
  };

  // An owner contests a review. It stays public — a reader today shouldn't see a
  // sanitised page just because a complaint is pending.
  const dispute = async (id) => {
    const reason = window.prompt(t("What's wrong with this review? An admin will read this and decide."));
    if (reason === null) return;
    if (!reason.trim()) { alert(t('Please say what is wrong with the review.')); return; }
    try {
      await api.disputeReview(id, reason.trim());
      alert(t('Sent to the moderators. The review stays visible until they decide.'));
      load();
    } catch (e) { alert(`${t('Could not send the dispute')}: ${e.message}`); }
  };

  const removeOwn = async (id) => {
    if (!window.confirm(t('Delete your review?'))) return;
    try { await api.deleteReview(id); setMyReview(null); load(); }
    catch (e) { alert(`Delete failed: ${e.message}`); }
  };

  const canReview = user && !isPlaceOwner; // owners can't review their own place
  // Reviews are pre-moderated. Without this notice the author writes a review,
  // sees nothing appear, and reasonably concludes it was lost.
  const myReviewPending = myReview && !myReview.approved_at;

  return (
    <div style={{ marginTop: 28 }}>
      {myReviewPending && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e',
          borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 14 }}>
          ⏳ {t('Thanks — your review has been sent for approval and will appear once a moderator has read it.')}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <h2 style={{ color: COLORS.primary, margin: 0 }}>{t('Reviews')}</h2>
        {canReview && (
          <button style={styles.button} onClick={() => setShowModal(true)}>
            {myReview ? `✏️ ${t('Update your review')}` : `✍️ ${t('Write a review')}`}
          </button>
        )}
        {!user && (
          <button style={styles.buttonOutline} onClick={onLogin}>{t('Log in to write a review')}</button>
        )}
      </div>

      {/* Summary card */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: COLORS.bg, borderRadius: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 36, fontWeight: 700, color: COLORS.primary, lineHeight: 1 }}>
            {data.summary.average ? data.summary.average.toFixed(1) : '—'}
          </div>
          <div style={{ marginTop: 4 }}>
            <StarRow value={data.summary.average} size={16} />
          </div>
        </div>
        <div style={{ color: COLORS.muted, fontSize: 14 }}>
          {data.summary.count} review{data.summary.count === 1 ? '' : 's'}
        </div>
      </div>

      {loading ? (
        <div style={styles.spinner}>{t('Loading reviews…')}</div>
      ) : data.reviews.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 14, padding: 12, textAlign: 'center' }}>
          {t("No reviews yet.")} {canReview && (t("Be the first to share your experience."))}
        </div>
      ) : (
        data.reviews.map((r) => {
          const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || (r.email || (t("Anonymous")));
          const isAuthor = user?.id === r.user_id;
          const canManage = isAdmin || isPlaceOwner;
          return (
            <div key={r.id} style={{ padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 10, background: r.hidden_at ? '#fafafa' : 'white', opacity: r.hidden_at ? 0.7 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <div>
                  <StarRow value={r.rating} size={14} />
                  {r.title && <strong style={{ marginLeft: 8, color: COLORS.text }}>{r.title}</strong>}
                </div>
                <div style={{ fontSize: 12, color: COLORS.muted }}>
                  {name} · {new Date(r.created_at).toLocaleDateString()}
                  {r.hidden_at && <span style={{ marginLeft: 6, background: '#fee2e2', color: '#991b1b', padding: '1px 6px', borderRadius: 3, fontWeight: 700 }}>{t("HIDDEN")}</span>}
                </div>
              </div>
              {r.body && <div style={{ fontSize: 14, color: COLORS.text, whiteSpace: 'pre-wrap' }}><Linkify text={r.body} /></div>}

              {/* Owner reply — rendered indented underneath the original */}
              {r.owner_reply && (
                <div style={{
                  marginTop: 10, padding: '8px 12px',
                  background: COLORS.bg, borderLeft: `3px solid ${COLORS.primary}`,
                  borderRadius: 4,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>
                    {t("Reply from the owner")} {r.owner_reply_at && `· ${new Date(r.owner_reply_at).toLocaleDateString()}`}
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.text, whiteSpace: 'pre-wrap' }}><Linkify text={r.owner_reply} /></div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {isAuthor && (
                  <button onClick={() => removeOwn(r.id)} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                    {t("Delete")}
                  </button>
                )}
                {/* Hiding is an ADMIN action. An owner hiding reviews of their own
                    business would undo the point of collecting them, so owners
                    dispute instead and the review stays up until a ruling. */}
                {isAdmin && !r.hidden_at && (
                  <button onClick={() => hide(r.id)} style={{ background: 'none', border: 'none', color: COLORS.muted, cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                    {t('Hide')}
                  </button>
                )}
                {isAdmin && r.hidden_at && (
                  <button onClick={() => unhide(r.id)} style={{ background: 'none', border: 'none', color: COLORS.success, cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                    {t('Unhide')}
                  </button>
                )}
                {isPlaceOwner && !isAdmin && !r.hidden_at && (
                  r.disputed_at && !r.dispute_resolved_at
                    ? <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>{t('Disputed — with the moderators')}</span>
                    : <button onClick={() => dispute(r.id)} style={{ background: 'none', border: 'none', color: COLORS.muted, cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                        {t('Dispute this review')}
                      </button>
                )}
                {canManage && (
                  <OwnerReplyToggle review={r} onSaved={load} />
                )}
                {/* Anyone other than the author can flag */}
                {!isAuthor && user && (
                  <ReportButton entityType="review" entityId={r.id} user={user} />
                )}
              </div>
            </div>
          );
        })
      )}

      {showModal && (
        <ReviewModal
          establishment={establishment}
          existing={myReview}
          onClose={() => setShowModal(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function DetailPage({ establishmentId, slugParams, establishment: initialEst, user, favorites, onToggleFavorite, onBack, onLogin, startInEdit = false }) {
  const [establishment, setEstablishment] = useState(initialEst || null);
  const [caricoContatti, setCaricoContatti] = useState(false);
  const [loading, setLoading] = useState(!initialEst);

  // Publish this place's city/country so the header Minyanim button can scope
  // to it (the place URL only carries a slug + country code). Cleared on unmount.
  useEffect(() => {
    currentPlaceGeo = establishment ? { country: establishment.country, city: establishment.city } : null;
    return () => { currentPlaceGeo = null; };
  }, [establishment ? establishment.country : null, establishment ? establishment.city : null]);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  // LA FOTOGRAFIA DA CUI IL MODULO E' PARTITO.
  //
  // Menachem, 26/10/2026: «dopo il merge ha mostrato entrambe le
  // certificazioni ... ma quando ho cliccato salva petach tikva e'
  // sparito». Nel registro: supervision «Badatz Beit Yosef, Petach Tikva
  // Rabbinate» -> «Badatz Beit Yosef», e `rate` da null a 0 nello stesso
  // salvataggio.
  //
  // Lo strumento di unione sta DENTRO la vista di modifica. Dopo
  // l'unione la scheda si ricarica — e infatti si vedevano le due
  // certificazioni — ma il modulo no: teneva ancora i valori di prima, e
  // il salvataggio li rimandava tutti, cancellando quello che l'unione
  // aveva appena messo insieme.
  //
  // Il modulo mandava SEMPRE ogni campo, anche i quaranta che nessuno ha
  // toccato. Basta che uno cambi sotto — un'unione, un altro admin, uno
  // script — perche' il salvataggio lo riporti indietro. Tenendo la
  // fotografia di partenza si manda solo cio' che l'utente ha davvero
  // cambiato, e tutto il resto resta com'e' nel frattempo diventato.
  const editSeed = useRef({});
  // The app-root certifier list is fetched once at startup; refetch it fresh when
  // the editor opens so a certifier added since then appears in the supervision
  // picker (was: a stale session list hid newly-added hechsherim).
  const [freshCerts, setFreshCerts] = useState(null);
  useEffect(() => { if (editMode) api.kosherCertifications(true, true).then(setFreshCerts).catch(() => {}); }, [editMode]);
  // Existing neighbourhoods/areas for this place's city — drives the Area
  // dropdown so owners pick a known area (and spellings stay consistent).
  // Le città che il sito ha già nello stesso paese: il campo Città le propone
  // (si sceglie o si scrive, ComboBox.js).
  const [cityRowsEdit, setCityRowsEdit] = useState([]);
  useEffect(() => {
    if (!editMode) return;
    api.cities().then((r) => setCityRowsEdit(Array.isArray(r) ? r : [])).catch(() => setCityRowsEdit([]));
  }, [editMode]);
  const cityOptionsEdit = React.useMemo(() => {
    const country = editForm.country || (establishment && establishment.country);
    return [...new Set(cityRowsEdit.filter((r) => !country || r.country === country).map((r) => r.city).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [cityRowsEdit, editForm.country, establishment]);
  const [areaOptions, setAreaOptions] = useState([]);
  useEffect(() => {
    if (!editMode || !establishment) { return; }
    const city = editForm.city || establishment.city;
    if (!city) { setAreaOptions([]); return; }
    api.areas({ city, country: editForm.country || establishment.country })
      .then((r) => setAreaOptions([...new Set((r || []).map((x) => x.area).filter(Boolean))].sort()))
      .catch(() => setAreaOptions([]));
  }, [editMode, editForm.city, editForm.country, establishment?.city, establishment?.country]);
  // Publish the sticky Save bar's height as a CSS var so the "new version"
  // refresh banner (fixed at bottom:0) can sit ABOVE it instead of covering the
  // Save button. Cleared when not editing.
  const saveBarRef = React.useRef(null);
  useEffect(() => {
    const el = saveBarRef.current;
    const clear = () => document.documentElement.style.removeProperty('--jc-savebar-h');
    if (!editMode || !el) { clear(); return; }
    const set = () => document.documentElement.style.setProperty('--jc-savebar-h', `${el.offsetHeight}px`);
    set();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(set); ro.observe(el); }
    window.addEventListener('resize', set);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', set); clear(); };
  }, [editMode]);
  // Admin "update from a link" (website / Google Maps / Facebook / Instagram)
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [importedImageUrl, setImportedImageUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showReserve, setShowReserve] = useState(false); // "Book a table" (restaurants)
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showClaim, setShowClaim] = useState(false);
  const [myClaim, setMyClaim] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  // Gallery multi-upload state (must be declared at top — React rules of hooks)
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryProgress, setGalleryProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [galleryDragActive, setGalleryDragActive] = useState(false);
  // Nearby places — shown as secondary pins on the location map when this
  // place has coordinates. Capped at ~50 entries within 1 km.
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  // Kosher hotels within walking distance of this place, nearest first.
  const [walkHotels, setWalkHotels] = useState([]);
  const [coveringEruvim, setCoveringEruvim] = useState([]); // eruvin whose perimeter contains this place
  // "Similar" places — same category, prioritized by same city, ordered by
  // distance. Shown as a carousel below the reviews. Used by visitors who
  // want to "browse more like this".
  const [similarPlaces, setSimilarPlaces] = useState([]);

  // Kosher certifications already on the site (places of type
  // kosher_certification_hechsher). The edit form's supervision picker uses
  // this list as a strict dropdown — adding a new certifier means creating a
  // new place of this type first, which keeps the data consistent.
  const [koshCerts, setKoshCerts] = useState([]);
  useEffect(() => {
    api.kosherCertifications().then(setKoshCerts).catch(() => setKoshCerts([]));
  }, []);

  // Places this certification supervises (only fetched when the current
  // establishment IS a kosher_certification_hechsher). Drives the
  // "Places we certify" section below the basic-info panels.
  const [certifiedPlaces, setCertifiedPlaces] = useState([]);
  useEffect(() => {
    if (!establishment?.id || !isCertifierEst(establishment)) {
      setCertifiedPlaces([]);
      return;
    }
    api.certifiedPlaces(establishment.id)
      .then((r) => setCertifiedPlaces(r?.results || []))
      .catch(() => setCertifiedPlaces([]));
  }, [establishment?.id, establishment?.type]);

  // LE ALTRE FILIALI DELLA CATENA, come la lista dei posti certificati
  // sotto un certificatore (chiesto da Menachem il 16/10). L'appartenenza
  // e il file del marchio: tutte le filiali di una catena portano lo stesso
  // /brand/<catena>.png. Si chiede solo se la scheda ne porta uno.
  const [chainInfo, setChainInfo] = useState(null);
  useEffect(() => {
    const logo = establishment && establishment.main_image_url;
    if (!establishment?.id || !logo || !logo.startsWith('/brand/') || logo.startsWith('/brand/certs/')) {
      setChainInfo(null);
      return undefined;
    }
    let active = true;
    api.chainBranches(establishment.id)
      .then((r) => { if (active) setChainInfo(r && r.total > 0 ? r : null); })
      .catch(() => { if (active) setChainInfo(null); });
    return () => { active = false; };
  }, [establishment?.id, establishment?.main_image_url]);

  // Arrived by clicking this certifier's logo? Once the certified list loads,
  // scroll to the certification section so a certifier that's primarily another
  // category still opens "as the certification", not its other-category content.
  useEffect(() => {
    if (certifiedPlaces.length && certFocusTarget && certFocusTarget === establishment?.id) {
      certFocusTarget = null;
      const el = document.getElementById('places-we-certify');
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }, [certifiedPlaces, establishment?.id]);

  // Fetch user's claim status for this place (if logged in and place has no owner)
  useEffect(() => {
    if (!user || !establishment?.id || establishment.owner_id) return;
    api.myClaim(establishment.id).then(setMyClaim).catch(() => setMyClaim(null));
  }, [user, establishment?.id, establishment?.owner_id]);

  // Fetch other establishments within 1 km of this one so the location map
  // can show them as context pins.
  useEffect(() => {
    const c = establishment?.coordinates;
    if (!c || typeof c.x !== 'number' || typeof c.y !== 'number') { setNearbyPlaces([]); return; }
    api.establishments({
      near_lat: c.y,
      near_lng: c.x,
      radius_km: 1,
      sort: 'nearest',
      limit: 50,
    })
      .then((r) => setNearbyPlaces(r.results || []))
      .catch(() => setNearbyPlaces([]));
  }, [establishment?.id, establishment?.coordinates?.x, establishment?.coordinates?.y]);

  // Kosher hotels within a ~2 km walk of this place, ordered by distance.
  // Only for places with a REAL location — a street address or a drawn boundary.
  // Skip services with no fixed address (e.g. drivers, tour guides) whose
  // coordinate is just a city-center fallback: "hotels nearby" there is meaningless.
  useEffect(() => {
    const c = establishment?.coordinates;
    const hasRealLocation = (establishment?.address && establishment.address.trim()) || establishment?.boundary;
    if (!c || typeof c.x !== 'number' || typeof c.y !== 'number' || establishment.type === 'kosher_hotel' || !hasRealLocation) {
      setWalkHotels([]); return;
    }
    api.establishments({ type: 'kosher_hotel', near_lat: c.y, near_lng: c.x, radius_km: 2, sort: 'nearest', limit: 8 })
      .then((r) => setWalkHotels((r.results || []).filter((h) => h.id !== establishment.id)))
      .catch(() => setWalkHotels([]));
  }, [establishment?.id, establishment?.type, establishment?.address, establishment?.boundary, establishment?.coordinates?.x, establishment?.coordinates?.y]);

  // DENTRO IL PERIMETRO, NON DENTRO IL RETTANGOLO CHE LO CONTIENE.
  //
  // La versione precedente decideva col riquadro: minimo e massimo delle
  // coordinate, e dentro se il posto ci cadeva. Per un eruv, che ha la
  // forma di un quartiere, vuol dire dire «sei dentro» a una buona parte
  // di chi e' fuori. Misurato sull'archivio: 771 schede dichiarate dentro
  // che non lo erano, contro 740 giuste — sbagliava piu' spesso di quanto
  // azzeccasse. Su una cosa di halakha non e' accettabile: uno legge
  // «Inside the Eruv» ed esce con le chiavi in tasca.
  //
  // IL PERIMETRO NON E' UN POLIGONO, QUASI MAI. Arriva da Google MyMaps e
  // sono i singoli tratti di filo, aperti e separati: Pinner ne ha 53,
  // North West London 128, Zurigo 247 piu' 16 poligoni. Il primo tentativo
  // chiudeva OGNI tratto su se' stesso — inventando un lato che non esiste
  // — e dava zero posti dentro a North West London, dove ci stanno decine
  // di shul.
  //
  // Si contano gli attraversamenti di una semiretta su TUTTI i segmenti
  // insieme, senza chiudere niente: l'insieme dei tratti e' gia' la curva
  // chiusa, e un numero dispari vuol dire dentro. Funziona anche con piu'
  // perimetri staccati nello stesso disegno — chi sta dentro il primo lo
  // attraversa una volta e l'altro zero, e uno resta dispari.
  //
  // Gli anelli dei poligoni, quelli si', si chiudono: li' l'ultimo punto
  // torna davvero al primo.
  const dentroAlPerimetro = (lat, lng, geo) => {
    let incroci = 0;
    const lato = (a, b) => {
      const [xi, yi] = a;
      const [xj, yj] = b;
      if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) incroci += 1;
    };
    const catena = (pts, chiudi) => {
      if (!Array.isArray(pts) || pts.length < 2) return;
      for (let i = 1; i < pts.length; i += 1) lato(pts[i - 1], pts[i]);
      if (chiudi) lato(pts[pts.length - 1], pts[0]);
    };
    const geometria = (g) => {
      if (!g) return;
      if (g.type === 'Polygon') g.coordinates.forEach((r) => catena(r, true));
      else if (g.type === 'MultiPolygon') g.coordinates.forEach((p) => p.forEach((r) => catena(r, true)));
      else if (g.type === 'LineString') catena(g.coordinates, false);
      else if (g.type === 'MultiLineString') g.coordinates.forEach((l) => catena(l, false));
      else if (g.type === 'GeometryCollection') (g.geometries || []).forEach(geometria);
    };
    try { (geo.features || []).forEach((f) => geometria(f.geometry)); }
    catch (err) { return false; }
    return incroci % 2 === 1;
  };

  // Gli eruv che contengono DAVVERO questo posto, disegnati sulla sua mappa.
  //
  // NON SI FILTRA PIU' PER CITTA'. Prima la ricerca chiedeva gli eruv della
  // stessa `city`, e a Londra non trovava niente: l'eruv di Golders Green
  // e' registrato sotto «London» e un posto a Hendon sotto «Hendon», oppure
  // il contrario. Un eruv non sa niente dei nomi delle citta' che gli
  // mettiamo noi — sa dove passa il suo filo. Si chiede il paese, che sono
  // al massimo una dozzina di perimetri, e decide la geometria.
  useEffect(() => {
    const c = establishment?.coordinates;
    if (!c || typeof c.x !== 'number' || typeof c.y !== 'number' || establishment.type === 'eruv') {
      setCoveringEruvim([]); return;
    }
    api.establishments({ type: 'eruv', country: establishment.country, limit: 100 })
      .then((r) => {
        const hits = (r.results || []).filter((e) => {
          if (!e.boundary || typeof e.boundary !== 'object' || e.id === establishment.id) return false;
          return dentroAlPerimetro(c.y, c.x, e.boundary);
        });
        setCoveringEruvim(hits);
      })
      .catch(() => setCoveringEruvim([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishment?.id, establishment?.type, establishment?.country, establishment?.coordinates?.x, establishment?.coordinates?.y]);

  // Fetch similar places: same type, same city if possible, distance-ordered
  // if we have coords. Drops the focus place itself client-side.
  useEffect(() => {
    if (!establishment?.id || !establishment?.type) { setSimilarPlaces([]); return; }
    const params = { type: establishment.type, limit: 12 };
    if (establishment.city) params.city = establishment.city;
    const c = establishment.coordinates;
    if (c && typeof c.x === 'number' && typeof c.y === 'number') {
      params.near_lat = c.y;
      params.near_lng = c.x;
      params.sort = 'nearest';
      // no radius_km — we want to see similar places even if they're far
    } else {
      params.sort = 'name';
    }
    api.establishments(params)
      .then((r) => setSimilarPlaces((r.results || []).filter((p) => p.id !== establishment.id).slice(0, 8)))
      .catch(() => setSimilarPlaces([]));
  }, [establishment?.id, establishment?.type, establishment?.city, establishment?.coordinates?.x, establishment?.coordinates?.y]);

  // Update document.title so this place's name appears in browser tabs and
  // (client-side) search-engine snapshots. Initial SSR title already injected
  // by services/og-render.js for crawlers that don't run JS.
  useEffect(() => {
    if (!establishment?.name) return;
    const where = [establishment.city, establishment.country].filter(Boolean).join(', ');
    document.title = where
      ? `${nomeVisibile(establishment)} — ${where} | Jewish Compass`
      : `${nomeVisibile(establishment)} | Jewish Compass`;
  }, [establishment?.name, establishment?.city, establishment?.country]);

  useEffect(() => {
    // initialEst lets the page paint instantly, but some sources pass only a
    // PARTIAL row (e.g. the home "Most Visited" list / search suggestions carry
    // just id/name/type/city). A full establishment row always has an `address`
    // key — if it's absent, fetch the complete record so the page isn't blank
    // below the title.
    if (initialEst && 'address' in initialEst) return;
    if (establishmentId) {
      setLoading(true);
      api.establishment(establishmentId)
        .then((est) => {
          setEstablishment(est);
          // If this row has a canonical slug URL, silently swap the address bar
          // over to it so visitors and search engines see the SEO-friendly form.
          const canonical = buildPlaceUrl(est);
          if (canonical && window.location.pathname.startsWith('/place/')) {
            // Keep the language on the canonical swap too, or arriving via
            // /place/<uuid> in Italian would silently drop it from the bar.
            window.history.replaceState({}, '', withLangPath(canonical));
          }
        })
        .catch(() => setEstablishment(null))
        .finally(() => setLoading(false));
    } else if (slugParams) {
      setLoading(true);
      api.placeBySlug(slugParams.cc, slugParams.city, slugParams.slug)
        .then((place) => {
          setEstablishment(place);
          // THIS URL IS AN OLD NAME OF THE PLACE. The backend matched it
          // against former_slugs and handed back where it lives now, so put
          // that in the address bar — replace, not push, because the link
          // that was clicked should not become a step in the back button.
          if (place && place._redirect_to && typeof window !== 'undefined') {
            window.history.replaceState({}, '', place._redirect_to + window.location.search);
          }
        })
        .catch(() => setEstablishment(null))
        .finally(() => setLoading(false));
    }
  }, [establishmentId, initialEst, slugParams?.cc, slugParams?.city, slugParams?.slug]);

  // Crop modal queue — routes picked files through the cropper one at a time
  // before the actual upload. Declared BEFORE the early returns below so the
  // hook order stays stable on every render (rules-of-hooks).
  const [cropQueue, setCropQueue] = useState([]); // [{ file, kind }]
  const [cropCurrent, setCropCurrent] = useState(null);
  const [imgVersion, setImgVersion] = useState(0); // cache-buster after watermarking
  const [boundaryText, setBoundaryText] = useState(''); // pasted GeoJSON/KML (Eruv)

  // Record a page view once per loaded place; if the viewer manages this place,
  // also pull back the view counts to show them. Kept above the early returns
  // so the hook order is stable.
  const [viewStats, setViewStats] = useState(null);
  const [clickStats, setClickStats] = useState(null);
  const viewEstId = establishment?.id;
  const isManager = !!(user && establishment && (user.role === 'admin' || (user.role === 'owner' && establishment.owner_id === user.id)));
  useEffect(() => {
    if (!viewEstId) return;
    api.recordView(viewEstId);
    if (isManager) {
      api.placeViews(viewEstId).then(setViewStats).catch(() => setViewStats(null));
      api.placeClicks(viewEstId).then(setClickStats).catch(() => setClickStats(null));
    } else {
      setViewStats(null); setClickStats(null);
    }
  }, [viewEstId, isManager]);

  // Arriving from the owner Dashboard's "Manage →" opens the edit form directly,
  // rather than the read-only view (owner then has to hunt for the Edit button).
  // Only fires once per mount, and only if the viewer can actually manage it.
  const autoEditRef = React.useRef(false);
  useEffect(() => {
    if (!startInEdit || !establishment || !isManager || autoEditRef.current) return;
    autoEditRef.current = true;
    startEdit();
  }, [startInEdit, establishment, isManager]);

  if (loading) return (
    <div style={styles.container}>
      <Skeleton width="40%" height={14} style={{ marginBottom: 18 }} />
      <Skeleton width="100%" height={400} radius={12} style={{ marginBottom: 20 }} />
      <Skeleton width="70%" height={28} style={{ marginBottom: 12 }} />
      <Skeleton width="50%" height={16} style={{ marginBottom: 24 }} />
      <Skeleton width="100%" height={120} radius={8} />
    </div>
  );
  if (!establishment) return <div style={styles.container}><div style={styles.empty}>{t('Establishment not found.')}</div></div>;

  const isBookable = ['kosher_hotel', 'jewish_tour', 'accommodation', 'shabbat_friendly_accommodation', 'kosher_vacation', 'jewish_driver'].includes(establishment.type);
  const canManage = user && (user.role === 'admin' || (user.role === 'owner' && establishment.owner_id === user.id));
  const gallery = Array.isArray(establishment.gallery_images) ? establishment.gallery_images : [];
  // Optional per-photo captions, keyed by the ORIGINAL gallery URL string.
  const galleryTitles = (establishment.gallery_titles && typeof establishment.gallery_titles === 'object')
    ? establishment.gallery_titles : {};
  // Combined images array for the lightbox (main first, then gallery)
  const allImages = [
    ...(establishment.main_image_url ? [imageUrl(establishment.main_image_url)] : []),
    ...gallery.map((g) => imageUrl(g)),
  ];
  // Captions aligned 1:1 with allImages (main has none; gallery uses its title).
  const allCaptions = [
    ...(establishment.main_image_url ? [''] : []),
    ...gallery.map((g) => galleryTitles[g] || ''),
  ];

  const API =
    process.env.REACT_APP_API_URL !== undefined
      ? process.env.REACT_APP_API_URL
      : 'http://localhost:5000';

  const refresh = async () => {
    const updated = await api.establishment(establishment.id);
    setEstablishment(updated);
  };

  // Save (or clear) the caption/name of one gallery photo, then reflect it.
  const handleGalleryTitle = async (url, title) => {
    try {
      const updated = await api.setGalleryTitle(establishment.id, url, title);
      setEstablishment(updated);
    } catch (e) {
      alert(`Could not save the photo name: ${e.message}`);
    }
  };

  // Add a cache-buster to freshly-watermarked uploads (same URL, new bytes) so
  // the browser refetches the stamped image WITHOUT a full page reload — which
  // keeps the visitor at the same scroll position on the page.
  const imgSrc = (url) => {
    const s = imageUrl(url);
    if (s && imgVersion && typeof url === 'string' && url.startsWith('/uploads/')) {
      return s + (s.includes('?') ? '&' : '?') + 'v=' + imgVersion;
    }
    return s;
  };

  // Measure an image's natural pixel size (File object-URL or a remote URL).
  const imgDims = (src) => new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
    im.onerror = () => resolve(null);
    im.src = src;
  });

  // Guard against silently downgrading the main image: if the replacement is
  // lower resolution than the current main image, ask before it's swapped in
  // (the old one gets deleted). Returns true to proceed, false to abort.
  // `newSrc` is a File object-URL or an image URL. Never blocks on error.
  const confirmMainNotSmaller = async (newSrc) => {
    const cur = establishment.main_image_url;
    if (!cur) return true; // nothing to replace
    const [nd, cd] = await Promise.all([imgDims(newSrc), imgDims(imageUrl(cur))]);
    if (!nd || !cd || !nd.w || !cd.w) return true; // couldn't measure → don't block
    if (nd.w * nd.h >= cd.w * cd.h) return true;   // same size or larger → fine
    return window.confirm(
      `⚠️ The new image is LOWER resolution than the current main image:\n\n` +
      `   New:      ${nd.w} × ${nd.h}\n` +
      `   Current:  ${cd.w} × ${cd.h}\n\n` +
      `Replacing will delete the current, higher-resolution image. Replace anyway?`
    );
  };

  const handleUpload = async (file, kind) => {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('kind', kind);
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API}/api/establishments/${establishment.id}/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      return data;
    } catch (e) {
      throw new Error(e.message || 'Upload failed');
    }
  };

  // Crop flow helpers — state is declared above the early returns so the
  // hook order stays stable.
  // Optional `replaceUrl` (gallery only) tells the post-upload step to also
  // delete this URL after the new file lands — used by the "Crop existing
  // gallery item" path so the same slot ends up with the new file instead of
  // appending a duplicate.
  const startCropFlow = (filesList, kind, replaceUrl = null) => {
    const files = Array.from(filesList).filter((f) => /^image\//.test(f.type)).slice(0, 30);
    if (files.length === 0) return;
    // For main image we only ever take the first one anyway.
    const queue = kind === 'main'
      ? [{ file: files[0], kind, replaceUrl }]
      : files.map((file) => ({ file, kind, replaceUrl }));
    setCropQueue(queue.slice(1));
    setCropCurrent(queue[0]);
    if (kind === 'gallery') {
      setGalleryUploading(true);
      setGalleryProgress({ done: 0, total: queue.length, failed: 0 });
    }
  };

  // Called by ImageCropModal with the file to actually upload (cropped or original).
  // Advances the queue.
  const handleCropDone = async (resultFile) => {
    const item = cropCurrent;
    setCropCurrent(null);
    if (!item) return;

    try {
      await handleUpload(resultFile, item.kind);
      // If this was a re-crop of an existing gallery item, delete the
      // original gallery URL so the same logical slot now holds the new
      // file instead of a duplicate.
      if (item.replaceUrl && item.kind === 'gallery') {
        try {
          const token = localStorage.getItem('jwt_token');
          await fetch(`${API}/api/establishments/${establishment.id}/images`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ url: item.replaceUrl, kind: 'gallery' }),
          });
        } catch (delErr) {
          console.error('Failed to delete original gallery image:', delErr.message);
        }
      }
      if (item.kind === 'gallery') {
        setGalleryProgress((p) => ({ ...p, done: p.done + 1 }));
      }
    } catch (e) {
      if (item.kind === 'gallery') {
        setGalleryProgress((p) => ({ ...p, done: p.done + 1, failed: p.failed + 1 }));
        console.error(`Upload failed for ${item.file.name}:`, e.message);
      } else {
        alert(`Upload failed: ${e.message}`);
      }
    }

    // Continue to next file in the queue, if any
    if (cropQueue.length > 0) {
      setCropCurrent(cropQueue[0]);
      setCropQueue(cropQueue.slice(1));
    } else {
      // Queue empty — finalize
      if (item.kind === 'gallery') {
        setGalleryUploading(false);
      }
      await refresh();
    }
  };

  // User cancelled the cropper — abort the whole batch
  const handleCropCancel = () => {
    setCropCurrent(null);
    setCropQueue([]);
    setGalleryUploading(false);
    refresh();
  };

  // Public entry point — same signature as before so existing call sites work.
  const handleMultiUpload = async (filesList, kind) => {
    // For a MAIN-image replacement, warn first if the new file is lower-res.
    if (kind === 'main') {
      const first = Array.from(filesList).find((f) => /^image\//.test(f.type));
      if (first) {
        const objUrl = URL.createObjectURL(first);
        const ok = await confirmMainNotSmaller(objUrl);
        URL.revokeObjectURL(objUrl);
        if (!ok) return;
      }
    }
    startCropFlow(filesList, kind);
  };

  // Re-crop an already-uploaded image. Fetches the file from the server,
  // wraps it as a File, and feeds it back into the crop queue.
  //   kind='main': the backend upload route already replaces (deletes old +
  //     sets the new URL), so this is a true replace.
  //   kind='gallery': the upload appends a new gallery item. We carry the
  //     `replaceUrl` through the crop queue so once the new upload succeeds
  //     we delete the original gallery URL. Net effect: same slot, new file.
  const recropExisting = async (url, kind) => {
    if (!url) return;
    try {
      const res = await fetch(imageUrl(url));
      if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
      const blob = await res.blob();
      const guessedExt = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const name = url.split('/').pop() || `current-${kind}.${guessedExt}`;
      const file = new File([blob], name, { type: blob.type || 'image/jpeg' });
      // For gallery: stash the original URL so the post-upload step can
      // delete it. For main: leave it null — backend replaces in place.
      startCropFlow([file], kind, kind === 'gallery' ? url : null);
    } catch (e) {
      alert(`Couldn't load the current image: ${e.message}`);
    }
  };

  const handleAddFromUrl = async (kind) => {
    const url = prompt(`Paste the image URL to add as ${kind === 'main' ? 'main image' : 'gallery item'}:`);
    if (!url) return;
    // Warn before replacing the main image with a lower-resolution one.
    if (kind === 'main' && !(await confirmMainNotSmaller(url))) return;
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API}/api/establishments/${establishment.id}/images/from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url, kind }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import failed');
      await refresh();
    } catch (e) {
      alert(`Import failed: ${e.message}`);
    }
  };

  const startEdit = () => {
    const seme = {
      name: establishment.name || '',
      address: establishment.address || '',
      city: establishment.city || '',
      area: establishment.area || '',
      state: establishment.state || '',
      country: establishment.country || '',
      zip: establishment.zip || '',
      phone: establishment.phone || '',
      toll_free: establishment.toll_free || '',
      whatsapp_number: establishment.whatsapp_number || '',
      ai_whatsapp_number: establishment.ai_whatsapp_number || '',
      has_whatsapp: establishment.has_whatsapp !== false, // default true
      email: establishment.email || '',
      website: establishment.website || '',
      booking_url: establishment.booking_url || '',
      donation_url: establishment.donation_url || '',
      order_url: establishment.order_url || '',
      reservation_url: establishment.reservation_url || '',
      has_sukkah: !!establishment.has_sukkah,
      gluten_free: !!establishment.gluten_free,
      open_on_pesach: !!establishment.open_on_pesach,
      wheelchair_accessible: !!establishment.wheelchair_accessible,
      dine_in: !!establishment.dine_in,
      seating_indoor: !!establishment.seating_indoor,
      seating_outdoor: !!establishment.seating_outdoor,
      takeaway: !!establishment.takeaway,
      slug: establishment.slug || '',
      facebook_url: establishment.facebook_url || '',
      instagram_url: establishment.instagram_url || '',
      youtube_url: establishment.youtube_url || '',
      whatsapp_channel_url: establishment.whatsapp_channel_url || '',
      tiktok_url: establishment.tiktok_url || '',
      x_url: establishment.x_url || '',
      linkedin_url: establishment.linkedin_url || '',
      vk_url: establishment.vk_url || '',
      shabbat_amenities: Array.isArray(establishment.shabbat_amenities) ? [...establishment.shabbat_amenities] : [],
      // Preserve any stored explicit tier as-is, but no longer freeze an
      // inferred value on edit (the dropdown is gone; tier is amenity-driven).
      kosher_food_level: establishment.kosher_food_level || '',
      boundary: establishment.boundary || null, // GeoJSON perimeter (Eruv)
      hours: establishment.hours || '',
      open_days: establishment.open_days || '',
      prayer_times_raw: establishment.prayer_times?.raw_text || '',
      contact_name: establishment.contact_name || '',
      contact_title: establishment.contact_title || '',
      contact_phone: establishment.contact_phone || '',
      contact_whatsapp: establishment.contact_whatsapp || '',
      contact_email: establishment.contact_email || '',
      contact_has_whatsapp: establishment.contact_has_whatsapp !== false, // default: number is on WhatsApp
      contact2_name: establishment.contact2_name || '',
      contact2_title: establishment.contact2_title || '',
      contact2_whatsapp: establishment.contact2_whatsapp || '',
      contact2_email: establishment.contact2_email || '',
      contact2_has_whatsapp: establishment.contact2_has_whatsapp !== false,
      supervision: establishment.supervision || '',
      supervision_level: establishment.supervision_level || '',
      subtype: establishment.subtype || '',
      community: establishment.community || '',
      cuisine: establishment.cuisine || '',
      is_closed: establishment.is_closed || false,
      no_longer_kosher: establishment.no_longer_kosher || false,
      unlisted: establishment.unlisted || false,
      history: establishment.history || establishment.description || '', // fall back to the blurb so the shown "About" is editable
      notes: establishment.notes || '',
      // Gli stessi campi in un'altra lingua. Si copia l'oggetto intero
      // perche' il modulo lo modifica un pezzo per volta, e il server
      // riscrive la colonna per intero: mandargliene solo un pezzo
      // cancellerebbe le altre lingue.
      translations: establishment.translations ? JSON.parse(JSON.stringify(establishment.translations)) : {},
      menu: establishment.menu || '',
      price_info: establishment.price_info || '',
      // The API sends a date as an ISO timestamp; <input type="date"> wants
      // just the day, and hands back '' when cleared.
      date_from: (establishment.date_from || '').slice(0, 10),
      date_to: (establishment.date_to || '').slice(0, 10),
      kosher_valid_to: (establishment.kosher_valid_to || '').slice(0, 10),
      rate: establishment.rate || 0,
      ...(user?.role === 'admin' ? {
        type: establishment.type || '',
        secondary_types: Array.isArray(establishment.secondary_types) ? [...establishment.secondary_types] : [],
        recommended_by: establishment.recommended_by || '',
      } : {}),
      coordinates: establishment.coordinates && typeof establishment.coordinates.x === 'number'
        ? { lat: establishment.coordinates.y, lng: establishment.coordinates.x }
        : null,
    };
    setEditForm(seme);
    // La stessa fotografia, congelata: il salvataggio ci si confronta.
    editSeed.current = JSON.parse(JSON.stringify(seme));
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditForm({});
  };

  // Pull data from a website / Google Maps / Facebook / Instagram link and
  // merge it into the edit form. Admin reviews and clicks Save to persist.
  const handleImportFromUrl = async () => {
    const u = importUrl.trim();
    if (!u) return;
    setImporting(true); setImportMsg(''); setImportedImageUrl('');
    try {
      const p = await api.adminImportFromUrl(u);
      const FIELDS = ['name', 'address', 'city', 'country', 'zip', 'phone', 'email', 'website',
        'facebook_url', 'instagram_url', 'youtube_url', 'whatsapp_channel_url', 'x_url', 'linkedin_url', 'tiktok_url', 'vk_url', 'hours'];
      const merge = {};
      const filled = [];
      for (const k of FIELDS) { if (p[k]) { merge[k] = p[k]; filled.push(k); } }
      if (p.coordinates && typeof p.coordinates.lat === 'number') merge.coordinates = p.coordinates;
      setEditForm((f) => ({ ...f, ...merge }));
      const logo = p.logo || p.main_image_url || '';
      setImportedImageUrl(logo);
      setImportMsg(
        (filled.length ? `Filled: ${filled.join(', ')}.` : 'No text fields found.')
        + (logo ? ' Logo/photo found — use the button below.' : '')
        + ' Review, then Save.'
      );
    } catch (e) {
      setImportMsg(`Import failed: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  // Download the imported logo/photo straight into this place as the main image.
  const handleImportImage = async () => {
    if (!importedImageUrl) return;
    setImportMsg('Importing image…');
    try {
      await api.importImageFromUrl(establishment.id, importedImageUrl, 'main');
      setImportedImageUrl('');
      setImportMsg('✓ Logo/photo set as the main image.');
      refresh();
    } catch (e) {
      setImportMsg(`Image import failed: ${e.message}`);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      // SOLO QUELLO CHE E' STATO DAVVERO CAMBIATO.
      //
      // Mandare il modulo intero vuol dire riscrivere quaranta campi per
      // cambiarne uno, e riportare indietro qualunque cosa sia cambiata
      // nel frattempo — un'unione fatta dal riquadro qui sotto, un altro
      // admin, uno script. Si confronta con la fotografia di partenza e
      // si manda la differenza.
      const seme = editSeed.current || {};
      const diff = {};
      for (const k of Object.keys(editForm)) {
        if (JSON.stringify(editForm[k]) !== JSON.stringify(seme[k])) diff[k] = editForm[k];
      }
      if (!Object.keys(diff).length) { setEditMode(false); setEditForm({}); return; }
      const res = await api.updateEstablishment(establishment.id, diff);
      // Owner edits don't apply live — they're queued for admin approval, so the
      // page keeps its current values and we just confirm the submission.
      if (res && res.pending) {
        setEditMode(false);
        setEditForm({});
        alert(res.message || 'Your changes were submitted and will appear once an admin approves them.');
        return;
      }
      setEstablishment(res);
      setEditMode(false);
      setEditForm({});
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Admin-only: stamp the jewishcompass.com watermark on one image, in place.
  // Confirm first — meant only for images that are 100% our own property.
  const handleWatermark = async (url) => {
    if (!window.confirm('Watermark this image with jewishcompass.com?\n\nOnly do this for photos that are 100% our own property — not images taken from a business website or Google.')) return;
    try {
      await api.adminWatermarkImage(url);
      // The file is replaced in place (same URL). Refetch the record and bump
      // the cache-buster so the stamped image reloads without a full page
      // reload — the visitor stays exactly where they were on the page.
      await refresh();
      setImgVersion(Date.now());
    } catch (e) {
      alert(`Watermark failed: ${e.message}`);
    }
  };

  // Admin-only: watermark the main image + every gallery photo in one go.
  const handleWatermarkAll = async () => {
    const urls = [
      establishment.main_image_url,
      ...(Array.isArray(establishment.gallery_images) ? establishment.gallery_images : []),
    ].filter((u) => u && u.startsWith('/uploads/'));
    if (urls.length === 0) { alert(t('No uploaded images to watermark.')); return; }
    if (!window.confirm(`Watermark all ${urls.length} image(s) (main + gallery) with the Jewish Compass logo?\n\nOnly do this for photos that are 100% our own property — not images from a business website or Google.`)) return;
    let ok = 0;
    for (const u of urls) {
      try { await api.adminWatermarkImage(u); ok += 1; } // eslint-disable-line no-await-in-loop
      catch (e) { console.error('watermark failed for', u, e); }
    }
    alert(`Watermarked ${ok} of ${urls.length} image(s).`);
    await refresh();
    setImgVersion(Date.now());
  };

  // Move the current main image into the gallery — keeps the photo, just clears
  // the main slot (unlike Remove Main, which deletes the file).
  const handleMainToGallery = async () => {
    if (!establishment.main_image_url) return;
    if (!window.confirm(t('Move the current main image into the gallery?\n\nThe photo is kept as a gallery item; the listing will have no main image until you set a new one.'))) return;
    try {
      await api.moveMainToGallery(establishment.id);
      await refresh();
    } catch (e) {
      alert(`Move failed: ${e.message}`);
    }
  };

  const handleDelete = async (url, kind) => {
    if (!window.confirm(`Delete this ${kind === 'main' ? 'main image' : 'photo'}?`)) return;
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API}/api/establishments/${establishment.id}/images`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url, kind }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      await refresh();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  return (
    <div style={styles.container}>
      {/* Search lives in the top bar now — no duplicate search on the place page. */}
      <Breadcrumbs establishment={establishment} />

      {/* Page-view stats — visible only to the place's owner/admin */}
      {canManage && viewStats && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: COLORS.text }}>
          <span style={{ fontWeight: 700, color: COLORS.primary }}>{t('👁 Page views')}</span>
          <span><strong>{viewStats.total}</strong> total</span>
          <span><strong>{viewStats.unique_visitors}</strong> {t("unique visitor")}{viewStats.unique_visitors === 1 ? '' : 's'}</span>
          <span style={{ color: COLORS.muted }}>{t("· last 30 days:")} {viewStats.last_30_days} {t("views,")} {viewStats.unique_30_days} unique</span>
          {/* Outbound-action clicks. WhatsApp always shown (even 0) so it's
              clear it's tracked; the rest appear once they happen. Driven off
              one list so a newly tracked action can't go unreported here. */}
          {clickStats && (
            <span style={{ color: COLORS.muted, borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 12 }}>
              💬 <strong>{clickStats.whatsapp?.total || 0}</strong> {t("WhatsApp")}
              {CLICK_LABELS.map(([key, icon, label]) => (
                clickStats[key] ? ` · ${icon} ${clickStats[key].total} ${t(label)}` : ''
              ))}
            </span>
          )}
        </div>
      )}

      {/* Hero image (only shown if available) - never enlarged beyond natural size */}
      {establishment.main_image_url ? (
        <div style={{ position: 'relative', marginBottom: 20, background: COLORS.bg, borderRadius: 12, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <img
            src={imgSrc(establishment.main_image_url)}
            alt={establishment.name}
            onClick={() => setLightboxIndex(0)}
            // `maxHeight: '100%'` e non 400: l'altezza la decide il
            // contenitore, che su telefono responsive.css porta a 240px.
            // Con un numero fisso l'immagine restava alta 354 dentro una
            // scatola di 240 e si faceva tagliare 57 pixel sopra e 57
            // sotto — sullo stemma di Or Yeudà sparivano la corona e la
            // scritta ROMA. Legandola al contenitore non puo' piu'
            // succedere, qualunque altezza qualcuno gli dia domani.
            style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block', borderRadius: 12, cursor: 'pointer' }}
          />
          {/* Icon badge enveloping the hero image */}
          <div
            title={formatCategoryLabel(establishment.type)}
            style={{ position: 'absolute', top: -12, left: -12, width: 64, height: 64, background: 'white', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}
          >
            <CategoryIcon type={establishment.type} />
          </div>
          {canManage && (
            <button
              onClick={() => handleDelete(establishment.main_image_url, 'main')}
              style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(220, 38, 38, 0.9)', color: 'white', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
            >
              {t("🗑 Remove")}
            </button>
          )}
        </div>
      ) : null}

      <div style={styles.detailHeader}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
          {/* No photo: a small marker, not a 96px void. The category is
              already written in the breadcrumb and again under the title, so
              this tile carries no information — it only has to stop the
              heading starting hard against the card edge. */}
          {!establishment.main_image_url && (
            <div
              title={formatCategoryLabel(establishment.type)}
              style={{ width: 52, height: 52, background: COLORS.bg, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}
            >
              <CategoryIcon type={establishment.type} />
            </div>
          )}
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            {editMode ? (
              <>
                <input
                  style={{ ...styles.input, fontSize: 24, fontWeight: 700, color: COLORS.primary, marginBottom: 8 }}
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder={t('Establishment name')}
                />
                <CampiTradotti campo="name" valori={editForm.translations} onChange={(tr) => setEditForm({ ...editForm, translations: tr })} conLinguaBase />
              </>
            ) : (
              <>
                <h1 style={{ margin: '0 0 2px 0', color: COLORS.primary, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {splitPlaceName(nomeVisibile(establishment)).title}
                  {establishment.is_closed && (
                    <span style={{ marginLeft: 12, background: COLORS.danger, color: 'white', padding: '4px 12px', borderRadius: 4, fontSize: 13, fontWeight: 700, verticalAlign: 'middle', letterSpacing: 1 }}>
                      {t("CLOSED")}
                    </span>
                  )}
                  {establishment.no_longer_kosher && !establishment.is_closed && (
                    <span style={{ marginLeft: 12, background: '#92400e', color: 'white', padding: '4px 12px', borderRadius: 4, fontSize: 13, fontWeight: 700, verticalAlign: 'middle', letterSpacing: 1 }}>
                      {t("NO LONGER KOSHER")}
                    </span>
                  )}
                  {/* Owner-managed: claimed and maintained by its owner. */}
                  {establishment.owner_id && !establishment.is_closed && !establishment.no_longer_kosher && (
                    <span title={t("Kept up to date by the owner")} style={{ marginLeft: 12, background: '#e0edff', color: COLORS.primary, padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, verticalAlign: 'middle', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
                      {t("✓ Owner-managed")}
                    </span>
                  )}
                </h1>
                {splitPlaceName(nomeVisibile(establishment)).subtitle && (
                  <div dir="rtl" style={{ margin: '0 0 8px', color: COLORS.muted, fontSize: 19, fontWeight: 600, lineHeight: 1.3 }}>
                    {splitPlaceName(nomeVisibile(establishment)).subtitle}
                  </div>
                )}
                {/* A REBRAND IS SOMETHING THE READER WANTS TO BE TOLD. Someone
                    who knew the King Solomon needs to recognise that Cassia is
                    the same hotel — otherwise the listing looks like a
                    different place and the one they remember looks closed. */}
                {establishment.former_name && (
                  <div style={{ margin: '0 0 8px', color: COLORS.muted, fontSize: 13 }}>
                    {t('Formerly')} <span style={{ fontWeight: 600 }}>{splitPlaceName(establishment.former_name).title}</span>
                  </div>
                )}
                {establishment.no_longer_kosher && !establishment.is_closed && (
                  <div style={{ marginTop: 8, padding: '12px 14px', background: '#fef3c7', borderLeft: `4px solid #f59e0b`, color: '#78350f', borderRadius: 4, fontSize: 14, lineHeight: 1.4 }}>
                    <strong>{t('⚠️ No longer kosher.')}</strong> {t("This business is still operating but is no longer under kosher supervision. The page is kept online for reference — please verify current status directly with the business before making assumptions about kashrut.")}
                  </div>
                )}
              </>
            )}
            <div style={{ color: COLORS.muted, fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>{formatCategoryLabel(establishment.type)}</span>
              {/* Additional categories this place is also listed under. These
                  link to THIS place (it IS that category too), not to a browse
                  list of every place in the category. */}
              {Array.isArray(establishment.secondary_types) && establishment.secondary_types.map((t) => (
                <a key={t} href={withLangPath(buildPlaceUrl(establishment) || `/place/${establishment.id}`)}
                   style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: COLORS.bg, color: COLORS.primary, padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                  <CategoryIcon type={t} /> also {formatCategoryLabelShort(t)}
                </a>
              ))}
              {/* Subtype/kind can be a comma-separated list (e.g. "Meat, Parev"
                  for restaurants, "Ashkenaz, Sefarad" for synagogues). Render
                  each value as its own pill. */}
              {splitCuisines(establishment.subtype).map((s) => (
                <span key={s} style={{ background: COLORS.bg, color: COLORS.primary, padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                  {t(s)}
                </span>
              ))}
              {establishment.community && (
                <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                  {t(establishment.community)}
                </span>
              )}
              {/* Cuisine can be a comma-separated list of multiple values — render each
                  as its own pill so they read as separate tags, not one long string. */}
              {splitCuisines(establishment.cuisine).map((c) => (
                <span key={c} style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                  {t(c)}
                </span>
              ))}
              {establishment.rate > 0 && (
                <span
                  style={{ color: '#f59e0b', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  title={['kosher_hotel', 'accommodation', 'shabbat_friendly_accommodation', 'kosher_vacation'].includes(establishment.type) ? `${establishment.rate}-star hotel` : `${establishment.rate} / 5`}
                >
                  {'★'.repeat(establishment.rate)}{'☆'.repeat(Math.max(0, 5 - establishment.rate))}
                  {['kosher_hotel', 'accommodation', 'shabbat_friendly_accommodation', 'kosher_vacation'].includes(establishment.type) && (
                    <span style={{ color: COLORS.muted, fontSize: 11, fontWeight: 600 }}>{establishment.rate}{t("-STAR")}</span>
                  )}
                </span>
              )}
              {establishment.chain && (
                <span style={{ color: COLORS.muted, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }} title={t("Hotel chain / brand")}>
                  • 🏨 {establishment.chain}
                </span>
              )}
              {establishment.area && (
                <a
                  href={withLangPath(`/list?${new URLSearchParams({
                    country: establishment.country || '', city: establishment.city || '', area: establishment.area,
                  }).toString()}`)}
                  title={t('See everything in this neighbourhood')}
                  style={{ background: COLORS.bg, color: COLORS.primary, padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
                >
                  📍 {establishment.area}
                </a>
              )}
              {establishment.city && <span> • {establishment.city}{establishment.state ? `, ${establishment.state}` : ''}</span>}
              {establishment.country && (
                <>
                  <Flag country={t(establishment.country)} code={establishment.country_code} height={14} />
                  <span>{t(establishment.country)}</span>
                </>
              )}
              {establishment.reply_rate != null && <ReplyRateBadge rate={establishment.reply_rate} size={13} />}
            </div>
          </div>
          {/* Share — native share sheet on mobile, copy-link fallback. Always
              available (no login needed). */}
          {!editMode && <SharePlace establishment={establishment} />}
          {/* Favorite (heart) — visible to any logged-in client; logged-out
              users get a login prompt via toggleFavorite. */}
          {onToggleFavorite && !editMode && (
            <button
              onClick={() => onToggleFavorite(establishment.id)}
              title={favorites && favorites.has(establishment.id) ? t('Remove from favorites') : t('Save to favorites')}
              style={{
                background: 'white',
                color: favorites && favorites.has(establishment.id) ? '#ef4444' : COLORS.muted,
                border: `1px solid ${favorites && favorites.has(establishment.id) ? '#ef4444' : COLORS.border}`,
                padding: '8px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <HeartIcon size={16} filled={favorites && favorites.has(establishment.id)} />
              {favorites && favorites.has(establishment.id) ? t('Saved') : t('Save')}
            </button>
          )}
          {canManage && !editMode && (
            <button style={{ ...styles.buttonOutline, flexShrink: 0 }} onClick={startEdit}>{t('✏️ Edit')}</button>
          )}
          {/* Suggest-edit (community submission). Hidden if the user already
              has direct edit rights (admin / owner of this place). Logged-out
              users see the button too — clicking prompts login. */}
          {!canManage && !editMode && (
            <>
              <button
                style={{ ...styles.buttonOutline, flexShrink: 0 }}
                onClick={() => {
                  if (!user) { onLogin(); return; }
                  setShowSuggest(true);
                }}
                title={t("Found something wrong or missing? Suggest an edit.")}
              >
                {t("✏️ Suggest an edit")}
              </button>
              {/* Report — quieter than the Suggest button since it's only used
                  for abuse / spam / closed-but-still-listed cases. */}
              <ReportButton
                entityType="establishment"
                entityId={establishment.id}
                user={user}
                label={t('⚑ Report')}
                style={{ alignSelf: 'center' }}
              />
            </>
          )}
        </div>
        {establishment.updated_at && (
          <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 6 }}>
            {t("Last updated")} {new Date(establishment.updated_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}
          </div>
        )}

        {editMode ? (
          <div style={{ marginTop: 20 }}>
            {user?.role === 'admin' && (
              <div style={{ background: '#f0f7ff', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 14, marginBottom: 18 }}>
                <div style={{ fontWeight: 700, color: COLORS.primary, marginBottom: 4 }}>{t('Update from a link')}</div>
                <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 8 }}>
                  {t('Paste a website, Google Maps, Facebook, or Instagram link to auto-fill the fields below (name, address, phone, hours, socials) and grab the logo. Review, then Save.')}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="url"
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    placeholder={t('https://… (site, Maps, Facebook, Instagram)')}
                    style={{ ...styles.input, flex: 1, minWidth: 220, marginBottom: 0 }}
                  />
                  <button type="button" onClick={handleImportFromUrl} disabled={importing || !importUrl.trim()}
                    style={{ ...styles.button, opacity: (importing || !importUrl.trim()) ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                    {importing ? t('Fetching…') : t('Fetch & fill')}
                  </button>
                  {importedImageUrl && (
                    <button type="button" onClick={handleImportImage}
                      style={{ ...styles.buttonOutline, whiteSpace: 'nowrap' }}>
                      🖼️ {t('Use logo/photo')}
                    </button>
                  )}
                </div>
                {importedImageUrl && (
                  <img src={importedImageUrl} alt={t('Imported logo preview')} style={{ height: 48, marginTop: 8, borderRadius: 6, border: `1px solid ${COLORS.border}`, background: 'white' }} />
                )}
                {importMsg && <div style={{ fontSize: 12, color: COLORS.text, marginTop: 8 }}>{importMsg}</div>}
              </div>
            )}
            <div style={styles.detailGrid}>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Address')}</div>
                <AddressVerify
                  value={editForm.address}
                  city={editForm.city}
                  country={editForm.country}
                  zip={editForm.zip}
                  verified={!!editForm.coordinates}
                  onChange={(v) => setEditForm({ ...editForm, address: v })}
                  onPick={({ lat, lng, parts, display_name }) => {
                    // Fill address/city/area/zip/country from the picked
                    // suggestion. The geocoder sometimes returns only a bare
                    // house number (no road) — fold in the street from the
                    // display name so we don't save just "4020".
                    const p = parts || {};
                    const addr = resolvePickedAddress(p.address, display_name);
                    setEditForm((f) => ({
                      ...f,
                      coordinates: { lat, lng },
                      address: addr || f.address,
                      state:   p.state   || f.state,
                      zip:     p.postcode || f.zip,
                      country: p.country  || f.country,
                    }));
                    // Città e quartiere a parte: il quartiere solo se la città
                    // lo ha già (AreaPicker.js spiega perché).
                    cityAndAreaFromPick(p, { city: editForm.city, area: editForm.area, country: editForm.country, options: areaOptions })
                      .then(({ city, area }) => setEditForm((f) => ({ ...f, city: city || f.city, area })));
                  }}
                  onClearVerified={() => setEditForm({ ...editForm, coordinates: null })}
                  placeholder={t('Start typing the street address…')}
                />
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Neighbourhood / Area')}</div>
                <AreaPicker style={styles.input} value={editForm.area || ''} options={areaOptions}
                  onChange={(v) => setEditForm((f) => ({ ...f, area: v }))} />
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('City')}</div>
                <ComboBox
                  style={styles.input}
                  value={editForm.city}
                  options={cityOptionsEdit}
                  onChange={(v) => setEditForm((f) => ({ ...f, city: v }))}
                  placeholder={t('City')}
                  onBlur={async (e) => {
                    // "Put in a city → know the rest": when state/country aren't
                    // set yet, resolve them from the city. Picking an address
                    // suggestion above is more precise (disambiguates same-name
                    // cities) and always wins — this only fills blanks.
                    const city = e.target.value.trim();
                    if (!city || (editForm.state && editForm.country)) return;
                    try {
                      const s = await api.geocodeSuggest(city);
                      const list = Array.isArray(s) ? s : (s && (s.results || s.suggestions)) || [];
                      const p = list[0] && list[0].parts;
                      if (p) setEditForm((f) => ({ ...f, state: f.state || p.state || '', country: f.country || p.country || '' }));
                    } catch {}
                  }}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>{t('State & country fill in automatically from the address or city.')}</div>
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('State / Province / Region')}</div>
                <input style={{ ...styles.input, background: '#f3f4f6', color: COLORS.muted, cursor: 'not-allowed' }} value={editForm.state || ''} readOnly title={t('Set automatically from the address')} placeholder={t('Auto-filled from the address')} />
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('ZIP / Postal Code')}</div>
                <input style={styles.input} value={editForm.zip} onChange={(e) => setEditForm({ ...editForm, zip: e.target.value })} placeholder={t('ZIP')} />
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Country')}</div>
                <input style={{ ...styles.input, background: '#f3f4f6', color: COLORS.muted, cursor: 'not-allowed' }} value={editForm.country || ''} readOnly title={t('Set automatically from the address')} placeholder={t('Auto-filled from the address')} />
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Phone')}</div>
                <input style={styles.input} value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="+1 234 567 890" />
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Toll-free number (optional)')}</div>
                <input style={styles.input} value={editForm.toll_free} onChange={(e) => setEditForm({ ...editForm, toll_free: e.target.value })} placeholder={t('e.g. 8 800 555 35 35')} />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('A freephone number that only works when dialled from inside the country (e.g. a Russian 8-800 line). Shown separately and labelled as free within the country.')}
                </div>
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('WhatsApp Number (optional)')}</div>
                <input
                  style={styles.input}
                  value={editForm.whatsapp_number}
                  onChange={(e) => setEditForm({ ...editForm, whatsapp_number: e.target.value })}
                  placeholder={t('Leave blank if same as phone')}
                  disabled={editForm.has_whatsapp === false}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('If set, the WhatsApp button uses this number instead of the regular phone (useful when the business phone is a landline).')}
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: COLORS.text, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.has_whatsapp === false}
                    onChange={(e) => setEditForm({ ...editForm, has_whatsapp: !e.target.checked })}
                  />
                  📵 {t('This business has no WhatsApp — hide the WhatsApp button on the site')}
                </label>
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>🤖 {t('AI WhatsApp Number (optional)')}</div>
                <input style={styles.input} value={editForm.ai_whatsapp_number} onChange={(e) => setEditForm({ ...editForm, ai_whatsapp_number: e.target.value })} placeholder="+1 234 567 890" />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('If your business has a WhatsApp number with an AI chatbot that auto-replies, add it here. A separate "🤖 AI Chat" button will appear so customers get instant answers about menu, hours, etc.')}
                </div>
              </div>
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Email')}</div>
                <input style={styles.input} type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder={t("contact@example.com")} />
              </div>
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>{t('Website')}</div>
                <input style={styles.input} value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder={t("https://...")} />
              </div>
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>{t('Social media (optional)')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                  <input style={styles.input} value={editForm.facebook_url || ''} onChange={(e) => setEditForm({ ...editForm, facebook_url: e.target.value })} placeholder={'📘 ' + t('Facebook URL')} />
                  <input style={styles.input} value={editForm.instagram_url || ''} onChange={(e) => setEditForm({ ...editForm, instagram_url: e.target.value })} placeholder={'📷 ' + t('Instagram URL')} />
                  <input style={styles.input} value={editForm.youtube_url || ''} onChange={(e) => setEditForm({ ...editForm, youtube_url: e.target.value })} placeholder={'▶️ ' + t('YouTube channel URL')} />
                  <input style={styles.input} value={editForm.whatsapp_channel_url || ''} onChange={(e) => setEditForm({ ...editForm, whatsapp_channel_url: e.target.value })} placeholder={'💬 ' + t('WhatsApp channel URL')} />
                  <input style={styles.input} value={editForm.tiktok_url || ''} onChange={(e) => setEditForm({ ...editForm, tiktok_url: e.target.value })} placeholder={'🎵 ' + t('TikTok URL')} />
                  <input style={styles.input} value={editForm.x_url || ''} onChange={(e) => setEditForm({ ...editForm, x_url: e.target.value })} placeholder={'✕ ' + t('X (Twitter) URL')} />
                  <input style={styles.input} value={editForm.linkedin_url || ''} onChange={(e) => setEditForm({ ...editForm, linkedin_url: e.target.value })} placeholder={'💼 ' + t('LinkedIn URL')} />
                  <input style={styles.input} value={editForm.vk_url || ''} onChange={(e) => setEditForm({ ...editForm, vk_url: e.target.value })} placeholder={'🅥 ' + t('VK URL')} />
                </div>
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('Paste the full profile URL (e.g. https://instagram.com/yourshop). Each one shows as a clickable icon on listing cards and the place page.')}
                </div>
              </div>
              {/* QUANDO SI E' APERTI — subito dopo i recapiti, prima di
                  tutto il resto. Menachem, 31/10/2026: «גם מכיוון ששעות
                  פתיחה זה דבר חשוב, תעלה את זה יותר למעלה בedit של מקום».
                  Stavano in fondo, dopo venticinque altri campi: per chi
                  compila una scheda l'orario e' la seconda cosa che sa,
                  e sepolto in fondo si lasciava in bianco. */}
              {/* Prayer times — synagogues */}
              {(hasAnyType({ type: editForm.type || establishment.type, secondary_types: editForm.secondary_types }, ['synagogue_shul'])) && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>🕐 {t('Prayer Times')}</div>
                  <textarea
                    style={{ ...styles.input, minHeight: 100, fontFamily: 'inherit', resize: 'vertical' }}
                    value={editForm.prayer_times_raw}
                    onChange={(e) => setEditForm({ ...editForm, prayer_times_raw: e.target.value })}
                    placeholder={t('Shacharit: Sun 8 AM, Mon-Thu 7 AM, Shabbat 9:30 AM.\nMincha-Arvit: at candle lighting.')}
                  />
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                    {t('The system auto-detects Shacharit/Mincha/Arvit. Use "at candle lighting" for sundown-based times.')}
                  </div>
                  <PrayerTimesPreview raw={editForm.prayer_times_raw} />
                </div>
              )}

              {/* Orari di apertura — PER TUTTI, sinagoghe comprese.

                  Menachem, 2/11/2026, su Chabad Tzfat: «dice che e' sempre
                  aperto ma non posso cambiarlo». Era vero alla lettera. La
                  scheda porta «Open 24 hours» per sette giorni, arrivati da
                  Google; la pagina li mostra, perche' `prayer_times` e' vuoto
                  e allora il riquadro degli orari non si tira indietro; e
                  questa casella si disegnava SOLO per chi non e' sinagoga. Il
                  dato c'era, la maniglia per cambiarlo no.

                  NON E' UNA SCHEDA SOLA: 134 sinagoghe hanno orari scritti che
                  nessuno poteva correggere. Dieci li mostrano in pagina, le
                  altre no perche' hanno le tefillot — ma invisibile non vuol
                  dire innocuo, visto che `hours` decide anche chi entra nel
                  filtro «aperto adesso».

                  E RESTA UNA COSA DIVERSA DALLE TEFILLOT, che hanno la loro
                  casella qui sopra: una sinagoga-museo come Siena o Merano ha
                  davvero due orari da scrivere, quello delle preghiere e
                  quello in cui si puo' entrare a vedere. */}
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>🕐 {t('Opening Hours')}</div>
                <textarea
                  style={{ ...styles.input, minHeight: 70, fontFamily: 'inherit', resize: 'vertical' }}
                  value={editForm.hours}
                  onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })}
                  placeholder={t('One line per day or day range, e.g. <days>: <open> - <close>')}
                />
                {![editForm.type || establishment.type, ...(editForm.secondary_types || [])].some((x) => x && x !== 'synagogue_shul') && (
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                    {t('When the building is open to visitors — not the prayer times, which have their own box above.')}
                  </div>
                )}
              </div>

              {/* About / History + Good to Know — core content, kept right after
                  the contact details (moved up from the bottom of the form). */}
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>📖 {t('About / History')}</div>
                <textarea
                  style={{ ...styles.input, minHeight: 120, fontFamily: 'inherit', resize: 'vertical' }}
                  value={editForm.history}
                  onChange={(e) => setEditForm({ ...editForm, history: e.target.value })}
                  placeholder={t('History of the place, story, background information...')}
                />
                <CampiTradotti campo="history" valori={editForm.translations} onChange={(tr) => setEditForm({ ...editForm, translations: tr })} multiriga />
              </div>
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>ℹ️ {t('Good to Know (notes for visitors)')}</div>
                <textarea
                  style={{ ...styles.input, minHeight: 80, fontFamily: 'inherit', resize: 'vertical' }}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder={t('Disabled access, guided visits, dress code, special tips...')}
                />
                <CampiTradotti campo="notes" valori={editForm.translations} onChange={(tr) => setEditForm({ ...editForm, translations: tr })} multiriga />
              </div>
              {/* Custom URL slug — both owners and admins can rename. Empty
                  field auto-regenerates from the place name on save. */}
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>🔗 {t('URL slug (advanced)')}</div>
                <input
                  style={styles.input}
                  value={editForm.slug || ''}
                  onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
                  placeholder={t('Leave blank to auto-generate from the name')}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('Lower-case letters, digits, and dashes. The full URL becomes')}{' '}<code>/{"<country>"}/{"<city>"}/{"<slug>"}</code>.{' '}
                  {t('If you change the slug, the old URL stops working — make sure no one is linking to it.')}
                </div>
              </div>
              {/* Booking link = affiliate revenue link → admin-only. */}
              {user?.role === 'admin' && (
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>🛏️ {t('Booking link (affiliate or partner — optional)')}</div>
                <input
                  style={styles.input}
                  value={editForm.booking_url || ''}
                  onChange={(e) => setEditForm({ ...editForm, booking_url: e.target.value })}
                  placeholder={t("https://www.hotels.com/affiliate/...")}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('When set, shows a prominent "Book Now" button on the place\'s page with an affiliate disclosure.')}
                </div>
              </div>
              )}
              {/* Recommendation lists. Saying a rabbi vouches for a place is a
                  claim on his name, so this stays admin-only like booking_url
                  — an owner must not be able to tick "Rabbi Moshe Zalmanov". */}
              {user?.role === 'admin' && (
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>⭐ {t('Recommended by (admin only)')}</div>
                <RecommendedByPicker
                  value={editForm.recommended_by || ''}
                  onChange={(v) => setEditForm({ ...editForm, recommended_by: v })}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>
                  {t('Shows a "Recommended by" badge on the place and puts it on that list\'s filter. A place can be on several lists.')}
                </div>
              </div>
              )}
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>❤️ {t('Donation link (optional)')}</div>
                <input
                  style={styles.input}
                  value={editForm.donation_url || ''}
                  onChange={(e) => setEditForm({ ...editForm, donation_url: e.target.value })}
                  placeholder={t('https://… (donate / support page)')}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('Shows a "Donate" button — useful for synagogues, schools, eruvim and other nonprofits.')}
                </div>
              </div>
              {ORDER_URL_TYPES.includes(editForm.type) && (
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>🛍️ {t('Order online link (optional)')}</div>
                <input
                  style={styles.input}
                  value={editForm.order_url || ''}
                  onChange={(e) => setEditForm({ ...editForm, order_url: e.target.value })}
                  placeholder={t('https://… (online ordering / delivery / shop)')}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('Shows an "Order Online" button — your own ordering, delivery or online-store page.')}
                </div>
              </div>
              )}
              {hasAnyType(editForm, [...RESERVATION_URL_TYPES, ...VISIT_BOOKING_TYPES]) && (() => { const table = reservationLabel(editForm) === (t("Reserve a table")); return (
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>📅 {t(table ? 'Table reservation link (optional)' : 'Visit or tour booking link (optional)')}</div>
                <input
                  style={styles.input}
                  value={editForm.reservation_url || ''}
                  onChange={(e) => setEditForm({ ...editForm, reservation_url: e.target.value })}
                  placeholder={t(table ? 'https://… (book a table)' : 'https://… (book a visit or a tour)')}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t(table
                    ? 'Shows a "Reserve a table" button — your own booking page, Tabit, OpenTable or similar.'
                    : 'Shows a "Book a visit" button — your own booking page, Tabit or similar.')}
                </div>
              </div>
              ); })()}
              {user?.role === 'admin' && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>{t('Category (Admin only)')}</div>
                  <select
                    style={styles.input}
                    value={editForm.type}
                    onChange={(e) => {
                      const nt = e.target.value;
                      // Switching the primary category drops it from the
                      // secondary list if it was there.
                      setEditForm({ ...editForm, type: nt, secondary_types: (editForm.secondary_types || []).filter((x) => x !== nt) });
                    }}
                  >
                    {editForm.type && !CATEGORY_ORDER.includes(editForm.type) && (
                      <option value={editForm.type}>{formatCategoryLabel(editForm.type)} ({t('current')})</option>
                    )}
                    {CATEGORY_ORDER.slice()
                      .sort((a, b) => formatCategoryLabelShort(a).localeCompare(formatCategoryLabelShort(b)))
                      .map((t) => (
                        <option key={t} value={t}>{formatCategoryLabel(t)}</option>
                      ))}
                  </select>
                  <div style={{ ...styles.detailLabel, marginTop: 10 }}>{t('Also listed under')}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {CATEGORY_ORDER.filter((t) => t !== (editForm.type || establishment.type)).map((t) => {
                      const cur = editForm.secondary_types || [];
                      const on = cur.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setEditForm({ ...editForm, secondary_types: on ? cur.filter((x) => x !== t) : [...cur, t] })}
                          style={{
                            padding: '5px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                            border: `1px solid ${on ? COLORS.primary : COLORS.border}`,
                            background: on ? COLORS.primary : 'white', color: on ? 'white' : COLORS.text, fontWeight: 600,
                          }}
                        >
                          {on ? '✓ ' : '+ '}{formatCategoryLabelShort(t)}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                    {t('Extra categories this place also appears under (e.g. a store that\'s also a café).')}
                  </div>
                </div>
              )}

              {user?.role === 'admin' && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>{t('Owners (Admin only)')}</div>
                  <OwnersManager establishmentId={establishment.id} />
                </div>
              )}

              {/* Hotel chain / brand — lodging only. Datalist: pick a known
                  chain or type one that isn't listed (the column is free text). */}
              {SUPPORTS_SHABBAT_AMENITIES.includes(editForm.type || establishment.type) && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>🏨 {t('Hotel chain / brand')}</div>
                  <input
                    list="jc-hotel-chains"
                    value={editForm.chain || ''}
                    onChange={(e) => setEditForm({ ...editForm, chain: e.target.value })}
                    placeholder={t('e.g. Marriott, Hilton, Accor — or type your own')}
                    style={{ ...styles.input, marginBottom: 0, maxWidth: 420 }}
                  />
                  <datalist id="jc-hotel-chains">
                    {HOTEL_CHAINS.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              )}
              {/* Official star rating — hotels / accommodations */}
              {SUPPORTS_SHABBAT_AMENITIES.includes(editForm.type || establishment.type) && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>⭐ {t('Official Star Rating')}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setEditForm({ ...editForm, rate: editForm.rate === star ? 0 : star })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, color: '#f59e0b', padding: 0, lineHeight: 1 }}
                        title={`${star} star${star > 1 ? 's' : ''}`}
                      >
                        {(editForm.rate || 0) >= star ? '★' : '☆'}
                      </button>
                    ))}
                    <span style={{ marginLeft: 8, fontSize: 13, color: COLORS.muted }}>
                      {editForm.rate ? `${editForm.rate}-${t('star')}` : t('Not rated')}
                    </span>
                  </div>
                </div>
              )}

              {/* Kosher-food tier dropdown removed — the tier is now derived
                  entirely from the Food amenity checkboxes below (see
                  inferKosherFoodLevel / the backend kosherRankExpr fallback). */}

              {/* Amenities — grouped checkboxes for hotels / apartments /
                  vacation rentals (Food · Kosher kitchen · Shabbat · Jewish life). */}
              {SUPPORTS_SHABBAT_AMENITIES.includes(editForm.type || establishment.type) && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>✡️ {t('Kosher & Shabbat Amenities')}</div>
                  {AMENITY_GROUPS.map((grp) => (
                    <div key={grp.key} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, margin: '6px 0 6px' }}>{t(grp.label)}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                        {SHABBAT_AMENITIES.filter((a) => a.group === grp.key).map((a) => {
                          const selected = Array.isArray(editForm.shabbat_amenities) ? editForm.shabbat_amenities : [];
                          const isOn = selected.includes(a.slug);
                          const toggle = () => {
                            const next = isOn ? selected.filter((s) => s !== a.slug) : [...selected, a.slug];
                            setEditForm({ ...editForm, shabbat_amenities: next });
                          };
                          return (
                            <label
                              key={a.slug}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 10px',
                                // Anche qui l'avviso si distingue: chi compila
                                // deve vedere che quella casella, spuntata,
                                // toglie l'etichetta invece di aggiungerla.
                                border: `1px solid ${isOn ? (a.warning ? COLORS.danger : COLORS.primary) : COLORS.border}`,
                                background: isOn ? (a.warning ? '#fef2f2' : '#eff6ff') : 'white',
                                color: isOn && a.warning ? COLORS.danger : undefined,
                                fontWeight: isOn && a.warning ? 700 : undefined,
                                borderRadius: 6, cursor: 'pointer', fontSize: 13,
                              }}
                            >
                              <input type="checkbox" checked={isOn} onChange={toggle} style={{ margin: 0 }} />
                              <span style={{ fontSize: 16 }}>{a.icon}</span>
                              <span>{t(a.label)}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(() => {
                const currentType = editForm.type || establishment.type;
                // Draw subtype options from the primary type AND any secondary
                // categories, so a merged multi-service listing (e.g. a shul that
                // also has a mikvah) can be tagged for each facet — Nusach for the
                // shul and Women/Men/Utensils for the mikvah — in one picker.
                const allTypes = [currentType, ...((editForm.secondary_types || establishment.secondary_types || []))];
                const opts = [...new Set(allTypes.flatMap((t) => SUBTYPE_OPTIONS[t] || []))];
                // STRAY VALUES MUST BE VISIBLE OR THEY CANNOT BE REMOVED.
                // Roladin Mamilla is a kosher_bakery carrying "Grocery, Dairy".
                // Grocery belongs to kosher_store, so it drew no chip here —
                // the public page showed it, the editor did not, and because
                // toggle() rebuilds the string from everything already
                // selected, touching any other chip carried it straight back.
                // A value you can see on the listing and cannot reach in the
                // form is worse than a wrong value: there is no way to fix it
                // short of the database. So anything stored that is not in the
                // category vocabulary gets a chip of its own, marked as out of
                // place, and clicking it removes it.
                const stored = (editForm.subtype || '').split(/\s*[,;]\s*/).map((s) => s.trim()).filter(Boolean);
                const strays = stored.filter((s) => !opts.some((o) => o.toLowerCase() === s.toLowerCase()));
                // Categories WITH a curated list (synagogue, restaurant, mikvah…)
                // pick from chips below. Categories WITHOUT one (tours, drivers,
                // museums…) still may hold an imported subtype value — so instead
                // of hiding the field, give a free-text box to view/edit it.
                if (!opts || opts.length === 0) {
                  return (
                    <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                      <div style={styles.detailLabel}>{t('Subtype / short descriptor (optional)')}</div>
                      <input
                        style={styles.input}
                        value={editForm.subtype || ''}
                        onChange={(e) => setEditForm({ ...editForm, subtype: e.target.value })}
                        placeholder={t('e.g. Tour of Jewish sites on request')}
                      />
                    </div>
                  );
                }
                const label = currentType === 'synagogue_shul' ? t('Nusach / subtype (pick one or more)')
                  : currentType === 'mikvah_ritual_bath' ? t('Mikvah for (pick one or more)')
                  : ['kosher_restaurant', 'kosher_eatery', 'kosher_catering', 'kosher_bakery'].includes(currentType) ? t('Kosher Kind (pick one or more)')
                  : t('Subtype / Tag (pick one or more)');
                const selected = (editForm.subtype || '').split(/\s*[,;\-]\s*/).map((s) => s.trim()).filter(Boolean);
                const isSelected = (opt) => selected.some((s) => s.toLowerCase() === opt.toLowerCase());
                const toggle = (opt) => {
                  const next = isSelected(opt)
                    ? selected.filter((s) => s.toLowerCase() !== opt.toLowerCase())
                    : [...selected, opt];
                  setEditForm({ ...editForm, subtype: next.join(', ') });
                };
                return (
                  <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                    <div style={styles.detailLabel}>{label}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {strays.map((s) => (
                        <button
                          key={`stray-${s}`}
                          type="button"
                          title={t('Not a value for this category — click to remove')}
                          onClick={() => setEditForm({
                            ...editForm,
                            subtype: stored.filter((x) => x !== s).join(', '),
                          })}
                          style={{
                            padding: '6px 12px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
                            border: `1px dashed ${COLORS.danger}`, background: '#fef2f2',
                            color: COLORS.danger, fontWeight: 600, fontFamily: 'inherit',
                          }}
                        >
                          {s} ✕
                        </button>
                      ))}
                      {opts.map((opt) => {
                        const active = isSelected(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => toggle(opt)}
                            style={{
                              background: active ? COLORS.primary : 'white',
                              color: active ? 'white' : COLORS.text,
                              border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
                              padding: '6px 12px',
                              borderRadius: 999,
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: active ? 600 : 500,
                            }}
                          >
                            {active ? '✓ ' : ''}{opt}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                      {t('Pick one or more from the list.')}
                    </div>
                  </div>
                );
              })()}

              {/* Community (synagogues secondary tag) */}
              {(hasAnyType({ type: editForm.type || establishment.type, secondary_types: editForm.secondary_types }, ['synagogue_shul'])) && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>{t('Community Type')}</div>
                  <input
                    list="community-list"
                    style={styles.input}
                    value={editForm.community}
                    onChange={(e) => setEditForm({ ...editForm, community: e.target.value })}
                    placeholder={t('Community / affiliation')}
                  />
                  <datalist id="community-list">
                    {(COMMUNITY_OPTIONS.synagogue_shul || []).map((o) => <option key={o} value={o} />)}
                  </datalist>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                    {t('Suggestions:')} {(COMMUNITY_OPTIONS.synagogue_shul || []).join(' · ')}
                  </div>
                </div>
              )}

              {/* Cuisine (restaurants secondary tag) — multi-select via checkboxes
                  plus a free-text field for any cuisine not in our preset list. */}
              {hasAnyType({ type: editForm.type || establishment.type, secondary_types: editForm.secondary_types }, FOOD_SERVICE_TYPES) && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>{t('Cuisine / Type (pick one or more)')}</div>
                  <CuisineMultiSelect
                    value={editForm.cuisine || ''}
                    options={CUISINE_OPTIONS[[editForm.type || establishment.type, ...(editForm.secondary_types || [])].find((t) => FOOD_SERVICE_TYPES.includes(t))] || []}
                    onChange={(v) => setEditForm({ ...editForm, cuisine: v })}
                    allowCustom={user?.role === 'admin'}
                  />
                </div>
              )}

              {/* Service — food places. Indoor and outdoor dining are the two
                  real choices; dine_in is just "either of them" and is kept in
                  step here rather than asked for a third time (the list-page
                  filter and older rows still read that column). Takeaway is
                  independent — a place can be both. */}
              {hasAnyType({ type: editForm.type || establishment.type, secondary_types: editForm.secondary_types }, DINE_TYPES) && (
                <div style={styles.detailField}>
                  <div style={styles.detailLabel}>{t('Service')}</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: COLORS.text, marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!editForm.seating_indoor}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        seating_indoor: e.target.checked,
                        dine_in: e.target.checked || !!editForm.seating_outdoor,
                      })}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    🪑 {t('Indoor dining (seating inside)')}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: COLORS.text, marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!editForm.seating_outdoor}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        seating_outdoor: e.target.checked,
                        dine_in: e.target.checked || !!editForm.seating_indoor,
                      })}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    🌤️ {t('Outdoor dining (terrace, garden, pavement)')}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: COLORS.text }}>
                    <input
                      type="checkbox"
                      checked={!!editForm.takeaway}
                      onChange={(e) => setEditForm({ ...editForm, takeaway: e.target.checked })}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    🥡 {t('Takeaway (order to go)')}
                  </label>
                  {/* Legacy rows that say "you can eat in" without saying where.
                      Shown only for those, so the owner can be specific. */}
                  {editForm.dine_in && !editForm.seating_indoor && !editForm.seating_outdoor && (
                    <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 8 }}>
                      🍽️ {t('Listed as dine-in — tick indoor or outdoor above to say where.')}
                    </div>
                  )}
                </div>
              )}
              {/* Seasonal flags — a sukkah for Sukkot and whether they're open
                  (kosher-for-Passover) during Pesach. Shown for anywhere you eat,
                  stay, or buy food (incl. hotels/bakeries/shops), not just
                  sit-down food service — "Open on Pesach" matters most for hotels. */}
              {hasAnyType({ type: editForm.type || establishment.type, secondary_types: editForm.secondary_types }, SEASONAL_TYPES) && (
                <div style={styles.detailField}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: COLORS.text, marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!editForm.has_sukkah}
                      onChange={(e) => setEditForm({ ...editForm, has_sukkah: e.target.checked })}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    🌿 {t('Has a sukkah (for eating during Sukkot)')}
                  </label>
                  {GLUTEN_FREE_TYPES.includes(editForm.type || establishment.type) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: COLORS.text, marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={!!editForm.gluten_free}
                        onChange={(e) => setEditForm({ ...editForm, gluten_free: e.target.checked })}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                      />
                      🌾 {t('Gluten-free options available')}
                    </label>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: COLORS.text }}>
                    <input
                      type="checkbox"
                      checked={!!editForm.open_on_pesach}
                      onChange={(e) => setEditForm({ ...editForm, open_on_pesach: e.target.checked })}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    🫓 {t('Open on Pesach (kosher for Passover)')}
                  </label>
                </div>
              )}

              {/* Accessibility — applies to any place type. */}
              <div style={styles.detailField}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: COLORS.text }}>
                  <input
                    type="checkbox"
                    checked={!!editForm.wheelchair_accessible}
                    onChange={(e) => setEditForm({ ...editForm, wheelchair_accessible: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  ♿ {t('Wheelchair accessible (disabled access)')}
                </label>
              </div>

              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>{t('Open / Season (summary)')}</div>
                <input
                  style={styles.input}
                  value={editForm.open_days}
                  onChange={(e) => setEditForm({ ...editForm, open_days: e.target.value })}
                  placeholder={t('e.g. Daily · Summer only · Open Pesach & Sukkot · June–September')}
                />
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                  {t('For seasonal places, use this for the period you’re open (e.g. “Summer only”, “Open for Pesach”).')}
                </div>
              </div>

              {/* Contact people — two slots, each with WhatsApp + email */}
              <div style={{ ...styles.detailField, gridColumn: '1 / -1', borderTop: `1px solid ${COLORS.border}`, paddingTop: 16, marginTop: 8 }}>
                <div style={{ ...styles.detailLabel, marginBottom: 8 }}>
                  {hasAnyType({ type: editForm.type || establishment.type, secondary_types: editForm.secondary_types }, ['synagogue_shul']) ? t('RABBI / CONTACT PEOPLE') : t('CONTACT PEOPLE')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                  {/* Contact 1 */}
                  <div style={{ background: COLORS.bg, padding: 12, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('Contact 1')}</div>
                    <input
                      style={{ ...styles.input, marginBottom: 6 }}
                      value={editForm.contact_name}
                      onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                      placeholder={t('Contact name')}
                    />
                    <input
                      style={{ ...styles.input, marginBottom: 6 }}
                      value={editForm.contact_title}
                      onChange={(e) => setEditForm({ ...editForm, contact_title: e.target.value })}
                      placeholder={t('Title / role (e.g. Rabbi, Manager)')}
                    />
                    {/* IL TELEFONO DEL CONTATTO MANCAVA DAL MODULO.
                        Menachem, 25/10/2026: «se vado in edit non vedo il
                        numero». C'erano nome, ruolo, WhatsApp ed email, ma non
                        `contact_phone` — ed e' li' che sta il numero di 6.658
                        contatti su 12.954, i mashgichim dei registri del
                        rabbinato compresi. Il numero si vedeva sulla pagina e
                        non si poteva ne' leggere ne' correggere dal pannello,
                        e chi voleva aggiungerlo finiva per scriverlo nella
                        casella accanto, lasciando due numeri diversi in due
                        campi. */}
                    <input
                      style={{ ...styles.input, marginBottom: 6 }}
                      value={editForm.contact_phone}
                      onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                      placeholder={t('Phone number')}
                    />
                    <input
                      style={{ ...styles.input, marginBottom: 6 }}
                      value={editForm.contact_whatsapp}
                      onChange={(e) => setEditForm({ ...editForm, contact_whatsapp: e.target.value })}
                      placeholder={t('WhatsApp number, if different')}
                    />
                    {((editForm.contact_whatsapp || '').trim() || (editForm.contact_phone || '').trim()) && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: COLORS.text, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editForm.contact_has_whatsapp === false}
                          onChange={(e) => setEditForm({ ...editForm, contact_has_whatsapp: !e.target.checked })}
                        />
                        📵 {t('This number is not on WhatsApp')}
                      </label>
                    )}
                    <input
                      style={styles.input}
                      type="email"
                      value={editForm.contact_email}
                      onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                      placeholder={t('Email')}
                    />
                  </div>
                  {/* Contact 2 */}
                  <div style={{ background: COLORS.bg, padding: 12, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('Contact 2 (optional)')}</div>
                    <input
                      style={{ ...styles.input, marginBottom: 6 }}
                      value={editForm.contact2_name}
                      onChange={(e) => setEditForm({ ...editForm, contact2_name: e.target.value })}
                      placeholder={t('Name')}
                    />
                    <input
                      style={{ ...styles.input, marginBottom: 6 }}
                      value={editForm.contact2_title}
                      onChange={(e) => setEditForm({ ...editForm, contact2_title: e.target.value })}
                      placeholder={t('Title / role (e.g. Rabbi, Manager)')}
                    />
                    <input
                      style={{ ...styles.input, marginBottom: 6 }}
                      value={editForm.contact2_whatsapp}
                      onChange={(e) => setEditForm({ ...editForm, contact2_whatsapp: e.target.value })}
                      placeholder={t('Phone / WhatsApp number')}
                    />
                    {(editForm.contact2_whatsapp || '').trim() && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: COLORS.text, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editForm.contact2_has_whatsapp === false}
                          onChange={(e) => setEditForm({ ...editForm, contact2_has_whatsapp: !e.target.checked })}
                        />
                        📵 {t('This number is not on WhatsApp')}
                      </label>
                    )}
                    <input
                      style={styles.input}
                      type="email"
                      value={editForm.contact2_email}
                      onChange={(e) => setEditForm({ ...editForm, contact2_email: e.target.value })}
                      placeholder={t('Email')}
                    />
                  </div>
                </div>
              </div>

              {/* Kosher supervision — type-or-pick: the datalist offers every
                  certifier already on the site (picking one links its logo + its
                  "places we certify" view), but you can also just TYPE a free
                  name for a one-off hechsher that isn't worth its own listing —
                  it stores as plain text (no logo, no DB record). Up to two,
                  comma-joined. Level below is a chip multi-select. */}
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={{ ...styles.detailLabel, marginBottom: 8 }}>✡️ {t('KOSHER SUPERVISION')}</div>
                {/* Any number of certifying bodies, stored as one comma-joined
                    supervision string; the matching layer splits it back out. */}
                <SupervisionPicker
                  value={editForm.supervision}
                  onChange={(v) => setEditForm({ ...editForm, supervision: v })}
                  certs={freshCerts || koshCerts}
                />
                {(() => {
                  const allT = [editForm.type || establishment.type, ...(editForm.secondary_types || establishment.secondary_types || [])];
                  const isMikvah = allT.includes('mikvah_ritual_bath');
                  const isFood = allT.some((x) => FOOD_SERVICE_TYPES.includes(x));
                  // A pure mikvah has no food "level" — its kosher detail is one
                  // or more construction/halachic methods (Bor al gabei bor, Mei
                  // Gheshamim, Hashaka, Zria). Who it serves (Women/Men/Utensils)
                  // is the SUBCATEGORY (subtype picker above), not here.
                  const levelOpts = isMikvah && !isFood ? MIKVAH_LEVELS : KOSHER_LEVELS;
                  const label = isMikvah && !isFood ? t('Mikvah kosher detail (pick one or more)') : t('Kosher level (pick one or more)');
                  const selected = splitCuisines(editForm.supervision_level || '');
                  const isOn = (v) => selected.some((s) => s.toLowerCase() === v.toLowerCase());
                  const toggle = (v) => {
                    const next = isOn(v)
                      ? selected.filter((s) => s.toLowerCase() !== v.toLowerCase())
                      : [...selected, v];
                    setEditForm({ ...editForm, supervision_level: joinCuisines(next) });
                  };
                  return (
                    <>
                      <div style={{ ...styles.detailLabel, marginBottom: 6, marginTop: 4 }}>{label}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {levelOpts.map((lvl) => {
                          const on = isOn(lvl);
                          return (
                            <button
                              key={lvl}
                              type="button"
                              onClick={() => toggle(lvl)}
                              style={{
                                padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                                border: `1px solid ${on ? '#92400e' : '#d1d5db'}`,
                                background: on ? '#fef3c7' : 'white',
                                color: on ? '#92400e' : '#1f2937',
                                cursor: 'pointer', fontFamily: 'inherit',
                              }}
                            >{on ? '✓ ' : '+ '}{t(lvl)}</button>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* WHEN THE TEUDAH RUNS OUT. The one date that makes a
                  kashrut record go stale on its own instead of staying
                  true forever. It is printed on the certificate on the
                  wall, so the owner is the person who knows it, and once
                  it is here the admin expiry list can ask to have it
                  checked rather than everyone assuming. */}
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>🗓️ {t('Hechsher valid until')}</div>
                <input
                  type="date"
                  style={styles.input}
                  value={editForm.kosher_valid_to || ''}
                  onChange={(e) => setEditForm({ ...editForm, kosher_valid_to: e.target.value })}
                />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                  {t('The expiry date on the certificate. Leave empty if you do not know it.')}
                </div>
              </div>

              {/* Menus (restaurants/catering) — multi-menu manager with OCR upload */}
              {(editForm.type === 'kosher_restaurant' || establishment.type === 'kosher_restaurant' || editForm.type === 'kosher_eatery' || establishment.type === 'kosher_eatery' || editForm.type === 'kosher_catering' || establishment.type === 'kosher_catering') && (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>🍽️ {t('Menus')}</div>
                  <MenusManager establishmentId={establishment.id} />
                </div>
              )}

              {/* WHEN A DATED PROGRAMME RUNS. Shown only for the kinds of
                  place that happen once rather than being open all year —
                  a Pesach hotel, a Chanukah ski week, a cruise. Asking a
                  restaurant for its start and end date would be noise. */}
              {DATED_TYPES.includes(editForm.type) && (
                <>
                  <div style={styles.detailField}>
                    <div style={styles.detailLabel}>📅 {t('Starts')}</div>
                    <input
                      type="date"
                      style={styles.input}
                      value={editForm.date_from || ''}
                      onChange={(e) => setEditForm({ ...editForm, date_from: e.target.value })}
                    />
                  </div>
                  <div style={styles.detailField}>
                    <div style={styles.detailLabel}>📅 {t('Ends')}</div>
                    <input
                      type="date"
                      style={styles.input}
                      value={editForm.date_to || ''}
                      onChange={(e) => setEditForm({ ...editForm, date_to: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* Pricing */}
              <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabel}>💰 {t('Pricing Info')}</div>
                <textarea
                  style={{ ...styles.input, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }}
                  value={editForm.price_info}
                  onChange={(e) => setEditForm({ ...editForm, price_info: e.target.value })}
                  placeholder={t('Pricing info')}
                />
                <CampiTradotti campo="price_info" valori={editForm.translations} onChange={(tr) => setEditForm({ ...editForm, translations: tr })} multiriga />
              </div>
            </div>

            {/* Editable map */}
            <div style={{ marginTop: 20 }}>
              <div style={{ ...styles.detailLabel, marginBottom: 8 }}>{t('Location on map')}</div>
              <SingleMap
                establishment={establishment}
                editable={true}
                position={editForm.coordinates}
                onPositionChange={(c) => setEditForm({ ...editForm, coordinates: c })}
                height={300}
                boundary={editForm.boundary || null}
              />
              {editForm.coordinates && (
                <button
                  onClick={() => setEditForm({ ...editForm, coordinates: null })}
                  style={{ marginTop: 8, background: 'transparent', border: `1px solid ${COLORS.muted}`, color: COLORS.muted, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                >
                  {t('Clear pin (no coordinates)')}
                </button>
              )}

              {/* Eruv perimeter — paste GeoJSON or a Google My Maps / Earth KML
                  export; we convert it and draw it on the map above. */}
              {(editForm.type || establishment.type) === 'eruv' && (() => {
                const b = editForm.boundary;
                const featCount = b ? (b.type === "FeatureCollection" ? (b.features || []).length : 1) : 0;
                const load = (text) => {
                  const g = parseBoundaryInput(text);
                  if (g) setEditForm({ ...editForm, boundary: g });
                  else alert(t("Couldn't read a boundary from that. Paste valid GeoJSON, or a KML export with a line/polygon."));
                };
                return (
                  <div style={{ marginTop: 14, padding: 12, background: COLORS.bg, borderRadius: 8 }}>
                    <div style={{ ...styles.detailLabel, marginBottom: 6 }}>🧵 {t('Eruv perimeter (GeoJSON or KML)')}</div>
                    {b
                      ? <div style={{ fontSize: 13, color: COLORS.success, marginBottom: 8 }}>✓ {t('Boundary loaded')} ({featCount} {featCount === 1 ? t('shape') : t('shapes')}) — {t('shown on the map above.')}</div>
                      : <div style={{ fontSize: 13, color: COLORS.muted, marginBottom: 8 }}>{t('No perimeter yet. Paste GeoJSON, or upload a KML from Google My Maps / Google Earth.')}</div>}
                    <textarea
                      value={boundaryText}
                      onChange={(e) => setBoundaryText(e.target.value)}
                      placeholder={t('Paste GeoJSON ({"type":"FeatureCollection",…}) or KML here…')}
                      style={{ width: '100%', minHeight: 90, fontFamily: 'ui-monospace, monospace', fontSize: 12, padding: 8, border: `1px solid ${COLORS.border}`, borderRadius: 6, boxSizing: 'border-box', resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
                      <button type="button" onClick={() => load(boundaryText)} disabled={!boundaryText.trim()}
                        style={{ background: COLORS.primary, color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: boundaryText.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, opacity: boundaryText.trim() ? 1 : 0.6 }}>
                        {t('Load boundary')}
                      </button>
                      <label style={{ background: 'white', border: `1px solid ${COLORS.border}`, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                        ⬆ {t('Upload file')}
                        <input type="file" accept=".geojson,.json,.kml,application/geo+json,application/vnd.google-earth.kml+xml,text/xml" style={{ display: 'none' }}
                          onChange={(e) => { const f = e.target.files && e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => { setBoundaryText(String(r.result || '')); load(String(r.result || '')); }; r.readAsText(f); e.target.value = ''; }} />
                      </label>
                      {b && (
                        <button type="button" onClick={() => { setEditForm({ ...editForm, boundary: null }); setBoundaryText(''); }}
                          style={{ background: 'transparent', border: `1px solid ${COLORS.muted}`, color: COLORS.muted, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                          {t('Clear boundary')}
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>
                      {t('Tip: in Google My Maps, open the eruv layer → ⋮ → “Export to KML/KMZ” (KML, not KMZ), then upload it here.')}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Mark closed toggle */}
            <div style={{ marginTop: 16, padding: 12, background: editForm.is_closed ? '#fee2e2' : COLORS.bg, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={!!editForm.is_closed}
                  onChange={(e) => setEditForm({ ...editForm, is_closed: e.target.checked })}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600, color: editForm.is_closed ? COLORS.danger : COLORS.text }}>
                  🚫 {t('Mark this place as permanently closed')}
                </span>
              </label>
              {editForm.is_closed && (
                <span style={{ fontSize: 12, color: COLORS.danger }}>
                  {t('Closed places are hidden from default lists but appear in search.')}
                </span>
              )}
            </div>

            {/* No longer kosher toggle — distinct from closed. Business still
                operates but has lost its kosher certification (or never had
                one and shouldn't be listed as kosher). Hidden from default
                listings; detail page still resolves so Google search results
                pointing here keep working. */}
            <div style={{ marginTop: 12, padding: 12, background: editForm.no_longer_kosher ? '#fef3c7' : COLORS.bg, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={!!editForm.no_longer_kosher}
                  onChange={(e) => setEditForm({ ...editForm, no_longer_kosher: e.target.checked })}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600, color: editForm.no_longer_kosher ? '#92400e' : COLORS.text }}>
                  ⚠️ {t('Mark this place as no longer kosher')}
                </span>
              </label>
              {editForm.no_longer_kosher && (
                <span style={{ fontSize: 12, color: '#92400e' }}>
                  {t('Hidden from default lists; detail page still loads for SEO.')}
                </span>
              )}
            </div>

            {/* Sticky action bar — pinned to the bottom of the viewport so Save
                is always reachable without scrolling to the end of the form. */}
            <div ref={saveBarRef} style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000,
              background: 'white', borderTop: `1px solid ${COLORS.border}`,
              boxShadow: '0 -2px 12px rgba(0,0,0,0.10)', padding: '10px 16px',
              display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end',
            }}>
              <span style={{ marginRight: 'auto', fontSize: 13, color: COLORS.muted, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t('Editing')} “{editForm.name || establishment.name}”
              </span>
              <button style={styles.buttonOutline} onClick={cancelEdit} disabled={saving}>{t('Cancel')}</button>
              <button style={styles.button} onClick={saveEdit} disabled={saving}>
                {saving ? t('Saving…') : '💾 ' + t('Save Changes')}
              </button>
            </div>

            {/* Admin: merge a duplicate into this place. */}
            {/* Dopo un'unione il modulo aperto parla di una scheda che non
                esiste piu' com'era: si chiude e si ricarica, cosi' chi vuole
                continuare a modificare riparte dai valori uniti. Il confronto
                con la fotografia in saveEdit basterebbe gia' a non perdere
                niente, ma un modulo che mostra i vecchi valori accanto alla
                pagina che ne mostra di nuovi e' comunque un posto dove
                sbagliare. */}
            {user?.role === 'admin' && (
              <MergePlacesTool
                keep={establishment}
                onMerged={() => { setEditMode(false); setEditForm({}); editSeed.current = {}; refresh(); }}
              />
            )}

            {/* Danger zone — admin-only permanent delete. */}
            {user?.role === 'admin' && (
              <div style={{ marginTop: 20, padding: 12, border: `1px solid ${COLORS.danger}`, borderRadius: 8, background: '#fff5f5' }}>
                <div style={{ fontWeight: 700, color: COLORS.danger, marginBottom: 6 }}>{t('Danger zone')}</div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`${t('Permanently DELETE')} "${establishment.name}"?\n\n${t('This removes the place and everything attached to it (rooms, reviews, messages, bookings, news links). This cannot be undone.')}`)) return;
                    try {
                      await api.deleteEstablishment(establishment.id);
                      alert(t('Place deleted.'));
                      navigateTo('/');
                    } catch (e) {
                      alert(`${t('Delete failed:')} ${e.message || e}`);
                    }
                  }}
                  style={{ background: COLORS.danger, color: 'white', border: 'none', padding: '10px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}
                >
                  🗑 {t('Delete this place')}
                </button>
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 6 }}>{t('Admins only · permanent · cascades to all attached records.')}</div>
              </div>
            )}
            {/* Spacer so the fixed Save bar never covers the last field. */}
            <div style={{ height: 76 }} />
          </div>
        ) : (
          <div style={styles.detailGrid}>
            {establishment.address && (
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Address')}</div>
                <div style={styles.detailValue}>
                  {(() => {
                    const fullAddr = [
                      establishment.address,
                      establishment.zip,
                      establishment.city,
                      establishment.state,
                      establishment.country,
                    ].filter(Boolean).join(', ');
                    // Prefer coordinates if available for precise navigation
                    const c = establishment.coordinates;
                    const query = c && typeof c.x === 'number'
                      ? `${c.y},${c.x}`
                      : encodeURIComponent(fullAddr);
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
                    return (
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => api.trackClick(establishment.id, 'directions')}
                        title={t("Open in Google Maps / navigation")}
                        style={{ color: COLORS.primary, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
                      >
                        <span>
                          {establishment.address}
                          {establishment.zip ? `, ${establishment.zip}` : ''}
                          {establishment.floor ? ` (${establishment.floor})` : ''}
                        </span>
                        <span style={{ background: COLORS.primary, color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                          🧭 {t('Directions')}
                        </span>
                      </a>
                    );
                  })()}
                </div>
              </div>
            )}
            {(() => (
            // I campi chiusi NON ci sono nella risposta: senza contarli qui, una
            // scheda che ha solo telefono e WhatsApp (Ela Tourism) non apriva
            // affatto il riquadro Contatti e i lucchetti non si vedevano.
            (establishment.email || establishment.donation_url || establishment.order_url || establishment.reservation_url
              || establishment.phone || establishment.website || (establishment.contacts_hidden || []).length > 0) && (
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Contact')}</div>
                <div style={{ ...styles.detailValue, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {phoneWithCountry(establishment.phone, establishment.country) && (
                    <a
                      href={`tel:${phoneWithCountry(establishment.phone, establishment.country)}`}
                      onClick={() => api.trackClick(establishment.id, 'phone')}
                      style={{ background: COLORS.primary, color: 'white', padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      {/* Anche qui il numero per esteso, non «Call»: stessa
                          ragione del contatto piu' sotto. */}
                      <PhoneIcon size={15} /> {establishment.phone}
                    </a>
                  )}
                  {establishment.toll_free && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <a
                        href={`tel:${(establishment.toll_free || '').replace(/[^\d+]/g, '')}`}
                        onClick={() => api.trackClick(establishment.id, 'phone')}
                        title={t('Toll-free — works only when dialled from inside the country')}
                        style={{ background: '#0e7490', color: 'white', padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <PhoneIcon size={15} /> {t('Toll-free')} {establishment.toll_free}
                      </a>
                      <span style={{ fontSize: 11, color: COLORS.muted }}>{t('free within')} {t(establishment.country || '')}</span>
                    </span>
                  )}
                  {(establishment.phone || establishment.whatsapp_number) && establishment.has_whatsapp !== false && (
                    <a
                      href={whatsappUrl(phoneForWhatsApp(establishment.whatsapp_number || establishment.phone, establishment.country), establishment.name, null, placeLink(establishment))}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => api.trackClick(establishment.id, 'whatsapp')}
                      style={{ background: '#25D366', color: 'white', padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <WhatsAppIcon size={15} /> {t("WhatsApp")}
                    </a>
                  )}
                  {establishment.ai_whatsapp_number && (
                    <a
                      href={whatsappUrl(phoneForWhatsApp(establishment.ai_whatsapp_number, establishment.country), establishment.name, null, placeLink(establishment))}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => api.trackClick(establishment.id, 'whatsapp')}
                      title={t("Chat with their AI assistant — instant replies")}
                      style={{ background: 'linear-gradient(135deg, #6366f1, #25D366)', color: 'white', padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      🤖 <WhatsAppIcon size={15} /> {t('AI Chat')}
                    </a>
                  )}
                  {/* UN BOTTONE SOLO, E POI LA PAGINA E' QUELLA DI PRIMA.
                      Telefono, WhatsApp, email e sito non arrivano insieme
                      alla scheda (services/gate-contacts.js): si chiedono
                      premendo qui. NON serve un account - serve un gesto.

                      La risposta viene rimessa dentro `establishment`, e a
                      quel punto i bottoni veri qui sopra si accendono da
                      soli: nessuno di loro ha dovuto imparare niente. E' il
                      motivo per cui questa modifica tocca tre punti invece
                      di quindici.

                      Un bottone solo e non uno per canale: finche' non si
                      preme non si sa nemmeno quanti ce ne sono, e tre
                      pastiglie grigie in fila occupavano la riga per dire
                      «non te lo dico». */}
                  {(establishment.contacts_hidden || []).length > 0 && (
                    <button
                      onClick={async () => {
                        if (caricoContatti) return;
                        setCaricoContatti(true);
                        try {
                          const c = await api.revealContacts(establishment.id);
                          setEstablishment((e) => ({ ...e, ...c, contacts_hidden: null }));
                          api.trackClick(establishment.id, 'contacts');
                        } catch (_) {
                          // la rete ha detto di no: si torna al bottone, non
                          // si lascia l'utente davanti a una riga vuota
                          setCaricoContatti(false);
                        }
                      }}
                      style={{ background: COLORS.primary, color: 'white', padding: '8px 14px', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: caricoContatti ? 'wait' : 'pointer', opacity: caricoContatti ? 0.7 : 1 }}
                    >
                      <PhoneIcon size={15} /> {t(caricoContatti ? 'One moment…' : 'Show contact details')}
                    </button>
                  )}
                  {/* IL BOTTONE EMAIL RESTA ANCHE QUANDO L'INDIRIZZO E'
                      NASCOSTO, perche' non e' un mailto: apre il modulo di
                      contatto SUL SITO, e il messaggio lo inoltra il server
                      senza mai scrivere l'indirizzo nella pagina.

                      Nasconderlo sarebbe stato il contrario di quello che
                      serve - Menachem, 23/10/2026: «voglio che tutto il
                      WhatsApp, email e siti passino tramite il mio sito».
                      Questo bottone E' il passaggio dal sito. */}
                  {(establishment.email || (establishment.contacts_hidden || []).includes('email')) && (
                    <button
                      onClick={() => { api.trackClick(establishment.id, 'email'); setShowContact(true); }}
                      style={{ background: COLORS.primary, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <EmailIcon size={15} /> {t('Email')}
                    </button>
                  )}
                  {establishment.type !== 'kosher_hotel' && establishment.website && (
                    true ? (
                      <a
                        href={establishment.website.startsWith('http') ? establishment.website : `https://${establishment.website}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => api.trackClick(establishment.id, 'website')}
                        style={{ background: '#6b7280', color: 'white', padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <WebIcon size={15} /> {t('Website')}
                      </a>
                    ) : (
                      // Guests: the website button is greyed and prompts a free
                      // account instead of opening the link.
                      <button
                        onClick={() => onLogin && onLogin()}
                        title={t("Create a free account to visit the website")}
                        style={{ background: '#9ca3af', color: 'white', padding: '8px 14px', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: 0.75 }}
                      >
                        <WebIcon size={15} /> {t('Website')} 🔒
                      </button>
                    )
                  )}
                  {establishment.donation_url && (
                    <a
                      href={establishment.donation_url.startsWith('http') ? establishment.donation_url : `https://${establishment.donation_url}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => api.trackClick(establishment.id, 'donate')}
                      style={{ background: '#e11d48', color: 'white', padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      ❤️ {t('Donate')}
                    </a>
                  )}
                  {establishment.order_url && (
                    <a
                      href={establishment.order_url.startsWith('http') ? establishment.order_url : `https://${establishment.order_url}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => api.trackClick(establishment.id, 'order')}
                      style={{ background: '#059669', color: 'white', padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      🛍️ {t('Order Online')}
                    </a>
                  )}
                  {establishment.reservation_url && (
                    <a
                      href={establishment.reservation_url.startsWith('http') ? establishment.reservation_url : `https://${establishment.reservation_url}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => api.trackClick(establishment.id, 'reserve')}
                      style={{ background: '#7c3aed', color: 'white', padding: '8px 14px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      📅 {t(reservationLabel(establishment))}
                    </a>
                  )}
                </div>
              </div>
            )))()}
            {/* Booking button — the place's own booking link (incl. hotels.com
                links from the Expedia partnership). Flows inline next to
                Address/Contact; affiliate disclosure below per FTC guidelines. */}
            {(() => {
              // Show the Book button only when there's an actual booking link the
              // owner set — no auto-generated hotels.com search.
              const href = (establishment.booking_url || '').trim();
              if (!href) return null;
              const affiliate = /hotels\.com|expedia|camref|affiliate/i.test(href);
              return (
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Book this place')}</div>
                <div style={styles.detailValue}>
                  <a
                    href={href}
                    target="_blank"
                    rel="nofollow sponsored noopener noreferrer"
                    onClick={() => api.trackClick(establishment.id, 'booking')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: COLORS.accent, color: COLORS.primaryDark,
                      padding: '10px 18px', borderRadius: 8, textDecoration: 'none',
                      fontWeight: 700, fontSize: 15, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    }}
                  >
                    🛏️ {t('Book Now')} →
                  </a>
                  {affiliate && (
                    <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>
                      {t('Affiliate link — we may earn a commission if you book through it, at no extra cost to you.')}
                    </div>
                  )}
                </div>
              </div>
              );
            })()}
            {(establishment.facebook_url || establishment.instagram_url || establishment.youtube_url || establishment.whatsapp_channel_url || establishment.tiktok_url || establishment.x_url || establishment.linkedin_url || establishment.vk_url) && (
              <div style={styles.detailField}>
                <div style={styles.detailLabel}>{t('Social')}</div>
                <div style={{ ...styles.detailValue, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <SocialIcons establishment={establishment} user={user} onLogin={onLogin} />
                </div>
              </div>
            )}

            {/* Contact people (Rabbi / responsible). Builds up to two entries
                — Contact 1 falls back to the legacy contact_phone for WhatsApp
                so older data still surfaces a button. Each contact renders as
                its own card with WhatsApp + Email CTAs. */}
            {(() => {
              // an email here (no direct WhatsApp); booking goes through the site
              // or the affiliate link. A contact with no email is dropped for them.
              const contacts = [
                {
                  name: establishment.contact_name,
                  title: establishment.contact_title,
                  // Tutti i numeri della persona, non uno solo — e qui stava
                  // la seconda meta' del guasto: l'elenco preferiva
                  // `contact_phone` e questa riga `contact_whatsapp`, cosi'
                  // lo stesso referente mostrava due numeri diversi nei due
                  // posti. Vedi numeriContatto in api.js.
                  phones: numeriContatto(establishment.contact_phone, establishment.contact_whatsapp),
                  // WhatsApp only when the number is actually on WhatsApp.
                  whatsapp: establishment.contact_has_whatsapp === false ? null : (establishment.contact_whatsapp || establishment.contact_phone),
                  email: establishment.contact_email,
                },
                {
                  name: establishment.contact2_name,
                  title: establishment.contact2_title,
                  phones: numeriContatto(establishment.contact2_whatsapp),
                  whatsapp: establishment.contact2_has_whatsapp === false ? null : establishment.contact2_whatsapp,
                  email: establishment.contact2_email,
                },
              ].filter((c) => c.email || c.name || c.whatsapp || c.phones.length)
                // Ogni bottone esiste solo se porta da qualche parte: un nome
                // utente di WhatsApp («@Rabbi.Landa») si scrive ma non si
                // chiama, e un campo che dice «Israel» non e' nessuna delle due.
                // E dove il contatto e' un nome utente, il bottone lo SCRIVE.
                // Per gli altri il bottone «Chiama» porta il numero per
                // esteso (25/10/2026) e il nome utente e' la stessa cosa:
                // l'unico modo di riconoscere la persona, e di cercarla a
                // mano dentro WhatsApp se il collegamento non si apre. Qui
                // poi il bottone «Chiama» non c'e', quindi senza questo il
                // visitatore non vedrebbe NIENTE di quel contatto.
                .map((c) => ({
                  ...c,
                  tels: c.phones.map((p) => phoneWithCountry(p, establishment.country)).filter(Boolean),
                  wa: phoneForWhatsApp(c.whatsapp, establishment.country),
                  handle: isWhatsAppHandle(c.whatsapp)
                    ? `@${phoneForWhatsApp(c.whatsapp, establishment.country)}` : null,
                }));
              if (contacts.length === 0) return null;

              const label = establishment.type === 'synagogue_shul'
                ? (contacts.length > 1 ? t('Rabbi / Contact People') : t('Rabbi / Contact Person'))
                : (contacts.length > 1 ? t('Contact People') : t('Contact Person'));

              return (
                <div style={{ ...styles.detailField, gridColumn: '1 / -1' }}>
                  <div style={styles.detailLabel}>{label}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                    {contacts.map((c, idx) => (
                      <div key={idx} style={{ background: COLORS.bg, padding: 12, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                        {c.name && (
                          <div style={{ fontWeight: 600, marginBottom: c.title ? 2 : 6 }}>👤 {c.name}</div>
                        )}
                        {c.title && (
                          <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 6, marginLeft: 20 }}>{c.title}</div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {c.tels.map((tel) => (
                            <a
                              key={tel}
                              href={`tel:${tel}`}
                              onClick={() => api.trackClick(establishment.id, 'phone')}
                              style={{ background: COLORS.primary, color: 'white', padding: '6px 12px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                              {/* IL NUMERO, NON LA PAROLA «CALL».
                                  Menachem, 25/10/2026, su Pizza Patzatz di
                                  Shoham: «perché qui c'è il bottone Call per il
                                  mashghaich ma non c'è numero?». Il numero
                                  c'era — 052-7168915, dietro il clic che apre i
                                  recapiti — ma il bottone scriveva «Call» e lo
                                  teneva solo dentro il link tel:. Da un
                                  telefono si chiama lo stesso; da un computer
                                  non succede niente e non si puo' nemmeno
                                  leggerlo per comporlo altrove. Scritto per
                                  esteso serve a tutti e due, e resta
                                  cliccabile. */}
                              <PhoneIcon size={13} /> {tel}
                            </a>
                          ))}
                          {c.wa && (
                            <a
                              href={whatsappUrl(c.wa, establishment.name, c.name, placeLink(establishment))}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => api.trackClick(establishment.id, 'whatsapp')}
                              style={{ background: '#25D366', color: 'white', padding: '6px 12px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                              <WhatsAppIcon size={13} /> {c.handle || (t("WhatsApp"))}
                            </a>
                          )}
                          {c.email && (
                            <a
                              href={mailtoUrl(c.email, establishment.name, c.name, placeLink(establishment))}
                              style={{ background: COLORS.primary, color: 'white', padding: '6px 12px', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                              <EmailIcon size={13} /> {t('Email')}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Kosher Certification — a single unified block: the certifier logo
            (when we have one) + the certifier name as a clear link to its page
            + the kosher level. Renders whenever there's a certifier OR a kosher
            level/detail (e.g. a mikvah's construction detail with no named body). */}
        {(establishment.supervision || establishment.supervision_level
          || hasCertifierLink(establishment)) && (() => {
          // Mikvaot store their CONSTRUCTION method (Bor al gabai bor / Bor min
          // hatzad / Built both ways) in `supervision` — that's a type, not a
          // certifying body, so it must not read as "Kosher Certification →
          // Certified by …". A mikvah with a real supervising Vaad still does.
          // Mikvah construction detail(s) live in `supervision` — one or more
          // methods, comma-separated (a "built both ways" mikvah = both Bor al
          // gabei bor AND Bor min hatzad). Treat it as a construction detail when
          // every comma-part is a known method.
          const MIKVAH_CONS = [(t("bor al gabei bor")), (t("bor al gabai bor")), (t("bor min hatzad")), (t("built both ways"))];
          const supParts = splitCuisines(establishment.supervision || '');
          const isMikvahType = establishment.type === 'mikvah_ritual_bath'
            && supParts.length > 0 && supParts.every((p) => MIKVAH_CONS.includes(p.trim().toLowerCase()));
          return (
          <div style={{ ...styles.detailHeader, marginTop: 20 }}>
            <h3 style={{ marginTop: 0, color: COLORS.primary, display: 'flex', alignItems: 'center', gap: 8 }}>{isMikvahType ? `✡️ ${t('Kosher supervision')}` : `✡️ ${t('Kosher Certification')}`}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              {!isMikvahType && (
              <CertLogos
                supervision={establishment.supervision}
                certifierIds={establishment.supervision_certifier_ids}
                certs={koshCerts}
                size={52}
                onOpen={(c) => navigateTo(buildPath('detail', null, c.id))}
              />
              )}
              <div>
                {(establishment.supervision || hasCertifierLink(establishment)) && (isMikvahType ? (
                <>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 4 }}>{t('Mikvah kosher detail')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {splitCuisines(establishment.supervision).map((v) => (
                    <span key={v} style={{ background: '#fef3c7', color: '#92400e', padding: '2px 10px', borderRadius: 999, fontSize: 13, fontWeight: 600 }}>💧 {v}</span>
                  ))}
                </div>
                </>
                ) : (<>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 2 }}>{t('Certified by')}</div>
                {(() => {
                  // Token by token, each with the certifier it links to —
                  // matched by name where the name matches, and otherwise by
                  // the stable id link, which is how a Hebrew supervision line
                  // reaches a certifier filed under its English name. See
                  // linkSupervisionTokens.
                  const parts = linkSupervisionTokens(
                    establishment.supervision, koshCerts, establishment.supervision_certifier_ids);
                  return (
                    <div style={{ fontSize: 16, fontWeight: 600 }}>
                      {parts.map(({ text: tok, cert }, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span style={{ color: COLORS.muted }}>, </span>}
                          {cert ? (
                            <a
                              href={buildPath('detail', null, cert.id)}
                              onClick={(e) => { e.preventDefault(); navigateTo(buildPath('detail', null, cert.id)); }}
                              style={{ color: COLORS.primary, textDecoration: 'underline' }}
                              title={`View ${tok}`}
                            >{tok}</a>
                          ) : (
                            <span style={{ color: COLORS.text }}>{tok}</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()}
                </>))}
                {establishment.supervision_level && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: establishment.supervision ? 8 : 0 }}>
                    {splitCuisines(establishment.supervision_level).map((lvl) => (
                      <span key={lvl} style={{ background: '#fef3c7', color: '#92400e', padding: '2px 10px', borderRadius: 999, fontSize: 13, fontWeight: 600 }}>{t(lvl)}</span>
                    ))}
                  </div>
                )}
                {/* WHEN THE CERTIFICATE RUNS OUT. Shown plainly rather
                    than buried: a hechsher that lapsed last month is the
                    one thing on this page a traveller must not take on
                    trust, and saying "we last knew it was valid until X"
                    is honest where showing the badge alone is not. */}
                {establishment.kosher_valid_to && (() => {
                  const until = new Date(establishment.kosher_valid_to);
                  const over = until < new Date(new Date().toDateString());
                  const shown = until.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
                  return (
                    <div style={{
                      marginTop: 8, fontSize: 13, fontWeight: 600,
                      color: over ? '#b91c1c' : COLORS.muted,
                    }}>
                      {over
                        ? `⚠️ ${t('This certificate expired on')} ${shown} — ${t('check with the place before relying on it.')}`
                        : `🗓️ ${t('Certificate valid until')} ${shown}`}
                    </div>
                  );
                })()}

                {/* DOVE L'ABBIAMO VISTA, E QUANDO.
                    Menachem, 27/10/2026: «la fonte di un posto che è
                    stato aggiunto può essere pubblica, non c'è problema
                    di dire questo posto è stato preso dal sito di,
                    anche con un link preciso, così quando c'è un
                    problema che non trovi la certificazione, c'è un
                    link a cui riferirti». E la data: «una persona può
                    vedere se è stato controllato qualche mese fa, si
                    ritiene tranquillo».
                    IL VERBO È «CONTROLLATO», NON «CERTIFICATO». Anche i
                    registri dei consigli restano indietro — è la cosa
                    che ci ha fatto perdere la giornata — quindi la riga
                    dice dove e quando abbiamo guardato, che è l'unica
                    cosa di cui possiamo rispondere noi. */}
                {Array.isArray(establishment.supervision_source) && establishment.supervision_source.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 12, color: COLORS.muted, lineHeight: 1.6 }}>
                    {establishment.supervision_source.map((s, i) => {
                      if (!s || !s.source) return null;
                      const quando = s.checked
                        ? new Date(s.checked).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
                        : null;
                      return (
                        <div key={s.source || i}>
                          {t('Checked on')}{' '}
                          {s.url ? (
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: COLORS.primary, textDecoration: 'underline' }}
                            >{s.name || s.source}</a>
                          ) : (s.name || s.source)}
                          {quando ? ` — ${quando}` : ''}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })()}

        {/* Recommended by — a rabbi or community vouching for the place. Kept
            OUT of the certification block above on purpose: a recommendation
            is not a hechsher, and sitting it among the supervision badges
            would read as one. Admin-set only. */}
        {String(establishment.recommended_by || '').trim() && (
          <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '12px 16px' }}>
            <span style={{ fontSize: 20 }}>👍</span>
            <span style={{ fontSize: 14, color: '#065f46' }}>
              {t('Recommended by')} <strong>{establishment.recommended_by}</strong>
            </span>
          </div>
        )}

        {/* Kosher-food + Shabbat headline badges for lodging — the at-a-glance
            "can I eat kosher / can I stay over Shabbos" answer. Renders for any
            lodging type that has a kosher-food tier or Shabbat amenities. */}
        {SUPPORTS_SHABBAT_AMENITIES.includes(establishment.type) &&
          (effectiveKosherFood(establishment) !== 'none' || isShabbatFriendly(establishment)) && (
          <div style={{ marginTop: 24 }}>
            <h2 style={{ color: COLORS.primary, marginBottom: 12 }}>🛎️ {t('At a glance')}</h2>
            <LodgingBadges est={establishment} size="md" />
          </div>
        )}

        {/* Service + seasonal flags — dine-in/takeaway, sukkah, Pesach, access. */}
        {(establishment.dine_in || establishment.takeaway || establishment.seating_indoor || establishment.seating_outdoor || establishment.has_sukkah || establishment.open_on_pesach || establishment.wheelchair_accessible || establishment.gluten_free) && (
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(() => {
              return seatingChips(establishment).map(([icon, label]) => (
                <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 600 }}>
                  {icon} {t(label)}
                </span>
              ));
            })()}
            {establishment.takeaway && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 600 }}>
                🥡 {t('Takeaway')}
              </span>
            )}
            {establishment.has_sukkah && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 600 }}>
                🌿 {t('Has a sukkah — eat in the sukkah during Sukkot')}
              </span>
            )}
            {establishment.open_on_pesach && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 600 }}>
                🫓 {t('Open on Pesach — kosher for Passover')}
              </span>
            )}
            {establishment.gluten_free && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 600 }}>
                🌾 {t('Gluten-free options available')}
              </span>
            )}
            {establishment.wheelchair_accessible && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', padding: '6px 12px', borderRadius: 999, fontSize: 14, fontWeight: 600 }}>
                ♿ {t('Wheelchair accessible')}
              </span>
            )}
          </div>
        )}

        {/* About — surfaced high up: the place's story / description + good-to-know
            (falls back to the `description` blurb when no history is written). */}
        {(() => {
          // Anche qui nella lingua di chi guarda, non solo il titolo:
          // Menachem, 26/10/2026, «estendi a descrizione e note». Come per
          // il nome, si traduce DOVE SI MOSTRA — il modulo di modifica piu'
          // sopra continua a leggere establishment.notes grezzo, o una
          // correzione sovrascriverebbe l'originale con la sua traduzione.
          const { advice, provenance } = splitNotes(tradotto(establishment, 'notes'), nomeVisibile(establishment));
          const story = tradotto(establishment, 'history') || tradotto(establishment, 'description');
          return (
            <>
              {(story || advice) && (
                <div style={{ ...styles.detailHeader, marginTop: 24 }}>
                  <h3 style={{ marginTop: 0, color: COLORS.primary }}>📖 {t('About')}</h3>
                  {story && (
                    <div style={{ fontSize: 14, lineHeight: 1.6, color: COLORS.text, whiteSpace: 'pre-wrap', marginBottom: advice ? 16 : 0 }}>
                      <Linkify text={story} />
                    </div>
                  )}
                  {advice && (
                    <div style={{ background: '#fef3c7', borderLeft: '4px solid #fcd34d', padding: '12px 14px', borderRadius: 6, fontSize: 14, lineHeight: 1.5, color: COLORS.text, whiteSpace: 'pre-wrap' }}>
                      <div style={{ fontWeight: 600, marginBottom: 4, color: '#92400e' }}>ℹ️ {t('Good to know')}</div>
                      <Linkify text={advice} />
                    </div>
                  )}
                </div>
              )}
              {provenance && (
                <div style={{ marginTop: 16, fontSize: 12, lineHeight: 1.5, color: COLORS.muted }}>
                  <Linkify text={provenance} />
                </div>
              )}
            </>
          );
        })()}

        {/* Amenities — hotels, vacation rentals, and Shabbat-friendly
            accommodations. Grouped (Food · Kosher kitchen · Shabbat · Jewish
            life); only renders groups that have at least one selected amenity. */}
        {SUPPORTS_SHABBAT_AMENITIES.includes(establishment.type) &&
          Array.isArray(establishment.shabbat_amenities) &&
          establishment.shabbat_amenities.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h2 style={{ color: COLORS.primary, marginBottom: 12 }}>✡️ {t('Kosher & Shabbat Amenities')}</h2>

            {/* GLI AVVISI STANNO IN CIMA, E SONO ROSSI.
                Menachem, 27/10/2026, sugli alberghi con i sensori nei
                corridoi: «se qualcuno fa una ricerca sul sito o su
                Google, è un'informazione importante da dare alla
                gente». Quelle schede le apre solo chi sta valutando
                quell'albergo adesso, quindi la cosa che glielo
                sconsiglia non può stare in fondo in mezzo alle altre,
                tutte uguali e tutte buone. Prima di ogni gruppo, e di
                un altro colore. */}
            {(() => {
              const avvisi = SHABBAT_AMENITIES.filter((a) => a.warning && establishment.shabbat_amenities.includes(a.slug));
              if (!avvisi.length) return null;
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {avvisi.map((a) => (
                    <span
                      key={a.slug}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: '#fef2f2', color: COLORS.danger,
                        padding: '6px 12px', borderRadius: 999,
                        fontSize: 13, fontWeight: 700,
                        border: `1px solid ${COLORS.danger}`,
                      }}
                    >
                      <span style={{ fontSize: 15 }}>{a.icon}</span>
                      {t(a.label)}
                    </span>
                  ))}
                </div>
              );
            })()}

            {AMENITY_GROUPS.map((grp) => {
              // Gli avvisi li ha gia' stampati il blocco qui sopra.
              const items = SHABBAT_AMENITIES.filter((a) => a.group === grp.key && !a.warning && establishment.shabbat_amenities.includes(a.slug));
              if (!items.length) return null;
              return (
                <div key={grp.key} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t(grp.label)}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {items.map((a) => (
                      <span
                        key={a.slug}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: COLORS.bg, color: COLORS.text,
                          padding: '6px 12px', borderRadius: 999,
                          fontSize: 13, fontWeight: 500,
                          border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        <span style={{ fontSize: 15 }}>{a.icon}</span>
                        {t(a.label)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Gallery — moved up so photos are prominent, right under the details. */}
        {gallery.length > 0 && (
          <div style={{ ...styles.detailHeader, marginTop: 20 }}>
            <h3 style={{ marginTop: 0, color: COLORS.primary }}>{t('Gallery')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {gallery.map((img, idx) => (
                <div key={idx} style={{ position: 'relative' }}>
                  <img
                    src={imgSrc(img)}
                    alt={galleryTitles[img] || `${establishment.name} ${idx + 1}`}
                    style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', display: 'block' }}
                    onClick={() => setLightboxIndex(idx + (establishment.main_image_url ? 1 : 0))}
                  />
                  {canManage && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); recropExisting(img, 'gallery'); }}
                        style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(26, 77, 143, 0.9)', color: 'white', border: 'none', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title={t("Crop this photo (replaces it)")}
                      >
                        ✂️
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(img, 'gallery'); }}
                        style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(220, 38, 38, 0.9)', color: 'white', border: 'none', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title={t("Remove from gallery")}
                      >
                        ✕
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleWatermark(img); }}
                          style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title={t("Watermark (only for our own photos)")}
                        >
                          ©
                        </button>
                      )}
                    </>
                  )}
                  {/* Photo name: editable for owners/admins, shown as a caption
                      to everyone else when set. */}
                  {canManage ? (
                    <input
                      defaultValue={galleryTitles[img] || ''}
                      placeholder={t("Add a photo name…")}
                      maxLength={200}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={(e) => { if ((e.target.value || '') !== (galleryTitles[img] || '')) handleGalleryTitle(img, e.target.value); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '5px 8px', fontSize: 12, border: `1px solid ${COLORS.border}`, borderRadius: 6, background: 'white' }}
                    />
                  ) : (galleryTitles[img] ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: COLORS.muted, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {galleryTitles[img]}
                    </div>
                  ) : null)}
                </div>
              ))}
            </div>
          </div>
        )}

        {isBookable && (
          <BookingRequest establishment={establishment} user={user} onLogin={onLogin} />
        )}

        {/* Book a table — every restaurant / eatery gets a reservation request
            button. The request is sent to the restaurant (email + inbox). */}
        {establishment.type === 'kosher_restaurant' && (
          <div style={{ background: 'white', padding: 16, borderRadius: 12, marginTop: 16, border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, color: COLORS.primary, fontSize: 16 }}>{t('🍽️ Book a table')}</div>
              <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 2 }}>{t('Request a reservation — the restaurant confirms by email.')}</div>
            </div>
            <button
              onClick={() => setShowReserve(true)}
              style={{ background: COLORS.accent, color: COLORS.primaryDark, border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {t("Book a table")}
            </button>
          </div>
        )}

      </div>

      {/* WHEN — first of the display sections, deliberately.
          "Are they open?" is the question a traveller arrives with, and this
          used to sit five screens down, below the certification and the news.
          Prayer times are the same answer for a synagogue. */}
      {/* Prayer times — a synagogue field. Only render for places in the
          synagogue category (primary or secondary), so an information point,
          store, etc. never shows minyan times even if data lingers. */}
      {hasAnyType(establishment, ['synagogue_shul']) && (
        <PrayerTimes establishment={establishment} />
      )}

      {/* Opening hours (non-synagogues) */}
      <OpeningHours establishment={establishment} />

      {/* In the news — articles that mention this place (hidden if none) */}
      <PlaceNews establishmentId={establishment.id} />

      {/* Menu (restaurants) — structured display via Menu component */}
      <Menu establishment={establishment} />

      {/* WHEN IT RUNS. Above the price, because for a programme the date
          decides whether the price is even worth reading. Shows whether it
          has already finished rather than leaving a visitor to work out
          that last April was in the past. */}
      {(establishment.date_from || establishment.date_to) && (() => {
        const over = establishment.date_to
          && new Date(String(establishment.date_to).slice(0, 10)) < new Date(new Date().toDateString());
        return (
          <div style={{ ...styles.detailHeader, marginTop: 20 }}>
            <h3 style={{ marginTop: 0, color: COLORS.primary }}>📅 {t('Dates')}</h3>
            <div style={{ fontSize: 16, fontWeight: 700, color: over ? COLORS.muted : COLORS.text }}>
              {formatDateRange(establishment.date_from, establishment.date_to)}
              {over && (
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, color: '#b91c1c',
                  background: '#fee2e2', border: '1px solid #fecaca', padding: '2px 8px', borderRadius: 10 }}>
                  {t('This programme has finished')}
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Price info */}
      {establishment.price_info && (
        <div style={{ ...styles.detailHeader, marginTop: 20 }}>
          <h3 style={{ marginTop: 0, color: COLORS.primary }}>{t('💰 Pricing')}</h3>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: COLORS.text, whiteSpace: 'pre-wrap' }}>
            <Linkify text={establishment.price_info} />
          </div>
        </div>
      )}

      {/* Public transport */}
      {establishment.transport && Object.keys(establishment.transport).length > 0 && (
        <div style={{ ...styles.detailHeader, marginTop: 20 }}>
          <h3 style={{ marginTop: 0, color: COLORS.primary }}>{t('🚍 Public Transport')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {establishment.transport.bus && (
              <div style={{ background: COLORS.bg, padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('🚌 BUS')}</div>
                <div style={{ fontSize: 14 }}>{establishment.transport.bus}</div>
              </div>
            )}
            {establishment.transport.tram && (
              <div style={{ background: COLORS.bg, padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('🚊 TRAM')}</div>
                <div style={{ fontSize: 14 }}>{establishment.transport.tram}</div>
              </div>
            )}
            {establishment.transport.metro && (
              <div style={{ background: COLORS.bg, padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('🚇 METRO')}</div>
                <div style={{ fontSize: 14 }}>{establishment.transport.metro}</div>
              </div>
            )}
            {establishment.transport.boat && (
              <div style={{ background: COLORS.bg, padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>{t('⛴ BOAT')}</div>
                <div style={{ fontSize: 14 }}>{establishment.transport.boat}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu + ordering — for restaurants/eateries/catering/shabbat meals,
          including places that are one of those as a SECONDARY category. */}
      {hasAnyType(establishment, [...FOOD_SERVICE_TYPES, 'shabbat_meal']) && (
        <MenuOrder establishment={establishment} user={user} />
      )}

      {/* Menu editor — owner/admin only */}
      {canManage && hasAnyType(establishment, [...FOOD_SERVICE_TYPES, 'shabbat_meal']) && (
        <MenuEditor establishment={establishment} />
      )}


      {/* Location map + walkable hotels, side by side. The map shows when we
          have coordinates AND either a real street address (not just a city-
          center fallback) OR a drawn boundary (e.g. an Eruv). The hotels panel
          lists nearby kosher hotels (nearest first) plus a "find hotels nearby"
          search centered on this exact spot. When there are no hotels to show,
          the map fills the whole row (full width); when there are, they split. */}
      {(() => {
        // A "real location" = a street address or a drawn boundary. Places
        // without one (drivers, tour guides) only have a city-center fallback
        // coordinate, so we show neither the map nor nearby hotels for them.
        // A drawn boundary (e.g. an Eruv) is a location on its own — the map
        // fits to the polygon and needs no point. An address needs coordinates
        // to place a pin. Nearby-hotels only makes sense around an address point.
        const hasBoundary = !!establishment.boundary;
        const hasAddressPoint = !!(establishment.coordinates && establishment.address && establishment.address.trim());
        const showMap = hasBoundary || hasAddressPoint;
        const showHotels = hasAddressPoint && establishment.type !== 'kosher_hotel' && (walkHotels.length > 0 || AFFILIATE_ENABLED);
        if (!showMap && !showHotels) return null;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start', marginTop: 20 }}>
            {showMap && (
              <div style={{ ...styles.detailHeader, marginTop: 0, flex: '1 1 340px', minWidth: 0 }}>
                <h3 style={{ marginTop: 0, marginBottom: 12, color: COLORS.primary }}>📍 {establishment.boundary && !(establishment.address && establishment.address.trim()) ? t('Area on map') : t('Location')}</h3>
                <SingleMap
                  establishment={establishment}
                  height={320}
                  nearby={nearbyPlaces}
                  onSelectNearby={(n) => navigateTo(buildPath('detail', null, n.id))}
                  boundary={establishment.boundary || null}
                  eruvim={coveringEruvim}
                />
                {/* IL NOME DELL'ERUV E' UN COLLEGAMENTO.
                    Menachem, 24/10/2026: «manca il collegamento alle RUV».
                    La riga diceva «Inside the Budapest Eruv» e finiva lì:
                    chi voleva vedere dove passa il filo non aveva dove
                    andare, pur esistendo la sua pagina col perimetro
                    disegnato e le note su chi lo controlla. */}
                {coveringEruvim.length > 0 && (
                  <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 6, fontWeight: 600 }}>
                    ⬚ {t('Inside the')}{' '}
                    {coveringEruvim.map((e, i) => (
                      <React.Fragment key={e.id}>
                        {i > 0 && ', '}
                        <a
                          href={withLangPath(buildPlaceUrl(e) || `/place/${e.id}`)}
                          onClick={(ev) => { ev.preventDefault(); navigateTo(buildPath('detail', null, e.id)); }}
                          style={{ color: '#7c3aed', textDecoration: 'underline' }}
                        >
                          {e.name}
                        </a>
                      </React.Fragment>
                    ))}
                    {' — '}{t('always check the eruv status before Shabbat.')}
                  </div>
                )}
                {(() => {
                  // The pins alone made you hunt: hover each one to learn what it
                  // is. The same places listed underneath, nearest first, answer
                  // "what else is around here" at a glance and give a tap target
                  // that doesn't need a steady finger on a phone.
                  const others = nearbyPlaces.filter((n) => n.id !== establishment.id);
                  if (others.length === 0) return null;
                  const row = (n) => (
                    <div
                      key={n.id}
                      onClick={() => navigateTo(buildPath('detail', null, n.id))}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', borderBottom: `1px solid ${COLORS.border}` }}
                    >
                      <CategoryIcon type={n.type} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COLORS.primary, fontWeight: 600, fontSize: 13 }}>
                        {n.name}
                      </span>
                      <span style={{ color: COLORS.muted, fontSize: 11, whiteSpace: 'nowrap' }}>
                        {formatCategoryLabelShort(n.type)}
                        {typeof n.distance_km === 'number' ? ` · ${formatDistance(n.distance_km, n.country || establishment.country)}` : ''}
                      </span>
                    </div>
                  );
                  // Six is about a screenful on a phone; the rest fold away rather
                  // than pushing everything below the map off the page. <details>
                  // does the toggling natively — no state, no re-render.
                  const FIRST = 6;
                  return (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 4 }}>
                        {others.length} {others.length === 1 ? t('other place') : t('other places')} {t('within 1 km')}
                      </div>
                      {others.slice(0, FIRST).map(row)}
                      {others.length > FIRST && (
                        <details>
                          <summary style={{ cursor: 'pointer', color: COLORS.primary, fontSize: 12, fontWeight: 600, padding: '6px 0' }}>
                            +{others.length - FIRST} {t('more')}
                          </summary>
                          {others.slice(FIRST).map(row)}
                        </details>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            {showHotels && (
              <div style={{ ...styles.detailHeader, marginTop: 0, flex: '1 1 300px', minWidth: 0 }}>
                <h3 style={{ marginTop: 0, marginBottom: 12, color: COLORS.primary }}>🏨 {walkHotels.length > 0 ? t('Hotels within walking distance') : t('Hotels nearby')}</h3>
                {walkHotels.map((h) => {
                  const km = typeof h.distance_km === 'number' ? h.distance_km : null;
                  const dist = formatDistance(km, h.country || establishment.country);
                  // IL NOME VA A CAPO, NON SI TRONCA.
                  //
                  // Menachem, 27/10/2026, con la foto: «non si vedono i
                  // nomi degli alberghi». Erano «…za Hotel», «…a Hotel»,
                  // «…el Hotel» — la riga teneva il nome su una riga sola
                  // e lo tagliava, mentre distanza e tasto «Prices», che
                  // non si restringono, si prendevano la larghezza. Ma uno
                  // legge questa riga PER il nome: se si deve sacrificare
                  // qualcosa, si sacrifichi l'altezza.
                  return (
                    <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                      <a href={withLangPath(`/place/${h.id}`)} style={{ fontWeight: 600, color: COLORS.primary, textDecoration: 'none', minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.35 }}>{h.name}</a>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {dist && <span style={{ fontSize: 13, color: COLORS.muted, whiteSpace: 'nowrap' }}>🚶 {dist}</span>}
                        {h.booking_url && (
                          <a href={h.booking_url}
                            target="_blank" rel="noreferrer sponsored"
                            onClick={() => api.trackClick(h.id, 'booking')}
                            title={t("Check availability & prices on hotels.com — we may earn a commission")}
                            style={{ fontSize: 12, fontWeight: 700, color: COLORS.primaryDark, background: COLORS.accent, padding: '3px 9px', borderRadius: 999, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                            {t("🏨 Prices")}
                          </a>
                        )}
                      </span>
                    </div>
                  );
                })}
                {AFFILIATE_ENABLED && (
                  <a href={hotelSearchNearUrl([establishment.address, establishment.area, establishment.city, establishment.country].filter(Boolean).join(', '))}
                    target="_blank" rel="noreferrer sponsored"
                    style={{ display: 'inline-block', marginTop: walkHotels.length > 0 ? 10 : 0, color: COLORS.primary, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
                    {t("🏨 Find hotels nearby →")}
                  </a>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Reviews & ratings — moved below the place details (lower than before). */}
      <ReviewsSection establishment={establishment} user={user} onLogin={onLogin} />

      {/* ── Owner/admin management — grouped at the bottom, out of the visitor's
          reading flow: claim, booking inventory, and image management. ── */}

      {/* Claim listing - shown only when establishment has no owner */}
      {!establishment.owner_id && !canManage && (
        <div style={{ marginTop: 24, padding: 16, background: '#eff6ff', borderRadius: 8, border: '1px solid #93c5fd' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('🏢 Is this your establishment?')}</div>
              <div style={{ fontSize: 13, color: COLORS.muted }}>
                {t("Claim this listing to manage info, upload photos, and respond to messages.")}
              </div>
            </div>
            {!user ? (
              <button style={styles.button} onClick={onLogin}>{t('Login to Claim')}</button>
            ) : myClaim?.status === 'pending' ? (
              <span style={{ background: '#fef3c7', color: '#92400e', padding: '8px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13 }}>
                {t("⏳ Claim Pending")}
              </span>
            ) : myClaim?.status === 'rejected' ? (
              <button style={styles.buttonOutline} onClick={() => setShowClaim(true)}>{t('Re-submit Claim')}</button>
            ) : (
              <button style={styles.button} onClick={() => setShowClaim(true)}>{t('Claim This Listing')}</button>
            )}
          </div>
        </div>
      )}

      {/* Admin/owner: add booking inventory (rooms or tour dates) */}
      {isBookable && canManage && (
        <div style={{ marginTop: 24, padding: 16, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
          <h3 style={{ margin: '0 0 4px 0', color: COLORS.primary }}>{t('🛏️ Manage booking inventory')}</h3>
          <p style={{ fontSize: 13, color: COLORS.muted, margin: '0 0 14px 0' }}>
            {t("Add the rooms or tour dates visitors can book for this")} {formatCategoryLabel(establishment.type).toLowerCase()}.
          </p>
          <PlaceInventoryManager place={establishment} />
        </div>
      )}

      {/* Owner/Admin image management */}
      {canManage && (
        <div style={{ ...styles.detailHeader, marginTop: 20, border: `2px dashed ${COLORS.primary}` }}>
          <h3 style={{ marginTop: 0, color: COLORS.primary }}>{t('📷 Manage Images')}</h3>
          <p style={{ color: COLORS.muted, fontSize: 14, marginTop: 0 }}>
            {t("Upload a main photo (shown as hero/logo) or add to the gallery. You can upload a file or import from a URL.")}
          </p>

          {user?.role === 'admin' && (establishment.main_image_url || (establishment.gallery_images || []).length > 0) && (
            <button
              style={{ ...styles.buttonOutline, marginBottom: 16 }}
              onClick={handleWatermarkAll}
              title={t("Stamp the Jewish Compass logo + jewishcompass.com on the main image and every gallery photo — only for our own photos")}
            >
              {t("© Watermark all (main + gallery)")}
            </button>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 8 }}>{t('MAIN IMAGE (logo / hero)')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ ...styles.button, cursor: 'pointer', display: 'inline-block' }}>
                📁 {establishment.main_image_url ? (t("Replace from File")) : (t("Upload File"))}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files[0] && handleMultiUpload([e.target.files[0]], 'main')}
                />
              </label>
              <button style={styles.buttonOutline} onClick={() => handleAddFromUrl('main')}>
                🔗 {establishment.main_image_url ? (t("Replace from URL")) : (t("Add from URL"))}
              </button>
              {establishment.main_image_url && (
                <button
                  style={styles.buttonOutline}
                  onClick={() => recropExisting(establishment.main_image_url, 'main')}
                  title={t("Re-crop the existing main image without re-uploading")}
                >
                  {t("✂️ Crop")}
                </button>
              )}
              {establishment.main_image_url && user?.role === 'admin' && (
                <button
                  style={styles.buttonOutline}
                  onClick={() => handleWatermark(establishment.main_image_url)}
                  title={t("Watermark with jewishcompass.com — only for our own photos")}
                >
                  {t("© Watermark")}
                </button>
              )}
              {establishment.main_image_url && (
                <button
                  style={styles.buttonOutline}
                  onClick={handleMainToGallery}
                  title={t("Move the current main image into the gallery (keeps the photo; clears the main image)")}
                >
                  {t("⬇ Move to Gallery")}
                </button>
              )}
              {establishment.main_image_url && (
                <button
                  style={{ ...styles.buttonOutline, color: COLORS.danger, borderColor: COLORS.danger }}
                  onClick={() => handleDelete(establishment.main_image_url, 'main')}
                >
                  {t("🗑 Remove Main")}
                </button>
              )}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 8 }}>{t('GALLERY PHOTOS')}</div>

            {/* Drop-zone supporting drag-and-drop OR clicking to multi-select. */}
            <div
              onDragEnter={(e) => { e.preventDefault(); setGalleryDragActive(true); }}
              onDragOver={(e) => { e.preventDefault(); setGalleryDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setGalleryDragActive(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setGalleryDragActive(false);
                if (galleryUploading) return;
                if (e.dataTransfer.files?.length) handleMultiUpload(e.dataTransfer.files, 'gallery');
              }}
              style={{
                border: `2px dashed ${galleryDragActive ? COLORS.primary : COLORS.border}`,
                background: galleryDragActive ? '#eff6ff' : 'white',
                borderRadius: 10,
                padding: '22px 16px',
                textAlign: 'center',
                cursor: galleryUploading ? 'wait' : 'pointer',
                transition: 'background 0.15s, border-color 0.15s',
              }}
            >
              <label style={{ cursor: galleryUploading ? 'wait' : 'pointer', display: 'block' }}>
                <div style={{ fontSize: 30, marginBottom: 6 }}>📁</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
                  {galleryUploading
                    ? `Uploading ${galleryProgress.done} / ${galleryProgress.total}…`
                    : (t("Drop photos here, or click to choose"))}
                </div>
                <div style={{ fontSize: 12, color: COLORS.muted }}>
                  {t("PNG, JPG, GIF, WEBP — up to 30 files at once, 10 MB each")}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={galleryUploading}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files?.length) handleMultiUpload(e.target.files, 'gallery');
                    e.target.value = ''; // allow re-selecting the same files later
                  }}
                />
              </label>
              {galleryUploading && galleryProgress.total > 0 && (
                <div style={{ marginTop: 12, height: 4, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${(galleryProgress.done / galleryProgress.total) * 100}%`,
                      height: '100%', background: COLORS.primary,
                      transition: 'width 0.2s',
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button style={styles.buttonOutline} onClick={() => handleAddFromUrl('gallery')} disabled={galleryUploading}>
                {t("🔗 Add from URL")}
              </button>
            </div>
            <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 8 }}>
              {t("Tip: To delete a gallery photo, hover over it above and click the ✕ button.")}
            </div>
          </div>
        </div>
      )}

      {/* Similar places — same category, ordered by city + distance.
          Lives at the very end of the page so visitors finish reading the place
          info first, then get suggestions for what to look at next. */}

      {/* "Places we certify" — shows for any certifier (primary OR secondary
          kosher_certification_hechsher role). Groups the supervised
          establishments by category for easy scanning. */}
      {isCertifierEst(establishment) && certifiedPlaces.length > 0 && (
        <div id="places-we-certify" style={{ ...styles.detailHeader, marginTop: 32 }}>
          <h2 style={{ color: COLORS.primary, marginTop: 0, marginBottom: 4, fontSize: 20 }}>
            {t("✡️ Places we certify")}
          </h2>
          <div style={{ fontSize: 13, color: COLORS.muted, marginBottom: 16 }}>
            {certifiedPlaces.length} {certifiedPlaces.length === 1 ? 'establishment' : 'establishments'} {t("listed under our supervision")}
          </div>
          {(() => {
            // Group by establishment type so the list reads category-by-category.
            const byType = {};
            for (const p of certifiedPlaces) {
              (byType[p.type] = byType[p.type] || []).push(p);
            }
            // Most populous category first; within each, alphabetical.
            const groups = Object.entries(byType).sort((a, b) => b[1].length - a[1].length);
            return groups.map(([type, list]) => (
              <div key={type} style={{ marginBottom: 18 }}>
                <h3 style={{ fontSize: 15, color: COLORS.text, margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{categoryIcon[type] || '📍'}</span>
                  <span>{formatCategoryLabel(type)}</span>
                  <span style={{ fontSize: 12, color: COLORS.muted, fontWeight: 400 }}>· {list.length}</span>
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                  {list.sort((a, b) => a.name.localeCompare(b.name)).map((p) => (
                    <a
                      key={p.id}
                      href={withLangPath(buildPlaceUrl(p) || `/place/${p.id}`)}
                      style={{
                        display: 'block', padding: '10px 12px', textDecoration: 'none', color: 'inherit',
                        background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8,
                        transition: 'border-color 0.15s, transform 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLORS.primary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border; }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[p.area, p.city, p.country].filter(Boolean).join(', ') || '—'}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* TUTTE LE FILIALI DELLA CATENA. Raggruppate per citta, perche chi
          guarda una filiale vuole sapere dove sono le altre — l'opposto dei
          certificati, che si leggono per categoria. */}
      {chainInfo && chainInfo.results.length > 0 && (
        <div id="chain-branches" style={{ ...styles.detailHeader, marginTop: 32 }}>
          <h2 style={{ color: COLORS.primary, marginTop: 0, marginBottom: 4, fontSize: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
            {chainInfo.logo && (
              <img src={imageUrl(chainInfo.logo)} alt="" style={{ height: 28, width: 'auto', maxWidth: 80, objectFit: 'contain' }} />
            )}
            <span>{t('All branches of')} {(() => {
              // Il registro scrive «Biga (ביגה)»: in ebraico si mostra la
              // parte fra parentesi, nelle altre lingue quella fuori.
              const n = chainInfo.chain;
              if (!n) return t('this chain');
              const m = n.match(/^(.*?)\s*\((.*)\)\s*$/);
              if (!m) return n;
              return getLang() === 'he' ? m[2] : m[1];
            })()}</span>
          </h2>
          <div style={{ fontSize: 13, color: COLORS.muted, marginBottom: 16 }}>
            {chainInfo.results.length} {chainInfo.results.length === 1 ? t('other branch') : t('other branches')}
          </div>
          {(() => {
            const byCity = {};
            for (const p of chainInfo.results) {
              const k = p.city || '—';
              (byCity[k] = byCity[k] || []).push(p);
            }
            return Object.entries(byCity)
              .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
              .map(([city, list]) => (
                <div key={city} style={{ marginBottom: 14 }}>
                  <h3 style={{ fontSize: 15, color: COLORS.text, margin: '0 0 8px 0' }}>
                    {t(city)} <span style={{ fontSize: 12, color: COLORS.muted, fontWeight: 400 }}>· {list.length}</span>
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                    {list.map((p) => (
                      <a
                        key={p.id}
                        href={withLangPath(buildPlaceUrl(p) || `/place/${p.id}`)}
                        style={{
                          display: 'block', padding: '10px 12px', textDecoration: 'none', color: 'inherit',
                          background: 'white', border: `1px solid ${COLORS.border}`, borderRadius: 8,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLORS.primary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border; }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.name}
                        </div>
                        <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[p.address, p.area].filter(Boolean).join(' · ') || t(p.city || '')}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              ));
          })()}
        </div>
      )}

      {similarPlaces.length > 0 && (
        <div style={{ ...styles.detailHeader, marginTop: 32 }}>
          <h2 style={{ color: COLORS.primary, marginTop: 0, marginBottom: 12, fontSize: 20 }}>
            {t("More")} {formatCategoryLabel(establishment.type)}
            {establishment.city ? ` in ${establishment.city}` : ''}
          </h2>
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12, scrollSnapType: 'x mandatory' }}>
            {similarPlaces.map((p) => (
              <a
                key={p.id}
                href={withLangPath(buildPlaceUrl(p) || `/place/${p.id}`)}
                style={{
                  flex: '0 0 200px', scrollSnapAlign: 'start', textDecoration: 'none', color: 'inherit',
                  background: 'white', borderRadius: 10, border: `1px solid ${COLORS.border}`,
                  overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ position: 'relative', height: 120, background: COLORS.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {p.main_image_url ? (
                    <img src={imageUrl(p.main_image_url)} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: 48 }}><CategoryIcon type={p.type} /></span>
                  )}
                  {typeof p.distance_km === 'number' && (
                    <div style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(255,255,255,0.95)', color: COLORS.text, fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4 }}>
                      {formatDistance(p.distance_km, p.country)}
                    </div>
                  )}
                </div>
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[p.area, p.city, p.country].filter(Boolean).join(', ') || formatCategoryLabel(p.type)}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Book-a-table (reservation) modal — reuses ContactModal in reservation mode */}
      {showReserve && (
        <ContactModal establishment={establishment} user={user} mode="reservation" onClose={() => setShowReserve(false)} onRequireLogin={() => { setShowReserve(false); onLogin && onLogin(); }} />
      )}

      {/* Contact form modal */}
      {showContact && (
        <ContactModal establishment={establishment} user={user} onClose={() => setShowContact(false)} onRequireLogin={() => { setShowContact(false); onLogin && onLogin(); }} />
      )}

      {/* Suggest-edit modal (community submission) */}
      {showSuggest && (
        <SuggestEditModal
          establishment={establishment}
          onClose={() => setShowSuggest(false)}
        />
      )}

      {/* Claim listing modal */}
      {showClaim && (
        <ClaimModal
          establishment={establishment}
          user={user}
          onClose={() => setShowClaim(false)}
          onSubmitted={(c) => { setMyClaim(c); setShowClaim(false); }}
        />
      )}

      {/* Image lightbox */}
      {lightboxIndex !== null && allImages.length > 0 && (
        <Lightbox
          images={allImages}
          captions={allCaptions}
          index={lightboxIndex}
          captionName={establishment.name}
          onClose={() => setLightboxIndex(null)}
          onChange={setLightboxIndex}
        />
      )}

      {/* Crop modal — fires once per picked file before the upload. */}
      {cropCurrent && (
        <ImageCropModal
          file={cropCurrent.file}
          // Main image looks best as a landscape hero; gallery photos are free-form.
          suggestedAspect={cropCurrent.kind === 'main' ? 16 / 9 : null}
          onCancel={handleCropCancel}
          onConfirm={handleCropDone}
        />
      )}
    </div>
  );
}

// === Path-based router (History API) ===
// URL formats:
//   /                                            → home
//   /list?type=kosher_hotel&country=Italy&city=X → list with filters
//   /place/{uuid}                                → detail page
//   /dashboard                                   → dashboard
function parseLocation() {
  const path = window.location.pathname || '/';
  const params = {};
  new URLSearchParams(window.location.search).forEach((v, k) => { params[k] = v; });
  const parts = path.split('/').filter(Boolean);

  if (parts.length === 0) return { view: 'home', params };
  if (parts[0] === 'list') return { view: 'list', params };
  if (parts[0] === 'place' && parts[1]) return { view: 'detail', id: parts[1], params };
  if (parts[0] === 'guide' && parts[1] && parts[2]) return { view: 'guide', cc: parts[1], city: parts[2], params };
  if (parts[0] === 'plan' && parts[1] && parts[2]) return { view: 'plan', cc: parts[1], city: parts[2], params };
  if (parts[0] === 'route') return { view: 'route', params };
  if (parts[0] === 'dashboard') return { view: 'dashboard', tab: parts[1] || null, params };
  if (parts[0] === 'favorites') return { view: 'favorites', params };
  if (parts[0] === 'privacy') return { view: 'privacy', params };
  if (parts[0] === 'terms') return { view: 'terms', params };
  if (parts[0] === 'our-story') return { view: 'our-story', params };
  if (parts[0] === 'profile') return { view: 'profile', params };
  if (parts[0] === 'reset-password') return { view: 'reset-password', params };
  if (parts[0] === 'register') return { view: 'register', params };
  if (parts[0] === 'verify-email') return { view: 'verify-email', params };
  if (parts[0] === 'reply' && parts[1]) return { view: 'thread-reply', token: parts[1], params };
  if (parts[0] === 'review' && parts[1]) return { view: 'review-token', token: parts[1], params };
  if (parts[0] === 'minyanim') return { view: 'minyanim', params };
  if (parts[0] === 'destinations') return { view: 'destinations', params };
  if (parts[0] === 'news') return parts[1] ? { view: 'article', slug: parts[1], params } : { view: 'news', params };
  if (parts[0] === 'kashrut-changes') return { view: 'kashrut-changes', params };
  // Le due cartelle di mezzo: /it/rome e /it. La scala dev'essere
  // percorribile all'indietro — chi toglie l'ultimo pezzo dalla barra degli
  // indirizzi deve atterrare da qualche parte. Prima cadeva nel vuoto.
  if (parts.length === 2 && !RESERVED_ROOTS_FE.has(parts[0]) && /^[a-z-]{2,12}$/.test(parts[0])) {
    return { view: 'guide', cc: parts[0], city: parts[1], params };
  }
  if (parts.length === 1 && !RESERVED_ROOTS_FE.has(parts[0]) && /^[a-z-]{2,12}$/.test(parts[0])) {
    return { view: 'country', cc: parts[0], params };
  }
  // Pagina di categoria — /:cc/:city/:categoria (/it/rome/kosher-restaurants).
  // Va PRIMA della scheda perché ha la stessa forma e, a differenza di
  // quella, si riconosce senza chiedere niente al server: il nome della
  // categoria sta in una tabella. Nessuna scheda può portare uno di questi
  // nomi — uniqueSlugInScope lato server lo impedisce — quindi le due cose
  // non si possono più scontrare, e il server (che prova prima la scheda)
  // arriva sempre alla stessa conclusione.
  if (parts.length === 3 && !RESERVED_ROOTS_FE.has(parts[0]) && tipoDaSlug(parts[2])) {
    return { view: 'category', cc: parts[0], city: parts[1], cat: parts[2], params };
  }
  // Canonical place URL — /:cc/:city/:slug (e.g. /it/rome/kasher-roma-5778)
  const slugPath = parsePlaceSlugPath(parts);
  if (slugPath) return { view: 'detail-slug', ...slugPath, params };
  // Anything else — show a real 404 instead of silently dropping back to home.
  return { view: 'not-found', params };
}

// Le radici che non sono un codice paese. Stessa lista del server; qui serve
// solo per non scambiare /news/qualcosa/altro per una pagina di categoria.
const RESERVED_ROOTS_FE = new Set(['api', 'uploads', 'list', 'place', 'guide', 'news', 'plan',
  'route', 'dashboard', 'favorites', 'privacy', 'terms', 'profile', 'reset-password', 'register',
  'verify-email', 'minyanim', 'destinations', 'kashrut-changes', 'reply', 'review', 'our-story',
  'je', 'c', 'static']);

// La pagina del paese, lato client: /it, /fr, /us.
//
// Dal solo «it» non si ricava «Italy»: la tabella dei codici che il frontend
// ha dentro copre 67 paesi su 111, e una ricerca a ritroso mancherebbe
// Eswatini, Curacao, Gibilterra e un'altra quarantina. Lo chiede al
// database, che la risposta ce l'ha.
function CountryPage({ cc, ...resto }) {
  const [filtri, setFiltri] = React.useState(null);
  const [vuoto, setVuoto] = React.useState(false);
  React.useEffect(() => {
    let vivo = true;
    setFiltri(null); setVuoto(false);
    api.countryByCode(cc)
      .then((d) => {
        if (!vivo) return;
        if (!d || !d.country) { setVuoto(true); return; }
        setFiltri({ country: d.country });
        try { document.title = `Jewish ${d.country} — ${d.total} Kosher Places & Synagogues | Jewish Compass`; } catch (e) { /* ignora */ }
      })
      .catch(() => { if (vivo) setVuoto(true); });
    return () => { vivo = false; };
  }, [cc]);

  if (vuoto) return <NotFoundPage />;
  if (!filtri) return <div style={{ padding: 48, textAlign: 'center', color: COLORS.muted }}>{t('Loading…')}</div>;
  return <ListPage initialFilters={filtri} {...resto} />;
}

// La pagina di categoria, lato client: /it/rome/kosher-restaurants.
//
// Il server ha gia mandato questa pagina scritta per intero — nomi,
// indirizzi, hechsher, ItemList — perche e cosi che la legge Google. Qui
// serve la stessa cosa per chi ha il JavaScript, e la si ottiene con la
// ListPage che esiste gia: l'unico lavoro e tradurre l'indirizzo nei filtri
// che lei si aspetta, che sono il NOME del paese e della citta, non il
// codice e lo slug.
//
// La traduzione la fa l'endpoint della guida, che per cc + slug restituisce
// i nomi veri. E lo stesso giro che fa /guide/:cc/:city.
function CategoryPage({ cc, city, cat, ...resto }) {
  const [filtri, setFiltri] = React.useState(null);
  const [vuoto, setVuoto] = React.useState(false);
  React.useEffect(() => {
    let vivo = true;
    setFiltri(null); setVuoto(false);
    api.cityGuide(cc, city)
      .then((d) => {
        if (!vivo) return;
        const tipo = tipoDaSlug(cat);
        if (d && d.country && tipo) setFiltri({ country: d.country, city: d.city || city, type: tipo });
        else setVuoto(true);
      })
      .catch(() => { if (vivo) setVuoto(true); });
    return () => { vivo = false; };
  }, [cc, city, cat]);

  if (vuoto) return <NotFoundPage />;
  if (!filtri) return <div style={{ padding: 48, textAlign: 'center', color: COLORS.muted }}>{t('Loading…')}</div>;
  return <ListPage initialFilters={filtri} {...resto} />;
}

function buildPath(view, filters = {}, id = null) {
  if (view === 'home') return '/';
  if (view === 'dashboard') return filters && filters.tab ? `/dashboard/${filters.tab}` : '/dashboard';
  if (view === 'favorites') return '/favorites';
  if (view === 'privacy') return '/privacy';
  if (view === 'terms') return '/terms';
  if (view === 'profile') return '/profile';
  if (view === 'reset-password') return '/reset-password';
  if (view === 'verify-email') return '/verify-email';
  if (view === 'detail' && id) return `/place/${id}`;
  if (view === 'list') {
    const pieni = Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== '');
    // Paese + città + categoria e nient'altro: è esattamente quello che
    // l'indirizzo pulito sa dire, e dirlo così fa sì che chi lo copia dalla
    // barra condivida la pagina indicizzabile invece della query. Con un
    // filtro in più si torna a /list, che sa esprimere tutto.
    if (pieni.length === 3) {
      const f = Object.fromEntries(pieni);
      const cat = f.country && f.city && f.type ? slugDaTipo(f.type) : null;
      const cc = cat ? countryToCode(f.country) : null;
      const cs = cat ? citySlug(f.city) : null;
      if (cat && cc && cs) return `/${cc}/${cs}/${cat}`;
    }
    const qs = new URLSearchParams();
    pieni.forEach(([k, v]) => qs.append(k, v));
    const s = qs.toString();
    return `/list${s ? `?${s}` : ''}`;
  }
  return '/';
}

// Remembered scroll position per URL, so going back lands where you left off.
const scrollPositions = {};
const scrollKey = () => window.location.pathname + window.location.search;

// pushState doesn't fire popstate, so we dispatch a custom event the app listens for
function navigateTo(pathStr) {
  // Remember where we were on the current page before leaving it.
  scrollPositions[scrollKey()] = window.scrollY;
  // Every in-site hop carries the language (?lang=it), so the address bar always
  // states it and a copied URL opens in the same language for whoever gets it.
  // No-op in English. This is the single choke point for SPA navigation, so
  // stamping it here covers every internal link rather than each call site.
  window.history.pushState({}, '', withLangPath(pathStr));
  window.dispatchEvent(new Event('locationchange'));
}

const navigate = (view, filters, id) => {
  navigateTo(buildPath(view, filters, id));
};

// UserBanners — site-wide info bars under the header. Currently shows:
//   - "Please confirm your email" for unverified accounts (with resend link)
//   - "Awaiting admin approval" for pending owners
//   - "You have N pending submissions" if the user has any pending edits
function UserBanners({ user }) {
  const [pendingCount, setPendingCount] = useState(0);
  const [unanswered, setUnanswered] = useState(0);
  const [resendStatus, setResendStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  React.useEffect(() => {
    if (!user) { setPendingCount(0); return; }
    api.mySubmissions()
      .then((d) => setPendingCount(d.pendingCount || 0))
      .catch(() => setPendingCount(0));
  }, [user]);

  // Owners/admins: surface messages that still need a reply, so logging in
  // immediately shows there's something to answer (with a one-click link).
  React.useEffect(() => {
    if (!user || !(user.role === 'owner' || user.role === 'admin')) { setUnanswered(0); return; }
    api.ownerStats()
      .then((s) => setUnanswered(Number(s?.unanswered_messages) || 0))
      .catch(() => setUnanswered(0));
  }, [user]);

  if (!user) return null;
  const isPendingOwner = user.role === 'owner' && (user.approval_status === 'pending' || user.approvalStatus === 'pending');
  // Admins are site operators — never gate them behind email verification.
  const isUnverified = user.role !== 'admin' && !user.email_verified_at && !user.emailVerifiedAt;

  if (!isUnverified && !isPendingOwner && pendingCount === 0 && unanswered === 0) return null;

  const bar = (bg, border, color, content) => (
    <div style={{ background: bg, borderBottom: `1px solid ${border}`, color, padding: '10px 24px', fontSize: 14, textAlign: 'center' }}>
      {content}
    </div>
  );

  const resend = async () => {
    setResendStatus('sending');
    try {
      const r = await api.resendVerification();
      // If the account is actually already verified, the server sends no email
      // and returns alreadyVerified. The banner is showing only because this
      // session's cached user is stale — reload to refetch /me and clear it.
      if (r && r.alreadyVerified) {
        window.location.reload();
        return;
      }
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <>
      {isUnverified && bar('#fff7ed', '#fed7aa', '#9a3412',
        <>
          ✉️ <strong>{t('Please confirm your email address.')}</strong> {t("Check your inbox for the verification link (you can write reviews and suggest edits once it's confirmed).")}{' '}
          {resendStatus === 'sent' ? (
            <span>{t('✓ A new link is on the way.')}</span>
          ) : resendStatus === 'error' ? (
            <span style={{ color: COLORS.danger }}>{t('Could not resend — try again later.')}</span>
          ) : (
            <button
              onClick={resend}
              disabled={resendStatus === 'sending'}
              style={{ background: 'none', border: 'none', color: '#9a3412', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit', fontWeight: 600 }}
            >
              {resendStatus === 'sending' ? (t("Sending…")) : (t("Resend the email"))}
            </button>
          )}
        </>
      )}
      {isPendingOwner && bar('#fef3c7', '#fde68a', '#92400e',
        <>⏳ <strong>{t('Your owner account is awaiting admin approval.')}</strong> {t('You can browse the site normally; owner-only actions will unlock once an admin reviews your request.')}</>
      )}
      {pendingCount > 0 && bar('#dbeafe', '#93c5fd', '#1e3a8a',
        <>{t('📝 You have')} <strong>{pendingCount}</strong> submission{pendingCount === 1 ? '' : 's'} {t("awaiting review. We'll email you when an admin or owner responds.")}</>
      )}
      {/* Unread messages are surfaced by the ✉️ badge in the header now. */}
    </>
  );
}

export default function App() {
  // Redirect legacy hash URLs (#/place/x) to the new path form, once, before first render
  const initialHashRedirect = () => {
    if (window.location.hash.startsWith('#/')) {
      const target = window.location.hash.slice(1); // strip leading '#'
      window.history.replaceState({}, '', target);
    }
  };
  initialHashRedirect();

  const [route, setRoute] = useState(parseLocation());
  const [selectedEst, setSelectedEst] = useState(null);
  // When a navigation should open the place straight in edit mode (owner
  // Dashboard "Manage"). Reset on every handleSelectEst call.
  const [startInEdit, setStartInEdit] = useState(false);
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showSiteContact, setShowSiteContact] = useState(false);
  const [showAddPlace, setShowAddPlace] = useState(false);
  // Set of establishment IDs the current user has favorited. Empty Set when
  // logged out. Loaded on login; mutated optimistically by toggleFavorite.
  const [favorites, setFavorites] = useState(() => new Set());
  // Last pathname seen, so a same-page change (tweaking sort/filters on the list,
  // which only changes the query string) doesn't yank the page to the top.
  const lastPathRef = React.useRef(typeof window !== 'undefined' ? window.location.pathname : '/');

  // Da dove è arrivato questo visitatore: una sola volta, al primo
  // caricamento, prima che la navigazione interna cancelli il referrer.
  useEffect(() => { api.recordReferral(); }, []);

  useEffect(() => {
    // Take manual control of scroll so we restore it ourselves (browser auto
    // restoration doesn't work with async-loaded SPA content).
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

    // Forward navigation → start at the top, BUT if only the query string
    // changed on the same page (sort/filter/pagination on the list), keep the
    // current scroll position so the user isn't thrown back to the top.
    const onLoc = () => {
      const prevPath = lastPathRef.current;
      const nextPath = window.location.pathname;
      setRoute(parseLocation());
      if (nextPath !== prevPath) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      lastPathRef.current = nextPath;
    };
    // Back/forward → return to where the user left that page. Content (lists,
    // detail) loads async, so the page may be too short to reach the saved
    // position immediately — retry briefly until it's tall enough.
    const onPop = () => {
      setRoute(parseLocation());
      lastPathRef.current = window.location.pathname;
      const y = scrollPositions[scrollKey()] || 0;
      if (!y) { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); return; }
      let tries = 0;
      const restore = () => {
        window.scrollTo({ top: y, left: 0, behavior: 'instant' });
        tries += 1;
        if (window.scrollY < y - 2 && tries < 20) setTimeout(restore, 60);
      };
      requestAnimationFrame(restore);
    };
    window.addEventListener('popstate', onPop);      // back/forward buttons
    window.addEventListener('locationchange', onLoc); // our pushState navigations

    // Intercept clicks on internal anchor links so they use SPA navigation
    // instead of triggering a full page reload.
    const onClick = (e) => {
      // Respect modifier keys / non-left clicks (open in new tab, etc.)
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      if (a.target && a.target !== '_self') return; // _blank etc.
      if (a.hasAttribute('download')) return;
      const href = a.getAttribute('href');
      if (!href || !href.startsWith('/')) return; // only internal absolute paths
      e.preventDefault();
      navigateTo(href);
    };
    document.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('locationchange', onLoc);
      document.removeEventListener('click', onClick);
    };
  }, []);

  useEffect(() => {
    const finishWithMe = () => api.me().then((u) => {
      // /me hands back a fresh token when the role changed (e.g. became an
      // owner since last login) — swap it in so /owner/* stops 403-ing.
      if (u && u.token) localStorage.setItem('jwt_token', u.token);
      setUser(u);
    }).catch((e) => {
      // Only END the session on a genuine auth failure (401 / bad token). A
      // transient network or 5xx error must NOT wipe the token — doing so used
      // to leave the app looking "logged in" (nav, admin UI) with no token, so
      // the next write failed with "No token provided" until a manual re-login.
      if (/token|auth|401/i.test(String((e && e.message) || ''))) {
        localStorage.removeItem('jwt_token');
        setUser(null);
      }
    });

    // Magic login: an owner clicked a ?magic=<token> link in a notification
    // email. Exchange it for a session, strip the token from the URL, and drop
    // into whatever page the link pointed at (dashboard / messages).
    const magic = new URLSearchParams(window.location.search).get('magic');
    if (magic) {
      api.magicLogin(magic)
        .then((res) => { if (res && res.token) localStorage.setItem('jwt_token', res.token); })
        .catch(() => { /* expired/invalid — user can still log in normally */ })
        .finally(() => {
          try { const u = new URL(window.location.href); u.searchParams.delete('magic'); window.history.replaceState({}, '', u.pathname + u.search + u.hash); } catch (_) {}
          if (localStorage.getItem('jwt_token')) finishWithMe();
        });
      return;
    }
    if (localStorage.getItem('jwt_token')) finishWithMe();
  }, []);

  // Set a meaningful document.title per route so each page is distinct in
  // search results, browser tabs, and history. /place/:id sets its own title
  // from DetailPage once the establishment is loaded.
  useEffect(() => {
    const base = 'Jewish Compass';
    const tagline = 'Jewish & Kosher Directory';
    let title;
    switch (route.view) {
      case 'home':      title = `${base} — ${tagline}`; break;
      case 'list': {
        // ANCHE IL TITOLO DELLA LINGUETTA VA TRADOTTO.
        //
        // Menachem, 27/10/2026, con /list?country=Italy&city=Rome&lang=he:
        // «quando metto un link così, dovrebbe mostrare in ebraico il
        // titolo della pagina». La pagina girava già in ebraico — dir
        // rtl, contenuto tradotto — e la linguetta del browser diceva
        // «Places in Rome, Italy».
        //
        // I pezzi erano già tutti nel dizionario: «Places», i nomi
        // delle categorie, e città e paesi (city-i18n ne porta 307).
        // Mancava passarli da t(), e un modello per l'ordine delle
        // parole: in ebraico la preposizione si attacca al nome
        // («מקומות ברומא»), quindi non si può incollare un «in» a mano.
        const bits = [];
        if (route.params.subtype) bits.push(t(route.params.subtype));
        if (route.params.type) {
          bits.push(String(route.params.type).includes(',')
            ? String(route.params.type).split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            : formatCategoryLabel(route.params.type));
        }
        // Capitalize each location word so a lowercase param (e.g. "united
        // states") still reads "United States" — matches the server-rendered
        // <title>. Don't force the tail lowercase (keeps "USA", "NYC" intact).
        const cap = (s) => String(s || '').replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
        const where = [route.params.city, route.params.area, route.params.country]
          .filter(Boolean).map((x) => t(cap(x))).join(', ');
        // Lead with a noun so the title never starts with a bare "in …".
        if (bits.length === 0) bits.push(t('Places'));
        const cosa = bits.join(' ');
        title = `${where ? t('{what} in {where}').replace('{what}', cosa).replace('{where}', where) : cosa} | ${base}`;
        break;
      }
      case 'category': {
        // Il server manda un titolo scritto per bene — «15 Kosher Restaurants
        // in Rome (2026) — Hours, Hechsher, Map» — e senza questo ramo React
        // glielo cancellava rimettendoci «Jewish Compass»: la scheda del
        // browser, il segnalibro e la cronologia perdevano tutto. Qui il
        // conteggio non c'è ancora (i risultati arrivano dopo), ma il nome
        // della categoria e la città sì, e bastano.
        const bello = (x) => String(x || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const tipo = tipoDaSlug(route.cat);
        title = `${tipo ? formatCategoryLabel(tipo) : bello(route.cat)} in ${bello(route.city)} | ${base}`;
        break;
      }
      case 'minyanim': {
        const cap = (s) => String(s || '').replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
        const where = [route.params.city, route.params.country].filter(Boolean).map((x) => t(cap(x))).join(', ');
        title = where
          ? `${t('{what} in {where}').replace('{what}', t('Minyan Times')).replace('{where}', where)} | ${base}`
          : `${t('Minyan Times')} | ${base}`;
        break;
      }
      case 'favorites': title = `${t('Favorites')} | ${base}`; break;
      case 'destinations': title = `${t('Destinations')} | ${base}`; break;
      case 'thread-reply': title = `${t('Reply')} | ${base}`; break;
      case 'review-token': title = `${t('Leave a review')} | ${base}`; break;
      case 'news':      title = `${t('News')} | ${base}`; break;
      case 'article':   title = `${t('News')} | ${base}`; break; // refined by ArticlePage once loaded
      case 'profile':   title = `${t('Profile')} | ${base}`; break;
      case 'dashboard': title = `${t('Dashboard')} | ${base}`; break;
      case 'privacy':   title = `${t('Privacy Policy')} | ${base}`; break;
      case 'terms':     title = `${t('Terms of Use')} | ${base}`; break;
      default:          title = base;
    }
    // 'country' fuori come 'detail': da «it» non si ricava «Italy», e un
    // titolo finto tipo «Jewish IT» e peggio di quello che il server ha
    // gia messo. Lo scrive CountryPage quando il nome ce l'ha davvero.
    if (route.view !== 'detail' && route.view !== 'country') document.title = title;
  }, [route]);

  // Whenever the logged-in user changes, refresh the set of favorite IDs so
  // heart icons render in the correct state across all pages.
  useEffect(() => {
    if (!user) { setFavorites(new Set()); return; }
    api.favoriteIds().then((ids) => setFavorites(new Set(ids))).catch(() => {});
  }, [user]);

  // Toggle favorite for an establishment. Optimistically updates the Set, then
  // syncs to the backend; rolls back on failure.
  const toggleFavorite = async (estId) => {
    if (!user) { setShowAuth(true); return; }
    const isFav = favorites.has(estId);
    const next = new Set(favorites);
    if (isFav) next.delete(estId); else next.add(estId);
    setFavorites(next);
    try {
      if (isFav) await api.removeFavorite(estId);
      else await api.addFavorite(estId);
    } catch (e) {
      // Roll back on failure
      setFavorites(favorites);
    }
  };

  const goHome = () => navigate('home');
  const goDashboard = () => navigate('dashboard');
  const goFavorites = () => navigate('favorites');
  const goProfile = () => navigate('profile');
  // Messages live in the profile page; ?view=messages tells it to scroll to the
  // "My messages" section so the header link lands right on the conversations.
  const goMessages = () => navigateTo('/dashboard/messages');
  // Trigger add-place flow: prompt login first if logged out; otherwise open
  // the modal directly.
  const handleAddPlace = () => {
    if (!user) { setShowAuth(true); return; }
    setShowAddPlace(true);
  };
  const handleCategorySelect = (type) => navigate('list', { type });
  const handleSearch = (search, extra = {}) => navigate('list', { ...extra, search: search || undefined });
  const handleSelectEst = (est, opts) => { setSelectedEst(est); setStartInEdit(!!(opts && opts.edit)); navigate('detail', null, est.id); };
  const handleLogout = () => {
    localStorage.removeItem('jwt_token');
    setUser(null);
    setFavorites(new Set());
    // Stay on the current page after logging out. Only redirect home from
    // views that require a logged-in user and would otherwise render empty.
    if (['dashboard', 'profile', 'favorites'].includes(route.view)) goHome();
  };

  return (
    <div style={styles.app}>
      {/* Skip-to-content link for keyboard users (visible only on focus). */}
      <a href="#main-content" className="jc-skip-link">{t('Skip to main content')}</a>
      <UpdateBanner />
      <Header user={user} onLogin={() => setShowAuth(true)} onLogout={handleLogout} onHome={goHome} onDashboard={goDashboard} onFavorites={goFavorites} onProfile={goProfile} onMessages={goMessages} onAddPlace={handleAddPlace} />
      <UserBanners user={user} />
      <main id="main-content" tabIndex={-1}>
      <React.Suspense fallback={<div style={{ padding: 48, textAlign: 'center', color: COLORS.muted }}>{t('Loading…')}</div>}>
      {(route.view === 'home' || route.view === 'register') && <HomePage onCategorySelect={handleCategorySelect} onSearch={handleSearch} onSelect={handleSelectEst} />}
      {route.view === 'list' && (
        <ListPage initialFilters={route.params} onSelect={handleSelectEst} onHome={goHome} user={user} favorites={favorites} onToggleFavorite={toggleFavorite} onLogin={() => setShowAuth(true)} />
      )}
      {route.view === 'guide' && (
        <CityGuide key={`${route.cc}/${route.city}`} cc={route.cc} city={route.city} onSelect={handleSelectEst} onHome={goHome} />
      )}
      {route.view === 'country' && (
        <CountryPage
          key={route.cc}
          cc={route.cc}
          onSelect={handleSelectEst} onHome={goHome} user={user} favorites={favorites}
          onToggleFavorite={toggleFavorite} onLogin={() => setShowAuth(true)}
        />
      )}
      {route.view === 'category' && (
        <CategoryPage
          key={`${route.cc}/${route.city}/${route.cat}`}
          cc={route.cc} city={route.city} cat={route.cat}
          onSelect={handleSelectEst} onHome={goHome} user={user} favorites={favorites}
          onToggleFavorite={toggleFavorite} onLogin={() => setShowAuth(true)}
        />
      )}
      {route.view === 'plan' && (
        <TripPlanner key={`plan/${route.cc}/${route.city}`} cc={route.cc} city={route.city} onSelect={handleSelectEst} onHome={goHome} />
      )}
      {route.view === 'route' && (
        <RoutePlanner onSelect={handleSelectEst} onHome={goHome} />
      )}
      {route.view === 'detail' && (
        <DetailPage
          // key forces a fresh mount when the route id changes — without it,
          // navigating from one place's detail page to another (e.g. after
          // creating a new place from the header) leaves DetailPage's
          // useState-held `establishment` stuck on the previous row.
          key={`uuid:${route.id}`}
          establishmentId={route.id}
          establishment={selectedEst && selectedEst.id === route.id ? selectedEst : null}
          startInEdit={startInEdit}
          user={user}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onBack={() => window.history.back()}
          onLogin={() => setShowAuth(true)}
        />
      )}
      {route.view === 'minyanim' && (
        // Keyed on the query string: MinyanPage reads its filters from the
        // URL only in useState initializers, so without the key a navigation
        // to /minyanim?country=X while already mounted (header link from a
        // city list, back/forward) would keep the stale filters and the
        // state→URL sync would immediately rewrite the address bar back.
        <MinyanPage key={window.location.search} user={user} />
      )}
      {route.view === 'detail-slug' && (
        <DetailPage
          key={`slug:${route.cc}/${route.city}/${route.slug}`}
          slugParams={{ cc: route.cc, city: route.city, slug: route.slug }}
          user={user}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onBack={() => window.history.back()}
          onLogin={() => setShowAuth(true)}
        />
      )}
      {route.view === 'favorites' && (
        <FavoritesPage user={user} onSelect={handleSelectEst} onLogin={() => setShowAuth(true)} favorites={favorites} onToggleFavorite={toggleFavorite} />
      )}
      {route.view === 'thread-reply' && <ThreadReplyPage token={route.token} />}
      {route.view === 'review-token' && <ReviewTokenPage token={route.token} />}
      {route.view === 'destinations' && <DestinationsPage />}
      {route.view === 'news' && <NewsPage />}
      {route.view === 'kashrut-changes' && <KashrutChangesPage />}
      {route.view === 'article' && <ArticlePage key={route.slug} slug={route.slug} user={user} />}
      {route.view === 'privacy' && (
        <PrivacyPage onContact={() => setShowSiteContact(true)} />
      )}
      {route.view === 'terms' && (
        <TermsPage onContact={() => setShowSiteContact(true)} />
      )}
      {route.view === 'our-story' && <OurStoryPage />}
      {route.view === 'profile' && (
        <ProfilePage user={user} onUserUpdate={setUser} onLogin={() => setShowAuth(true)} />
      )}
      {route.view === 'reset-password' && (
        <ResetPasswordPage onDone={goHome} />
      )}
      {route.view === 'verify-email' && (
        <VerifyEmailPage
          onDone={goHome}
          onUserReload={() => {
            if (localStorage.getItem('jwt_token')) api.me().then((u) => { if (u && u.token) localStorage.setItem('jwt_token', u.token); setUser(u); }).catch(() => {});
          }}
        />
      )}
      {route.view === 'not-found' && (
        <NotFoundPage onSelect={handleSelectEst} onSearch={handleSearch} />
      )}
      {route.view === 'dashboard' && user && (user.role === 'admin' || user.role === 'owner') && (
        <Dashboard
          user={user}
          onSelectEstablishment={handleSelectEst}
          initialTab={route.tab}
          onSelectTab={(t) => navigate('dashboard', { tab: t })}
        />
      )}
      {route.view === 'dashboard' && user && user.role !== 'admin' && user.role !== 'owner' && (
        <UserDashboard
          user={user}
          initialTab={route.tab}
          onSelectTab={(t) => navigate('dashboard', { tab: t })}
        />
      )}
      {route.view === 'dashboard' && !user && (
        <div style={{ maxWidth: 600, margin: '60px auto', padding: 32, textAlign: 'center' }}>
          <h2>{t('Sign in to see your messages')}</h2>
          <p>{t("Log in to view the messages and booking requests you've sent.")}</p>
          <button style={styles.button} onClick={() => setShowAuth(true)}>{t('Log in')}</button>
        </div>
      )}
      </React.Suspense>
      </main>
      {(showAuth || route.view === 'register') && (
        <AuthModal
          onClose={() => { setShowAuth(false); if (route.view === 'register') goHome(); }}
          onLogin={setUser}
          initialMode={route.view === 'register' ? 'register' : 'login'}
          initialEmail={route.view === 'register' ? (route.params.email || '') : ''}
        />
      )}
      {showSiteContact && <SiteContactModal onClose={() => setShowSiteContact(false)} />}
      {showAddPlace && user && (
        <AddPlaceModal
          user={user}
          onClose={() => setShowAddPlace(false)}
          onCreated={(created) => { setShowAddPlace(false); handleSelectEst(created); }}
        />
      )}
      <Footer onContact={() => setShowSiteContact(true)} />
    </div>
  );
}
